import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { VENUE_DB } from './constants/venueDb';
import supabase from './supabase';
import TravelChatbot from './TravelChatbot';
import {
  Globe, MapPin, Compass, Search, Calendar, Hotel, User, Clock,
  ChevronRight, ChevronLeft, Plane, Train, Car, Navigation2,
  Ticket, Utensils, Star,
  Zap, Bus, AlertTriangle, Sparkles, ArrowRight, Download,
  Heart, ShieldCheck, LogOut, Mail, X,
  CheckCircle2, Loader2, Languages, ExternalLink, Share2,
  Menu, Plus, Smartphone
} from 'lucide-react';
import { useFavorites, useAffiliateReward, useLocalTime, useGeolocation, useTranslatedDesc } from './hooks';
import { ArticlePhoto, IntelAttractionCard } from './components/ArticlePhoto';
import { localName } from './components/AttractionCard';
import { estimateTravelBetween, haversineKm } from './utils/geo';
import { mealRepickPlan, pickNearestFood } from './utils/meals';
import { fetchRealWeather } from './utils/weather';
import FeedbackPage from './components/pages/FeedbackPage';
import ContactPage from './components/pages/ContactPage';
import EnquiryPage from './components/pages/EnquiryPage';
import AboutPage from './components/pages/AboutPage';
import { T, APP_VERSION, GROQ_MODEL, GROQ_MODEL_FAST, IS_DEV,
  KLOOK_AID, TRIP_AID, TRIP_SID, KKDAY_CID, KLOOK_CITY, stay22Link, STAY22_AID, radicalStorageLink, RADICAL_STORAGE_COUPON,
  hopegooCityKey, hopegooHotelUrl, hopegooTrains, hopegooTrainStations, HOPEGOO_TRAIN_SEARCH, HOPEGOO_FLIGHT_SEARCH, HOPEGOO_BUSES, HOPEGOO_JR_PASS, HOPEGOO_AREX, hopegooIsJapan, hopegooIsArexCity, withSid,
  safeHref, getAffiliateLinks, sailyLink, sailyCouponLabel,
  CC,
  REGIONS, COUNTRY_TO_EN, POPULAR_COUNTRIES_LOCALIZED,
  applyCountryOverrides, applyCityOverrides,
  getCityTimezone, getStaticCountries, getStaticCities, STATIC_CITIES, CITY_DISTRICTS, GEO_OVERRIDES,
  S, getDistricts, availableDistricts, resolveDistrict, findCityByDistrict, matchesDistrict, districtMembers,
  isReliableImageUrl, districtLabels,
} from './constants';
import { DD, CompactDD } from './components/DD';
import { CityBanner } from './components/CityBanner';
import { KlookHotelDealsCard } from './components/KlookHotelDeals';
import { WeatherStrip } from './components/WeatherStrip';
import { InfoCards } from './components/InfoCards';
import { TaxRefundCard } from './components/TaxRefundCard';
import { DeepStrip } from './components/DeepStrip';
import { ThemeEntryStrip } from './components/pages/ThemePage';
import { CategorySection } from './components/ThemedSections';
import { RecommendCard } from './components/RecommendCard';
import { TravelExtras } from './components/TravelExtras';
import { RainyDayPicks } from './components/RainyDayPicks';
import { TripRobotPanel, TripRobotTrigger } from './components/TripRobot';
import { ActivityPicker } from './components/ActivityPicker';
import { SwapCandidateThumb } from './components/SwapCandidateThumb';
import { FavHeartButton } from './components/FavHeartButton';
import { RelevantCoupons } from './components/RelevantCoupons';
import HopeGooPanel from './components/HopeGooPanel';
import FoldRow from './components/FoldRow';
import { KlookAffBanner, KlookDynamicWidget } from './components/KlookWidgets';
import { AuthModal } from './components/AuthModal';
import { GuestGoogleGate } from './components/GuestGoogleGate';
import { TripGuideModal } from './components/TripGuideModal';
import CoverImage from './components/CoverImage';
import { RESIDENCY_CODES, residencyLabel } from './constants/residency';
import { byRankScore } from './utils/rank';
import { getDistrictPins } from './utils/districtMapPins';
import { DistrictMap, SpotMiniMap, TripMap, dayColor } from './components/maps';

// 頁尾社群連結；網址留空則該圖示不顯示
const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/travelec1985/',
  instagram: 'https://www.instagram.com/travel_essy/',
};

// City display names in every UI language -> English key, from STATIC_CITIES (zh/sc/ja/ko) and GEO_OVERRIDES.cities
// (Traditional/Simplified Chinese, Japanese, Korean). CITY_EN_MAP and CITY_DISTRICTS only know English and
// Traditional-Chinese keys, so in the 简体/日本語/한국어 UI a city shown as 东京/花莲/화롄 got no attractions and no
// district chips (found 2026-10-05). Existing keys always win.
const CITY_NAME_ALIASES = (() => {
  const out = [];
  Object.values(STATIC_CITIES).forEach(list => list.forEach(c => out.push([c.en, [c.zh, c.sc, c.ja, c.ko]])));
  Object.values((GEO_OVERRIDES && GEO_OVERRIDES.cities) || {}).forEach(list => list.forEach(c => out.push([c.en, Object.values(c.local || {})])));
  return out;
})();
CITY_NAME_ALIASES.forEach(([en, names]) => {
  const d = CITY_DISTRICTS[en];
  if (!d) return;
  names.forEach(n => { if (n && !(n in CITY_DISTRICTS)) CITY_DISTRICTS[n] = d; });
});

// Lazy chunks — AdminPanel alone is ~3.5k lines that ordinary visitors must not pay for
const AdminPanel = React.lazy(() => import('./AdminPanel'));
const DealsPage  = React.lazy(() => import('./components/pages/DealsPage'));
const EventsPage = React.lazy(() => import('./components/pages/EventsPage'));

// useAttractionPhoto — imported from hooks/useAttractionPhoto.js

// Radical Storage 行李寄存卡片 — 首頁及搜尋頁 eSIM 橫幅下方共用
const RadicalStorageCard = ({ lang }) => (
  <div style={{ border:'1.5px solid #e2e8f0', background:'#fff', borderRadius:16, padding:'12px 16px', margin:'0 0 12px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
    <img src="/Radical_Storage.png" alt="Radical Storage" style={{ height:20, width:'auto', flexShrink:0 }}/>
    <p style={{ margin:0, fontSize:11, color:'#64748b', flex:1, minWidth:120 }}>
      {(lang==='en'||lang==='ja'||lang==='ko')?'Store your bags before exploring — use code for 10% off':'逛街前先寄放行李，輸入優惠碼享10%折扣'}
    </p>
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <span style={{ fontFamily:'ui-monospace, monospace', fontSize:12, fontWeight:800, background:'#fff7e6', border:'1px solid #f59e0b', color:'#92400e', borderRadius:7, padding:'4px 9px' }}>
        {RADICAL_STORAGE_COUPON}
      </span>
      <a href={radicalStorageLink()} target="_blank" rel="sponsored noopener noreferrer"
        style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:4, background:'#0f9488', color:'#fff', borderRadius:10, padding:'7px 12px', fontSize:12, fontWeight:900, whiteSpace:'nowrap' }}>
        {(lang==='en'||lang==='ja'||lang==='ko')?'Open Link':'打開連結'}
        <ChevronRight size={14}/>
      </a>
    </div>
  </div>
);

// Country → Saily eSIM page slug mapping (saily.com/esim-<slug>/)
const ESIM_SLUGS = {
  'Japan':'japan','South Korea':'south-korea','Taiwan':'taiwan','Thailand':'thailand',
  'Singapore':'singapore','Hong Kong':'hong-kong','Macau':'macao','China':'china',
  'Vietnam':'vietnam','Malaysia':'malaysia','Philippines':'philippines','Indonesia':'indonesia',
  'Australia':'australia','New Zealand':'new-zealand','United Kingdom':'united-kingdom',
  'France':'france','Germany':'germany','Italy':'italy','Spain':'spain',
  'United States':'united-states','Canada':'canada','Turkey':'turkey',
  'United Arab Emirates':'united-arab-emirates',
};
// Saily regional/global plans — label/desc/badge keyed by lang (zh/en/ja/ko)
const ESIM_REGIONAL = [
  { slug:'asia-and-oceania', icon:'🌏',
    label:{ zh:'亞洲通', en:'Asia & Oceania', ja:'アジア・オセアニア', ko:'아시아·오세아니아' },
    desc:{ zh:'覆蓋亞洲及大洋洲多國', en:'Covers multiple Asia-Pacific destinations', ja:'アジア・オセアニアの複数の目的地をカバー', ko:'아시아·오세아니아 여러 목적지 커버' },
    badge:{ zh:'熱門', en:'Popular', ja:'人気', ko:'인기' } },
  { slug:'global', icon:'🌍',
    label:{ zh:'全球通', en:'Global', ja:'グローバル', ko:'글로벌' },
    desc:{ zh:'覆蓋全球目的地', en:'Covers destinations worldwide', ja:'世界中の目的地をカバー', ko:'전 세계 목적지 커버' },
    badge:{ zh:'靈活', en:'Flexible', ja:'柔軟', ko:'유연' } },
  { slug:'europe', icon:'🇪🇺',
    label:{ zh:'歐洲通', en:'Europe', ja:'ヨーロッパ', ko:'유럽' },
    desc:{ zh:'覆蓋 35 個歐洲國家', en:'Covers 35 European countries', ja:'ヨーロッパ35カ国をカバー', ko:'유럽 35개국 커버' },
    badge:{ zh:'', en:'', ja:'', ko:'' } },
];

// _kl helper lives in src/constants/venueDb.js (moved with VENUE_DB)

// Strip stray symbols AI models tend to insert into plain-text fields.
// Covers: replacement char, geometric shapes, dingbats, misc arrows/symbols,
// 4-byte emoji blocks (requires `u` flag), and common bullet/dagger chars.
// Emergency number overrides — cities whose country field doesn't match their actual emergency system.
// "other" is per-language since it carries a human-readable note, not just a number.
const EMERGENCY_OVERRIDE = {
  'Hong Kong':  { police:'999', ambulance:'999', fire:'999', other:{ zh:'1823 (政府熱線)', en:'1823 (Government hotline)', ja:'1823（政府ホットライン）', ko:'1823 (정부 상담 전화)' } },
  'Macau':      { police:'999', ambulance:'999', fire:'999', other:{ zh:'1000 (旅遊查詢)', en:'1000 (tourist hotline)', ja:'1000（観光ホットライン）', ko:'1000 (관광 상담 전화)' } },
  'Macao':      { police:'999', ambulance:'999', fire:'999', other:{ zh:'1000', en:'1000', ja:'1000', ko:'1000' } },
  '香港':       { police:'999', ambulance:'999', fire:'999', other:{ zh:'1823 (政府熱線)', en:'1823 (Government hotline)', ja:'1823（政府ホットライン）', ko:'1823 (정부 상담 전화)' } },
  '澳門':       { police:'999', ambulance:'999', fire:'999', other:{ zh:'1000 (旅遊查詢)', en:'1000 (tourist hotline)', ja:'1000（観光ホットライン）', ko:'1000 (관광 상담 전화)' } },
  'Taiwan':     { police:'110', ambulance:'119', fire:'119', other:{ zh:'1999 (市政服務)', en:'1999 (municipal services hotline)', ja:'1999（市民サービスホットライン）', ko:'1999 (시정 서비스 전화)' } },
  'Taipei':     { police:'110', ambulance:'119', fire:'119', other:{ zh:'1999', en:'1999', ja:'1999', ko:'1999' } },
  'Kaohsiung':  { police:'110', ambulance:'119', fire:'119', other:{ zh:'1999', en:'1999', ja:'1999', ko:'1999' } },
  'Tainan':     { police:'110', ambulance:'119', fire:'119', other:{ zh:'1999', en:'1999', ja:'1999', ko:'1999' } },
  'Hualien':    { police:'110', ambulance:'119', fire:'119', other:{ zh:'1999', en:'1999', ja:'1999', ko:'1999' } },
};
const applyEmergencyOverride = (city, emergency, lang) => {
  const ov = EMERGENCY_OVERRIDE[city];
  if (!ov) return emergency;
  return { ...ov, other: ov.other[lang] || ov.other.zh };
};

// Hong Kong facts the AI kept getting wrong by defaulting to mainland-China
// norms (no tipping, WeChat/Alipay/DiDi as if it's the only option) — kept
// as an offline fallback for when the country_facts DB table is unreachable.
// Only applied when dbFacts is actually missing — it used to fire
// unconditionally and clobber the (correctly localized) DB-sourced facts.
const FACTS_OVERRIDE = {
  zh: {
    tipping: '按照不同的餐廳而定，有些餐廳已設定10%的服務費。',
    local_apps: [
      { name:'八達通 App',     desc:'交通支付' },
      { name:'Visa / Mastercard', desc:'感應式支付' },
      { name:'AlipayHK',      desc:'流動支付' },
      { name:'WeChat Pay HK', desc:'流動支付' },
      { name:'Uber',          desc:'叫車服務' },
      { name:'滴滴出行',       desc:'叫車服務' },
      { name:'85飛的',         desc:'的士叫車' },
    ],
  },
  en: {
    tipping: 'Depends on the restaurant — some already add a 10% service charge.',
    local_apps: [
      { name:'Octopus App',     desc:'Transit payment' },
      { name:'Visa / Mastercard', desc:'Contactless payment' },
      { name:'AlipayHK',      desc:'Mobile payment' },
      { name:'WeChat Pay HK', desc:'Mobile payment' },
      { name:'Uber',          desc:'Ride-hailing' },
      { name:'DiDi',          desc:'Ride-hailing' },
      { name:'85CallCab',     desc:'Taxi booking' },
    ],
  },
  ja: {
    tipping: 'レストランによって異なり、すでに10%のサービス料を加算している店もあります。',
    local_apps: [
      { name:'Octopus App',     desc:'交通系決済' },
      { name:'Visa / Mastercard', desc:'タッチ決済' },
      { name:'AlipayHK',      desc:'モバイル決済' },
      { name:'WeChat Pay HK', desc:'モバイル決済' },
      { name:'Uber',          desc:'配車サービス' },
      { name:'DiDi',          desc:'配車サービス' },
      { name:'85CallCab',     desc:'タクシー配車' },
    ],
  },
  ko: {
    tipping: '식당마다 다르며, 일부는 이미 10% 봉사료를 포함합니다.',
    local_apps: [
      { name:'Octopus App',     desc:'교통 결제' },
      { name:'Visa / Mastercard', desc:'비접촉 결제' },
      { name:'AlipayHK',      desc:'모바일 결제' },
      { name:'WeChat Pay HK', desc:'모바일 결제' },
      { name:'Uber',          desc:'차량 호출' },
      { name:'DiDi',          desc:'차량 호출' },
      { name:'85CallCab',     desc:'택시 호출' },
    ],
  },
};
const HK_FACTS_NAMES = new Set(['香港','Hong Kong','홍콩']);
const applyFactsOverride = (city, info, dbFacts, lang) =>
  (!dbFacts && HK_FACTS_NAMES.has(city)) ? { ...info, ...(FACTS_OVERRIDE[lang] || FACTS_OVERRIDE.zh) } : info;

// country_facts DB table (see create_country_facts.sql) holds curated,
// verified currency/voltage/tipping/emergency/local_apps per country instead
// of relying on the AI to generate them fresh (and hallucinate) every time.
// COUNTRY_TO_EN maps 香港/澳門 to 'China' for search-grouping purposes, which
// is exactly the bug this table exists to route around — override those two
// specifically so their own row is looked up instead of China's.
const FACTS_COUNTRY_KEY = {
  ...COUNTRY_TO_EN,
  '香港':'Hong Kong', 'Hong Kong':'Hong Kong', '홍콩':'Hong Kong',
  '澳門':'Macau', '澳门':'Macau', 'Macau':'Macau', 'マカオ':'Macau', '마카오':'Macau',
};
const _countryFactsCache = {};
const getCountryFacts = async (countryOrCity) => {
  const key = FACTS_COUNTRY_KEY[countryOrCity] || countryOrCity;
  if (key in _countryFactsCache) return _countryFactsCache[key];
  try {
    const { data } = await supabase.from('country_facts').select('*').eq('country_key', key).maybeSingle();
    _countryFactsCache[key] = data || null;
    return data || null;
  } catch { return null; }
};
// Merge DB facts (authoritative) over whatever the AI returned; fall back to
// the hardcoded HK safety net only if the DB row is missing/unreachable.
// dbFacts.currency is only ever a static "幣別 (CODE)" name (see
// create_country_facts.sql) with no exchange rate — the AI's own currency
// string DID carry a live-guessed rate, but that guess is just whatever the
// model remembers from training, not a real number, so it's replaced here
// with a real rate from getExchangeRate (cached weekly) instead of restored.
const applyCountryFacts = async (city, info, dbFacts, targetCurrency, lang) => {
  // country_facts base columns (currency/tipping/emergency/local_apps) are
  // only ever written in Traditional Chinese; translations->'en'/'ja'/'ko'
  // holds the same facts translated for those UI languages (see
  // scripts/country_facts_translations.json). Fall back to the base
  // columns for zh or when a translation is missing.
  const t = dbFacts?.translations?.[lang];
  const localized = dbFacts ? {
    currency: t?.currency || dbFacts.currency,
    voltage: dbFacts.voltage,
    tipping: t?.tipping || dbFacts.tipping,
    emergency: t?.emergency || dbFacts.emergency,
    local_apps: t?.local_apps || dbFacts.local_apps,
  } : null;
  let currency = localized ? localized.currency : info?.currency;
  if (localized?.currency && targetCurrency) {
    const fromCode = String(localized.currency).match(/\(([A-Z]{3})\)/)?.[1];
    if (fromCode && fromCode !== targetCurrency) {
      const rate = await getExchangeRate(fromCode, targetCurrency);
      if (rate) currency = `${localized.currency} · 1 ${fromCode} ≈ ${rate.toFixed(2)} ${targetCurrency}`;
    }
  }
  const merged = localized ? {
    ...info,
    currency, voltage: localized.voltage, tipping: localized.tipping,
    emergency: localized.emergency, local_apps: localized.local_apps,
  } : info;
  return applyFactsOverride(city, merged, dbFacts, lang);
};

const cleanAIText = (s) => {
  if (!s) return s;
  return s
    .replace(/�/g, '')                        // replacement character
    .replace(/[■-◿]/g, '')               // geometric shapes ■□▲◆…
    .replace(/[☀-⛿]/g, '')               // misc symbols ☆★♦♠♥…
    .replace(/[✀-➿]/g, '')               // dingbats ✦✧❖…
    .replace(/[⬀-⯿]/g, '')               // misc arrows & symbols ⬥⬦⬧…
    .replace(/[•·∙†‡§¶※◎]/g, '')                  // common standalone bullets
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')        // misc symbols & pictographs 🔷💠…
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')        // emoticons
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')        // transport & map symbols
    .replace(/[\u{1F900}-\u{1FAFF}]/gu, '')        // supplemental symbols
    .replace(/\s{2,}/g, ' ')
    .trim();
};

// Sanity-check that AI text is actually written in the target script — the small fallback
// model occasionally hallucinates CJK character-soup (valid Unicode ideographs strung
// together into nonsense) when asked for a language it's weak at, instead of real text.
// Hiragana/katakana and hangul are reliable tells: real Japanese/Korean sentences contain
// them, while hallucinated ideograph-soup never does.
const looksLikeScript = (text, al) => {
  if (!text || typeof text !== 'string') return true;
  if (al === 'Japanese') return /[぀-ヿ]/.test(text);
  if (al === 'Korean')   return /[가-힣]/.test(text);
  if (al === 'Traditional Chinese' || al === 'Simplified Chinese') {
    // Hiragana/katakana bleeding into a Chinese-target response is an unambiguous
    // corruption tell — real zh-TW/zh-CN text never contains kana.
    if (/[぀-ヿ]/.test(text)) return false;
    // Chinese ideograph-soup can't be caught by script presence alone (it's still
    // valid Chinese characters, sometimes even real words strung together without
    // grammar) — but real sentences this long almost always use 2+ DISTINCT function
    // particles (的/了/是/在/和...); soup rarely manages more than one by chance.
    const cjkLen = (text.match(/[一-鿿]/g) || []).length;
    // Threshold raised from 8→20: fields like "transport" are legitimately
    // comma-separated lists ("地鐵、巴士、計程車等") by design (see the
    // cityInfoSys prompt) — real ones routinely clear 8 hanzi with zero
    // grammatical particles, which the check below was flagging as fake and
    // deleting from cache on every read. Genuine hallucinated ideograph-soup
    // shows up in the long prose fields (intro/taboos), which comfortably
    // exceed 20 hanzi, so this keeps that detection while stopping list
    // fields from being false-positived.
    if (cjkLen < 20) return true; // too short/list-like to judge reliably
    // A pure enumeration ("地鐵、JR、City Loop 觀光巴士、六甲山纜車") is
    // legitimate Chinese with zero grammatical particles — real lists don't
    // always add a connector (和/及/跟...) before the last item. 2+ 頓號
    // is unambiguous evidence of a real list (ideograph-soup doesn't produce
    // clean comma-separated segments), so let it pass before the particle
    // check below, which is for prose fields instead.
    if ((text.match(/、/g) || []).length >= 2) return true;
    // Lowered from >=2 distinct particles to >=1: found live in production
    // that a genuinely well-formed single-clause sentence (the "tips" field
    // is prompted as ONE practical tip) can easily use only one particle
    // from this list — e.g. "使用 Grab App 預訂車程，能避免走路找計程車
    // 的麻煩，並且能更輕鬆比較價格。" (28 hanzi, real grammatical Chinese)
    // has only "的" and was being rejected as fake, deleted from cache, and
    // regenerated every read. Requiring 2+ turned out to reject real short
    // sentences too often to be worth what it additionally catches over
    // requiring just 1 — true ideograph-soup essentially never manages even
    // a single distinct particle by chance.
    const particles = new Set(text.match(/[的了是在和與及跟或也就還有不這那會可以因為所以對於]/g) || []);
    return particles.size >= 1;
  }
  return true;
};

// Checks every city-info text field independently — joining fields into one blob
// before checking let a single coherent field (usually "intro", the easiest one
// for a weak model to fake generically) vouch for the whole response even when
// fact-heavy fields (transport, taboos, emergency numbers...) were ideograph-soup.
const cityInfoFieldsLookOk = (d, al) => {
  if (!d) return false;
  const fields = [d.intro, d.currency, d.voltage, d.transport, d.tips, d.taboos, d.tipping,
    d.emergency && typeof d.emergency === 'object' ? d.emergency.other : null];
  return fields.filter(Boolean).every(t => looksLikeScript(t, al));
};

// Ticketmaster category mapping
const TM_CLASS = { 'concerts':'music', 'sports matches':'sports' };

const fetchTicketmasterEvents = async (cityName, countryName, tmClass, year, month) => {
  try {
    const params = new URLSearchParams({ service:'ticketmaster', city:cityName, country:countryName||'', tmClass:tmClass||'Music' });
    if (year)  params.set('year',  year);
    if (month) params.set('month', month);
    const res = await fetch(`/api/events?${params}`);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
};


// ── AI Response Cache (localStorage) ─────────────────────────────────────────
const AI_CACHE_PFX = 'ec_ai_';
const TTL_GEO   = 30*24*60*60*1000;  // 30 days  — country/city lists
const TTL_INFO  =  7*24*60*60*1000;  //  7 days   — currency/exchange rate (the one part of city info that actually drifts)
const TTL_FACTS = 365*24*60*60*1000; //  1 year   — city facts that barely change (voltage/transport/taboos/tips/etc)
const TTL_ATTR  =  3*24*60*60*1000;  //  3 days  — attractions
const TTL_WX    =  6*60*60*1000;     //  6 hours — weather
const TTL_XRATE =  7*24*60*60*1000;  //  7 days  — exchange rates: refreshed weekly, not live
// TTL_DEAL (deals/pricing) and TTL_INTEL (news/intel) removed 2026-09-27 along
// with every AI-fabrication call site that used them (booking-page dining/
// accommodation/venues/concerts, fetchDeals, fetchIntel) — see those functions.

const getAICache = key => {
  try {
    const item = JSON.parse(localStorage.getItem(AI_CACHE_PFX + key));
    if (!item || Date.now() > item.exp) { localStorage.removeItem(AI_CACHE_PFX + key); return null; }
    return item.data;
  } catch { return null; }
};
const setAICache = (key, data, ttl) => {
  try { localStorage.setItem(AI_CACHE_PFX + key, JSON.stringify({ data, exp: Date.now() + ttl })); } catch {}
};

// ── Server-side AI cache (Supabase) — shared across all users ────────────────
const getServerAICache = async (key) => {
  try {
    const { data, error } = await supabase
      .from('ai_cache').select('data,expires_at').eq('cache_key', key).maybeSingle();
    if (error || !data) return null;
    if (new Date(data.expires_at) < new Date()) return null;
    return data.data;
  } catch { return null; }
};
const setServerAICache = async (key, data, ttlMs) => {
  try {
    await supabase.from('ai_cache').upsert(
      { cache_key: key, data, expires_at: new Date(Date.now() + ttlMs).toISOString() },
      { onConflict: 'cache_key' }
    );
  } catch {}
};

// Real exchange rate (not AI-guessed) — a free, no-key API updated daily
// upstream, but we only actually re-fetch once a week (TTL_XRATE) since the
// currency card only needs to be roughly current, not live-market-accurate,
// and this keeps it to one shared request per pair per week across all users.
const getExchangeRate = async (fromCode, toCode) => {
  if (!fromCode || !toCode || fromCode === toCode) return 1;
  const key = `xrate_${fromCode}_${toCode}`;
  const local = getAICache(key);
  if (typeof local === 'number') return local;
  const server = await getServerAICache(key);
  if (typeof server === 'number') { setAICache(key, server, TTL_XRATE); return server; }
  try {
    const r = await fetch(`https://open.er-api.com/v6/latest/${fromCode}`);
    if (!r.ok) return null;
    const d = await r.json();
    const rate = d?.rates?.[toCode];
    if (typeof rate !== 'number') return null;
    setAICache(key, rate, TTL_XRATE); setServerAICache(key, rate, TTL_XRATE);
    return rate;
  } catch { return null; }
};

// Global throttle — minimum 1.5s gap between consecutive Groq API calls
// prevents bursting free-tier TPM limits (llama-3.3-70b ~6k TPM/min)
let _lastGroqCall = 0;
const _groqThrottle = () => {
  const wait = Math.max(0, 1500 - (Date.now() - _lastGroqCall));
  _lastGroqCall = Date.now() + wait;
  return wait > 0 ? new Promise(r => setTimeout(r, wait)) : Promise.resolve();
};

const callAI = async (prompt, system, maxTokens = 1400, model = GROQ_MODEL, opts = {}) => {
  const { isItinerary = false, accessToken = null, guestId = null, geminiFirst = false, onProvider = null } = opts;
  const tryModel = async (m) => {
    await _groqThrottle();
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    else if (guestId) headers['X-Guest-Id'] = guestId; // 未登入訪客的免費試用
    const __reqT0 = Date.now();
    const r = await fetch('/api/groq', {
      method: 'POST',
      headers,
      body: JSON.stringify({ model: m, max_tokens: maxTokens, messages: [{ role:'system', content:system }, { role:'user', content:prompt }], isItinerary, geminiFirst }),
    });
    if (IS_DEV) console.log(`[callAI] /api/groq (${m}, max_tokens=${maxTokens}) responded in ${Date.now() - __reqT0}ms, status=${r.status}, provider=${r.headers.get('x-ai-provider')}`);
    // 只有成功回應才會帶X-AI-Provider（api/groq.js只在成功路徑set這個header）
    // ——用來讓genItinerary的P0驗證log記錄這次是Gemini還是Groq回應。
    if (r.ok && onProvider) onProvider(r.headers.get('x-ai-provider'));
    if (!r.ok) {
      // 訪客試用已用完／名額已滿／未登入 —— 不重試，交由呼叫者顯示 Google 登入
      if (isItinerary && (r.status === 403 || r.status === 401)) {
        const d = await r.json().catch(() => ({}));
        return { ok: false, status: r.status, isGuestLimit: true, body: d };
      }
      if (r.status === 429) {
        const d = await r.json().catch(() => ({}));
        if (d.error === 'daily_limit_reached') return { ok: false, status: 429, isDailyLimit: true, body: d };
        return { ok: false, status: 429, body: d }; // body already consumed — don't call r.text()
      }
      return { ok: false, status: r.status, body: await r.text() };
    }
    return { ok: true, status: r.status, body: await r.json() };
  };
  const parse = raw => {
    const clean = raw.replace(/```json[\s\S]*?```|```[\s\S]*?```/g, m => m.replace(/```json|```/g,'')).trim();
    const match = clean.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]);
    } catch {
      // Observed failure mode (not truncation — the response is complete):
      // the model occasionally drops the `{` that should open the next day
      // object, so two day objects fuse into one with a duplicate "day" key,
      // e.g. `..."dailyCost":"¥15500"},"day":2,...` instead of `},{"day":2,...`.
      // This exact boundary only ever occurs between day objects in this
      // schema, so it's safe to patch mechanically before falling back to
      // the lossier brace-depth recovery below.
      // Another observed pattern: the model drops the `}` that should close
      // the "evening" slot object before the day-level "hotel" key, e.g.
      // `..."tip":"..."，"hotel":{...}` instead of `..."tip":"..."},"hotel":{...}`.
      // "hotel" only ever appears once per day, immediately after evening
      // closes, so it's safe to insert the missing brace whenever "hotel"
      // isn't already preceded by one.
      let repaired = match[0].replace(/([^}])\s*,\s*"hotel"\s*:/g, '$1},"hotel":');
      repaired = repaired.replace(/}(\s*,\s*)"day"\s*:/g, '},{"day":');
      if (repaired !== match[0]) {
        try { return JSON.parse(repaired); } catch { /* fall through to recovery */ }
      }
      // JSON was truncated — try to recover complete day objects from partial response
      const partial = repaired;
      const recovered = {};
      const etMatch = partial.match(/"estimatedTotal"\s*:\s*"([^"]+)"/);
      if (etMatch) recovered.estimatedTotal = etMatch[1];
      const days = [];
      let depth = 0, start = -1, inStr = false, esc = false;
      for (let i = 0; i < partial.length; i++) {
        const c = partial[i];
        if (esc) { esc = false; continue; }
        if (c === '\\') { esc = true; continue; }
        if (c === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (c === '{') { if (depth === 0) start = i; depth++; }
        if (c === '}') { depth--; if (depth === 0 && start >= 0) {
          try { const o = JSON.parse(partial.slice(start, i + 1)); if (o.day && o.morning) days.push(o); } catch {}
          start = -1;
        }}
      }
      if (days.length) { recovered.days = days; return recovered; }
      return null;
    }
  };
  // Normalize top-level keys to lowercase (AI sometimes capitalizes them)
  const normalizeKeys = obj => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
  };
  try {
    let res = await tryModel(model);
    if (res.isGuestLimit) return { __guestLimit: true, reason: res.body?.error || '' };
    if (!res.ok && res.status === 429) {
      if (res.isDailyLimit) return { __dailyLimit: true }; // propagate to caller
      // Groq rate-limited on primary model — switch to fast model after 10s
      console.warn('[callAI] rate limited on primary, retrying with fast model in 10s');
      await new Promise(r => setTimeout(r, 10000));
      res = await tryModel(GROQ_MODEL_FAST);
    }
    // If fast model also rate-limited, wait 25s and final retry
    if (!res.ok && res.status === 429 && !res.isDailyLimit) {
      console.warn('[callAI] fast model also rate limited, final retry in 25s');
      await new Promise(r => setTimeout(r, 25000));
      res = await tryModel(GROQ_MODEL_FAST);
    }
    if (!res.ok) {
      console.error('[callAI]', res.status, res.body);
      if (res.status === 429) return { __rateLimited: true };
      return null;
    }
    const raw = res.body.choices?.[0]?.message?.content || '';
    if (!raw) return null;
    const parsed = parse(raw);
    return parsed ? (Array.isArray(parsed) ? parsed : normalizeKeys(parsed)) : null;
  } catch(e) { console.error('[callAI]', e); return null; }
};

// Pull out every COMPLETE {"day":N,...} object from a possibly-still-growing
// JSON string, by brace-depth scanning (same technique callAI's own
// truncation-recovery uses) — lets the itinerary UI reveal finished days
// while the model is still writing later ones, instead of waiting for the
// whole response. Ignores anything that isn't a full day object yet.
const extractCompleteDayObjects = (text) => {
  // Uses a stack of opening-brace positions rather than requiring depth to
  // return to 0 — the response is wrapped in an outer object
  // ({"estimatedTotal":...,"days":[{day:1,...},...]}), so each day object's
  // closing brace only brings depth back to 1, never 0, until the entire
  // response finishes. That made this always report 0 days (both mid-stream
  // and on the final text), silently discarding every successful stream and
  // falling back to the slow non-streaming call. Matching brace pairs via a
  // stack instead finds day objects at whatever depth they're nested at.
  const days = [];
  const stack = [];
  let inStr = false, esc = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (esc) { esc = false; continue; }
    if (c === '\\') { esc = true; continue; }
    if (c === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (c === '{') stack.push(i);
    if (c === '}') {
      const start = stack.pop();
      if (start === undefined) continue;
      try { const o = JSON.parse(text.slice(start, i + 1)); if (o && o.day && o.morning) days.push(o); } catch {}
    }
  }
  return days;
};

// ── P0 摸底驗證層（2026-09-24）──────────────────────────────────────────────
// prompt裡的規則（不可重複景點、只能用清單內景點、時段邏輯）目前完全靠AI
// 「盡量遵守」，沒有任何程式碼保證。這裡先只做「量測違規率」，只寫
// console.warn，不做自動修正/局部重生成——目的是先摸底看違規頻率高不高，
// 再決定要不要投入資源做修正機制。之後若要接自動修正，可直接沿用這幾個函式。
// lunch 只在中間日（第 2 天至倒數第 2 天）出現，其他日子冇呢個 key
const ITIN_SLOTS = ['morning', 'lunch', 'afternoon', 'evening'];
// 某日實際的時段次序：有 lunch key（即使已清空為 null）先計入
const daySlotOrder = (day) => ['morning', ...(day && 'lunch' in day ? ['lunch'] : []), 'afternoon', 'evening'];
const stripTrailingParenForValidation = (s) => (s || '').replace(/[（(][^）(]*[）)]\s*$/u, '');
// 車站/機場是抵達日、離開日、換城市當天本來就會重複出現、也不會在觀光景點
// 清單裡的交通樞紐——2026-09-24跑回歸測試集時發現這類地點被誤判「捏造」
// 「重複」，已與使用者確認這是正常行為，兩個validator都要排除。
const TRANSIT_HUB_RE = /車站|機場|港口|碼頭|airport|station|駅/i;

// 是否為捏造（不在VERIFIED REAL VENUE LIST內）——candidateNames需已用norm()處理過
const validateVenueInList = (itinerary, candidateNames, norm) => {
  if (!candidateNames || !candidateNames.size) return [];
  const issues = [];
  (itinerary.days || []).forEach(day => {
    ITIN_SLOTS.forEach(slot => {
      const loc = day[slot]?.location;
      if (!loc || TRANSIT_HUB_RE.test(loc)) return;
      const key = norm(loc), keyStripped = norm(stripTrailingParenForValidation(loc));
      if (!candidateNames.has(key) && !candidateNames.has(keyStripped)) {
        issues.push({ day: day.day, slot, location: loc, reason: 'not_in_verified_list' });
      }
    });
  });
  return issues;
};

// 全程重複景點（同一景點出現在兩天以上）
const validateNoDuplicates = (itinerary, norm) => {
  const issues = [];
  const seen = new Map();
  (itinerary.days || []).forEach(day => {
    ITIN_SLOTS.forEach(slot => {
      const loc = day[slot]?.location;
      if (!loc || TRANSIT_HUB_RE.test(loc)) return;
      const key = norm(loc);
      if (seen.has(key)) {
        issues.push({ day: day.day, slot, location: loc, reason: 'duplicate_venue', firstSeenDay: seen.get(key).day });
      } else {
        seen.set(key, { day: day.day });
      }
    });
  });
  return issues;
};

// 時段錯置——只保留規則4字面上明確、不需要營業時間資料就能判斷的方向：
// 夜市/酒吧/居酒屋這類本質上夜間限定經營的場所，不該出現在早上。
// （公園/博物館/寺廟是否能排進晚上，取決於當天實際營業時間，目前資料庫
// 沒有這個欄位，硬用關鍵字判斷容易誤判——2026-09-24已與使用者確認保留
// 規則4原本字面定義，公園/博物館/寺廟一律視為只能早上，這條連同下面
// 的NIGHT_ONLY一起留著，不因為「可能有夜間例外」而放寬。）
const NIGHT_ONLY_RE = /夜市|酒吧|夜店|clubbing|bar\b|night\s*market|居酒屋|夜遊/i;
const MORNING_ONLY_RE = /早市|早餐|breakfast|博物館|美術館|museum|寺|神社|庭園|公園/i;
const validateTimeSlots = (itinerary) => {
  const issues = [];
  (itinerary.days || []).forEach(day => {
    const m = `${day.morning?.activity || ''}${day.morning?.location || ''}`;
    const e = `${day.evening?.activity || ''}${day.evening?.location || ''}`;
    if (NIGHT_ONLY_RE.test(m)) issues.push({ day: day.day, slot: 'morning', location: day.morning?.location, reason: 'night_venue_in_morning' });
    if (MORNING_ONLY_RE.test(e)) issues.push({ day: day.day, slot: 'evening', location: day.evening?.location, reason: 'morning_venue_in_evening' });
  });
  return issues;
};

// Streaming counterpart to callAI, used ONLY for itinerary generation so the
// loading screen can reveal days as they're written instead of one long
// silent wait. Talks to the SAME /api/groq endpoint with stream:true, which
// streams from Gemini only (no dual-provider race — that complexity stays
// in callAI's non-streaming path, which this function's caller falls back
// to on any failure, so reliability never regresses). Returns the full
// accumulated text, or null if the stream produced nothing usable.
const streamAI = async (prompt, system, maxTokens, accessToken, onDelta) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const r = await fetch('/api/groq', {
      method: 'POST',
      headers,
      body: JSON.stringify({ model: GROQ_MODEL, max_tokens: maxTokens, messages: [{ role:'system', content:system }, { role:'user', content:prompt }], isItinerary: true, stream: true }),
    });
    if (!r.ok || !r.body) return null;
    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let full = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      full += decoder.decode(value, { stream: true });
      onDelta(full);
    }
    return full || null;
  } catch (e) { console.warn('[streamAI]', e.message); return null; }
};

// Short fingerprint of translatable text — cache key changes when the admin edits the article, auto-busting stale translations
const fingerprint = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

// Translate an article's text fields into the active UI language on demand.
// Cached locally (localStorage) and shared across all users via the Supabase ai_cache table,
// so the same article+language is only ever translated by AI once.
const translateArticle = async (article, lang) => {
  const al = T[lang]?.aiLang;
  if (!al || lang === 'zh-HK') return null;
  const src = {
    title:    article.title || '',
    subtitle: article.subtitle || '',
    teaser:   article.teaser || '',
    sections: (article.sections || []).map(s => ({ heading: s.heading || '', content: s.content || s.body || '', secret_tip: s.secret_tip || '', mustTry: s.mustTry || '', photoSpot: s.photoSpot || '', transport: s.transport || '', transport_station: s.transport_station || '', content_source: s.content_source || '', place_id: s.place_id || null })),
    tips:     article.tips || [],
    bestFor:  article.bestFor || '',
  };
  const cacheKey = `article_tr_${article.id}_${lang}_${fingerprint(JSON.stringify(src))}`;

  const local = getAICache(cacheKey);
  if (local) return local;
  const server = await getServerAICache(cacheKey);
  if (server) { setAICache(cacheKey, server, TTL_GEO); return server; }

  const out = await callAI(
    `Translate this travel article from Traditional Chinese into ${al}, preserving the exact JSON shape and array lengths. Translate every text value naturally; keep place names and brand names recognisable in the target language.\n\n${JSON.stringify(src)}`,
    `Reply ONLY a valid JSON object with this exact shape, no markdown: {"title":"…","subtitle":"…","teaser":"…","sections":[{"heading":"…","content":"…"}],"tips":["…"],"bestFor":"…"}`,
    3000, GROQ_MODEL
  );
  if (!out?.title || !Array.isArray(out.sections)) return null;

  const result = {
    id: article.id, lang,
    title:    out.title,
    subtitle: out.subtitle || '',
    teaser:   out.teaser || '',
    sections: article.sections.map((s, i) => ({
      ...s,
      heading: out.sections[i]?.heading ?? s.heading,
      content: out.sections[i]?.content ?? (s.content || s.body),
    })),
    tips:    (Array.isArray(out.tips) && out.tips.length === (article.tips||[]).length) ? out.tips : article.tips,
    bestFor: out.bestFor || article.bestFor,
  };
  setAICache(cacheKey, result, TTL_GEO);
  setServerAICache(cacheKey, result, TTL_GEO);
  return result;
};

const fetchEventbriteEvents = async (city, country, year, month) => {
  try {
    const params = new URLSearchParams({ service:'eventbrite', city, country:country||'' });
    if (year)  params.set('year',  year);
    if (month) params.set('month', month);
    const res = await fetch(`/api/events?${params}`);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
};

// ── Viator Partner API — real attraction/ticket products ─────────────────────
// eslint-disable-next-line no-unused-vars
const VIATOR_CAT_TAGS = {
  'theme parks':         [12027, 11872],
  'museums':             [12045, 11868],
  'zoos':                [12047],
  'aquariums':           [12051],
  'exhibitions':         [12057, 11868],
  'tourist attractions': [11872, 12057],
  'concerts':            [12069],
  'sports matches':      [12089, 12091],
};

// eslint-disable-next-line no-unused-vars
const fetchViatorProducts = async (city, country, category, lang = 'zh-HK') => {
  const searchTerm = `${city} ${category}`;
  try {
    const res = await fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ service: 'viator', searchTerm, currency: 'HKD', count: 10, lang }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const items = data?.products?.results || [];
    const hrLabel  = lang==='en'?'hr':lang==='ja'?'時間':lang==='ko'?'시간':'小時';
    const fromLabel= lang==='en'?'From HK$':lang==='ja'?'HK$から':lang==='ko'?'HK$부터':'HK$';
    const seeLabel = lang==='en'?'View Details':lang==='ja'?'詳細を見る':lang==='ko'?'자세히 보기':'查看詳情';
    const cancelLabel = lang==='en'?'Free Cancellation':lang==='ja'?'無料キャンセル':lang==='ko'?'무료 취소':'免費取消';
    const soldOutLabel= lang==='en'?'Selling Fast':lang==='ja'?'売れ筋':lang==='ko'?'마감 임박':'快售罄';
    const reviewLabel = lang==='en'?'reviews':lang==='ja'?'件':lang==='ko'?'개':'則';
    return items.map(p => {
      const img   = p.images?.find(i => i.isCover)?.variants?.sort((a,b)=>b.width-a.width)?.[0]?.url || p.images?.[0]?.variants?.[0]?.url || '';
      const price = p.pricing?.summary?.fromPrice;
      const mins  = p.duration?.fixedDurationInMinutes || p.duration?.variableDurationFromMinutes;
      const dur   = mins ? `${Math.round(mins/60)}${hrLabel}${p.duration?.variableDurationFromMinutes?'+':''}` : '';
      const flags = p.flags || [];
      const badge = flags.includes('FREE_CANCELLATION') ? cancelLabel : flags.includes('LIKELY_TO_SELL_OUT') ? soldOutLabel : '';
      return {
        title:    p.title || '',
        venue:    city,
        desc:     (p.description || '').slice(0, 100).replace(/<[^>]*>/g, ''),
        price:    price ? `${fromLabel}${Math.round(price)}起` : seeLabel,
        platform: 'Viator',
        link:     p.productUrl || `https://www.viator.com/search/${encodeURIComponent(searchTerm)}`,
        highlight: [badge, p.reviews?.combinedAverageRating ? `⭐ ${p.reviews.combinedAverageRating.toFixed(1)} (${p.reviews.totalReviews}${reviewLabel})` : ''].filter(Boolean).join(' · '),
        duration: dur,
        img,
      };
    }).filter(p => p.title);
  } catch (e) { console.error('Viator:', e); return null; }
};

// ── Hong Kong: LCSD (data.gov.hk) free events API ───────────────────────────
// Categories: 1=Music 2=Dance 3=Drama 4=Film 5=ChineseOpera 6=Art 7=Other
const LCSD_CAT = { concerts:'1', exhibitions:'6', default:'1' };

const fetchLCSDEvents = async (category) => {
  const cat = LCSD_CAT[category] || LCSD_CAT.default;
  try {
    const res = await fetch(
      `https://www.lcsd.gov.hk/datagovhk/event/events_by_cat.php?cat=${cat}&lang=tc`,
      { headers: { Accept: 'application/json' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return (data?.events || []).slice(0, 10).map(ev => ({
      title:    ev.titleName || ev.title || '',
      venue:    ev.venueName || '',
      desc:     ev.presenter ? `主辦：${ev.presenter}` : '',
      price:    ev.price || '查看詳情',
      platform: 'URBTIX 城市售票',
      link:     ev.internet || 'https://www.urbtix.hk',
      highlight: ev.dateAndTime ? `📅 ${ev.dateAndTime}` : '',
      duration: '',
    }));
  } catch { return null; }
};

// HK-specific platform search cards
const generateHKCards = (category) => {
  const isConcert = category === 'concerts';
  const isSports  = category === 'sports matches';
  const cards = [];

  if (isConcert) {
    cards.push(
      { title:'Timable — 香港演唱會', venue:'香港各大表演場地', desc:'Timable 香港最完整的演唱會及表演活動資訊，每日更新', price:'實時票價', platform:'Timable', link:'https://timable.com/hk/zh/event?search=%E6%BC%94%E5%94%B1%E6%9C%83', highlight:'🎤 最新演唱會資訊', duration:'' },
      { title:'HKTicketing 城市售票網', venue:'香港各大表演場地', desc:'香港最大商業票務平台，涵蓋所有本地及國際演唱會', price:'實時票價', platform:'HKTicketing', link:'https://www.hkticketing.com/events?cat=CNT', highlight:'🎫 即買即用電子票', duration:'' },
      { title:'URBTIX 康文署售票', venue:'香港文化中心、大會堂、葵青劇院', desc:'政府主辦文化演出，包括管弦樂、爵士樂、戲劇等', price:'HK$120起', platform:'URBTIX', link:'https://www.urbtix.hk', highlight:'🎵 古典音樂・文化演出', duration:'' },
      { title:'Cityline 售票', venue:'香港各演出場地', desc:'大型演唱會及表演門票，涵蓋 K-pop、流行音樂', price:'實時票價', platform:'Cityline', link:'https://www.cityline.com/Events/Category/Concert.html', highlight:'🌟 K-pop・流行演唱會', duration:'' },
      { title:'Peatix 本地活動', venue:'香港各區', desc:'本地獨立演出、音樂表演及藝術活動', price:'實時票價', platform:'Peatix', link:'https://peatix.com/search?q=concert+hongkong&location=HK', highlight:'🎸 本地藝人・獨立音樂', duration:'' },
    );
  }

  if (isSports) {
    cards.push(
      { title:'HKFA 香港足球總會', venue:'旺角大球場・元朗大球場', desc:'香港超級聯賽、港超聯及國際賽事門票', price:'HK$60起', platform:'HKFA', link:'https://www.hkfa.com/ch/match/index', highlight:'⚽ 港超聯賽程', duration:'' },
      { title:'香港欖球七人賽 HK Sevens', venue:'香港大球場', desc:'全球頂級欖球賽事，每年4月在香港大球場舉行', price:'HK$400起', platform:'HK Sevens', link:'https://www.hksevens.com/tickets', highlight:'🏉 年度頂級欖球盛事', duration:'3日賽事' },
      { title:'香港馬術六環賽', venue:'沙田馬場・跑馬地', desc:'香港賽馬會主辦賽事及夜馬入場', price:'HK$10起', platform:'HKJC', link:'https://www.hkjc.com/home/chinese/racing/', highlight:'🏇 夜馬・日馬', duration:'' },
      { title:'Peatix 香港體育賽事', venue:'香港各運動場館', desc:'本地體育賽事、馬拉松、單車賽等活動門票', price:'實時票價', platform:'Peatix', link:'https://peatix.com/search?q=sports+hongkong&location=HK', highlight:'🏃 本地運動賽事', duration:'' }
    );
  }

  return cards;
};

// Region-specific ticketing platform cards for concerts / sports
const generateConcertCards = (cat, city, country, lang = 'zh-HK') => {
  const isConcert = cat === 'concerts';
  const isSports  = cat === 'sports matches';
  const cards = [];

  // Determine region by country / city name
  const c = (country || '').toLowerCase();
  const ci = (city || '').toLowerCase();

  const isJapan     = c.includes('japan') || c.includes('日本') || ci.includes('tokyo') || ci.includes('osaka') || ci.includes('kyoto') || ci.includes('sapporo') || ci.includes('fukuoka') || ci.includes('nagoya') || ci.includes('東京') || ci.includes('大阪') || ci.includes('京都') || ci.includes('札幌') || ci.includes('福岡') || ci.includes('名古屋');
  const isKorea     = c.includes('korea') || c.includes('韓國') || c.includes('한국') || ci.includes('seoul') || ci.includes('busan') || ci.includes('incheon') || ci.includes('jeju') || ci.includes('首爾') || ci.includes('釜山') || ci.includes('仁川') || ci.includes('濟州');
  const isTaiwan    = c.includes('taiwan') || c.includes('台灣') || c.includes('臺灣') || ci.includes('taipei') || ci.includes('kaohsiung') || ci.includes('taichung') || ci.includes('台北') || ci.includes('高雄') || ci.includes('台中');
  const isChina     = c.includes('china') || c.includes('中國') || c.includes('中国') || ci.includes('beijing') || ci.includes('shanghai') || ci.includes('guangzhou') || ci.includes('chengdu') || ci.includes('shenzhen') || ci.includes('北京') || ci.includes('上海') || ci.includes('廣州') || ci.includes('成都') || ci.includes('深圳');
  const isSingapore = c.includes('singapore') || c.includes('新加坡') || ci.includes('singapore') || ci.includes('新加坡');
  const isMalaysia  = c.includes('malaysia') || c.includes('馬來西亞') || ci.includes('kuala lumpur') || ci.includes('penang') || ci.includes('吉隆坡') || ci.includes('檳城');
  const isThailand  = c.includes('thailand') || c.includes('泰國') || ci.includes('bangkok') || ci.includes('phuket') || ci.includes('chiang mai') || ci.includes('曼谷') || ci.includes('普吉') || ci.includes('清邁');
  const isAustralia = c.includes('australia') || c.includes('澳洲') || c.includes('澳大利亞') || ci.includes('sydney') || ci.includes('melbourne') || ci.includes('brisbane') || ci.includes('perth') || ci.includes('悉尼') || ci.includes('墨爾本') || ci.includes('布里斯班');
  const isUK        = c.includes('united kingdom') || c.includes('england') || c.includes('英國') || ci.includes('london') || ci.includes('manchester') || ci.includes('edinburgh') || ci.includes('倫敦') || ci.includes('曼徹斯特');
  const isEurope    = c.includes('france') || c.includes('germany') || c.includes('spain') || c.includes('italy') || c.includes('法國') || c.includes('德國') || c.includes('西班牙') || c.includes('意大利') || c.includes('義大利') || ci.includes('paris') || ci.includes('berlin') || ci.includes('barcelona') || ci.includes('巴黎') || ci.includes('柏林') || ci.includes('巴塞隆拿') || ci.includes('巴塞罗那');
  const isUSA       = c.includes('united states') || c.includes('usa') || c.includes('美國') || ci.includes('new york') || ci.includes('los angeles') || ci.includes('chicago') || ci.includes('las vegas') || ci.includes('紐約') || ci.includes('洛杉磯') || ci.includes('芝加哥') || ci.includes('拉斯維加斯');

  const cityEnc = encodeURIComponent(city);

  if (isJapan) {
    if (isConcert) {
      cards.push(
        { title:`e+ イープラス — ${city}演唱會`, venue:city, desc:'日本最大票務平台，涵蓋演唱會、Live House、音樂祭', price:'查看詳情', platform:'e+', link:`https://eplus.jp/sf/search?q=${cityEnc}+concert`, highlight:'🎤 日本No.1票務平台', duration:'' },
        { title:`チケットぴあ — ${city}演唱會`, venue:city, desc:'Ticket Pia 日本老牌票務，Live演出・音樂祭門票', price:'查看詳情', platform:'Ticket Pia', link:`https://t.pia.jp/pia/search_all.do?kw=${cityEnc}+concert`, highlight:'🎵 Live演出・音樂祭', duration:'' },
        { title:`ローソンチケット — ${city}演唱會`, venue:city, desc:'Lawson Ticket 便利店取票，涵蓋大型演唱會', price:'查看詳情', platform:'Lawson Ticket', link:`https://l-tike.com/search/?q=${cityEnc}+concert`, highlight:'🏪 便利店即取票', duration:'' },
        { title:`LiveNation Japan — ${city}`, venue:city, desc:'國際流行巨星日本巡演，環球音樂主辦', price:'查看詳情', platform:'LiveNation', link:`https://www.livenation.jp/search?q=${cityEnc}`, highlight:'🌟 國際巨星日本公演', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`Jリーグ — ${city}球賽`, venue:city, desc:'J聯賽足球賽事，球場氣氛一流', price:'¥1,500起', platform:'J.League', link:`https://www.jleague.jp/match/`, highlight:'⚽ J聯賽賽程', duration:'' },
        { title:`チケットぴあ — ${city}スポーツ`, venue:city, desc:'野球・籃球・相撲等各類體育賽事', price:'查看詳情', platform:'Ticket Pia', link:`https://t.pia.jp/pia/search_all.do?kw=${cityEnc}+sports`, highlight:'⚾ 野球・籃球賽事', duration:'' },
        { title:`e+ スポーツ — ${city}`, venue:city, desc:'e+ 體育賽事門票，棒球・足球・格鬥技', price:'查看詳情', platform:'e+', link:`https://eplus.jp/sf/search?q=${cityEnc}+sports`, highlight:'🏟️ 體育館賽事', duration:'' },
      );
    }
  } else if (isKorea) {
    if (isConcert) {
      cards.push(
        { title:`인터파크 Interpark — ${city}콘서트`, venue:city, desc:'韓國最大票務平台，K-pop演唱會・音樂節', price:'查看詳情', platform:'Interpark', link:`https://tickets.interpark.com/search?keyword=${cityEnc}+concert`, highlight:'🎤 K-pop演唱會門票', duration:'' },
        { title:`멜론티켓 Melon Ticket — ${city}`, venue:city, desc:'Melon音樂串流旗下票務，偶像演唱會主要平台', price:'查看詳情', platform:'Melon Ticket', link:`https://ticket.melon.com/search/?keyword=${cityEnc}`, highlight:'🎵 偶像・K-pop', duration:'' },
        { title:`YES24 티켓 — ${city}공연`, venue:city, desc:'YES24 書店旗下票務，演出・展覽', price:'查看詳情', platform:'YES24', link:`https://ticket.yes24.com/search/?kwd=${cityEnc}+concert`, highlight:'🎫 韓國熱門演出', duration:'' },
        { title:`텐마켓 Ten Markets — ${city}`, venue:city, desc:'韓國二手票務，限量演唱會票轉讓', price:'查看詳情', platform:'Ten Markets', link:`https://www.tenmarkets.com/search?q=${cityEnc}`, highlight:'🔄 二手轉讓票', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`KBO 韓國職棒 — ${city}`, venue:city, desc:'韓國職業棒球聯賽，現場氣氛狂熱', price:'KRW 10,000起', platform:'KBO', link:`https://www.koreabaseball.com/Schedule/Schedule.aspx`, highlight:'⚾ 韓國職棒賽事', duration:'' },
        { title:`K리그 K-League — ${city}`, venue:city, desc:'韓國職業足球聯賽門票', price:'KRW 8,000起', platform:'K-League', link:`https://ticket.interpark.com/search?keyword=${cityEnc}+football`, highlight:'⚽ K聯賽足球', duration:'' },
        { title:`인터파크 스포츠 — ${city}`, venue:city, desc:'Interpark 體育賽事，籃球・排球・格鬥', price:'查看詳情', platform:'Interpark', link:`https://tickets.interpark.com/search?keyword=${cityEnc}+sports`, highlight:'🏀 籃球・排球賽事', duration:'' },
      );
    }
  } else if (isTaiwan) {
    if (isConcert) {
      cards.push(
        { title:`KKTIX — ${city}演唱會`, venue:city, desc:'台灣最大票務平台，演唱會・音樂節・展覽', price:'查看詳情', platform:'KKTIX', link:`https://kktix.com/events?tag=concert`, highlight:'🎤 台灣演唱會首選', duration:'' },
        { title:`寬宏藝術 — ${city}演唱會`, venue:city, desc:'寬宏藝術主辦大型演唱會，歷史最悠久的台灣票務', price:'查看詳情', platform:'寬宏', link:`https://www.kham.com.tw/category/concerts`, highlight:'🎵 大型演唱會主辦', duration:'' },
        { title:`ibon售票 — ${city}演出`, venue:city, desc:'7-11 ibon 便利店取票，演唱會・舞台劇', price:'查看詳情', platform:'ibon', link:`https://www.ibon.com.tw/product_search_result.aspx#?type=concert`, highlight:'🏪 便利店即取票', duration:'' },
        { title:`拓元售票 — ${city}`, venue:city, desc:'拓元票務，台灣小巨蛋・國立劇院等大型場館演出', price:'查看詳情', platform:'拓元', link:`https://www.ticketplus.com.tw/search?q=${cityEnc}`, highlight:'🎫 大型場館指定票務', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`中華職棒 CPBL — ${city}`, venue:city, desc:'台灣職業棒球，樂天桃猿・中信兄弟・富邦悍將', price:'NT$120起', platform:'CPBL', link:`https://www.cpbl.com.tw/schedule`, highlight:'⚾ 台灣職棒賽事', duration:'' },
        { title:`KKTIX 體育賽事 — ${city}`, venue:city, desc:'KKTIX 運動賽事門票，馬拉松・籃球・電競', price:'查看詳情', platform:'KKTIX', link:`https://kktix.com/events?tag=sports`, highlight:'🏃 體育賽事門票', duration:'' },
      );
    }
  } else if (isChina) {
    if (isConcert) {
      cards.push(
        { title:`大麦网 — ${city}演唱会`, venue:city, desc:'阿里巴巴旗下，中国最大票务平台，演唱会・音乐节', price:'查看详情', platform:'大麦', link:`https://www.damai.cn/search/?from=top&query=${cityEnc}+演唱会`, highlight:'🎤 中国演唱会首选', duration:'' },
        { title:`猫眼演出 — ${city}演唱会`, venue:city, desc:'猫眼影票旗下演出票务，流行演唱会・话剧', price:'查看详情', platform:'猫眼', link:`https://maoyan.com/concerts?city=${cityEnc}`, highlight:'🎵 猫眼演出票务', duration:'' },
        { title:`票星球 — ${city}演出`, venue:city, desc:'票星球专注明星演唱会，原价票+二手票', price:'查看详情', platform:'票星球', link:`https://www.piaoxingqiu.com/search?city=${cityEnc}&kw=演唱会`, highlight:'⭐ 明星演唱会专属', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`大麦网体育 — ${city}赛事`, venue:city, desc:'大麦网体育门票，足球・篮球・网球・格斗', price:'查看详情', platform:'大麦', link:`https://www.damai.cn/search/?from=top&query=${cityEnc}+体育`, highlight:'⚽ 体育赛事门票', duration:'' },
        { title:`CBA 中国篮球 — ${city}`, venue:city, desc:'CBA 职业篮球联赛现场观赛', price:'查看详情', platform:'CBA', link:`https://www.cbaleague.com/schedule/`, highlight:'🏀 CBA职业篮球', duration:'' },
      );
    }
  } else if (isSingapore) {
    if (isConcert) {
      cards.push(
        { title:`SISTIC — ${city} Concerts`, venue:'Singapore Indoor Stadium / Star Theatre', desc:'Singapore\'s main ticketing platform for concerts and live shows', price:'From SGD 68', platform:'SISTIC', link:`https://www.sistic.com.sg/events/search?q=concert`, highlight:'🎤 Singapore\'s Official Ticketer', duration:'' },
        { title:`StarHub Live — ${city}`, venue:'Singapore', desc:'International concert tours and live entertainment in Singapore', price:'查看詳情', platform:'StarHub', link:`https://www.starhublive.com/concerts/`, highlight:'🌟 International Tours', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`SISTIC Sports — Singapore`, venue:'National Stadium', desc:'Singapore sports events — football, athletics, rugby', price:'From SGD 15', platform:'SISTIC', link:`https://www.sistic.com.sg/events/search?q=sports`, highlight:'⚽ National Stadium Events', duration:'' },
        { title:`Singapore Premier League`, venue:'Jalan Besar Stadium', desc:'Local football league matches in Singapore', price:'From SGD 10', platform:'SPL', link:`https://spl.sg/fixtures/`, highlight:'⚽ Local Football', duration:'' },
      );
    }
  } else if (isMalaysia) {
    if (isConcert) {
      cards.push(
        { title:`TicketFlap — ${city} Concerts`, venue:city, desc:'Malaysia\'s leading ticketing platform for concerts and events', price:'查看詳情', platform:'TicketFlap', link:`https://www.ticketflap.com/search?q=concert+${cityEnc}`, highlight:'🎤 Malaysia Concerts', duration:'' },
        { title:`StubHub Malaysia — ${city}`, venue:city, desc:'International and regional concert tickets in Malaysia', price:'查看詳情', platform:'StubHub', link:`https://www.stubhub.com/search?q=${cityEnc}+concert`, highlight:'🎵 International Acts', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`TicketFlap Sports — ${city}`, venue:city, desc:'Malaysia sports events — football, badminton, athletics', price:'查看詳情', platform:'TicketFlap', link:`https://www.ticketflap.com/search?q=sports+${cityEnc}`, highlight:'🏸 Badminton · Football', duration:'' },
      );
    }
  } else if (isThailand) {
    if (isConcert) {
      cards.push(
        { title:`Thai Ticket Major — ${city}演唱會`, venue:city, desc:'泰國最大票務平台，涵蓋國際及本地演唱會', price:'查看詳情', platform:'ThaiTicketMajor', link:`https://www.thaiticketmajor.com/concert/`, highlight:'🎤 泰國演唱會首選', duration:'' },
        { title:`Eventpop — ${city}`, venue:city, desc:'泰國音樂節・戶外演出・電子音樂活動', price:'查看詳情', platform:'Eventpop', link:`https://www.eventpop.me/search?q=concert`, highlight:'🎵 音樂節・戶外演出', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`泰超聯賽 Thai League — ${city}`, venue:city, desc:'泰國職業足球聯賽，現場氛圍熱烈', price:'THB 100起', platform:'Thai League', link:`https://www.thai-league.co.th/schedule`, highlight:'⚽ 泰超聯賽', duration:'' },
      );
    }
  } else if (isAustralia) {
    if (isConcert) {
      cards.push(
        { title:`Ticketek — ${city} Concerts`, venue:city, desc:'Australia\'s largest ticketing platform — major concerts & tours', price:'查看詳情', platform:'Ticketek', link:`https://premier.ticketek.com.au/shows/genre.aspx?genreCode=MUSIC`, highlight:'🎤 Australia\'s #1 Ticketer', duration:'' },
        { title:`Moshtix — ${city}`, venue:city, desc:'Independent music events, festivals and club shows in Australia', price:'查看詳情', platform:'Moshtix', link:`https://www.moshtix.com.au/v2/search?q=${cityEnc}+concert`, highlight:'🎵 Indie · Festival · Club', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`Ticketek Sports — ${city}`, venue:city, desc:'AFL, NRL, cricket and major sports events in Australia', price:'查看詳情', platform:'Ticketek', link:`https://premier.ticketek.com.au/shows/genre.aspx?genreCode=SPORT`, highlight:'🏉 AFL · NRL · Cricket', duration:'' },
        { title:`Ticketmaster AU — ${city}`, venue:city, desc:'Ticketmaster Australia — international sports and tours', price:'查看詳情', platform:'Ticketmaster AU', link:`https://www.ticketmaster.com.au/search?q=${cityEnc}+sports`, highlight:'🏟️ Major Venues', duration:'' },
      );
    }
  } else if (isUK) {
    if (isConcert) {
      cards.push(
        { title:`Ticketmaster UK — ${city} Concerts`, venue:city, desc:'UK\'s top ticketing platform — arena tours, festival headline acts', price:'查看詳情', platform:'Ticketmaster UK', link:`https://www.ticketmaster.co.uk/search?q=${cityEnc}+concert`, highlight:'🎤 Arena · O2 · Hyde Park', duration:'' },
        { title:`See Tickets — ${city}`, venue:city, desc:'Independent gigs, festivals and theatre in the UK', price:'查看詳情', platform:'See Tickets', link:`https://www.seetickets.com/category/concerts?q=${cityEnc}`, highlight:'🎵 Indie · Glastonbury · Festivals', duration:'' },
        { title:`Dice — ${city}`, venue:city, desc:'App-based gig discovery for underground and emerging artists', price:'查看詳情', platform:'Dice', link:`https://dice.fm/events?q=${cityEnc}+music`, highlight:'🎸 Underground · Emerging Acts', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`Ticketmaster UK Sports — ${city}`, venue:city, desc:'Premier League football, rugby, tennis and major UK sports', price:'查看詳情', platform:'Ticketmaster UK', link:`https://www.ticketmaster.co.uk/search?q=${cityEnc}+sports`, highlight:'⚽ Premier League · Rugby · Tennis', duration:'' },
        { title:`StubHub UK — ${city}`, venue:city, desc:'Secondary market for UK sports events — football, cricket, F1', price:'查看詳情', platform:'StubHub', link:`https://www.stubhub.co.uk/search?q=${cityEnc}+sports`, highlight:'🏏 Cricket · F1 · Football', duration:'' },
      );
    }
  } else if (isEurope) {
    if (isConcert) {
      cards.push(
        { title:`Ticketmaster EU — ${city} Concerts`, venue:city, desc:'Europe-wide concert and festival tickets', price:'查看詳情', platform:'Ticketmaster', link:`https://www.ticketmaster.com/search?q=${cityEnc}+concert`, highlight:'🎤 European Tours', duration:'' },
        { title:`Songkick — ${city}`, venue:city, desc:'Concert discovery app — track your favourite artists\' tour dates', price:'查看詳情', platform:'Songkick', link:`https://www.songkick.com/search?query=${cityEnc}`, highlight:'🎵 Artist Tour Tracker', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`Viagogo — ${city} Sports`, venue:city, desc:'European football, Formula 1 and major sports events', price:'查看詳情', platform:'Viagogo', link:`https://www.viagogo.com/search?q=${cityEnc}+sports`, highlight:'⚽ Champions League · F1', duration:'' },
      );
    }
  } else if (isUSA) {
    if (isConcert) {
      cards.push(
        { title:`Ticketmaster — ${city} Concerts`, venue:city, desc:'America\'s largest ticketing platform — arena shows, stadium tours', price:'查看詳情', platform:'Ticketmaster', link:`https://www.ticketmaster.com/search?q=${cityEnc}+concert`, highlight:'🎤 Arena · Stadium Tours', duration:'' },
        { title:`StubHub — ${city}`, venue:city, desc:'Secondary market for sold-out shows and hard-to-find tickets', price:'查看詳情', platform:'StubHub', link:`https://www.stubhub.com/search?q=${cityEnc}+concert`, highlight:'🎵 二手票・保障出票', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`Ticketmaster Sports — ${city}`, venue:city, desc:'NFL, NBA, MLB, NHL — all major US sports leagues', price:'查看詳情', platform:'Ticketmaster', link:`https://www.ticketmaster.com/search?q=${cityEnc}+sports`, highlight:'🏈 NFL · NBA · MLB · NHL', duration:'' },
        { title:`SeatGeek — ${city}`, venue:city, desc:'Best-price sports ticket aggregator with interactive seating maps', price:'查看詳情', platform:'SeatGeek', link:`https://seatgeek.com/search#q=${cityEnc}+sports`, highlight:'🗺️ 互動座位圖', duration:'' },
      );
    }
  } else {
    // Generic fallback for other regions
    if (isConcert) {
      cards.push(
        { title:`Songkick — ${city} Concerts`, venue:city, desc:'Global concert discovery — track artists\' tour dates worldwide', price:'查看詳情', platform:'Songkick', link:`https://www.songkick.com/search?query=${cityEnc}`, highlight:'🎤 全球演唱會資訊', duration:'' },
        { title:`StubHub — ${city} Concerts`, venue:city, desc:'International concert tickets with buyer guarantee', price:'查看詳情', platform:'StubHub', link:`https://www.stubhub.com/search?q=${cityEnc}+concert`, highlight:'🎫 全球票務保障', duration:'' },
        { title:`Viagogo — ${city}`, venue:city, desc:'Global ticket marketplace for concerts and live events', price:'查看詳情', platform:'Viagogo', link:`https://www.viagogo.com/search?q=${cityEnc}+concert`, highlight:'🌐 國際票務平台', duration:'' },
      );
    }
    if (isSports) {
      cards.push(
        { title:`Viagogo Sports — ${city}`, venue:city, desc:'International sports tickets — football, basketball, tennis', price:'查看詳情', platform:'Viagogo', link:`https://www.viagogo.com/search?q=${cityEnc}+sports`, highlight:'⚽ 國際體育賽事', duration:'' },
        { title:`StubHub Sports — ${city}`, venue:city, desc:'Global sports ticket marketplace with buyer guarantee', price:'查看詳情', platform:'StubHub', link:`https://www.stubhub.com/search?q=${cityEnc}+sports`, highlight:'🏟️ 全球保障出票', duration:'' },
      );
    }
  }

  // Always append Klook search card at the end
  const klLink = klookSearch(city, isConcert ? 'concert show' : 'sports game ticket', lang);
  cards.push({
    title: `🔍 在 Klook 搜尋「${city} ${isConcert ? '演唱會' : '球賽'}體驗」`,
    venue: city, desc: `Klook 精選 ${city} 演出及體育體驗`, price: '查看所有結果',
    platform: 'Klook', link: klLink, highlight: '⚡ 即時確認 · HK$ 結算', duration: '',
  });

  return cards;
};

// Klook/KKday search keywords per category
const KLOOK_TERMS = {
  'theme parks':        '主題樂園 theme park',
  'museums':            '博物館 museum',
  'zoos':               '動物園 zoo',
  'aquariums':          '海洋館 aquarium',
  'exhibitions':        '展覽 exhibition',
  'tourist attractions':'景點 attraction',
  'concerts':           '演唱會 concert',
  'sports matches':     '球賽 sports',
};

// Language-aware Klook locale prefix
const klookLocale = lang => lang === 'ja' ? 'ja' : lang === 'ko' ? 'ko' : lang === 'zh-CN' ? 'zh-CN' : lang === 'en' ? 'en-HK' : 'zh-HK';
// Stable Klook search URL (won't 404 as activity IDs change)
const klookSearch = (city, query, lang = 'zh-HK') =>
  `https://www.klook.com/${klookLocale(lang)}/search/?query=${encodeURIComponent(`${city} ${query}`)}&aid=${KLOOK_AID}`;

// Generate Klook + KKday search result cards for a city/category
const generatePlatformCards = (cat, city, country, lang = 'zh-HK') => {
  const term = KLOOK_TERMS[cat] || cat;
  const klookLink = klookSearch(city, term, lang);
  // zh-hk avoids KKday defaulting to Taiwan results (zh-tw is Taiwan-centric)
  const kkdayLocale = lang === 'zh-CN' ? 'zh-cn' : lang === 'ja' ? 'ja-jp' : lang === 'ko' ? 'ko-kr' : lang.startsWith('zh') ? 'zh-hk' : 'en';
  const kq = encodeURIComponent(`${city} ${cat}`);
  const catLabel = KLOOK_TERMS[cat] || cat;
  return [
    {
      title: `🔍 在 Klook 搜尋「${city} ${catLabel}」`,
      venue: city,
      desc: `前往 Klook 搜尋頁面，瀏覽 ${city} 所有相關門票及體驗`,
      price: '查看所有結果',
      platform: 'Klook',
      link: klookLink,
      highlight: '⚡ 即時確認 · HK$ 結算',
      duration: '',
    },
    {
      title: `🔍 在 KKday 搜尋「${city} ${cat}」`,
      venue: city,
      desc: `前往 KKday 搜尋頁面，瀏覽 ${city} 精選門票及私家團體驗`,
      price: '查看所有結果',
      platform: 'KKday',
      link: `https://www.kkday.com/${kkdayLocale}/product/search?q=${kq}&cid=${KKDAY_CID}`,
      highlight: '🎫 多種票種 · 繁中介面',
      duration: '',
    },
  ];
};

// 城市中文名對英文名
const CITY_EN = { '香港':'Hong Kong','東京':'Tokyo','大阪':'Osaka','首爾':'Seoul','曼谷':'Bangkok','峇里島':'Bali','新加坡':'Singapore','台北':'Taipei','京都':'Kyoto','澳門':'Macau','倫敦':'London','巴黎':'Paris' };
// eslint-disable-next-line no-unused-vars
const _REMOVED_SEED = [
  {
    id:'seed_1', topic:'首爾明洞藥妝攻略：買什麼最值得', destination:'首爾', mood:'購物', photoKw:'Seoul Myeongdong shopping cosmetics street',
    title:'首爾明洞：藥妝控的朝聖地', subtitle:'不是每瓶都值得買，這篇幫你選',
    teaser:'走進明洞的那一刻，你會被密密麻麻的藥妝店嚇到。每家都在說「最平」、「限定」，但真正值得帶回家的，其實只有那幾樣。',
    sections:[
      { heading:'先搞清楚：哪些真的值得買', content:'韓國護膚品在香港貴三至五成，明洞的價格已是全首爾最低之一。最值回票價的是精華液和面膜，Cosrx、Some By Mi 等品牌在香港專門店動輒三四百港元，在明洞只需百多元。悅詩風吟（innisfree）的自然樂園護膚系列和 Etude House 的氣墊粉底也是首選，尤其適合送禮。' },
      { heading:'掃貨策略：從地下到五樓', content:'不要只逛地面層。明洞的大型藥妝店如 Olive Young 和 Daiso，二樓以上往往有不同品牌和更多選擇。建議先在 Olive Young 的 App 加入購物車，到店掃碼核實是否有優惠，通常比直接買平 10-15%。另外，留意店內的「1+1」和「2+1」捆綁優惠，面膜類最划算。' },
      { heading:'一個香港人的誠實心得', content:'不要被「韓國限定」的包裝衝昏頭腦。很多所謂限定款只是換了個禮盒設計，成分一模一樣。最後回家最常用的，往往是那幾件你平時已經認識的品牌，只是在這裡買便宜了三成。帶一個可以拉的行李袋去明洞，你懂的。' },
    ],
    tips:['Olive Young App 先加購物車，到店掃碼確認優惠後再付款', '兌換韓元在機場或明洞換匯所，比在香港換划算約 3-5%', '避開週末人潮，週二至週四早上 10 點開門後最舒適'],
    bestFor:'首次到首爾的購物控',
  },
  {
    id:'seed_2', topic:'大阪環球影城：讓你忘記年齡的地方', destination:'大阪', mood:'玩樂', photoKw:'Universal Studios Japan theme park fun crowd',
    title:'大阪 USJ：三十歲後再去的理由', subtitle:'那些你以為「太幼稚」的，其實最值得',
    teaser:'第一次去環球影城，你可能覺得是帶小孩的地方。第二次，你才明白，那個排了兩小時的過山車，是成年人最好的解壓方法。',
    sections:[
      { heading:'哈利波特的世界：不只是打卡', content:'踏進霍格莫德村的那一刻，你會忘記自己站在大阪。石板路、橡皮糖蛙、奶油啤酒，細節精緻到令人屏息。建議一開園立即衝向禁忌之旅，排隊人龍在開園頭一小時最短。奶油啤酒有冷熱兩款，熱的在冬天喝特別治癒。' },
      { heading:'任天堂世界：大人比小孩更瘋狂', content:'馬力奧主題園區是近年 USJ 最大亮點。買一個能量手環（約 3,500 日圓），可以在園內「敲問號磚」、累積星星、挑戰 Boss，整個園區變成你的遊戲世界。最受歡迎的 Yoshi 冒險開放時間排最少，建議用 Express Pass 解決。' },
      { heading:'玩法建議：Express Pass 值不值', content:'旺季（暑假、寒假、週末）Express Pass 是必須的。比較：普通門票約 8,600 日圓，Express Pass 7 項目約 10,000-16,000 日圓不等。旺季每項目人龍輕易 120 分鐘，有 Pass 可省超過 5 小時等候時間，絕對值回票價。' },
    ],
    tips:['開園前 30 分鐘入場，第一個衝禁忌之旅或任天堂世界', '奶油啤酒冷款：¥650，熱款：¥700，必試', 'Klook 或 KKday 提前購票，現場買旺季常售罄'],
    bestFor:'想找理由放鬆的大人',
  },
  {
    id:'seed_3', topic:'在峇里島，時間是另一種語言', destination:'峇里島', mood:'放鬆', photoKw:'Bali rice terrace morning mist peaceful sunrise',
    title:'峇里島：讓你重新學會發呆', subtitle:'放空，是這裡最重要的行程',
    teaser:'到峇里島第三天，你會發現手機充電插頭根本不重要。那種慢，不是懶，是一種你從城市帶不走、也帶不回去的節奏。',
    sections:[
      { heading:'早晨的烏布：在薄霧裡醒來', content:'烏布的早晨屬於雞鳴聲和供品的香氣。六點起床，走去最近的梯田——不需要攻略，跟著小路走就對了。Tegallalang 的梯田人多，但清晨六點半前幾乎是你一個人的。帶杯在青旅買的 Kopi Bali（峇里島黑咖啡，約 15,000 盾），坐在梯田旁發呆四十分鐘。那種平靜，比任何 spa 都有效。' },
      { heading:'峇里島 spa 不是奢侈，是生存必需', content:'正宗的峇里島傳統按摩（Balinese Massage）一小時在烏布約 100,000-150,000 盾（約 50-75 港元），包括熱薑茶和花浴。不需要找五星酒店的 spa——路邊的小型 spa 館往往更真實、技術更好。推薦下午三至五點去，剛好避開午後雷陣雨，也不占用早晨和晚上的好時光。' },
      { heading:'離開前的最後一個下午', content:'別把最後一天排滿行程。把行李寄存，去 Seminyak 海灘找張躺椅，要一杯椰青，看著印度洋的落日。峇里島的日落在下午六點後，顏色橙得不真實。這個畫面你不會拍到，但你會記住很久。' },
    ],
    tips:['包車一天約 400-600 港元，多人分攤比叫車划算', '烏布市場砍價從標價的 30% 開始喊，最終約 50-60% 成交', '雨季（11-3月）短暫陣雨，下午帶備小雨傘即可應付'],
    bestFor:'需要放空充電的城市人',
  },
  {
    id:'seed_4', topic:'京都千鳥居：人生必去一次的紅色海洋', destination:'京都', mood:'玩樂', photoKw:'Kyoto Fushimi Inari torii gates red path mountain',
    title:'伏見稻荷：凌晨四點的秘密', subtitle:'你去過，但你可能去錯了時間',
    teaser:'伏見稻荷大社是日本最多人拍照的景點之一。問題不是要不要去，而是什麼時候去——這個決定，決定你看到的是人牆還是奇蹟。',
    sections:[
      { heading:'凌晨四點的千鳥居：只屬於你的', content:'伏見稻荷全天候開放，但大多數人在早上九點後才到。建議你在凌晨四點前抵達——這個時間，整條千鳥居隧道幾乎沒有其他遊客。橙紅色的鳥居在黎明前的昏暗中更顯神秘，只有遠處偶爾傳來烏鴉叫聲。從最底的入口走到山頂約 2-3 小時，帶手電筒或開手機手電。' },
      { heading:'不只是打卡：每座鳥居背後的故事', content:'每一座鳥居都是企業或個人捐獻，刻有捐獻者名字和日期。小鳥居約 175,000 日圓，大型可達百萬日圓以上。走著走著，你會看到有些鳥居已經有些破損——這不是沒人管理，而是依照神道的傳統，讓其自然回歸。這種「不完美」反而令整個地方更有靈魂。' },
      { heading:'山頂的風景是你的獎勵', content:'很多人走到半山就回頭，但山頂才是重點。從四辻（よつつじ）望出去的京都全景，特別是日出後薄霧漸散的那一刻，是你爬山的理由。帶備足夠的水，山上有販賣機但價格較貴。下山時走不同的路線，會發現隱藏的小神社和更安靜的鳥居群。' },
    ],
    tips:['凌晨 3:30 從京都車站坐的士前往，約 1,500-2,000 日圓', '穿適合行山的鞋，石板路不平，高跟鞋是自殺', '帶 JR Pass 或 ICOCA，搭稻荷站下車即到'],
    bestFor:'喜歡避開人群的攝影愛好者',
  },
  {
    id:'seed_5', topic:'曼谷托缽文化：清晨遇見真實的泰國', destination:'曼谷', mood:'體驗', photoKw:'Bangkok monk alms giving morning orange robe culture',
    title:'曼谷清晨六點：看見真實的泰國', subtitle:'在遊客還沒醒來的時候',
    teaser:'大多數到曼谷的旅客，第一餐是在酒店餐廳吃 buffet。但清晨六點，在鄭王廟附近的街道，泰國另一個靈魂正在甦醒。',
    sections:[
      { heading:'托缽儀式：不是表演，是日常', content:'每天清晨五點半，曼谷的僧侣穿著橙色僧袍，赤腳走在街道上接受信眾供奉食物。這不是為遊客設計的表演，是延續千年的泰國佛教文化日常。最好的觀察地點在鄭王廟（Wat Arun）附近的 Itsaraphap 路，當地人在此供奉的場景最真實，沒有商業化的感覺。' },
      { heading:'如何參與而不打擾', content:'如果你想供奉，請向路邊小販購買供品套裝（約 50-100 泰銖），包括米飯、糯米和甜點。供奉時，女性不可直接觸碰僧侶，需將食物放在特定的容器中。拍照前請先觀察，不要直接對著僧侶面孔拍，更不要用閃燈。這是他人的信仰，你是旁觀者，請懷著尊重的心。' },
      { heading:'儀式結束後：找最好的泰式早餐', content:'托缽儀式在六點半至七點結束。之後走去附近的河邊市場，這時正是當地人吃早餐的時間。一碗船麵（Kuay Teow Ruea）配上一杯泰式奶茶，合共不超過 60 泰銖。坐在塑料椅上，看著湄南河在朝陽下閃光，這是比任何米芝蓮餐廳都難忘的早餐。' },
    ],
    tips:['著裝保守，男女都應遮蓋肩膀和膝蓋', '不要在儀式期間大聲說話或笑鬧', '鄭王廟渡輪約 5 泰銖，交通博物館附近的碼頭出發'],
    bestFor:'想了解真實泰國文化的旅客',
  },
]; /* end _REMOVED_SEED */


// ─── INTEL PAGE CONFIG ──────────────────────────────────────────────────────────
const INTEL_DESTS = [
  { key:'japan',   label:'日本',   flag:'', active:true,
    countryNames:['日本','Japan'],
    cities:['東京','大阪','京都','福岡','沖繩','北海道','奈良','神戶','名古屋','廣島'],
    cityEn:{東京:'Tokyo',大阪:'Osaka',京都:'Kyoto',福岡:'Fukuoka',沖繩:'Okinawa',北海道:'Hokkaido',奈良:'Nara',神戶:'Kobe',名古屋:'Nagoya',廣島:'Hiroshima'},
    youtubers:['@Joyjphub','@yoshidajapantv','@KikiMay','@FukuokaKong'],
  },
  { key:'gba',     label:'大灣區', flag:'', active:true,
    countryNames:['中國','China','大灣區'],
    cities:['香港','澳門','廣州','深圳','珠海','佛山','東莞'],
    cityEn:{香港:'Hong Kong',澳門:'Macau',廣州:'Guangzhou',深圳:'Shenzhen',珠海:'Zhuhai',佛山:'Foshan',東莞:'Dongguan'},
    youtubers:['@saidorsi'],
  },
  { key:'aus',     label:'澳洲',   flag:'', active:true,
    countryNames:['澳洲','Australia','澳大利亞','紐西蘭','New Zealand'],
    cities:['悉尼','墨爾本','布里斯本','黃金海岸','珀斯','阿德萊德','凱恩斯'],
    cityEn:{悉尼:'Sydney',墨爾本:'Melbourne',布里斯本:'Brisbane',黃金海岸:'Gold Coast',珀斯:'Perth',阿德萊德:'Adelaide',凱恩斯:'Cairns'},
    youtubers:[],
  },
  { key:'hk',      label:'香港',   flag:'🇭🇰', active:false, countryNames:['香港','Hong Kong'] },
  { key:'korea',   label:'韓國',   flag:'', active:true, countryNames:['韓國','Korea','South Korea','한국'],
    cities:['首爾','釜山','濟州','仁川','大邱','光州'],
    cityEn:{首爾:'Seoul',釜山:'Busan',濟州:'Jeju',仁川:'Incheon',大邱:'Daegu',光州:'Gwangju'},
    youtubers:[],
  },
  { key:'taiwan',  label:'台灣',   flag:'', active:true, countryNames:['台灣','Taiwan','臺灣'],
    cities:['台北','台中','高雄','台南','花蓮','墾丁'],
    cityEn:{台北:'Taipei',台中:'Taichung',高雄:'Kaohsiung',台南:'Tainan',花蓮:'Hualien',墾丁:'Kenting'},
    youtubers:[],
  },
  { key:'seasia',  label:'星馬泰', flag:'🌴',   active:false, countryNames:['新加坡','Singapore','馬來西亞','Malaysia','泰國','Thailand','印尼','Indonesia','菲律賓','Philippines'] },
  { key:'vietnam', label:'越南',   flag:'🇻🇳', active:false, countryNames:['越南','Vietnam'] },
  { key:'usca',    label:'美加',   flag:'🌎',   active:false, countryNames:['美國','USA','United States','加拿大','Canada'] },
  { key:'europe',  label:'歐洲',   flag:'🏰',   active:false, countryNames:['英國','UK','United Kingdom','法國','France','德國','Germany','義大利','Italy','西班牙','Spain','葡萄牙','Portugal','荷蘭','Netherlands','瑞士','Switzerland','奧地利','Austria','捷克','Czech','希臘','Greece','冰島','Iceland','北歐','Scandinavia','挪威','Norway','瑞典','Sweden','丹麥','Denmark'] },
  { key:'sam',     label:'南美洲', flag:'🌿',   active:false, countryNames:['巴西','Brazil','秘魯','Peru','阿根廷','Argentina','智利','Chile'] },
  { key:'middleeast', label:'中東', flag:'🕌', active:false, countryNames:['阿聯酋','UAE','Dubai','杜拜','土耳其','Turkey','埃及','Egypt','摩洛哥','Morocco'] },
];
const INTEL_CATS = [
  { key:'購物', icon:'🛍️', color:'#db2777' },
  { key:'飲食', icon:'🍜', color:'#ea580c' },
  { key:'玩樂', icon:'🎡', color:'#7c3aed' },
  { key:'放鬆', icon:'🌸', color:'#059669' },
  { key:'體驗', icon:'🎎', color:'#0284c7' },
];


const validatePw = p => p.length>=8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p);

// ─── URL ↔ TAB 路由對應 ────────────────────────────────────────────────────────
// 新增路徑時要同步加到 api/og-intel.js 的 PAGES 或 OTHER_SPA_PATHS，否則伺服器會回傳 404
const TAB_TO_PATH = {
  home:     '/',
  search:   '/search',
  trips:    '/trips',
  booking:  '/booking',
  profile:  '/profile',
  intel:    '/intel',
  events:   '/events',
  about:    '/about',
  contact:  '/contact',
  feedback: '/feedback',
  enquiry:  '/enquiry',
  compare:  '/compare',
  deals:    '/deals',
  admin:    '/admin',
};
const PATH_TO_TAB = Object.fromEntries(
  Object.entries(TAB_TO_PATH).map(([k, v]) => [v, k])
);
// 私隱政策／使用條款有獨立網址（/privacy、/terms），以彈出視窗形式疊在目前頁面上，不切換 tab。
// 伺服器端內容見 lib/legalPages.js
const LEGAL_PATH_RE = /^\/(privacy|terms)\/?$/;

// Real, free alternative to the AI-guessed single hotel pick: geocode the
// stay area via Nominatim, then pull actual named hotels/guesthouses nearby
// from OSM via Overpass — no affiliate API/approval needed for either. Falls
// back to the AI's single suggestion if OSM has nothing mapped there.
function DayHotelOptions({ hotel, city, day, lang, currency, t }) {
  const [state, setState] = useState({ loading: true, hotels: [], failed: false });
  const query = `${hotel.area || ''} ${city || ''}`.trim();

  // Centre of the day's verified stops. Geocoding "area + city" by name is
  // unreliable (e.g. 三宮 神戶 resolves to Zaragoza) and lands on a spot with no
  // hotels, which collapsed the card to the single AI suggestion.
  const stops = ['morning', 'lunch', 'afternoon', 'evening']
    .map(k => day?.[k]).filter(s => s?._lat != null && s?._lng != null);
  // 兩天一夜溫泉那晚要住溫泉區：當天景點橫跨市區與溫泉，平均點會落在兩地中間的荒郊
  const hasStay = day?._stayLat != null && day?._stayLng != null;
  const anchorLat = hasStay ? day._stayLat : stops.length ? stops.reduce((s, x) => s + x._lat, 0) / stops.length : null;
  const anchorLng = hasStay ? day._stayLng : stops.length ? stops.reduce((s, x) => s + x._lng, 0) / stops.length : null;

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, hotels: [], failed: false });
    (async () => {
      try {
        let lat = anchorLat, lon = anchorLng;
        if (lat == null || lon == null) {
          const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`);
          const geo = await geoRes.json();
          const hit = geo?.[0];
          if (!hit) throw new Error('no geocode match');
          lat = parseFloat(hit.lat); lon = parseFloat(hit.lon);
        }
        // nwr: many hotels are mapped as building outlines, not single points.
        const overpassQL = `[out:json][timeout:15];nwr["tourism"~"^(hotel|guest_house|hostel)$"]["name"](around:1500,${lat},${lon});out center 40;`;
        const opRes = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(overpassQL)}`);
        const opData = await opRes.json();
        const hotels = (opData.elements || [])
          .map(e => ({ name: e.tags?.name, nameEn: e.tags?.['name:en'], eLat: e.lat ?? e.center?.lat, eLon: e.lon ?? e.center?.lon }))
          .filter(e => e.name && e.eLat != null && e.eLon != null)
          .map(e => ({ ...e, distKm: haversineKm({ lat, lng: lon }, { lat: e.eLat, lng: e.eLon }) }))
          .sort((a, b) => a.distKm - b.distKm)
          .slice(0, 6);
        if (!cancelled) setState({ loading: false, hotels, failed: hotels.length === 0 });
      } catch {
        if (!cancelled) setState({ loading: false, hotels: [], failed: true });
      }
    })();
    return () => { cancelled = true; };
  }, [query, anchorLat, anchorLng]);

  if (!state.loading && state.failed) {
    // OSM has nothing mapped here — fall back to the single AI suggestion.
    return (
      <>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:9 }}>
          <div>
            <p style={{ fontWeight:900,color:'#0f172a',margin:'0 0 2px' }}>{hotel.name}</p>
            <p style={{ fontSize:11,color:'#64748b',margin:'0 0 3px' }}>{hotel.area}</p>
            <div style={{ display:'flex',gap:2 }}>{Array(hotel.stars||4).fill(0).map((_,i)=><Star key={i} size={10} color="#f59e0b" fill="#f59e0b"/>)}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <p style={{ fontWeight:900,color:'#0f172a',margin:0 }}>{hotel.pricePerNight}</p>
            <p style={{ fontSize:10,color:'#94a3b8',margin:0 }}>/night</p>
          </div>
        </div>
        <a href={stay22Link(`${hotel.name} ${hotel.area||''} ${city||''}`, { hotelname: hotel.name })} target="_blank" rel="sponsored noopener noreferrer"
           style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:7,background:'#0f9488',color:'white',borderRadius:12,padding:'10px',fontSize:11,fontWeight:900,textDecoration:'none' }}>
          <Hotel size={12}/> {(lang==='en'||lang==='ja'||lang==='ko')?'See more nearby stays':'查看附近更多住宿'} <ExternalLink size={10}/>
        </a>
        <p style={{ fontSize:10,color:'#94a3b8',margin:'6px 0 0',textAlign:'center' }}>{t.hotelAiNote}</p>
      </>
    );
  }

  if (state.loading) {
    return <p style={{ fontSize:12, color:'#94a3b8', margin:0, textAlign:'center', padding:'8px 0' }}>
      {(lang==='en'||lang==='ja'||lang==='ko')?'Finding nearby stays…':'搜尋附近住宿中…'}
    </p>;
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {state.hotels.map((h,i)=>(
        <div key={i} style={{ display:'flex', alignItems:'center', gap:10, paddingBottom:i<state.hotels.length-1?8:0, borderBottom:i<state.hotels.length-1?'1px solid #f1f5f9':'none' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ margin:'0 0 2px', fontSize:12.5, fontWeight:800, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.name}</p>
            <p style={{ margin:0, fontSize:10.5, color:'#64748b' }}>{h.distKm<1 ? `${Math.round(h.distKm*1000)}m` : `${h.distKm.toFixed(1)}km`} {(lang==='en'||lang==='ja'||lang==='ko')?'from stay area':'距離住宿區域'}</p>
          </div>
          <a href={stay22Link(`${h.nameEn || h.name} ${city || ''}`, { hotelname: h.nameEn || h.name, lat: h.eLat, lng: h.eLon })} target="_blank" rel="sponsored noopener noreferrer"
             style={{ textDecoration:'none', background:'#0f9488', color:'#fff', borderRadius:8, padding:'6px 11px', fontSize:10.5, fontWeight:900, flexShrink:0 }}>Stay22</a>
        </div>
      ))}
      <p style={{ fontSize:9.5, color:'#94a3b8', margin:0, textAlign:'center' }}>
        {(lang==='en'||lang==='ja'||lang==='ko')?'Real listings via OpenStreetMap · price/availability on Stay22':'真實地點資料來自 OpenStreetMap，房價/空房請至 Stay22 查看'}
      </p>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const navigate   = useNavigate();
  const location   = useLocation();

  // 從 URL 路徑讀取初始頁面（/intel/{city}/{id} 也算 intel tab）
  // 首次打開網站根目錄 / 時預設顯示行程頁；之後按 Logo／返回首頁仍可回到首頁
  const [tab, setTabState] = useState(
    () => location.pathname.startsWith('/intel/') ? 'intel'
      : (location.pathname === '/' || LEGAL_PATH_RE.test(location.pathname)) ? 'trips'
      : (PATH_TO_TAB[location.pathname] || 'home')
  );
  const isFirstLoadRef = useRef(true);

  // setTab：同時更新 React state 和瀏覽器 URL
  const setTab = useCallback((newTab) => {
    setTabState(newTab);
    const path = TAB_TO_PATH[newTab] || '/';
    navigate(path);
  }, [navigate]);

  // 瀏覽器返回/前進鍵同步 tab state
  useEffect(() => {
    // 首次載入 / 時用 replace 導去 /trips，避免按返回鍵又跳回 /
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      if (location.pathname === '/') { navigate('/trips', { replace: true }); return; }
    }
    if (location.pathname.startsWith('/intel/')) { setTabState('intel'); return; }
    if (LEGAL_PATH_RE.test(location.pathname)) return;
    const newTab = PATH_TO_TAB[location.pathname] || 'home';
    setTabState(newTab);
  }, [location.pathname, navigate]);
  const [lang, setLang] = useState('zh-HK');
  const t = T[lang];

  // Admin access: now via /admin path directly (no longer uses #admin hash)


  // Cookie consent
  const [cookieConsent, setCookieConsent] = useState(null); // null=unknown, 'all'=accepted, 'essential'=declined ads

  useEffect(() => {
    const stored = localStorage.getItem('ec_cookie_consent');
    if (stored) setCookieConsent(stored);
  }, []);

  const acceptAllCookies = () => {
    localStorage.setItem('ec_cookie_consent', 'all');
    setCookieConsent('all');
  };
  const essentialOnly = () => {
    localStorage.setItem('ec_cookie_consent', 'essential');
    setCookieConsent('essential');
  };

  // Auth — Supabase session
  const [user, setUser] = useState(null);
  const isLoggedIn = !!user;
  const [showLogin, setShowLogin] = useState(false);
  const [loginReturnTab, setLoginReturnTab] = useState(null);
  const openLogin = (returnTab) => { setLoginReturnTab(returnTab || null); setShowLogin(true); };
  // 訪客試用：未登入可免費生成 1 次行程，之後（或要儲存／用 AI 助手修改時）只提供 Google 登入。
  // guest_id 存在 localStorage，伺服器以 guest_id + IP 雜湊把關（見 api/groq.js）。
  const [googleGate, setGoogleGate] = useState(null); // null | 'trial_used' | 'quota_full' | 'save' | 'robot' | 'days'
  const pendingSaveAfterLoginRef = useRef(false);
  const getGuestId = () => {
    try {
      let id = localStorage.getItem('ec_guest_id');
      if (!id) { id = crypto.randomUUID(); localStorage.setItem('ec_guest_id', id); }
      return id;
    } catch { return crypto.randomUUID(); }
  };
  const isGuestTrialUsed = () => { try { return localStorage.getItem('ec_guest_trial_used') === '1'; } catch { return false; } };
  const markGuestTrialUsed = () => { try { localStorage.setItem('ec_guest_trial_used', '1'); } catch {} };
  const openGoogleGate = (reason) => { trackEvent('guest_gate_shown', reason); setGoogleGate(reason); };
  const trackEvent = (eventName, context) => {
    // session_id 用同一個 guest_id，方便統計不重複人數（同一瀏覽器視為同一人）
    try { supabase.from('analytics_events').insert({ event_name: eventName, context: context || null, session_id: getGuestId() }).then(()=>{}); } catch {}
  };
  // 頁面瀏覽自己存一份（Vercel Analytics 只保留 30 天）
  const pvLoggedRef = useRef(false);
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    // 自動化瀏覽器（爬蟲／無頭瀏覽器）不記錄
    if (navigator.webdriver) return;
    // 首次打開 / 會即時導去 /trips，只記 /trips 一次，避免每個訪客被算兩次瀏覽
    if (!pvLoggedRef.current && location.pathname === '/') return;
    pvLoggedRef.current = true;
    // 經 /api/events 寫入，伺服器端才拿得到訪客國家
    try {
      fetch('/api/events', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service: 'pageview',
          path: location.pathname,
          referrer: document.referrer || null,
          session_id: getGuestId(),
          lang: navigator.language || null,
          device: window.innerWidth < 640 ? 'mobile' : 'desktop',
        }),
      }).catch(()=>{});
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [chatOpen, setChatOpen] = useState(false);
  const [fontLarge, setFontLarge] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [intelQuery, setIntelQuery] = useState('');
  const [intelData, setIntelData]   = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelHero, setIntelHero]   = useState(null);
  const [articles, setArticles]           = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleTranslation, setArticleTranslation] = useState(null); // { id, lang, title, subtitle, teaser, sections, tips, bestFor }
  const [translatingArticle, setTranslatingArticle] = useState(false);
  const [articleFilter, setArticleFilter]     = useState('全部');
  const [cityFilter, setCityFilter]           = useState('全部');
  // Pending article ID from URL (e.g. /intel/Taipei/123) — resolved once articles load
  const pendingArticleId = useRef(
    location.pathname.startsWith('/intel/') ? location.pathname.split('/').pop() : null
  );
  const openArticle  = useCallback((a) => {
    setSelectedArticle(a);
    // 從「下一篇」切換文章時捲回頂部，否則畫面停在底部，看起來像沒有跳轉
    window.scrollTo(0, 0);
    if (a?.id) {
      const citySlug = (a.destination || a.city || 'world').replace(/\s+/g, '-');
      navigate(`/intel/${citySlug}/${a.id}`);
    }
    // Update meta tags for SEO
    if (a?.title) {
      document.title = `${a.title} | EC Travel`;
      const desc = document.querySelector('meta[name="description"]');
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDesc = document.querySelector('meta[property="og:description"]');
      const ogImg = document.querySelector('meta[property="og:image"]');
      const teaser = a.teaser || a.subtitle || '';
      if (desc) desc.setAttribute('content', teaser.slice(0, 160));
      if (ogTitle) ogTitle.setAttribute('content', `${a.title} | EC Travel`);
      if (ogDesc) ogDesc.setAttribute('content', teaser.slice(0, 200));
      if (ogImg && a.photoUrl) ogImg.setAttribute('content', a.photoUrl);
    }
  }, [navigate]);
  const closeArticle = useCallback(() => {
    setSelectedArticle(null);
    navigate('/intel');
    // Restore default meta tags
    document.title = 'EC Travel — 智能旅遊助手 | AI 行程規劃 · 景點推薦 · 旅遊優惠';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'EC Travel 是你的 AI 智能旅遊助手，提供全球目的地搜尋、AI 一鍵行程規劃、熱門景點推薦及獨家旅遊優惠。');
  }, [navigate]);
  // 瀏覽器返回/前進鍵：URL 不再指向文章頁時，同步關閉文章閱讀畫面（否則畫面會停留在舊文章）
  useEffect(() => {
    if (!location.pathname.startsWith('/intel/')) {
      setSelectedArticle(prev => {
        if (!prev) return prev;
        document.title = 'EC Travel — 智能旅遊助手 | AI 行程規劃 · 景點推薦 · 旅遊優惠';
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute('content', 'EC Travel 是你的 AI 智能旅遊助手，提供全球目的地搜尋、AI 一鍵行程規劃、熱門景點推薦及獨家旅遊優惠。');
        return null;
      });
    }
  }, [location.pathname]);
  // 每個路由的 canonical 應指向自己，而非寫死首頁，否則 Google 會把所有子頁當成首頁的替代頁面而不建立索引
  useEffect(() => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `https://travel-ec.com${location.pathname}`);
  }, [location.pathname]);
  const [intelMode, setIntelMode]             = useState('magazine');
  const [compareQuery, setCompareQuery]         = useState('');
  const [compareType, setCompareType]           = useState(null);
  const [intelDest, setIntelDest]               = useState(null); // destination region key
  const [compareSituation, setCompareSituation] = useState('');
  const [compareData, setCompareData]           = useState(null);
  const [compareLoading, setCompareLoading]     = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [copiedCode, setCopiedCode] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [dealsData, setDealsData]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dealsLoading, setDealsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [dealsCat, setDealsCat]     = useState('activities');
  // eslint-disable-next-line no-unused-vars
  const [dealsQuery, setDealsQuery] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data:{ session } }) => setUser(session?.user ?? null));
    const { data:{ subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') setUser(session?.user ?? null);
      else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') setUser(null);
      else setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Auto-reset stale error state when trips tab is opened
  useEffect(() => {
    if (tab === 'trips' && itinerary?.error) resetTripForm();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Favorites — extracted to useFavorites hook
  const MAX_FAVORITES = 50;
  const { favorites, favCities, toggleFav } = useFavorites(user, lang, () => setShowLogin(true));

  // Check subscription status when user ID changes (not every object reference change)
  useEffect(() => {
    if (!user?.email) { setIsSubscribed(false); return; }
    supabase.auth.getSession().then(({ data:{ session } }) => {
      const token = session?.access_token;
      if (!token) { setIsSubscribed(false); return; }
      return fetch('/api/subscribe', {
        method:'POST', headers:{'Content-Type':'application/json', 'Authorization':`Bearer ${token}`},
        body: JSON.stringify({ email: user.email, action:'status' }),
      }).then(r=>r.json()).then(d=>setIsSubscribed(d.subscribed===true));
    }).catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Handle ?unsubscribe=TOKEN in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('unsubscribe');
    if (!token) return;
    fetch(`/api/subscribe?token=${token}`)
      .then(()=>{ setIsSubscribed(false); window.history.replaceState({}, '', '/'); alert('✅ 已取消訂閱旅遊情報'); })
      .catch(()=>{});
  }, []);

  const toggleSubscription = async () => {
    if (!user?.email) return;
    setSubLoading(true);
    try {
      const action = isSubscribed ? 'unsubscribe' : 'subscribe';
      const r = await fetch('/api/subscribe', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: user.email, user_id: user.id, name: user.user_metadata?.full_name||'', action }),
      });
      if (r.ok) setIsSubscribed(!isSubscribed);
    } catch {}
    setSubLoading(false);
  };

  // ── Affiliate reward — extracted to useAffiliateReward hook
  const { affiliateReward, setAffiliateReward, affToast } = useAffiliateReward(user);

  // Local time — extracted to useLocalTime hook
  const { localTime, destTime, destTimezone } = useLocalTime(lang);

  // ── HOME DATA ──────────────────────────────────────────────────────────────
  // ── Currency state ──────────────────────────────────────────────────────────
  const [currency, setCurrency] = useState('HKD');
  const CURRENCIES = [
    {key:'HKD', sym:'HK$', name:'港元 HKD'},
    {key:'USD', sym:'US$', name:'美元 USD'},
    {key:'CNY', sym:'元',   name:'人民幣 CNY'},
    {key:'TWD', sym:'NT$', name:'台幣 TWD'},
    {key:'MOP', sym:'P',   name:'澳門幣 MOP'},
    {key:'JPY', sym:'¥',   name:'日元 JPY'},
    {key:'KRW', sym:'₩',   name:'韓元 KRW'},
    {key:'EUR', sym:'€',   name:'歐元 EUR'},
    {key:'GBP', sym:'£',   name:'英鎊 GBP'},
    {key:'SGD', sym:'S$',  name:'星幣 SGD'},
    {key:'THB', sym:'฿',   name:'泰銖 THB'},
    {key:'AUD', sym:'A$',  name:'澳元 AUD'},
    {key:'CAD', sym:'C$',  name:'加元 CAD'},
    {key:'MYR', sym:'RM',  name:'馬幣 MYR'},
    {key:'IDR', sym:'Rp',  name:'印尼盾 IDR'},
    {key:'VND', sym:'₫',   name:'越南盾 VND'},
  ];
  const currObj = CURRENCIES.find(c=>c.key===currency)||CURRENCIES[0];
  const currSymbol = currObj.sym;

  const [homeCity, setHomeCity] = useState('Hong Kong');
  const [homeCountry, setHomeCountry] = useState('Hong Kong');
  const [homeInfo, setHomeInfo] = useState(null);
  const [homeWeather, setHomeWeather] = useState(null);
  const [homeAttractions, setHomeAttractions] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [homeLatest, setHomeLatest] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [homeDeep,   setHomeDeep]   = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [homeDeals, setHomeDeals] = useState([]);
  const [homeDistrict, setHomeDistrict] = useState('');
  const [homeAttrQuery, setHomeAttrQuery] = useState('');
  const [homeActiveTheme, setHomeActiveTheme] = useState(null); // which of the 5 category cards is expanded below the strip
  const [districtResults, setDistrictResults]   = useState({}); // key `city:district` → places[]
  const [districtLoading, setDistrictLoading]   = useState(false);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeInfoLoading, setHomeInfoLoading] = useState(true);

  // Admin-managed attractions from Supabase (public read)
  // managed_attractions has 100k+ rows; a single city view shows ~30. Load
  // per-city on demand (see loadCityRows) instead of downloading the entire
  // table on every visit — that whole-table pull was the main loading stall.
  const [managedAttractions, setManagedAttractions] = useState([]);
  const loadedCitiesRef = useRef(new Set());

  // Cities that have DB data — powers the search dropdown's "only cities with
  // data" filter. ~195 names via RPC instead of downloading all 100k+ rows.
  const [dbCitySet, setDbCitySet] = useState(() => new Set());
  useEffect(() => {
    supabase.rpc('distinct_managed_cities').then(({ data }) => {
      if (Array.isArray(data) && data.length)
        setDbCitySet(new Set(data.map(r => (r.city || '').toLowerCase())));
    }).catch(() => {}); // graceful: empty set → dropdown just shows all cities
  }, []);

  // Convert a Supabase managed_attraction row to the app's attraction shape
  const toAppAttr = (a) => ({
    name:     a.name,
    nameEn:   a.name_en    || a.photo_kw || a.name,
    nameZhCn: a.name_zh_cn || a.name,
    nameJa:   a.name_ja    || a.name_en || a.photo_kw || a.name,
    nameKo:   a.name_ko    || a.name_en || a.photo_kw || a.name,
    desc: a.description,
    category: a.category,
    rating: a.rating,
    openedWhen: a.opened_when,
    photoKw: a.photo_kw || a.name,
    imageUrl: isReliableImageUrl(a.image_url) ? a.image_url : null,
    district: a.district || null,
    deepType: a.deep_type || null,
    route: a.deep_route || null,
    specify_cat: a.specify_cat || null,
    isIndoor: !!a.is_indoor,
    lat: a.lat ?? null,
    lng: a.lng ?? null,
    place_id: a.place_id || null,
    notability: a.notability || 0,
  });

  // Get managed attractions for a specific city (match Chinese or English)
  const CITY_EN_MAP = {
    // Greater China
    '香港':'Hong Kong','澳門':'Macau','台北':'Taipei','北京':'Beijing','上海':'Shanghai',
    '廣州':'Guangzhou','深圳':'Shenzhen','成都':'Chengdu','重慶':'Chongqing','西安':"Xi'an",
    '杭州':'Hangzhou','廈門':'Xiamen','桂林':'Guilin','三亞':'Sanya','張家界':'Zhangjiajie',
    '昆明':'Kunming','青島':'Qingdao','蘇州':'Suzhou',
    // Japan
    '東京':'Tokyo','大阪':'Osaka','京都':'Kyoto','福岡':'Fukuoka','札幌':'Sapporo',
    '名古屋':'Nagoya','沖繩':'Okinawa','廣島':'Hiroshima','奈良':'Nara','神戶':'Kobe',
    '北海道':'Hokkaido','日光':'Nikko','仙台':'Sendai','熊本':'Kumamoto',
    '長崎':'Nagasaki','箱根':'Hakone','金澤':'Kanazawa','鹿兒島':'Kagoshima',
    // 輕井澤 had no alias at all — a search for it could never match its DB
    // rows (stored as city='Karuizawa'), confirmed 2026-09-28 via a stale
    // fabricated-attractions cache entry keyed on the unmapped Chinese name.
    '輕井澤':'Karuizawa',
    // Korea
    '首爾':'Seoul','釜山':'Busan','濟州':'Jeju','濟州島':'Jeju','光州':'Gwangju',
    // Southeast Asia
    '新加坡':'Singapore','曼谷':'Bangkok','清邁':'Chiang Mai','普吉島':'Phuket',
    '峇里島':'Bali','雅加達':'Jakarta','日惹':'Yogyakarta','吉隆坡':'Kuala Lumpur',
    '檳城':'Penang','河內':'Hanoi','胡志明市':'Ho Chi Minh City','峴港':'Da Nang',
    '馬尼拉':'Manila','宿霧':'Cebu','暹粒':'Siem Reap','可倫坡':'Colombo',
    '馬爾地夫':'Maldives','馬爾代夫':'Maldives','馬爾代夫（全島）':'Maldives',
    '馬累':'Maldives','馬富士島':'Maldives','巴環礁':'Maldives','北馬累環礁':'Maldives','南馬累環礁':'Maldives',
    'Malé':'Maldives','Maafushi':'Maldives','Baa Atoll':'Maldives','Maldives':'Maldives',
    // South Asia
    '孟買':'Mumbai','德里':'Delhi','新德里':'Delhi','New Delhi':'Delhi','果阿':'Goa','加德滿都':'Kathmandu',
    // Middle East
    '杜拜':'Dubai','迪拜':'Dubai','阿布達比':'Abu Dhabi','阿布扎比':'Abu Dhabi',
    '多哈':'Doha','伊斯坦堡':'Istanbul','伊斯坦布爾':'Istanbul',
    // Europe
    '巴黎':'Paris','倫敦':'London','羅馬':'Rome','米蘭':'Milan',
    '佛羅倫薩':'Florence','佛羅倫斯':'Florence',
    '威尼斯':'Venice','巴塞隆納':'Barcelona','巴塞羅那':'Barcelona','馬德里':'Madrid','阿姆斯特丹':'Amsterdam',
    '柏林':'Berlin','慕尼黑':'Munich','布拉格':'Prague','維也納':'Vienna','布達佩斯':'Budapest',
    '里斯本':'Lisbon','雅典':'Athens','聖托里尼':'Santorini','愛丁堡':'Edinburgh',
    '哥本哈根':'Copenhagen','斯德哥爾摩':'Stockholm','奧斯陸':'Oslo','赫爾辛基':'Helsinki',
    '蘇黎世':'Zurich','布魯塞爾':'Brussels','都柏林':'Dublin','雷克雅維克':'Reykjavik',
    '杜布羅夫尼克':'Dubrovnik',
    // Americas
    '紐約':'New York','洛杉磯':'Los Angeles','舊金山':'San Francisco','拉斯維加斯':'Las Vegas',
    '邁阿密':'Miami','芝加哥':'Chicago','夏威夷':'Hawaii','溫哥華':'Vancouver',
    '多倫多':'Toronto','坎昆':'Cancun','墨西哥城':'Mexico City','里約熱內盧':'Rio de Janeiro',
    '布宜諾斯艾利斯':'Buenos Aires',
    // Oceania
    '悉尼':'Sydney','雪梨':'Sydney','墨爾本':'Melbourne','黃金海岸':'Gold Coast',
    '奧克蘭':'Auckland','皇后鎮':'Queenstown',
    // Africa
    '開普敦':'Cape Town','馬拉喀什':'Marrakech','開羅':'Cairo',
    // Chinese DB city names → canonical English
    '臺北市':'Taipei','臺南市':'Tainan','千代田區':'Tokyo',
    '台中':'Taichung','台南':'Tainan','墾丁':'Kenting',
    '佛山':'Foshan','珠海':'Zhuhai','珀斯':'Perth','東莞':'Dongguan',
    // Extra Swiss/Canada
    '伯恩':'Bern','蒙特利爾':'Montreal',
    // 2026-10-02: 9 個C類資料缺口城市只有'伯恩'原本有別名，其餘8個（含簡體）
    // 都漏掉——shapeManagedRows()用這個表把中文顯示名換成DB的英文city欄位，
    // 漏掉的城市會配不到任何列，即使DB裡有真實資料也會走到doSearch的slow
    // path（略過country_facts覆寫，直接顯示AI生成的app名稱/緊急電話等未經
    // 查核內容）。劍橋就是這樣被發現的：19筆真實景點資料庫都有，但城市資訊
    // 卡片仍顯示AI編造的App名稱「CamBridgeGuide」。
    '劍橋':'Cambridge','剑桥':'Cambridge',
    '曼徹斯特':'Manchester','曼彻斯特':'Manchester',
    '馬賽':'Marseille','马赛':'Marseille',
    '烏蘭巴托':'Ulaanbaatar','乌兰巴托':'Ulaanbaatar',
    '別府':'Beppu','别府':'Beppu',
    '盧塞恩':'Lucerne','卢塞恩':'Lucerne',
    '烏波盧島':'Upolu','乌波卢岛':'Upolu',
    // Fiji
    'Fralei Island':'Fiji','斐濟':'Fiji','菲濟':'Fiji',
    // More Taiwan cities/counties
    '高雄':'Kaohsiung','花蓮':'Hualien','九份':'Jiufen','新北':'New Taipei',
    '桃園':'Taoyuan','宜蘭':'Yilan','彰化縣':'Changhua County','嘉義縣':'Chiayi County',
    '新竹縣':'Hsinchu County','苗栗縣':'Miaoli County','南投縣':'Nantou County',
    '澎湖縣':'Penghu County','屏東縣':'Pingtung County','台東縣':'Taitung County',
    '雲林縣':'Yunlin County',
    // More Korea
    '大邱':'Daegu','慶州':'Gyeongju','仁川':'Incheon',
    // More Southeast Asia
    '清萊':'Chiang Rai','芭提雅':'Pattaya','甲米':'Krabi','怡保':'Ipoh','亞庇':'Kota Kinabalu',
    '馬六甲':'Malacca','科莫多':'Komodo','拉布安巴霍':'Komodo','Labuan Bajo':'Komodo','泗水':'Surabaya','芽莊':'Nha Trang','富國島':'Phu Quoc',
    // More South Asia
    '阿格拉':'Agra','齋浦爾':'Jaipur',
    // More Middle East
    '吉達':'Jeddah','利雅德':'Riyadh','特拉維夫':'Tel Aviv',
    // More Europe
    '巴斯':'Bath','阿瑪菲':'Amalfi','博洛尼亞':'Bologna','那不勒斯':'Naples','都靈':'Turin',
    '格拉納達':'Granada','塞維利亞':'Seville','瓦倫西亞':'Valencia',
    '德累斯頓':'Dresden','海德堡':'Heidelberg','斯普利特':'Split','薩格勒布':'Zagreb',
    '克拉科夫':'Krakow','華沙':'Warsaw','里加':'Riga','塔林':'Tallinn','維爾紐斯':'Vilnius',
    '布加勒斯特':'Bucharest','蒂爾堡':'Tilburg',
    // More Americas
    '庫斯科':'Cusco','利馬':'Lima','波哥大':'Bogota','卡塔赫納':'Cartagena',
    // 馬丘比丘 has no managed_attractions rows of its own — it's scraped/stored
    // as a district ("馬丘比丘") under city='Cusco' (47 real rows there as of
    // 2026-09-28), so route the city search straight to Cusco rather than
    // adding it as its own empty city.
    '馬丘比丘':'Cusco','Machu Picchu':'Cusco',
    '聖地牙哥':'Santiago','聖保羅':'Sao Paulo','烏尤尼':'Uyuni',
    '新奧爾良':'New Orleans','西雅圖':'Seattle','華盛頓':'Washington DC','波士頓':'Boston',
    // More Oceania
    '阿德萊德':'Adelaide','布里斯本':'Brisbane','布里斯班':'Brisbane','凱恩斯':'Cairns','霍巴特':'Hobart',
    '羅托魯阿':'Rotorua','基督城':'Christchurch','威靈頓':'Wellington','烏盧魯':'Uluru',
    // More Africa
    '內羅畢':'Nairobi','塞倫蓋蒂':'Serengeti',
    // 2026-09-29 補上缺失城市
    '松山':'Matsuyama','高松':'Takamatsu','江陵':'Gangneung','全州':'Jeonju','束草':'Sokcho',
    '統營':'Tongyeong','麗水':'Yeosu','大城':'Ayutthaya','華欣':'Hua Hin','蘇梅島':'Koh Samui',
    '下龍灣':'Ha Long Bay','會安':'Hoi An','順化':'Hue','龍目島':'Lombok','烏布':'Ubud',
    '蘭卡威':'Langkawi','麗江':'Lijiang','米佛峽灣':'Milford Sound','安錫':'Annecy','波爾多':'Bordeaux',
    '坎城':'Cannes','里昂':'Lyon','尼斯':'Nice','斯特拉斯堡':'Strasbourg','五漁村':'Cinque Terre',
    '西西里島':'Sicily','科隆':'Cologne','漢堡':'Hamburg','法蘭克福':'Frankfurt','牛津':'Oxford',
    '巴塔哥尼亞':'Patagonia','桑吉巴爾':'Zanzibar','耶路撒冷':'Jerusalem',
  };
  // The map above only knows Traditional-Chinese (plus a few hand-added) display names, so in the
  // Simplified-Chinese / Korean UI a city shown as 东京/花莲/도쿄 never resolved to its English key and
  // the city page came up with no attractions or district chips (found 2026-10-05). Derive the missing
  // zh/sc/ja/ko display names from STATIC_CITIES; hand-written entries above always win.
  CITY_NAME_ALIASES.forEach(([en, names]) => {
    names.forEach(n => { if (n && !(n in CITY_EN_MAP)) CITY_EN_MAP[n] = en; });
  });
  const shapeManagedRows = (rows, cityDisplay) => {
    const key = cityDisplay.toLowerCase();
    const enKey = (CITY_EN_MAP[cityDisplay] || '').toLowerCase();
    const matched = rows.filter(a => {
      const c = (a.city || '').toLowerCase();
      // Forward: search display → DB English
      if (c === key || (enKey && c === enKey)) return true;
      // Reverse: DB Chinese city → canonical English matches search
      const dbCityEn = (CITY_EN_MAP[a.city] || '').toLowerCase();
      return dbCityEn && (dbCityEn === key || (enKey && dbCityEn === enKey));
    });
    // AI-generated fallback attractions are prompted to only return rating
    // 4.0+ venues — apply the same floor to real DB rows so a real 3.x-rated
    // scrape result doesn't stand out next to them. Unrated (null) rows pass
    // through (not yet reviewed isn't the same as reviewed-and-mediocre).
    const ratingOk = (a) => a.rating == null || a.rating >= 4;
    return {
      hot:    matched.filter(a => a.type === 'hot' && ratingOk(a)).map(toAppAttr),
      latest: matched.filter(a => a.type === 'latest' && ratingOk(a)).map(toAppAttr),
      deep:   matched.filter(a => a.type === 'deep' && ratingOk(a)).map(toAppAttr),
    };
  };
  const getManagedForCity = (cityDisplay) =>
    cityDisplay ? shapeManagedRows(managedAttractions, cityDisplay) : { hot: [], latest: [], deep: [] };

  // Only offer districts that have something to show. Home starts on the first
  // such district (or the GPS-matched one, set elsewhere) instead of dumping
  // every district's attractions in one list at once.
  const homeDistrictRows = useMemo(() => {
    const managed = getManagedForCity(homeCity);
    return [...managed.hot, ...managed.deep, ...homeAttractions];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getManagedForCity only depends on managedAttractions
  }, [managedAttractions, homeCity, homeAttractions]);
  const homeDistricts = useMemo(
    () => availableDistricts(getDistricts(homeCity), homeDistrictRows, CITY_EN_MAP[homeCity] || homeCity),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CITY_EN_MAP is a fixed lookup table
    [homeCity, homeDistrictRows]
  );
  useEffect(() => {
    setHomeDistrict(current => resolveDistrict(current, getDistricts(homeCity), homeDistricts, homeDistrictRows.length > 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the city or its options change, not on every pick
  }, [homeCity, homeDistricts.join('|'), homeDistrictRows.length > 0]);

  // Fetch one city's managed rows on demand and merge into state. Returns the
  // rows too, so a caller mid-execution (doSearch) can use them immediately
  // without waiting for the async setState to land. Matches the same city-name
  // variants (English + Chinese aliases) getManagedForCity/shapeManagedRows do.
  const MANAGED_FIELDS = 'city,type,name,name_en,name_zh_cn,name_ja,name_ko,description,category,rating,opened_when,photo_kw,image_url,district,deep_type,deep_route,specify_cat,is_indoor,lat,lng,place_id,notability';
  const loadCityRows = async (cityDisplay) => {
    if (!cityDisplay) return [];
    const en = CITY_EN_MAP[cityDisplay] || cityDisplay;
    const cacheKey = en.toLowerCase();
    const candidates = new Set([cityDisplay, en]);
    for (const [zh, e] of Object.entries(CITY_EN_MAP)) if (e === en) candidates.add(zh);
    if (loadedCitiesRef.current.has(cacheKey)) {
      const cl = new Set([...candidates].map(c => c.toLowerCase()));
      return managedAttractions.filter(a => cl.has((a.city || '').toLowerCase()));
    }
    loadedCitiesRef.current.add(cacheKey); // mark first so concurrent calls dedupe
    // Supabase caps an unbounded select at 1000 rows. Cities with more active
    // rows than that (Hong Kong alone has 3,000+) silently lost everything
    // past row 1000 — and since `sort_order` is 0 for the vast majority of
    // rows with no unique tiebreaker, WHICH 1000 survived was arbitrary, not
    // even "first N added". Paginate explicitly, ordered by id for a stable
    // cursor so every row is fetched exactly once across pages.
    const PAGE = 1000;
    let rows = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase.from('managed_attractions')
        .select(MANAGED_FIELDS).eq('is_active', true)
        .in('city', [...candidates]).order('id')
        .range(from, from + PAGE - 1);
      if (error) { loadedCitiesRef.current.delete(cacheKey); return []; }
      rows = rows.concat(data || []);
      if (!data || data.length < PAGE) break;
    }
    if (rows.length) setManagedAttractions(prev => prev.concat(rows));
    return rows;
  };

  // Auto-fetch district attractions when no pre-imported data exists
  useEffect(() => {
    const district = homeDistrict;
    const city     = homeCity;
    if (!district || !city) return;
    const cityEn = CITY_EN_MAP[city] || city;
    const key = `${cityEn}:${district}`;
    if (districtResults[key] !== undefined) return; // already fetched or fetching
    // Fall back to the API district search unless the DB already has a healthy
    // set for this district — a lone row (older imports have district=null and
    // enrichment is mid-way) used to suppress the fallback entirely, leaving
    // e.g. Ikebukuro showing 3 nature-only rows.
    const dbCount = managedAttractions.filter(a => {
      const c = (a.city || '').toLowerCase();
      return (c === city.toLowerCase() || c === cityEn.toLowerCase()) && matchesDistrict(a.district, district);
    }).length;
    if (dbCount >= 8) return;
    setDistrictLoading(true);
    setDistrictResults(prev => ({ ...prev, [key]: null })); // mark as in-flight
    fetch(`/api/photo?district_search=1&city=${encodeURIComponent(cityEn)}&district=${encodeURIComponent(district)}&lang=${lang}`)
      .then(r => r.json())
      .then(d => setDistrictResults(prev => ({ ...prev, [key]: d.places || [] })))
      .catch(() => setDistrictResults(prev => ({ ...prev, [key]: [] })))
      .finally(() => setDistrictLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeDistrict, homeCity]);

  // Geolocation — one shared request/coordinate for both this district
  // auto-detect (below) and RecommendCard's nearby-vs-hot ranking (see
  // useGeolocation() in src/hooks/useGeolocation.js for why these used to
  // be two independent navigator.geolocation calls and what that broke).
  const geo = useGeolocation();
  const [geoCity, setGeoCity] = useState('');
  const [geoDistrict, setGeoDistrict] = useState('');
  useEffect(() => { geo.request(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (geo.status !== 'ok' || !geo.gps) return;
    (async () => {
        try {
          const nomLang = lang === 'en' ? 'en,zh-TW' : lang === 'ja' ? 'ja,zh-TW' : lang === 'ko' ? 'ko,zh-TW' : 'zh-TW,zh,en';
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${geo.gps.lat}&lon=${geo.gps.lng}&format=json&accept-language=${nomLang}`);
          const d = await r.json();
          const cityRaw = d.address?.city || d.address?.town || d.address?.county || '';
          const country = d.address?.country || '';
          // `quarter` is Nominatim's finer neighbourhood-level field (e.g.
          // "尖沙咀") and often the one that actually matches our chip
          // granularity — `suburb` alone can return a broader administrative
          // area instead (e.g. "油尖旺區" for a Tsim Sha Tsui coordinate),
          // which never matches any of our 尖沙咀/旺角/油麻地 chips since none
          // of them are substrings of "油尖旺區" or vice versa. Join both so
          // the substring match below can hit on either granularity.
          const districtRaw = [d.address?.quarter, d.address?.suburb, d.address?.city_district, d.address?.district].filter(Boolean).join(' / ');
          if (cityRaw) {
            // Nominatim sometimes returns a sub-city area (e.g. a Tokyo ward
            // like 千代田区) in `address.city` — a known top-level city takes
            // it as-is, otherwise try the broader state/province field
            // (usually the prefecture, e.g. 東京都), then fall back to
            // treating `cityRaw` itself as a district of some known city.
            // Names come back in whatever variant Nominatim has — 臺北市,
            // 东京都 (simplified!), 新宿区 (Japanese 区) — so normalize before
            // matching or Tokyo/Taipei residents get a ward as their "city"
            // and lose the district filter entirely.
            const S2T = { '东':'東', '广':'廣', '岛':'島', '门':'門', '厦':'廈', '庆':'慶', '济':'濟', '汉':'漢', '韩':'韓', '湾':'灣', '绳':'繩', '户':'戶', '丰':'豐', '涩':'澀', '浅':'淺', '龙':'龍', '凤':'鳳', '泽':'澤', '滨':'濱', '关':'關' };
            const normCityName = (s) => {
              let t = (s || '').trim().replace(/^臺/, '台').replace(/[东广岛门厦庆济汉韩湾绳户丰涩浅龙凤泽滨关]/g, ch => S2T[ch]);
              if (t.length > 2) t = t.replace(/[市都府県省区區]$/, ''); // 東京都→東京 but 京都 stays
              return t;
            };
            // Returns the canonical key our tables know, or null. Nominatim
            // labels can be composites ("丰岛区 / 豐島區") — try each part.
            // Prefer a key the DISTRICT table also knows — CITY_EN_MAP alone
            // isn't enough ('臺北市' maps to Taipei there but has no district
            // list, which is how the filter went missing).
            const findKnownCity = (c) => {
              for (const part of (c || '').split(/\s*\/\s*/)) {
                for (const cand of [normCityName(part), part]) {
                  if (!cand) continue;
                  if (getDistricts(cand).length) return cand;
                  const en = CITY_EN_MAP[cand];
                  if (en && getDistricts(en).length) return en;
                  if (en) return cand;
                }
              }
              return null;
            };
            // The ISO region code is the most deterministic field Nominatim
            // gives (name labels vary wildly: 臺北市, 东京都, "丰岛区 / 豐島區",
            // wards-as-city, missing state…). Resolve it first.
            const ISO_TO_CITY = {
              'JP-13':'東京','JP-27':'大阪','JP-26':'京都','JP-40':'福岡','JP-01':'札幌',
              'JP-23':'名古屋','JP-47':'沖繩','JP-34':'廣島','JP-29':'奈良','JP-28':'神戶',
              'TW-TPE':'台北','TW-KHH':'高雄','TW-TXG':'台中','TW-TNN':'台南',
              'KR-11':'首爾','KR-26':'釜山','KR-49':'濟州','KR-28':'仁川','KR-27':'大邱',
            };
            const COUNTRY_TO_CITY = { 'hk':'香港', 'mo':'澳門', 'sg':'新加坡' };
            const isoCity = ISO_TO_CITY[d.address?.['ISO3166-2-lvl4']] || COUNTRY_TO_CITY[d.address?.country_code];
            let city = isoCity || findKnownCity(cityRaw);
            let district = districtRaw;
            if (isoCity && !findKnownCity(cityRaw)) district = districtRaw || cityRaw;
            if (!city) {
              const stateKnown = findKnownCity(d.address?.state || d.address?.province || '');
              if (stateKnown) {
                district = cityRaw;
                city = stateKnown;
              } else {
                const parentCity = findCityByDistrict(cityRaw) || findCityByDistrict(normCityName(cityRaw));
                if (parentCity) { district = cityRaw; city = parentCity; }
                else city = cityRaw; // unknown city — keep raw (no district data anyway)
              }
            }
            setGeoCity(city);
            setHomeCity(city);
            setHomeCountry(country);
            if (district) {
              // Display only the first (finest) part — `district` itself may
              // now be a "quarter / suburb" join for matching purposes (see
              // districtRaw above), which would look wrong as banner text.
              setGeoDistrict(district.split(' / ')[0]);
              // Auto-match against district chips list (works best for Chinese names).
              // Re-enabled 2026-07-19: enrichment is complete and the GPS
              // district-correction pass (correct_districts.js) fixed the
              // thin/skewed per-district data that made this unsafe before
              // (e.g. Shinjuku used to show nature-only results).
              const districtList = getDistricts(city);
              const dNorm = normCityName(district);
              // A chip can group several raw district values (e.g. HK's "中西區"
              // covers 中環/上環/西營盤) — match against every member, not just
              // the chip's own label, or geolocation inside a grouped area
              // (e.g. standing in 中環) would fail to auto-select its chip.
              const districtShort = district.replace(/[區区域市]/g, '');
              const matched = districtList.find(dl => districtMembers(dl, city).some(m =>
                district.includes(m) || dNorm.includes(m) || m.includes(districtShort)
              ));
              if (matched) setHomeDistrict(matched);
            }
          }
        } catch {}
    })();
  }, [geo.status, geo.gps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Split home loading: fast info + weather first, attractions separately
  const loadHomeInfo = useCallback(async (city, country, ln, cur) => {
    // Nominatim/GPS reverse-geocoding reports Hong Kong's sovereign country
    // as "中國"/"China", which then made the AI answer with mainland-China
    // currency/tipping/apps for a Hong Kong location — override to the actual
    // city-level jurisdiction for the facts prompt (see factsCountry in the
    // search flow for the same fix).
    const factsCountry = HK_FACTS_NAMES.has(city) ? city : country;
    // Curated DB facts take priority over AI-generated currency/voltage/
    // tipping/emergency/local_apps — see create_country_facts.sql.
    const dbFacts = await getCountryFacts(factsCountry);
    const cacheKey = `home_info6_${city}_${factsCountry}_${ln}_${cur}`;
    const al = T[ln].aiLang;
    const _isValidHomeInfo = dd => dd && !dd.__rateLimited && ['currency','voltage','transport'].some(k => dd[k]) &&
      cityInfoFieldsLookOk(dd, al);
    const _mapHomeInfo = async rawD => {
      const d = await applyCountryFacts(city, rawD, dbFacts, cur, ln);
      return {
        currency:d.currency, voltage:d.voltage, transport:d.transport,
        tips:typeof d.tips==='string'?cleanAIText(d.tips):d.tips,
        emergency:applyEmergencyOverride(city, d.emergency, ln), taboos:typeof d.taboos==='string'?cleanAIText(d.taboos):d.taboos,
        intro:cleanAIText(d.intro||''), highlights:d.highlights||[],
        tap_water:d.tap_water||null, tipping:d.tipping||null,
        local_apps:d.local_apps||null,
        checklist:Array.isArray(d.checklist)?d.checklist:[],
        phrases:Array.isArray(d.phrases)?d.phrases:[],
      };
    };
    // 2026-07-26: this used to cache under TTL_WX (6h) because the payload
    // bundles a weather forecast alongside near-static facts (currency,
    // voltage, taboos, etc). That forced a fresh AI call for every
    // city/lang/currency combo every 6 hours across ALL users, which is what
    // exhausted the shared Gemini quota and cascaded into production 429s
    // (see the comment above callGeminiDirect in enrich_attractions.js for
    // the full incident). The Supabase `ai_cache` row here already IS the
    // "write once to DB" the fix calls for — TTL_INFO (7 days) is the actual
    // lever that stops re-generating it constantly.
    // 2026-09-28：天氣已改由 Open-Meteo 提供真實預報（見下方 homeWeather effect），
    // 不再由 AI 生成；舊快取內的 weather 欄位一律忽略。
    // 1. localStorage (instant, no network)
    const local = getAICache(cacheKey);
    if (local && _isValidHomeInfo(local)) {
      setHomeInfo(await _mapHomeInfo(local));
      setHomeInfoLoading(false); return;
    }
    if (local && !_isValidHomeInfo(local)) localStorage.removeItem(AI_CACHE_PFX + cacheKey); // purge stale gibberish
    setHomeInfoLoading(true);
    setHomeInfo(null);
    // 2. Supabase server cache (shared across all users)
    const server = await getServerAICache(cacheKey);
    if (server && _isValidHomeInfo(server)) {
      setAICache(cacheKey, server, TTL_INFO);
      setHomeInfo(await _mapHomeInfo(server));
      setHomeInfoLoading(false); return;
    }
    if (server && !_isValidHomeInfo(server)) supabase.from('ai_cache').delete().eq('cache_key', cacheKey).then(() => {}, () => {}); // purge stale gibberish (best-effort, ignore failure)
    // 3. Call Groq API
    const homeInfoPrompt = `You are a travel expert. Provide accurate travel essentials for ${city}, ${factsCountry}. Answer in ${al}.`;
    const homeInfoSys = `Reply ONLY a valid JSON object, no markdown:
{
  "intro": "2-3 sentences why travellers love ${city}",
  "currency": "Official local currency of ${factsCountry} with symbol, plus exchange rate to ${cur}. Example: 日圓 (JPY) · 1 ${cur} ≈ 19 JPY",
  "voltage": "Electrical voltage and plug type in ${factsCountry}. Example: 220V · Type G",
  "transport": "Main transport options in ${city}",
  "tips": "One practical travel tip for ${city}",
  "taboos": "2-3 SPECIFIC, concrete taboos a tourist could actually break in ${factsCountry} — STRICTLY about (a) dining/table manners, (b) dress codes or behavior at temples/religious sites, or (c) everyday social customs like greetings, gift-giving, photography, gestures. Every point must be a SPECIFIC actionable do/don't. NEVER include: politics, government, protests, national symbols, free speech, or even a passing mention that tourists should 'avoid discussing politics' — that topic is entirely off-limits. NEVER include vague generic social advice that isn't a real cultural/dining/customs taboo (e.g. 'respect elders', 'don't argue with locals', 'be polite', 'don't be loud'). Use proper punctuation.",
  "tap_water": "Is tap water safe to drink in ${city}? One short sentence.",
  "tipping": "Tipping culture in ${factsCountry}: expected/not expected, typical amount. One short sentence.",
  "local_apps": [{"name":"real, well-known app name — no truncation, no invented names", "desc":"REQUIRED, non-empty, what it's for in 4-8 characters"}, {"name":"app 2", "desc":"REQUIRED desc 2"}, {"name":"app 3", "desc":"REQUIRED desc 3"}],
  "checklist": ["short item 1 tourists must check before visiting ${city}", "short item 2", "short item 3", "short item 4"],
  "phrases": [
    {"zh":"你好","local":"local language","phonetic":"pronunciation"},
    {"zh":"謝謝","local":"local language","phonetic":"pronunciation"},
    {"zh":"對不起","local":"local language","phonetic":"pronunciation"},
    {"zh":"廁所在哪？","local":"local language","phonetic":"pronunciation"},
    {"zh":"多少錢？","local":"local language","phonetic":"pronunciation"},
    {"zh":"我不明白","local":"local language","phonetic":"pronunciation"},
    {"zh":"幫幫我！","local":"local language","phonetic":"pronunciation"}
  ],
  "emergency": {
    "police": "actual police emergency number in ${factsCountry}",
    "ambulance": "actual ambulance number in ${factsCountry}",
    "fire": "actual fire department number in ${factsCountry}",
    "other": "other important hotline"
  }
}
RULES: currency must be the LOCAL currency of ${factsCountry} not ${cur}. All emergency fields must contain real phone numbers. phrases MUST be in the actual local language of ${factsCountry}. local_apps MUST be exactly 3-4 items, every item MUST have both a real complete app name (never a truncated fragment) and a non-empty desc — never omit desc, never invent a fake app.`;

    // geminiFirst on every attempt: Gemini is far less prone to ideograph-soup than
    // the Groq fallback chain, and this call is infrequent enough (cached for hours,
    // shared across all users) that the extra Gemini quota cost is worth it.
    const fetchHomeD = (opts = {}) => callAI(homeInfoPrompt, homeInfoSys, 1800, GROQ_MODEL, { geminiFirst: true, ...opts });
    let d = await fetchHomeD();
    if (!_isValidHomeInfo(d)) {
      console.warn('[loadHomeInfo] gibberish/incomplete response, retrying for', city);
      d = await fetchHomeD();
    }
    if (_isValidHomeInfo(d)) {
      setAICache(cacheKey, d, TTL_INFO);
      setServerAICache(cacheKey, d, TTL_INFO);
      setHomeInfo(await _mapHomeInfo(d));
    } else if (d) {
      console.warn('[loadHomeInfo] dropping unusable AI response for', city);
    }
    setHomeInfoLoading(false);
  }, []);

  // 2026-07-26: disabled AI-generated fallback for hot/latest/deep attractions
  // (used to call Groq to invent "24 popular attractions" etc. when the DB
  // didn't have enough for a city). Per user decision: a plain text-generation
  // call has no grounding against real venues, so it could and did fabricate
  // places — inconsistent with the site's 100%-real-content policy. Real
  // Gemini Search-grounding could reduce (not eliminate) that risk but costs
  // real money at scale ($14-35 per 1,000 grounded queries) and wasn't worth
  // it versus just showing fewer cards for thin-coverage cities. The DB-backed
  // managed.hot/.latest/.deep (see getManagedForCity) are the only source now.
  const loadHomeAttractions = useCallback(async () => {
    setHomeAttractions([]); setHomeLatest([]); setHomeDeep([]); setHomeDeals([]);
    setHomeLoading(false);
  }, []);

  useEffect(() => {
    loadHomeInfo(homeCity, homeCountry, lang, currency);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, currency, homeCity]);

  // 首頁天氣：Open-Meteo 真實預報，與 AI 城市資訊分開載入
  useEffect(() => {
    let cancelled = false;
    setHomeWeather([]);
    fetchRealWeather(homeCity, homeCountry, lang).then(wx => { if (!cancelled) setHomeWeather(wx); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, homeCity]);

  useEffect(() => {
    setHomeDistrict('');
    loadHomeAttractions(homeCity, homeCountry, lang);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, homeCity]);

  // Load the home city's managed rows on demand (see loadCityRows)
  useEffect(() => {
    loadCityRows(homeCity);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeCity]);

  // ── SEARCH DATA ─────────────────────────────────────────────────────────────
  const [srRegion, setSrRegion] = useState('');
  const [srCountry, setSrCountry] = useState('');
  const [srCity, setSrCity] = useState('');
  const [srCityCustom, setSrCityCustom] = useState('');
  const [srCountries, setSrCountries] = useState([]);
  const [srCities, setSrCities] = useState([]);
  const [srCntMap, setSrCntMap] = useState({});
  const [srCityMap, setSrCityMap] = useState({});
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [srResult, setSrResult] = useState(null);
  const [srLoading, setSrLoading] = useState(false);
  const [srError, setSrError] = useState(false);
  const [srDistrict, setSrDistrict] = useState('');
  const [srAttrQuery, setSrAttrQuery] = useState('');
  const [srActiveTheme, setSrActiveTheme] = useState(null); // which of the 5 category cards is expanded below the strip
  // Same as the homepage: only offer districts with something to show, and
  // start on the first of them.
  const srCityName = srResult?.cityDisplay || srResult?.city || '';
  const srDistrictRows = useMemo(() => {
    const managed = getManagedForCity(srCityName);
    return [...managed.hot, ...managed.deep, ...(srResult?.attractions || []), ...(srResult?.deep || [])];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getManagedForCity only depends on managedAttractions
  }, [managedAttractions, srCityName, srResult]);
  const srDistricts = useMemo(
    () => availableDistricts(getDistricts(srCityName), srDistrictRows, CITY_EN_MAP[srCityName] || srCityName),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CITY_EN_MAP is a fixed lookup table
    [srCityName, srDistrictRows]
  );
  useEffect(() => {
    if (!srCityName) return;
    setSrDistrict(current => resolveDistrict(current, getDistricts(srCityName), srDistricts, srDistrictRows.length > 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the city or its options change, not on every pick
  }, [srCityName, srDistricts.join('|'), srDistrictRows.length > 0]);

  useEffect(() => {
    const district = srDistrict;
    const city     = srResult?.cityDisplay || srResult?.city || '';
    if (!district || !city) return;
    const cityEn = CITY_EN_MAP[city] || city;
    const key = `${cityEn}:${district}`;
    if (districtResults[key] !== undefined) return;
    const dbCount = managedAttractions.filter(a => {
      const c = (a.city || '').toLowerCase();
      return (c === city.toLowerCase() || c === cityEn.toLowerCase()) && matchesDistrict(a.district, district);
    }).length;
    if (dbCount >= 8) return;
    setDistrictLoading(true);
    setDistrictResults(prev => ({ ...prev, [key]: null }));
    fetch(`/api/photo?district_search=1&city=${encodeURIComponent(cityEn)}&district=${encodeURIComponent(district)}&lang=${lang}`)
      .then(r => r.json())
      .then(d => setDistrictResults(prev => ({ ...prev, [key]: d.places || [] })))
      .catch(() => setDistrictResults(prev => ({ ...prev, [key]: [] })))
      .finally(() => setDistrictLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srDistrict, srResult]);

  // Attraction detail bottom sheet
  const [selAttraction, setSelAttraction] = useState(null);
  const [attrDetail, setAttrDetail] = useState({});
  // intro is the vetted managed_attractions.description (Traditional Chinese
  // only in the DB) — translate it on demand into the active UI language,
  // same mechanism AttractionCard/LatestCard already use for their blurbs.
  const selAttrDetailKey = selAttraction ? `${selAttraction.cityKey || ''}_${selAttraction.name}_${lang}` : null;
  const selAttrIntro = selAttrDetailKey ? attrDetail[selAttrDetailKey]?.intro : null;
  const translatedSelAttrIntro = useTranslatedDesc(selAttrIntro, lang, !!selAttraction);

  const fetchAttractionDetail = async (a, cityKey) => {
    const cacheKey = `${cityKey}_${a.name}_${lang}`;
    const existing = attrDetail[cacheKey];
    if (existing && !existing._loading) return; // already loaded — skip
    if (existing?._loading) return;             // currently fetching — skip

    // Set loading placeholder synchronously → spinner shows immediately, no flash
    setAttrDetail(prev => ({ ...prev, [cacheKey]: { _loading: true } }));
    // Intro is the vetted managed_attractions.description — NOT an on-demand AI
    // guess. An earlier version had the model invent intro/highlights/tips/bestFor
    // per attraction with zero grounding, which produced factually wrong text
    // (e.g. Sky100 placed at "Tsim Sha Tsui Admiralty Station" — two unrelated
    // places stitched together). That violates the same real-data-only policy
    // that already stripped AI "insider tips" from articles (see DB_CHANGES.md).
    // highlights/tips/bestFor/duration have no DB equivalent, so they're left
    // empty rather than fabricated.
    const cityEnKey = (CITY_EN_MAP[cityKey] || cityKey || '').toLowerCase();
    const dbRow = a.description ? a : managedAttractions.find(r => {
      if (r.name !== a.name) return false;
      const rCityEn = (CITY_EN_MAP[r.city] || r.city || '').toLowerCase();
      return rCityEn === cityEnKey || (r.city || '').toLowerCase() === (cityKey || '').toLowerCase();
    });
    const cleaned = {
      intro: dbRow?.description || '',
      bestFor: '', duration: '', highlights: [], tips: [],
      // Suggested itinerary/transport for 深度體驗 wilderness picks — shown in
      // the detail sheet, not on the outer DeepStrip card (see DeepStrip.jsx).
      route: dbRow?.deep_route || a.route || '',
      nearby: null,
    };
    setAttrDetail(prev => ({ ...prev, [cacheKey]: cleaned }));
    // "Nearby" used to be a second AI call here — plain text generation, zero
    // grounding against real venues, same fabrication class the comment above
    // already flagged and fixed for intro/highlights/tips/bestFor (2026 Sky100
    // incident) but missed for this one. Found 2026-09-27 auditing the same bug
    // in doSearch's attraction-list fallback. Removed rather than left running:
    // `nearby` stays null (the UI already renders that as "no section" — this
    // is the same fallback path a rate-limited/failed call already took).
  };

  // Clear stale AI content when language changes
  useEffect(() => {
    setSrResult(null);
    setSrCountries([]); setSrCities([]); setSrRegion(''); setSrCountry(''); setSrCity('');
  }, [lang]);

  // 從 URL 參數恢復搜尋城市（支援直接連結 /search?city=Tokyo&country=Japan）
  useEffect(() => {
    if (location.pathname !== '/search') return;
    const params = new URLSearchParams(location.search);
    const cityParam    = params.get('city');
    const countryParam = params.get('country');
    if (cityParam && countryParam && !srResult) {
      setSrCity(cityParam);
      setSrCountry(countryParam);
      // 自動觸發搜尋（延遲讓 state 先更新）
      setTimeout(() => {
        setSrCity(cityParam);
        setSrCountry(countryParam);
      }, 100);
    }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSrCountries = async (rgn, ln) => {
    setLoadingGeo(true); setSrCountry(''); setSrCity(''); setSrCountries([]); setSrCities([]);
    const al = T[ln].aiLang;
    // Static-first: always use verified static list, no AI needed for country names
    const { en: sEn, map: sMap } = getStaticCountries(rgn, al);
    const { en: finalEn, map: finalMap } = applyCountryOverrides(rgn, sEn, sMap, al);
    setSrCountries(finalEn); setSrCntMap(finalMap);
    setLoadingGeo(false);
  };

  const fetchSrCities = async (ctry, ln) => {
    setLoadingGeo(true); setSrCity(''); setSrCities([]);
    const al = T[ln].aiLang;
    // Static-first: use verified list, only call AI if country not in static data
    const { en: sEn, map: sMap } = getStaticCities(ctry, al);
    if (sEn.length > 0) {
      let { en: finalEn, map: finalMap } = applyCityOverrides(ctry, sEn, sMap, al);
      // Only show cities that have DB data (distinct-city list from RPC)
      if (dbCitySet.size > 0) {
        const withData = finalEn.filter(c => dbCitySet.has(c.toLowerCase()));
        if (withData.length > 0) {
          finalEn = withData;
          finalMap = Object.fromEntries(withData.map(en => [en, finalMap[en]]).filter(([,v]) => v));
        }
      }
      setSrCities(finalEn); setSrCityMap(finalMap);
      setLoadingGeo(false); return;
    }
    const ck = `sr_cit_${ctry}_${ln}`;
    const cached = getAICache(ck);
    if (cached) { setSrCities(cached.en); setSrCityMap(cached.map); setLoadingGeo(false); return; }
    const d = await callAI(
      `List 20 real cities/towns in ${ctry} that travelers visit or use as a base — no monasteries, temples, mountains, deserts, national parks, museums, or provinces/regions, only actual municipalities.`,
      `Reply ONLY JSON no markdown: {"cities":[{"en":"string","local":"string"}]} where "local" is TRANSLATED INTO ${al}`,
      900, GROQ_MODEL_FAST
    );
    if (d?.cities) {
      const en=d.cities.map(c=>c.en); const m={};
      d.cities.forEach(c=>{ m[c.en]=c.local||c.en; });
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, en, m, al);
      setAICache(ck, { en: finalEn, map: finalMap }, TTL_GEO);
      setSrCities(finalEn); setSrCityMap(finalMap);
    }
    setLoadingGeo(false);
  };

  const doSearch = async () => {
    if (!srRegion||!srCountry||!srCity) return;
    setSrLoading(true); setSrResult(null); setSrError(false);
    // Everything below is one big async chain (Supabase reads, AI calls) with
    // no error handling of its own — without this wrapper, any unexpected
    // throw anywhere in it (a network blip, a Supabase hiccup) kills the
    // function silently and leaves the search button stuck on "搜尋中…"
    // forever with no error shown, since setSrLoading(false) would never run.
    try {
    const al = T[lang].aiLang;
    // City "facts" (intro/voltage/transport/tips/taboos/tap_water/tipping/
    // local_apps/checklist/phrases/emergency) barely ever change, so they're
    // cached separately from currency — keyed without `currency` and kept for
    // a year — instead of forcing a full AI re-generation every time someone
    // just switches their display currency. Only the exchange-rate line is
    // cached per-currency, on the shorter TTL_INFO window.
    // Hong Kong and Macau are listed as cities of "China" in the geo hierarchy
    // (for search/nav grouping), but they have their own currency, tipping norms,
    // ride-hailing apps, and emergency numbers distinct from mainland China — so
    // AI-generated facts must be asked about them specifically, not about "China".
    const HK_NAMES = new Set(['香港','Hong Kong','홍콩']);
    const MO_NAMES = new Set(['澳門','澳门','Macau','マカオ','마카오']);
    const factsCountry = HK_NAMES.has(srCity) || MO_NAMES.has(srCity) ? srCity : srCountry;
    // Curated DB facts (currency/voltage/tipping/emergency/local_apps) take
    // priority over whatever the AI generates for these fields — see
    // create_country_facts.sql and getCountryFacts above.
    const dbFacts = await getCountryFacts(factsCountry);
    // Keyed by factsCountry (not srCountry) so existing bad Hong Kong/Macau
    // cache entries generated under "China" are bypassed and regenerated fresh.
    const ckFacts = `sr_facts_${srCity}_${factsCountry}_${lang}`;
    const ckCur = `sr_cur_${srCity}_${factsCountry}_${lang}_${currency}`;
    const ck2 = `sr_attr4_${srCity}_${srCountry}_${lang}`;
    // Check localStorage first, then Supabase server cache
    const [lcF, lcC, lc2] = [getAICache(ckFacts), getAICache(ckCur), getAICache(ck2)];
    const [scF, scC, sc2] = await Promise.all([
      lcF ? null : getServerAICache(ckFacts),
      lcC ? null : getServerAICache(ckCur),
      lc2 ? null : getServerAICache(ck2),
    ]);
    const _rawFacts = lcF || (scF ? (setAICache(ckFacts, scF, TTL_FACTS), scF) : null);
    const _rawCur = lcC || (scC ? (setAICache(ckCur, scC, TTL_INFO), scC) : null);
    // Validate cached data — if all key fields are null, or the text is gibberish
    // (e.g. cached before the gibberish check existed), treat as no cache and clear it
    const _factsValid = _rawFacts && ['voltage','transport','intro'].some(k => _rawFacts[k]) &&
      cityInfoFieldsLookOk(_rawFacts, al);
    if (_rawFacts && !_factsValid) {
      localStorage.removeItem(AI_CACHE_PFX + ckFacts);
      supabase.from('ai_cache').delete().eq('cache_key', ckFacts).then(() => {}, () => {}); // best-effort purge, ignore failure
    }
    const _curValid = _rawCur && typeof _rawCur.currency === 'string' && looksLikeScript(_rawCur.currency, al);
    if (_rawCur && !_curValid) {
      localStorage.removeItem(AI_CACHE_PFX + ckCur);
      supabase.from('ai_cache').delete().eq('cache_key', ckCur).then(() => {}, () => {}); // best-effort purge, ignore failure
    }
    // Only skip the AI call entirely when BOTH pieces are cached — a facts
    // hit + currency miss (e.g. first time seeing this currency for a city
    // already visited) still needs one fresh call, same as before.
    const c1 = (_factsValid && _curValid) ? { ..._rawFacts, currency: _rawCur.currency } : null;
    const _rawC2 = lc2 || (sc2 ? (setAICache(ck2, sc2, TTL_ATTR), sc2) : null);
    // A rate-limited AI response (or one with no attractions) must never be
    // treated as a valid cache hit — it would otherwise get reused for the
    // full TTL_ATTR window and permanently block both the DB-first path and
    // a retry AI call from ever running for that city.
    const _c2Valid = _rawC2 && !_rawC2.__rateLimited && _rawC2.attractions?.length;
    if (_rawC2 && !_c2Valid) {
      localStorage.removeItem(AI_CACHE_PFX + ck2);
      supabase.from('ai_cache').delete().eq('cache_key', ck2).then(() => {}, () => {}); // best-effort purge, ignore failure
    }
    const c2 = _c2Valid ? _rawC2 : null;
    // 2026-09-28：天氣改用 Open-Meteo 真實預報（有自己的 localStorage 快取），
    // 不再由 AI 生成，亦不再寫入 ai_cache。
    const wxPromise = fetchRealWeather(srCity, srCountry, lang);

    const cityInfoSys = `Reply ONLY a valid JSON object, no markdown, no explanation:
{
  "intro": "2-3 sentences why travellers love ${srCity}",
  "currency": "The official currency of ${factsCountry} with its symbol, plus approximate exchange rate to ${currency}. Example format: 日圓 (JPY) · 1 ${currency} ≈ 19 JPY",
  "voltage": "Electrical voltage and plug type in ${factsCountry}. Example: 220V · Type G",
  "transport": "Main transport options available in ${srCity}",
  "tips": "One practical travel tip specific to ${srCity}",
  "taboos": "2-3 SPECIFIC, concrete taboos a tourist could actually break in ${factsCountry} — STRICTLY about (a) dining/table manners, (b) dress codes or behavior at temples/religious sites, or (c) everyday social customs like greetings, gift-giving, photography, gestures. Every point must be a SPECIFIC actionable do/don't. NEVER include: politics, government, protests, national symbols, free speech, or even a passing mention that tourists should 'avoid discussing politics' — that topic is entirely off-limits. NEVER include vague generic social advice that isn't a real cultural/dining/customs taboo (e.g. 'respect elders', 'don't argue with locals', 'be polite', 'don't be loud'). Use proper punctuation between points.",
  "tap_water": "Is tap water safe to drink in ${srCity}? One short sentence.",
  "tipping": "Tipping culture in ${factsCountry}: expected/not expected, typical amount. One short sentence.",
  "local_apps": [{"name":"real, well-known app name — no truncation, no invented names", "desc":"REQUIRED, non-empty, what it's for in 4-8 characters"}, {"name":"app 2", "desc":"REQUIRED desc 2"}, {"name":"app 3", "desc":"REQUIRED desc 3"}],
  "checklist": ["short item 1 tourists must check before visiting ${srCity}", "short item 2", "short item 3", "short item 4"],
  "phrases": [
    {"zh":"你好","local":"local translation","phonetic":"pronunciation guide"},
    {"zh":"謝謝","local":"local translation","phonetic":"pronunciation guide"},
    {"zh":"對不起","local":"local translation","phonetic":"pronunciation guide"},
    {"zh":"廁所在哪？","local":"local translation","phonetic":"pronunciation guide"},
    {"zh":"多少錢？","local":"local translation","phonetic":"pronunciation guide"},
    {"zh":"我不明白","local":"local translation","phonetic":"pronunciation guide"},
    {"zh":"幫幫我！","local":"local translation","phonetic":"pronunciation guide"}
  ],
  "emergency": {
    "police": "actual police emergency phone number in ${factsCountry}",
    "ambulance": "actual ambulance phone number in ${factsCountry}",
    "fire": "actual fire department phone number in ${factsCountry}",
    "other": "any other important hotline (coast guard, tourist hotline etc)"
  }
}
RULES: currency MUST be the local currency of ${factsCountry} not ${currency}. All emergency fields must have real phone numbers. phrases MUST be in the actual local language of ${factsCountry}. local_apps MUST be exactly 3-4 items, every item MUST have both a real complete app name (never a truncated fragment) and a non-empty desc — never omit desc, never invent a fake app.`;

    const cityInfoPrompt = `You are a travel expert. Provide accurate travel essentials for ${srCity}, ${factsCountry}. Answer in ${al}.`;

    const _isValidInfo = d => d && ['currency','voltage','transport'].some(k => d[k]) &&
      cityInfoFieldsLookOk(d, al);
    // Fetch d1 with validation — retry up to 2 times if response has all-null fields
    // or gibberish content. geminiFirst on every attempt: Gemini is far less prone to
    // hallucinating CJK ideograph-soup than the Groq fast-model fallback, and this call
    // is cached for hours and shared across all users, so the extra Gemini quota is worth it.
    const fetchD1 = async () => {
      const r1 = await callAI(cityInfoPrompt, cityInfoSys, 1800, GROQ_MODEL_FAST, { geminiFirst: true });
      if (_isValidInfo(r1)) return r1;
      await new Promise(res => setTimeout(res, 2500));
      const r2 = await callAI(cityInfoPrompt, cityInfoSys, 1000, GROQ_MODEL_FAST, { geminiFirst: true });
      if (_isValidInfo(r2)) return r2;
      await new Promise(res => setTimeout(res, 3000));
      return callAI(cityInfoPrompt, cityInfoSys, 1800, GROQ_MODEL_FAST, { geminiFirst: true });
    };

    // DB-first: use whatever real attractions the DB has for this city.
    // Load this city's rows on demand (returns them directly so we don't race
    // the async setState) — the table is too large to keep entirely client-side.
    const srCityDisplay = srCityMap[srCity] || srCity;
    const srCityRows = await loadCityRows(srCity);
    const { hot: dbHot, latest: dbLatest, deep: dbDeep } =
      shapeManagedRows(srCityRows.length ? srCityRows : managedAttractions, srCityDisplay);
    // Used to require >= 8 "hot" rows before trusting the DB, otherwise fall
    // through to an AI call that filled the rest up to 24 — a threshold for
    // "is there enough real data to skip generating fake data". That AI call
    // is gone (2026-09-27 audit); auditing which cities it used to fire for
    // found 17 cities that already had hundreds of real rows by the time this
    // was checked (Tokyo, Kyoto, Seoul, Osaka, Barcelona... — the DB simply
    // hadn't been scraped yet when each was first searched, back in May-Aug
    // 2026) — so the threshold was never a correctness gate, just a "wait for
    // scraping to catch up" gate that no longer serves a purpose. With no AI
    // to fall back to, requiring 8 only hides the cities that today sit at
    // 1-7 real hot picks (confirmed 25 such cities, e.g. Karuizawa, Liverpool,
    // Yokohama, Frankfurt) behind an empty results page instead of showing
    // the real ones. Show real data whenever there is any.
    const d2FromDb = dbHot.length > 0
      ? { attractions: dbHot, latest: dbLatest, deep: dbDeep }
      : null;

    // Shared by both the fast (progressive) and slow (sequential) paths below.
    const buildInfo = async (rawInfo) => {
      if (!rawInfo) return null;
      const info = await applyCountryFacts(srCity, rawInfo, dbFacts, currency, lang);
      return {
        currency:info.currency, voltage:info.voltage, transport:info.transport,
        tips:(Array.isArray(info.tips)?info.tips:info.tips?[String(info.tips)]:[]).map(cleanAIText),
        emergency:applyEmergencyOverride(srCity, info.emergency, lang),
        taboos:typeof info.taboos==='string'?cleanAIText(info.taboos):info.taboos,
        intro:cleanAIText(info.intro||''), highlights:[],
        tap_water: info.tap_water || null, tipping: info.tipping || null,
        local_apps: info.local_apps || null,
        checklist: Array.isArray(info.checklist) ? info.checklist : [],
        phrases: Array.isArray(info.phrases) ? info.phrases : [],
      };
    };

    // Fast path: attractions are already known (server cache or DB) — paint the
    // page immediately, then stream city-info + weather in afterwards instead of
    // blocking the whole result on three sequential AI round-trips.
    const d2sync = c2 || d2FromDb;
    if (d2sync && d2sync.attractions?.length) {
      destTimezone.current = getCityTimezone(srCity) || null;
      setSrDistrict('');
      const initialInfo = c1 ? await buildInfo(c1) : null;
      setSrResult({
        city: srCity, country: srCountry,
        cityDisplay: srCityMap[srCity]||srCity,
        countryDisplay: srCntMap[srCountry]||srCountry,
        info: initialInfo,
        // fetchD1() below retries internally (up to 3 attempts with backoff)
        // before giving up — without tracking that as its own loading state,
        // the render below has no way to tell "still retrying" apart from
        // "genuinely failed", so it flashed a false "城市資訊載入失敗" the
        // moment the first attempt hit a 429, even though a later retry
        // then succeeded a few seconds after.
        infoLoading: !c1,
        weather: [],
        latest: (d2sync.latest||[]).slice(0,6),
        attractions: (d2sync.attractions||[]).slice(0,16),
        deep: (d2sync.deep||[]).slice(0,8),
        deals: [],
      });
      navigate(`/search?city=${encodeURIComponent(srCity)}&country=${encodeURIComponent(srCountry)}`);
      if (!c1) fetchD1().then(async info => {
        if (info && _isValidInfo(info)) {
          setAICache(ckFacts, info, TTL_FACTS); setServerAICache(ckFacts, info, TTL_FACTS);
          setAICache(ckCur, { currency: info.currency }, TTL_INFO); setServerAICache(ckCur, { currency: info.currency }, TTL_INFO);
          const builtInfo = await buildInfo(info);
          setSrResult(prev => (prev && prev.city === srCity) ? { ...prev, info: builtInfo, infoLoading: false } : prev);
        } else {
          setSrResult(prev => (prev && prev.city === srCity) ? { ...prev, infoLoading: false } : prev);
        }
      });
      wxPromise.then(wx => {
        if (wx.length) setSrResult(prev => (prev && prev.city === srCity) ? { ...prev, weather: wx } : prev);
      });
      return;
    }

    // Slow path (city has no DB/cached attractions): sequential to avoid Groq 429
    const d1 = c1 ? c1 : await fetchD1();
    // d2 used to fall back to a plain-text Groq call here inventing "24 real-world
    // attractions" for any city with no DB coverage — zero grounding, same class
    // of fabrication already banned for the home page's hot/latest/deep strips
    // (commit 4241ca9, 2026-07-26). Audited 2026-09-27 after Cambridge — a city
    // that's in the static UK city list (src/constants/geo.js) but was never
    // scraped — turned out to have invented venues (a non-existent "劍橋藝術與
    // 設計博物館") served from this exact path, cached in ai_cache and shown to
    // real users. Removed: a city with no DB attractions now shows city
    // facts/weather (still real/typical, not venue-level claims) with an empty
    // attractions list, never a fabricated one.
    const d2 = (c2 || d2FromDb) || null;
    const wxArr = await wxPromise;

    if (d1 && !c1 && _isValidInfo(d1)) {
      setAICache(ckFacts, d1, TTL_FACTS); setServerAICache(ckFacts, d1, TTL_FACTS);
      setAICache(ckCur, { currency: d1.currency }, TTL_INFO); setServerAICache(ckCur, { currency: d1.currency }, TTL_INFO);
    }

    if (d1 || d2) {
      const tz = getCityTimezone(srCity);
      destTimezone.current = tz || null;
      setSrDistrict('');
      setSrResult({
        city: srCity, country: srCountry,
        cityDisplay: srCityMap[srCity]||srCity,
        countryDisplay: srCntMap[srCountry]||srCountry,
        info: d1
          ? { currency:d1.currency, voltage:d1.voltage, transport:d1.transport,
              tips:(Array.isArray(d1.tips)?d1.tips:d1.tips?[String(d1.tips)]:[]).map(cleanAIText),
              emergency:applyEmergencyOverride(srCity, d1.emergency, lang), taboos:typeof d1.taboos==='string'?cleanAIText(d1.taboos):d1.taboos,
              intro:cleanAIText(d1.intro||''), highlights:[],
              tap_water: d1.tap_water || null,
              tipping: d1.tipping || null,
              local_apps: d1.local_apps || null,
              checklist: Array.isArray(d1.checklist) ? d1.checklist : [],
              phrases: Array.isArray(d1.phrases) ? d1.phrases : [],
            }
          : null,
        weather: wxArr,
        latest: (d2?.latest||[]).slice(0,6),
        attractions: (d2?.attractions||[]).slice(0,16),
        deep: (d2?.deep||[]).slice(0,8),
        deals: [],
      });
      // 搜尋成功後把城市寫入 URL（可書籤、可分享）
      navigate(`/search?city=${encodeURIComponent(srCity)}&country=${encodeURIComponent(srCountry)}`);
    } else {
      setSrError(true);
    }
    } catch (e) {
      console.error('[doSearch]', e);
      setSrError(true);
    } finally {
      setSrLoading(false);
    }
  };

  // Progressive search flow: auto-run once region+country+city are all picked
  const srAutoSearchedRef = useRef('');
  useEffect(() => {
    if (srRegion && srCountry && srCity) {
      const key = `${srRegion}|${srCountry}|${srCity}`;
      if (srAutoSearchedRef.current !== key) {
        srAutoSearchedRef.current = key;
        doSearch();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srRegion, srCountry, srCity]);

  // ── TRIP DATA ───────────────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const [trRegion, setTrRegion] = useState('');
  const [trCountry, setTrCountry] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [trCountries, setTrCountries] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [trCities, setTrCities] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [trCntMap, setTrCntMap] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [trCityMap, setTrCityMap] = useState({});
  const [trSelCities, setTrSelCities] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingTGeo, setLoadingTGeo] = useState(false);

  const [trDays, setTrDays] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [trDepDate, setTrDepDate] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [trArrTime, setTrArrTime] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [trRetTime, setTrRetTime] = useState('');
  const [trActivities, setTrActivities] = useState([]);
  const [trCustomSpots, setTrCustomSpots] = useState([]);
  const [trCustomInput, setTrCustomInput] = useState('');
  const [trDestinations, setTrDestinations] = useState([{ id:1, country:'', city:'', date:'', hotelArea:'' }]);
  const [trReturnDate, setTrReturnDate] = useState(''); // optional — when set alongside the first destination's date, auto-calculates trDays
  const [trDestCityData, setTrDestCityData] = useState({});
  // Real outlet-mall options for the trip's own cities, shown once "Outlet" is
  // ticked so the traveller picks the actual venue instead of the itinerary
  // generator silently auto-selecting the highest-rated one behind the scenes.
  const [trOutletOptions, setTrOutletOptions] = useState([]);
  const [trSelectedOutlet, setTrSelectedOutlet] = useState(null);
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  // 溫泉：跟 Outlet 同一套做法 — 勾選「溫泉」後列出行程城市內資料庫裡的真實溫泉讓旅客自己挑
  const [trOnsenOptions, setTrOnsenOptions] = useState([]);
  const [trSelectedOnsen, setTrSelectedOnsen] = useState(null);
  const [loadingOnsens, setLoadingOnsens] = useState(false);
  // 動物園／海洋館／博物館：勾選後列出行程城市的真實景點讓旅客挑（多城市先選城市）。
  // 選好的景點在生成時併入「自訂景點」流程（指定日子＋保險補位）。
  const [trVenuePicks, setTrVenuePicks] = useState({}); // { '動物園': [資料列, ...] }
  const [trVenueCity, setTrVenueCity] = useState({});   // { '動物園': 旅客填的城市字串 }
  const [trVenueRows, setTrVenueRows] = useState({});   // { '動物園|旅客填的城市': 資料列[] | 'loading' }
  const [trVenueQuery, setTrVenueQuery] = useState({}); // 搜尋框文字
  const [trVenueMore, setTrVenueMore] = useState({});   // 是否展開全部
  const [trStyle, setTrStyle] = useState({ culture:20, shopping:20, entertainment:20, nature:20, food:20 });
  const [trStyleCards, setTrStyleCards] = useState(['balanced']);
  const [trModes, setTrModes] = useState([]);
  const [trCompanion, setTrCompanion] = useState('solo');
  // 獨立開關：不管上面選哪種同行關係，只要有長者同行就疊加放慢節奏/避樓梯的限制
  const [trElderly, setTrElderly] = useState(false);
  // 規劃行程改成3步驟精簡流程：1=必填(目的地/天數) 2=選填細節 3=風格與出行方式
  const [tripStep, setTripStep] = useState(1);
  const [showTripGuide, setShowTripGuide] = useState(false); // 行程規劃使用教學彈窗
  const [itinerary, setItinerary] = useState(null);
  // Make sure every trip city's full row set is loaded into managedAttractions
  // so swap candidates work even for a saved/reopened itinerary that didn't
  // go through genItinerary's own loadCityRows calls this session.
  useEffect(() => {
    if (!itinerary?.days?.length) return;
    const cities = [...new Set(itinerary.days.map(d => d.city).filter(Boolean))];
    cities.forEach(c => { loadCityRows(c).catch(() => {}); });
  }, [itinerary]); // eslint-disable-line react-hooks/exhaustive-deps

  // Category buckets that can substitute for each other on a "換一批" swap.
  // Food only swaps with food (a dinner slot shouldn't turn into a museum);
  // play/shopping share a bucket since a shopping street and an entertainment
  // venue are both reasonable "afternoon activity" substitutes; culture and
  // nature stay in their own lane.
  const CATEGORY_SWAP_GROUP = { food:['food'], play:['play','shopping'], shopping:['play','shopping'], culture:['culture'], nature:['nature'] };
  const SWAP_RADIUS_KM = 6;

  // Logistics slots (check-out → airport, end of trip) aren't venues — the AI
  // leaves them with no category/coordinates, which used to make the swap row
  // offer any random attraction in the city as a "replacement" for the flight
  // home. Real stops belong in the slot BEFORE the transfer, so no swap row.
  const LOGISTICS_SLOT_RE = /退房|退宿|check[\s-]?out|前往.{0,6}(機場|机场|空港|공항|airport)|往.{0,4}(機場|机场)|(to|至|到).{0,12}(airport|機場|机场)|結束行程|结束行程|end of (the )?trip|チェックアウト|空港へ|체크아웃|공항으로/i;
  // 冇 category 的時段用嚟推斷「呢個係食飯時段」，換一批只推餐飲。
  const FOOD_SLOT_RE = /早餐|午餐|晚餐|晚飯|午飯|宵夜|夜宵|餐廳|餐厅|食堂|美食|小吃|燒肉|烧肉|牛排|拉麵|拉面|壽司|寿司|居酒屋|料理|咖啡|breakfast|brunch|lunch|dinner|supper|restaurant|izakaya|cafe|朝食|昼食|夕食|ディナー|ランチ|아침|점심|저녁|식당|맛집/i;
  const isLogisticsSlot = (sd) => !!sd && !sd.category && LOGISTICS_SLOT_RE.test(`${sd.activity || ''} ${sd.location || ''}`);

  // Real-venue swap options for a given day/slot: same-city candidates from
  // managedAttractions within SWAP_RADIUS_KM of the slot's own coordinates.
  // Deliberately proximity-based rather than district-text matching — a
  // meaningful chunk of DB rows have a null district, and even non-null
  // values mix granularities (ward names like 中區 vs neighbourhood names
  // like 大須), so exact-string matching silently dropped most candidates.
  const cityAliasesFor = (cityRaw) => {
    const en = (CITY_EN_MAP[cityRaw] || cityRaw || '').toLowerCase();
    const aliases = new Set([(cityRaw || '').toLowerCase(), en]);
    for (const [zh, e] of Object.entries(CITY_EN_MAP)) if (e.toLowerCase() === en) aliases.add(zh.toLowerCase());
    return aliases;
  };

  // sd (day[slotKey]) may be null — an empty slot (cleared, or never filled
  // by the AI) still needs "換一批" to work, anchored on a sibling slot.
  const computeSwapCandidates = (day, slotKey, cityRaw, usedNames) => {
    const sd = day[slotKey];
    if (isLogisticsSlot(sd)) return [];
    const cityAliases = cityAliasesFor(cityRaw);
    // AI 自創、對不上資料庫的時段冇 category，以前會完全不過濾類別，
    // 令「歡迎晚餐」都推纜車站／行山團。按文字推斷是否餐飲時段。
    // 午餐、晚餐時段一律只推餐飲（即使 AI 填咗非餐飲 category）。
    const isMealSlot = slotKey === 'lunch' || slotKey === 'evening';
    const slotCat = isMealSlot ? 'food'
      : sd?.category || (sd && FOOD_SLOT_RE.test(`${sd.activity || ''} ${sd.location || ''} ${sd.tips || ''}`) ? 'food' : null);
    const allowedCats = slotCat ? (CATEGORY_SWAP_GROUP[slotCat] || [slotCat]) : null;
    const cityRows = managedAttractions
      .filter(a => a.lat != null && a.lng != null && cityAliases.has((a.city || '').toLowerCase()));
    const basePool = cityRows
      .filter(a => !usedNames.has(a.name) && (!allowedCats || allowedCats.includes(a.category)));

    // The AI sometimes leaves a slot with no verified coordinates at all
    // (invented/unmatched venue), or the slot is empty entirely — either way
    // swapCandidates used to just return [] then, which meant exactly the
    // slots most likely to need a real pick had no "pick a real one instead"
    // option. Anchor on another verified slot from the same day if one
    // exists; only fall back to city-wide (sorted by rating, no distance
    // cutoff) when nothing that day has coordinates.
    let origin = (sd?._lat != null && sd?._lng != null) ? { lat: sd._lat, lng: sd._lng } : null;
    // 冇座標但有地區（例如「三宮」「南京町」）：用資料庫同地區景點的中位數座標做錨點，
    // 比借用其他時段或全城排序更貼近該時段實際位置。
    if (!origin && sd) {
      const keys = [sd.district, sd.location].map(s => (s || '').trim()).filter(s => s.length >= 2);
      const hits = keys.length ? cityRows.filter(a => keys.some(k =>
        (a.district && a.district.length >= 2 && (a.district === k || a.district.includes(k) || k.includes(a.district))) || a.name === k)) : [];
      if (hits.length) {
        const median = (arr) => { const s = arr.slice().sort((x, y) => x - y); return s[Math.floor(s.length / 2)]; };
        origin = { lat: median(hits.map(a => a.lat)), lng: median(hits.map(a => a.lng)) };
      }
    }
    if (!origin) {
      const otherSlot = daySlotOrder(day)
        .filter(k => k !== slotKey)
        .map(k => day[k])
        .find(s => s?._lat != null && s?._lng != null);
      if (otherSlot) origin = { lat: otherSlot._lat, lng: otherSlot._lng };
    }

    const pool = origin
      ? basePool
          .map(a => ({ ...a, _distKm: haversineKm(origin, { lat: a.lat, lng: a.lng }) }))
          .filter(a => a._distKm <= SWAP_RADIUS_KM)
          .sort((a, b) => a._distKm - b._distKm)
      : basePool.slice().sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0));

    // Prefer the SAME specify_cat as the current venue, not just the broad
    // category — "play" alone spans theme parks, cinemas, and nightlife, so
    // without this a theme-park slot could offer a strip club as a swap
    // (both filed under play/其它休閒/娛樂景點-style catch-alls). Only fall
    // back to the wider category pool if the narrow match is too thin.
    if (sd?.specify_cat && (!isMealSlot || sd.category === 'food')) {
      const narrow = pool.filter(a => a.specify_cat === sd.specify_cat);
      if (narrow.length >= 3) return narrow.slice(0, 12);
    }
    return pool.slice(0, 12);
  };

  // swapCandidates used to redo the filter/haversine/sort work above from
  // scratch on every render of every day/slot — including renders triggered
  // by state that has nothing to do with the itinerary (typing in an
  // unrelated search box, toggling a favourite, opening the trip-robot
  // panel). managedAttractions can run into the thousands for one city
  // (Hong Kong alone is 3,000+ active rows), so that added up. Precompute
  // every day/slot's candidate list once per itinerary/managedAttractions
  // change instead, and have swapCandidates just look up the cached result.
  const swapCandidatesCache = useMemo(() => {
    const map = new Map();
    if (!itinerary?.days?.length) return map;
    const usedNames = new Set(
      itinerary.days.flatMap(d => [
        d.morning?.location, d.morning?.activity, d.lunch?.location, d.lunch?.activity,
        d.afternoon?.location, d.afternoon?.activity,
        d.evening?.location, d.evening?.activity,
      ].filter(Boolean))
    );
    for (const day of itinerary.days) {
      const cityRaw = day.city || trDestinations[0]?.city || trCountry || '';
      for (const slotKey of daySlotOrder(day)) {
        map.set(`${day.day}_${slotKey}`, computeSwapCandidates(day, slotKey, cityRaw, usedNames));
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itinerary, managedAttractions, trDestinations, trCountry]);

  const swapCandidates = (day, slotKey) => swapCandidatesCache.get(`${day.day}_${slotKey}`) || [];

  // Which "page" of 5 each slot's candidate row is currently showing.
  // "換一批" advances this per slot; the row itself always displays 5 cards
  // (name + photo), each with its own 詳情/選擇 actions.
  const [swapBatchPage, setSwapBatchPage] = useState({});
  const swapBatchKey = (day, slotKey) => `${day.day}_${slotKey}`;
  const swapBatchShown = (day, slotKey, cityRaw) => {
    const candidates = swapCandidates(day, slotKey, cityRaw);
    if (!candidates.length) return [];
    const pages = Math.ceil(candidates.length / 5);
    const page = (swapBatchPage[swapBatchKey(day, slotKey)] || 0) % pages;
    return candidates.slice(page * 5, page * 5 + 5);
  };
  const swapNextBatch = (day, slotKey) => {
    const k = swapBatchKey(day, slotKey);
    setSwapBatchPage(prev => ({ ...prev, [k]: (prev[k] || 0) + 1 }));
  };

  // Recompute estimatedTotal from the actual per-day costs, same rationale as
  // genItinerary's own version: the displayed total must always agree with
  // the per-day breakdown, so any edit that can change dailyCost has to
  // re-run this before writing the new itinerary state.
  const recomputeEstimatedTotal = (days, currentTotal) => {
    const parseCost = (s) => Number(String(s || '').replace(/[^\d.]/g, '')) || 0;
    const sum = days.reduce((s, d) => s + parseCost(d.dailyCost), 0);
    return sum > 0 ? `${currSymbol}${Math.round(sum).toLocaleString()}` : currentTotal;
  };

  // Replace one time-slot's venue with a picked alternative, then recompute
  // travelToNext for that slot and the one before it using real coordinates.
  const swapSlot = (day, slotKey, alt, cityRaw) => {
    const order = daySlotOrder(day);
    const idx = order.indexOf(slotKey);
    const newDay = { ...day };
    newDay[slotKey] = {
      ...day[slotKey],
      activity: alt.name,
      location: alt.name,
      district: alt.district || day[slotKey]?.district,
      category: alt.category || day[slotKey]?.category,
      specify_cat: alt.specify_cat || day[slotKey]?.specify_cat,
      _lat: alt.lat, _lng: alt.lng, _place_id: alt.place_id,
    };
    const coordOf = (s) => (s?._lat != null && s?._lng != null) ? { lat: s._lat, lng: s._lng } : null;
    const langShort = lang.startsWith('zh') ? 'zh' : 'en';
    if (idx > 0) {
      const prevKey = order[idx - 1];
      const est = estimateTravelBetween(coordOf(newDay[prevKey]), coordOf(newDay[slotKey]), null, langShort);
      if (est) newDay[prevKey] = { ...newDay[prevKey], travelToNext: est };
    }
    if (idx < order.length - 1) {
      const nextKey = order[idx + 1];
      const est = estimateTravelBetween(coordOf(newDay[slotKey]), coordOf(newDay[nextKey]), null, langShort);
      if (est) newDay[slotKey] = { ...newDay[slotKey], travelToNext: est };
    }
    setItinerary(prev => {
      const days = prev.days.map(d => d === day ? newDay : d);
      return { ...prev, days, estimatedTotal: recomputeEstimatedTotal(days, prev.estimatedTotal) };
    });
  };

  // Clear a time slot entirely — lets the traveller deliberately leave a
  // gap (rest / free time) instead of the AI always filling all 3 slots
  // every single day. Also drops the now-stale travelToNext on the slot
  // before it, since there's nothing left to travel to at this position.
  const clearSlot = (day, slotKey) => {
    const order = daySlotOrder(day);
    const idx = order.indexOf(slotKey);
    const parseCost = (s) => Number(String(s || '').replace(/[^\d.]/g, '')) || 0;
    const removedCost = parseCost(day[slotKey]?.cost);
    const newDailyCost = Math.max(0, parseCost(day.dailyCost) - removedCost);
    const newDay = { ...day, [slotKey]: null, dailyCost: `${currSymbol}${newDailyCost.toLocaleString()}` };
    if (idx > 0) {
      const prevKey = order[idx - 1];
      if (newDay[prevKey]) newDay[prevKey] = { ...newDay[prevKey], travelToNext: null };
    }
    setItinerary(prev => {
      const days = prev.days.map(d => d === day ? newDay : d);
      return { ...prev, days, estimatedTotal: recomputeEstimatedTotal(days, prev.estimatedTotal) };
    });
  };

  // String key of whichever slot's inline "瀏覽分類" panel is expanded
  // (e.g. "3_morning" or "3_extra_1"), else null. Only one open at a time.
  const [categoryBrowseKey, setCategoryBrowseKey] = useState(null);
  const MAX_EXTRAS = 2;

  // Up to MAX_EXTRAS additional activities per day, on top of the fixed
  // morning/afternoon/evening slots — genuinely additive (the original 3
  // never disappear), each independently swappable and removable.
  const addExtraActivity = (day, alt) => {
    const extras = day.extras || [];
    if (extras.length >= MAX_EXTRAS) return;
    const newExtra = { activity: alt.name, location: alt.name, district: alt.district, category: alt.category, specify_cat: alt.specify_cat, _lat: alt.lat, _lng: alt.lng, _place_id: alt.place_id };
    const newDay = { ...day, extras: [...extras, newExtra] };
    setItinerary(prev => ({ ...prev, days: prev.days.map(d => d === day ? newDay : d) }));
  };
  const swapExtraActivity = (day, idx, alt) => {
    const extras = [...(day.extras || [])];
    extras[idx] = { ...extras[idx], activity: alt.name, location: alt.name, district: alt.district, category: alt.category, specify_cat: alt.specify_cat, _lat: alt.lat, _lng: alt.lng, _place_id: alt.place_id };
    const newDay = { ...day, extras };
    setItinerary(prev => ({ ...prev, days: prev.days.map(d => d === day ? newDay : d) }));
  };
  const removeExtraActivity = (day, idx) => {
    const extras = (day.extras || []).filter((_, i) => i !== idx);
    const newDay = { ...day, extras };
    setItinerary(prev => ({ ...prev, days: prev.days.map(d => d === day ? newDay : d) }));
  };

  const [loadingTrip, setLoadingTrip] = useState(false);
  const [genTripErr, setGenTripErr] = useState('');
  // Progress UI for genItinerary — reflects REAL pipeline stages (not a fake
  // timer): 0=prepping request, 1=candidate pool built, 2=AI generating,
  // 3=post-processing (travel times/dedup), 4=done. tripStreamDays fills in
  // as each complete day object is parsed out of the streaming AI response,
  // so the loading screen can reveal finished days before the whole trip
  // is ready — see streamGenItinerary below.
  const [tripGenStage, setTripGenStage] = useState(0);
  const [tripStreamDays, setTripStreamDays] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [tripMapMode, setTripMapMode] = useState('areas'); // 行程地圖：'areas' 每日地區（預設）／'route' 當日景點路線
  const [tripRobotOpen, setTripRobotOpen] = useState(false);
  const [tripRating, setTripRating] = useState(null);
  const [tripRatingComment, setTripRatingComment] = useState('');
  const [showRatingPopup, setShowRatingPopup] = useState(false);
  const [ratingDismissed, setRatingDismissed] = useState(false);
  const [tripToast, setTripToast] = useState(null);
  const [shareToast, setShareToast] = useState(null);
  const showShareToast = (msg) => { setShareToast(msg); setTimeout(() => setShareToast(null), 2500); };

  // Itinerary usage control
  const MAX_FREE_PER_DAY = 3; // enforced server-side in api/groq.js
  const MAX_TRIP_DAYS = 7;
  const AFFILIATE_MAX_DAYS = 14;
  const GUEST_MAX_DAYS = 4; // 未登入訪客最多規劃 4 天（伺服器 api/groq.js 以 max_tokens 同步把關）
  // 目前用戶可選的最多天數：管理員／聯盟禮遇 14 天、已登入 7 天、訪客 4 天
  const getTripMaxDays = () => (user?.email===ADMIN_EMAIL || affiliateReward) ? AFFILIATE_MAX_DAYS : !isLoggedIn ? GUEST_MAX_DAYS : MAX_TRIP_DAYS;
  // 14 天禮遇說明（取代舊歡迎彈窗第 3 頁）。觸發條件見 useAffiliateReward：登入後撳本站 Klook／KKday 連結即解鎖，故寫「經連結前往」而非「預訂後」
  const affPerkText = () => lang==='en' ? `Signed in? Open Klook or KKday through our links to unlock one ${AFFILIATE_MAX_DAYS}-day itinerary (valid 1 year).`
    : lang==='ja' ? `ログイン後、当サイトのKlook・KKdayリンクから移動すると、最大${AFFILIATE_MAX_DAYS}日の旅程作成が1回解放されます（1年間有効）。`
    : lang==='ko' ? `로그인 후 저희 사이트의 Klook·KKday 링크로 이동하면 최대 ${AFFILIATE_MAX_DAYS}일 일정 1회가 해제됩니다(1년 유효).`
    : lang==='zh-CN' ? `登录后经本站 Klook／KKday 链接前往，即可解锁一次最多 ${AFFILIATE_MAX_DAYS} 天行程规划（一年内有效）。`
    : `登入後經本站 Klook／KKday 連結前往，即可解鎖一次最多 ${AFFILIATE_MAX_DAYS} 天行程規劃（一年內有效）。`;
  // "YYYY-MM-DD" start/end -> inclusive day count (UTC-midnight math, same
  // approach as the itinerary generator's own parseDateOnlyUTC, to avoid
  // local-TZ off-by-one). Returns null when either date is missing/invalid
  // or the end date is before the start date.
  const calcTripDays = (startStr, endStr) => {
    const parse = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ''); return m ? Date.UTC(+m[1], +m[2]-1, +m[3]) : null; };
    const a = parse(startStr), b = parse(endStr);
    if (a == null || b == null) return null;
    const n = Math.round((b - a) / 86400000) + 1;
    return n > 0 ? n : null;
  };
  const getTripUsage = () => {
    try {
      const today = new Date().toDateString();
      const raw = JSON.parse(localStorage.getItem('ec_trip_usage') || '{}');
      return raw.date === today ? raw : { date: today, count: 0 };
    } catch { return { date: new Date().toDateString(), count: 0 }; }
  };
  const incrementTripUsage = () => {
    const u = getTripUsage(); u.count += 1;
    localStorage.setItem('ec_trip_usage', JSON.stringify(u));
  };

  // Sponsor card state
  const [showSponsor, setShowSponsor] = useState(false);
  const [sponsorCountdown, setSponsorCountdown] = useState(10);
  const [sponsorDone, setSponsorDone] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [pendingGen, setPendingGen] = useState(false);

  const startSponsorCountdown = () => {
    setSponsorCountdown(10); setSponsorDone(false);
    const iv = setInterval(() => {
      setSponsorCountdown(n => {
        if (n <= 1) { clearInterval(iv); setSponsorDone(true); return 0; }
        return n - 1;
      });
    }, 1000);
  };

  const ADMIN_EMAIL = 'ecforwork1985@gmail.com';

  // Per-destination dates drive dayCityMap (genItinerary's deterministic
  // day->city schedule) — without a date on every destination, a multi-city
  // trip can't be scheduled properly (see the dayCityMap fallback logic in
  // genItinerary). Require and validate them here instead of letting a
  // vague/contradictory itinerary come out the other end silently.
  const parseDateOnlyUTC_ = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ''); return m ? Date.UTC(+m[1], +m[2]-1, +m[3]) : null; };
  const validateTripDates = () => {
    const withCity = trDestinations.filter(d => d.city);
    if (!withCity.length) return null; // no destination picked yet — generate button is disabled separately
    if (withCity.some(d => !d.date)) {
      return lang==='en' ? 'Please pick a date for every destination.'
        : lang==='ja' ? 'すべての目的地に日付を入力してください。'
        : lang==='ko' ? '모든 목적지에 날짜를 입력해 주세요.'
        : '請為每個目的地選擇日期。';
    }
    for (let i = 1; i < withCity.length; i++) {
      const prevMs = parseDateOnlyUTC_(withCity[i-1].date), curMs = parseDateOnlyUTC_(withCity[i].date);
      if (prevMs != null && curMs != null && curMs <= prevMs) {
        return lang==='en' ? `${withCity[i].city}'s date must be after ${withCity[i-1].city}'s.`
          : lang==='ja' ? `${withCity[i].city}の日付は${withCity[i-1].city}より後にしてください。`
          : lang==='ko' ? `${withCity[i].city}의 날짜는 ${withCity[i-1].city}보다 늦어야 합니다.`
          : `「${withCity[i].city}」的日期必須晚於「${withCity[i-1].city}」。`;
      }
    }
    if (trReturnDate) {
      const lastMs = parseDateOnlyUTC_(withCity[withCity.length-1].date), retMs = parseDateOnlyUTC_(trReturnDate);
      if (lastMs != null && retMs != null && retMs < lastMs) {
        return lang==='en' ? 'Return date must be on or after your last destination date.'
          : lang==='ja' ? '帰国日は最後の目的地の日付以降にしてください。'
          : lang==='ko' ? '귀국일은 마지막 목적지 날짜 이후여야 합니다.'
          : '回程日期不能早於最後一個目的地的日期。';
      }
      // 有回程日期時天數由日期計出，超過上限就直接講清楚，唔再靜靜截短
      const n = calcTripDays(withCity[0].date, trReturnDate);
      const maxDays = getTripMaxDays();
      if (n && n > maxDays) {
        const guestHint = !isLoggedIn && n <= MAX_TRIP_DAYS;
        return lang==='en' ? `These dates make a ${n}-day trip, over the ${maxDays}-day limit.${guestHint ? ` Sign in to plan up to ${MAX_TRIP_DAYS} days.` : ' Please shorten the dates.'}`
          : lang==='ja' ? `この日程は${n}日間で、上限の${maxDays}日を超えています。${guestHint ? `ログインすると最大${MAX_TRIP_DAYS}日まで計画できます。` : '日程を短くしてください。'}`
          : lang==='ko' ? `이 날짜는 ${n}일 일정으로 최대 ${maxDays}일을 초과합니다. ${guestHint ? `로그인하면 최대 ${MAX_TRIP_DAYS}일까지 계획할 수 있습니다.` : '날짜를 줄여 주세요.'}`
          : `按日期計共 ${n} 天，超過上限 ${maxDays} 天。${guestHint ? `登入後最多可規劃 ${MAX_TRIP_DAYS} 天。` : '請縮短日期。'}`;
      }
    }
    // 手動揀天數時，最後一個目的地的日期唔可以超出行程範圍
    if (trDays && withCity.length > 1) {
      const firstMs = parseDateOnlyUTC_(withCity[0].date), last = withCity[withCity.length-1], lastMs = parseDateOnlyUTC_(last.date);
      if (firstMs != null && lastMs != null) {
        const dayNo = Math.round((lastMs - firstMs) / 86400000) + 1;
        if (dayNo > trDays) {
          return lang==='en' ? `${last.city} starts on day ${dayNo}, but the trip is only ${trDays} days. Add days or adjust the date.`
            : lang==='ja' ? `${last.city}は${dayNo}日目からですが、旅程は${trDays}日間です。日数を増やすか日付を調整してください。`
            : lang==='ko' ? `${last.city}은(는) ${dayNo}일차부터인데 일정은 ${trDays}일입니다. 일수를 늘리거나 날짜를 조정해 주세요.`
            : `「${last.city}」是第 ${dayNo} 天才去，但行程只有 ${trDays} 天，請增加天數或調整日期。`;
        }
      }
    }
    return null;
  };

  const handleGenItinerary = () => {
    const dateErr = validateTripDates();
    // trip_blocked：按了生成但被擋下（登入牆另由 openGoogleGate 記 guest_gate_shown）
    const _who = !isLoggedIn ? 'guest' : user?.email === ADMIN_EMAIL ? 'admin' : 'member';
    if (dateErr) { trackEvent('trip_blocked', JSON.stringify({ reason: 'date_invalid', user: _who })); setGenTripErr(dateErr); return; }
    if (!isLoggedIn) {
      // 訪客：已用過免費試用 → 只提供 Google 登入；未用過 → 直接生成（不看贊助廣告）
      if (isGuestTrialUsed()) { openGoogleGate('trial_used'); return; }
      if (trDays > GUEST_MAX_DAYS) { openGoogleGate('days'); return; }
      trackEvent('guest_trial_started');
      genItinerary(false);
      return;
    }
    if (user?.email === ADMIN_EMAIL) { genItinerary(true); return; } // admin: no ad, no limit
    const usage = getTripUsage();
    if (usage.count >= MAX_FREE_PER_DAY) {
      // Hard daily cap reached — blocked, no ad bypass
      trackEvent('trip_blocked', JSON.stringify({ reason: 'member_daily_cap', user: _who }));
      setGenTripErr(lang==='en'
        ? `Daily limit of ${MAX_FREE_PER_DAY} itineraries reached. Come back tomorrow!`
        : lang==='ja' ? `本日の上限（${MAX_FREE_PER_DAY}回）に達しました。明日またお試しください。`
        : lang==='ko' ? `오늘 한도(${MAX_FREE_PER_DAY}회)에 도달했습니다. 내일 다시 시도해주세요.`
        : `每日行程生成上限為 ${MAX_FREE_PER_DAY} 次，請明天再試。`);
      return;
    }
    // Every generation (for regular users) requires watching the 10-second sponsor ad
    setShowSponsor(true);
    startSponsorCountdown();
  };

  const onSponsorUnlock = () => {
    setShowSponsor(false);
    genItinerary(false); // normal gen — server checks & increments trip_usage on success
  };

  const resetTripForm = () => {
    setItinerary(null);
    setGenTripErr('');
    setTrDays(null);
    setTrDestinations([{ id: 1, country: '', city: '', date: '', hotelArea: '' }]);
    setTrReturnDate('');
    setTrDepDate('');
    setTrRetTime('');
    setTrModes([]);
    setTrStyle({ culture: 20, shopping: 20, entertainment: 20, nature: 20, food: 20 });
    setTrActivities([]);
    setTrCustomSpots([]);
    setTrCustomInput('');
    setTrOutletOptions([]);
    setTrSelectedOutlet(null);
    setTrOnsenOptions([]);
    setTrSelectedOnsen(null);
    setTrVenuePicks({}); setTrVenueCity({}); setTrVenueRows({}); setTrVenueQuery({}); setTrVenueMore({});
    setTripStep(1);
  };

  // eslint-disable-next-line no-unused-vars
  const fetchTrCountries = async (rgn, ln) => {
    setLoadingTGeo(true); setTrCountry(''); setTrSelCities([]); setTrCountries([]); setTrCities([]);
    const al = T[ln].aiLang;
    const { en: sEn, map: sMap } = getStaticCountries(rgn, al);
    const { en: finalEn, map: finalMap } = applyCountryOverrides(rgn, sEn, sMap, al);
    setTrCountries(finalEn); setTrCntMap(finalMap);
    setLoadingTGeo(false);
  };

  // eslint-disable-next-line no-unused-vars
  const fetchTrCities = async (ctry, ln) => {
    setLoadingTGeo(true); setTrSelCities([]); setTrCities([]);
    const al = T[ln].aiLang;
    const { en: sEn, map: sMap } = getStaticCities(ctry, al);
    if (sEn.length > 0) {
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, sEn, sMap, al);
      setTrCities(finalEn); setTrCityMap(finalMap);
      setLoadingTGeo(false); return;
    }
    const ck = `tr_cit_${ctry}_${ln}`;
    const cached = getAICache(ck);
    if (cached) { setTrCities(cached.en); setTrCityMap(cached.map); setLoadingTGeo(false); return; }
    const d = await callAI(
      `List 20 tourist destinations in ${ctry}, mix of famous cities AND regional/lesser-known gems.`,
      `Reply ONLY JSON no markdown: {"cities":[{"en":"string","local":"string"}]} where "local" is TRANSLATED INTO ${al}`,
      900, GROQ_MODEL_FAST
    );
    if (d?.cities) {
      const en=d.cities.map(c=>c.en); const m={};
      d.cities.forEach(c=>{ m[c.en]=c.local||c.en; });
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, en, m, al);
      setAICache(ck, { en: finalEn, map: finalMap }, TTL_GEO);
      setTrCities(finalEn); setTrCityMap(finalMap);
    }
    setLoadingTGeo(false);
  };

  const fetchDestCities = async (country) => {
    if (!country || trDestCityData[country]) return;
    const al = T[lang].aiLang;
    // Normalize to English key for STATIC_CITIES lookup
    const countryEn = COUNTRY_TO_EN[country] || country;
    const { en: sEn, map: sMap } = getStaticCities(countryEn, al);
    if (sEn.length > 0) {
      const { en, map } = applyCityOverrides(countryEn, sEn, sMap, al);
      setTrDestCityData(p=>({...p,[country]:{cities:en,map}})); return;
    }
    const ck = `dest_cit_${countryEn}_${lang}`;
    const cached = getAICache(ck);
    if (cached) { setTrDestCityData(p=>({...p,[country]:cached})); return; }
    const d = await callAI(
      `List 15 popular tourist cities in ${countryEn}.`,
      `Reply ONLY JSON no markdown: {"cities":[{"en":"string","local":"string"}]} where "local" is in ${al}. Only list real, well-known tourist cities.`,
      700, GROQ_MODEL_FAST
    );
    if (d?.cities) {
      const en=d.cities.map(c=>c.en); const map={};
      d.cities.forEach(c=>{ map[c.en]=c.local||c.en; });
      const result={cities:en,map};
      setAICache(ck, result, TTL_GEO);
      setTrDestCityData(p=>({...p,[country]:result}));
    }
  };

  // Real outlet-mall candidates for the trip's own cities — same query used as
  // genItinerary's server-side fallback (bypasses the per-city rating cap that
  // otherwise crowds outlet malls out of the candidate pool entirely), run
  // here up front so the traveller can see and pick among them instead of the
  // itinerary generator silently auto-selecting the highest-rated one.
  const fetchOutletOptions = async () => {
    const cities = [...new Set(trDestinations.map(d => d.city).filter(Boolean))];
    if (!cities.length) { setTrOutletOptions([]); return; }
    setLoadingOutlets(true);
    try {
      // managed_attractions.city is always the canonical English name — the
      // destination picker can hand back a localized name instead (e.g. "神戶"
      // picked from the datalist), which never matches a raw .eq() query.
      // One query across all cities (was one query per city) — cityEnList may
      // map several raw destination strings to the same English city, so fan
      // the results back out per raw city name via cityEnToRaw below.
      const cityEnToRaw = new Map(); // English city -> [raw destination strings]
      cities.forEach(city => {
        const cityEn = CITY_EN_MAP[city] || city;
        if (!cityEnToRaw.has(cityEn)) cityEnToRaw.set(cityEn, []);
        cityEnToRaw.get(cityEn).push(city);
      });
      const { data } = await supabase.from('managed_attractions')
        .select('name,name_en,lat,lng,place_id,district,category,specify_cat,rating,city')
        .eq('is_active', true).in('city', [...cityEnToRaw.keys()]).eq('category', 'shopping')
        .or('name.ilike.%outlet%,name.ilike.%暢貨%,name.ilike.%奧特萊斯%,name.ilike.%奥特莱斯%,name.ilike.%lalaport%,name.ilike.%aeon mall%')
        .order('rating', { ascending: false, nullsFirst: false }).limit(6 * cityEnToRaw.size);
      // Keep `city` as the traveller's own raw destination string (not the
      // English lookup key) so it stays consistent with how genItinerary
      // compares trOutletOptions/trSelectedOutlet against trDestinations later.
      const hits = (data || []).flatMap(r =>
        (cityEnToRaw.get(r.city) || [r.city]).map(rawCity => ({ ...r, city: rawCity }))
      );
      setTrOutletOptions(hits);
      // Default to the best-rated one so generation still works if the
      // traveller ticks "Outlet" but never explicitly picks a card — but never
      // override a choice they already made.
      setTrSelectedOutlet(prev => (prev && hits.some(h => h.place_id ? h.place_id === prev.place_id : h.name === prev.name)) ? prev : (hits[0] || null));
    } catch (e) {
      // Callers (onBlur/onClick handlers below) fire this without awaiting it,
      // so an uncaught rejection here becomes a silent unhandled-promise error
      // with no feedback — the outlet picker would just stay empty forever.
      if (IS_DEV) console.error('[fetchOutletOptions]', e);
      setTrOutletOptions([]);
    } finally {
      setLoadingOutlets(false);
    }
  };

  // 判斷一筆景點是否真的是「可以去泡的溫泉」。資料庫沒有溫泉專屬分類，
  // 名字含「溫泉/Onsen」的資料又混了大量商店、車站、案內所、博物館、餐廳
  // （例如「有馬温泉店」「鬼怒川温泉駅」「溫泉拉麵」），所以名稱命中之外還要排除這些。
  // 同時接受 DB 原始列（name_en）與 toAppAttr 之後的物件（nameEn）。
  // 「溫泉・SPA」選項同時涵蓋汗蒸幕（首爾等地沒有真正的溫泉）。
  // 專門補進來的資料標為 ONSEN_SPECIFY_CAT，名字不含關鍵字（如 Dragon Hill Spa）也認得。
  const ONSEN_SPECIFY_CAT = '溫泉/SPA/汗蒸幕';
  const ONSEN_NAME_RE = /溫泉|温泉|온천|湯屋|足湯|泡腳|汗蒸幕|찜질방|チムジルバン|jjimjilbang|\bonsen\b|hot\s*springs?\b|thermal\s*baths?|nature\s*baths/i;
  const ONSEN_EXCLUDE_NAME_RE = /站|駅|station|案內所|案内所|information|monument|紀念|illumination|turntable|轉車台|ropeway|纜車|museum|博物館|stream|溫泉川|事務所|jimusho|eight views|mountain top|store|shop|店$|拉麵|ramen|食堂/i;
  const ONSEN_EXCLUDE_CAT_RE = /餐|料理|咖啡|禮品|服飾|零售|超級市場|市集|夜市|博物館|電玩/;
  const isOnsenVenue = (v) => {
    if (v?.specify_cat === ONSEN_SPECIFY_CAT) return true;
    if (!v || v.category === 'food' || v.category === 'shopping') return false;
    const names = [v.name, v.name_en ?? v.nameEn].filter(Boolean);
    if (!names.some(n => ONSEN_NAME_RE.test(n))) return false;
    if (names.some(n => ONSEN_EXCLUDE_NAME_RE.test(n))) return false;
    return !ONSEN_EXCLUDE_CAT_RE.test(v.specify_cat || '');
  };
  // 一次查完行程所有城市的真實溫泉，結果的 city 還原成旅客自己輸入的城市字串
  // （同 fetchOutletOptions 的處理），每城最多 perCity 筆、依評分排序。
  // 選單與 genItinerary 共用，確保兩邊看到的是同一份名單。
  // 區域型目的地也要查它底下的城市：選「北海道」的旅客應該看得到札幌定山溪的溫泉，
  // 查到的結果一律歸回區域本身（city=旅客填的「北海道」），排程才認得是這一站的溫泉。
  // 只往下包含、不往上：選「札幌」不該出現車程 4 小時的函館湯之川。
  const ONSEN_REGION_INCLUDES = { Hokkaido: ['Sapporo'] };
  // 距市中心超過這個距離的溫泉，一般會當兩天一夜去（當天下午出發、住溫泉區、隔天早上再回）
  const ONSEN_OVERNIGHT_KM = 30;
  // 足湯（泡腳池）不值得專程住一晚，再遠也只排一般時段
  const FOOTBATH_RE = /足湯|泡腳|foot\s*bath|hot\s*foot|ashiyu/i;
  const isFootbath = (v) => !!v && [v.name, v.name_en ?? v.nameEn].some(n => n && FOOTBATH_RE.test(n));
  const FOOTBATH_NEARBY_KM = 1.5; // 足湯附近要有景點可以一起逛的距離（步行範圍）
  const needsOvernight = (v) => v?.distKm != null && v.distKm > ONSEN_OVERNIGHT_KM && !isFootbath(v);
  const queryOnsenVenues = async (cities, perCity) => {
    const cityEnToRaw = new Map();
    const addMapping = (cityEn, raw) => {
      if (!cityEnToRaw.has(cityEn)) cityEnToRaw.set(cityEn, []);
      if (!cityEnToRaw.get(cityEn).includes(raw)) cityEnToRaw.get(cityEn).push(raw);
    };
    cities.forEach(city => {
      const cityEn = CITY_EN_MAP[city] || city;
      addMapping(cityEn, city);
      // 行程若同時有「札幌」和「北海道」，札幌的溫泉兩站都會列出；perCity 上限各自計算
      (ONSEN_REGION_INCLUDES[cityEn] || []).forEach(sub => addMapping(sub, city));
    });
    const { data } = await supabase.from('managed_attractions')
      .select('name,name_en,lat,lng,place_id,district,category,specify_cat,rating,city')
      .eq('is_active', true).in('city', [...cityEnToRaw.keys()]).not('category', 'in', '(food,shopping)')
      .or(`specify_cat.eq.${ONSEN_SPECIFY_CAT},name.ilike.%溫泉%,name.ilike.%温泉%,name.ilike.%온천%,name.ilike.%湯屋%,name.ilike.%足湯%,name.ilike.%泡腳%,name.ilike.%汗蒸幕%,name.ilike.%찜질방%,name_en.ilike.%onsen%,name_en.ilike.%hot spring%,name_en.ilike.%thermal bath%,name_en.ilike.%nature bath%,name_en.ilike.%jjimjilbang%`)
      .order('rating', { ascending: false, nullsFirst: false }).limit(40 * cityEnToRaw.size);
    // 各站的「市中心」＝該城市（含區域底下城市）所有景點座標的中位數，用來算溫泉距離；
    // 取中位數而非平均，避免被少數偏遠景點拉偏
    const median = (arr) => { const s = [...arr].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)] : null; };
    const centers = new Map(); // 旅客填的城市字串 -> { lat, lng }
    await Promise.all([...new Set(cities)].map(async (raw) => {
      const cityEn = CITY_EN_MAP[raw] || raw;
      const { data: pts } = await supabase.from('managed_attractions').select('lat,lng')
        .eq('is_active', true).in('city', [cityEn, ...(ONSEN_REGION_INCLUDES[cityEn] || [])])
        .not('lat', 'is', null).not('lng', 'is', null).limit(1000);
      if (pts?.length) centers.set(raw, { lat: median(pts.map(p => p.lat)), lng: median(pts.map(p => p.lng)) });
    }));
    const perCityCount = new Map();
    // 區域型目的地涵蓋好幾個城市，上限跟著放大，否則沒評分的溫泉區（登別、洞爺湖）會被擠掉
    const capOf = (raw) => perCity * (1 + (ONSEN_REGION_INCLUDES[CITY_EN_MAP[raw] || raw] || []).length);
    const venues = (data || []).filter(isOnsenVenue).flatMap(r =>
      (cityEnToRaw.get(r.city) || [r.city]).map(rawCity => {
        const c = centers.get(rawCity);
        const distKm = (c && r.lat != null && r.lng != null) ? Math.round(haversineKm(c, { lat: r.lat, lng: r.lng })) : null;
        return { ...r, city: rawCity, distKm };
      })
    );
    // 足湯不單獨成行：只有 1.5 公里內有其他景點（非餐廳、非溫泉）可以一起逛才列入，
    // 並把附近景點（依評分前 3）帶在 nearby 上，排程時與足湯安排在同一時段
    const withFootbathCheck = await Promise.all(venues.map(async (r) => {
      if (!isFootbath(r) || r.lat == null || r.lng == null) return r;
      const dLat = FOOTBATH_NEARBY_KM / 111, dLng = FOOTBATH_NEARBY_KM / (111 * Math.cos(r.lat * Math.PI / 180));
      const { data: nb } = await supabase.from('managed_attractions')
        .select('name,name_en,lat,lng,place_id,district,category,specify_cat,rating,city')
        .eq('is_active', true).in('category', ['culture', 'nature'])
        .gte('lat', r.lat - dLat).lte('lat', r.lat + dLat).gte('lng', r.lng - dLng).lte('lng', r.lng + dLng)
        .limit(80);
      // 只算真正的觀光點：分類名稱是商店／餐飲／KTV／運動的排除（資料常錯分到 nature）；
      // 5.0 滿分多半是評論極少的虛高分數，排序時視為最低
      const NOT_SIGHT_CAT_RE = /餐|料理|咖啡|禮品|服飾|零售|超級市場|市集|夜市|電玩|KTV|體育|俱樂部/;
      const score = (a) => { const x = Number(a.rating) || 0; return x >= 5 ? 0 : x; };
      const nearby = (nb || [])
        .filter(a => !isOnsenVenue(a) && !NOT_SIGHT_CAT_RE.test(a.specify_cat || '') && a.lat != null && haversineKm(r, a) <= FOOTBATH_NEARBY_KM)
        .sort((a, b) => score(b) - score(a)).slice(0, 3);
      return nearby.length ? { ...r, nearby } : null;
    }));
    return withFootbathCheck.filter(Boolean).filter(r => {
      const n = perCityCount.get(r.city) || 0;
      if (n >= capOf(r.city)) return false;
      perCityCount.set(r.city, n + 1);
      return true;
    });
  };

  const fetchOnsenOptions = async () => {
    const cities = [...new Set(trDestinations.map(d => d.city).filter(Boolean))];
    if (!cities.length) { setTrOnsenOptions([]); return; }
    setLoadingOnsens(true);
    try {
      const hits = await queryOnsenVenues(cities, 6);
      setTrOnsenOptions(hits);
      // 預設選評分最高的一個，但不覆蓋旅客已經挑好的
      setTrSelectedOnsen(prev => (prev && hits.some(h => h.place_id ? h.place_id === prev.place_id : h.name === prev.name)) ? prev : (hits[0] || null));
    } catch (e) {
      if (IS_DEV) console.error('[fetchOnsenOptions]', e);
      setTrOnsenOptions([]);
    } finally {
      setLoadingOnsens(false);
    }
  };

  // ── 動物園／海洋館／博物館選單 ──
  // 資料庫沒有專屬分類（動植物園類別混了植物園、牧場；水族館散在「其它休閒」），
  // 所以跟溫泉一樣：伺服器端先用名稱粗篩，再用正規表示式排除寵物店、動物醫院、商店等。
  // 博物館類單城就有近 30 筆，選單預設只顯示評分最前 6 筆，其餘按「顯示更多」或用搜尋框找。
  const VENUE_PICK_LIMIT = 2;      // 每種活動最多選幾間，避免一天塞太滿
  const VENUE_PREVIEW_COUNT = 6;   // 每城預設顯示幾筆
  const VENUE_PICKERS = {
    '動物園': {
      or: 'name.ilike.%動物%,name.ilike.%猴子公園%,name.ilike.%zoo%,name_en.ilike.%zoo%,name_en.ilike.%animal kingdom%,name_en.ilike.%monkey park%',
      test: (v) => [v.name, v.name_en].filter(Boolean).some(n => /動物園|动物园|動物王國|动物王国|動物|zoo\b|animal kingdom|猴子公園|monkey park/i.test(n))
        && ![v.name, v.name_en].filter(Boolean).some(n => /病院|醫院|医院|寵物|宠物|pet\b|clinic|hospital|trimming|shop|store|站|駅|cafe|café|カフェ/i.test(n)),
      emptyHint: { en: 'No zoo in our database for this city yet.', zh: '資料庫裡暫時查不到這個城市的動物園。' },
      color: ['#f0fdf4', '#bbf7d0', '#166534', '#16a34a'],
    },
    '海洋館': {
      or: 'name.ilike.%水族%,name.ilike.%海洋館%,name.ilike.%海遊館%,name_en.ilike.%aquarium%,name_en.ilike.%aqualife%,name_ja.ilike.%水族館%',
      test: (v) => [v.name, v.name_en, v.name_ja].filter(Boolean).some(n => /水族|海洋館|海洋馆|海遊館|aquarium|aqualife|アクアリウム/i.test(n))
        && ![v.name, v.name_en].filter(Boolean).some(n => /shop|store|店|專賣|魚料理|restaurant/i.test(n)),
      emptyHint: { en: 'No aquarium in our database for this city yet.', zh: '資料庫裡暫時查不到這個城市的海洋館／水族館。' },
      color: ['#eff6ff', '#bfdbfe', '#1e3a8a', '#2563eb'],
    },
    '博物館': {
      eq: { specify_cat: '博物館/美術館/藝文空間' },
      search: true,
      test: () => true,
      emptyHint: { en: 'No museum in our database for this city yet.', zh: '資料庫裡暫時查不到這個城市的博物館／美術館。' },
      color: ['#faf5ff', '#e9d5ff', '#581c87', '#9333ea'],
    },
  };
  const venueTripCities = () => [...new Set(trDestinations.map(d => d.city).filter(Boolean))];
  const fetchVenueRows = async (act, rawCity) => {
    const cfg = VENUE_PICKERS[act];
    const key = `${act}|${rawCity}`;
    if (!cfg || !rawCity) return;
    setTrVenueRows(p => (p[key] ? p : { ...p, [key]: 'loading' }));
    try {
      let q = supabase.from('managed_attractions')
        .select('name,name_en,name_zh_cn,name_ja,name_ko,lat,lng,place_id,district,category,specify_cat,rating,city')
        .eq('is_active', true).eq('city', CITY_EN_MAP[rawCity] || rawCity).not('category', 'in', '(food,shopping)');
      if (cfg.eq) Object.entries(cfg.eq).forEach(([k, v]) => { q = q.eq(k, v); });
      if (cfg.or) q = q.or(cfg.or);
      const { data, error } = await q.order('rating', { ascending: false, nullsFirst: false }).limit(300);
      if (error) throw error;
      // 評分 5.0 多半是評論極少的虛高分數，排序時視為最低
      const score = (a) => { const x = Number(a.rating) || 0; return x >= 5 ? 0 : x; };
      const rows = (data || []).filter(cfg.test).map(r => ({ ...r, city: rawCity })).sort((a, b) => score(b) - score(a));
      setTrVenueRows(p => ({ ...p, [key]: rows }));
    } catch (e) {
      if (IS_DEV) console.error('[fetchVenueRows]', e);
      setTrVenueRows(p => ({ ...p, [key]: [] }));
    }
  };
  const toggleVenueAct = (act, wasSelected) => {
    if (wasSelected) {
      setTrVenuePicks(p => { const n = { ...p }; delete n[act]; return n; });
      setTrVenueCity(p => { const n = { ...p }; delete n[act]; return n; });
      setTrVenueQuery(p => ({ ...p, [act]: '' }));
      setTrVenueMore(p => ({ ...p, [act]: false }));
      return;
    }
    // 單一城市由下面的 useEffect 自動載入；多城市要先選城市（選單上方的城市按鈕）
  };
  const pickVenueCity = (act, rawCity) => {
    setTrVenueCity(p => ({ ...p, [act]: rawCity }));
    setTrVenueMore(p => ({ ...p, [act]: false }));
    setTrVenueQuery(p => ({ ...p, [act]: '' }));
    fetchVenueRows(act, rawCity);
  };
  const sameVenue = (a, b) => (a.place_id && b.place_id) ? a.place_id === b.place_id : a.name === b.name;
  const toggleVenuePick = (act, row) => {
    setTrVenuePicks(p => {
      const cur = p[act] || [];
      if (cur.some(x => sameVenue(x, row))) return { ...p, [act]: cur.filter(x => !sameVenue(x, row)) };
      if (cur.length >= VENUE_PICK_LIMIT) return p;
      return { ...p, [act]: [...cur, row] };
    });
  };
  // 單一城市時自動載入（旅客可能先勾活動、後填城市）；多城市要等旅客先選城市
  const venueCitiesKey = venueTripCities().join('|');
  useEffect(() => {
    const cities = venueTripCities();
    if (cities.length !== 1) return;
    Object.keys(VENUE_PICKERS).filter(act => trActivities.includes(act)).forEach(act => {
      if (!trVenueRows[`${act}|${cities[0]}`]) fetchVenueRows(act, cities[0]);
    });
  }, [trActivities, venueCitiesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The AI tweak assistant (TripRobotPanel) regenerates the *entire* itinerary
  // JSON for even a trivial edit (e.g. swapping two days), and in doing so
  // drops the _lat/_lng/_place_id metadata that genItinerary's candidate-pool
  // matching attached — verified via scenario testing: every location field
  // loses its coordinates on every tweak, even when the venue name is
  // unchanged. That silently breaks travel-time display and downstream
  // distance checks. Re-attach verified coordinates by matching venue NAME
  // against the pre-tweak itinerary (which still carries them for anything
  // the tweak didn't actually rename) rather than re-querying the DB.
  const reconcileTweakedItinerary = (next, prev) => {
    if (!next?.days?.length || !prev?.days?.length) return next;
    const norm = s => (s || '').toLowerCase().replace(/\s+/g, '').trim();
    const verified = new Map();
    prev.days.forEach(day => {
      ITIN_SLOTS.forEach(slotName => {
        const s = day[slotName];
        if (s?.location && s._lat != null) {
          verified.set(norm(s.location), { _lat: s._lat, _lng: s._lng, _place_id: s._place_id, district: s.district, category: s.category, specify_cat: s.specify_cat });
        }
      });
    });
    next.days.forEach(day => {
      ITIN_SLOTS.forEach(slotName => {
        const s = day[slotName];
        if (!s?.location) return;
        const hit = verified.get(norm(s.location));
        if (hit) Object.assign(s, hit);
      });
    });
    return next;
  };

  const genItinerary = async (isAdmin = false) => {
    const __t0 = Date.now();
    const __mark = (label) => { if (IS_DEV) console.log(`[genItinerary] ${label}: ${Date.now() - __t0}ms`); };
    const destCities = trDestinations.map(d=>d.city).filter(Boolean);
    const destCountry = trDestinations[0]?.country || trCountry;
    // Multi-destination trips can span more than one country (e.g. Tokyo -> Seoul).
    // Previously only trDestinations[0].country was ever sent to the AI, so a
    // second/third country's cities were silently planned as if they were still
    // in the first country (wrong currency/local norms) and the AI had no idea an
    // international border — not just an in-country city switch — was involved.
    const destCountriesUnique = [...new Set(trDestinations.map(d => d.country).filter(Boolean))];
    const isMultiCountryTrip = destCountriesUnique.length > 1;
    const destDate = trDestinations[0]?.date || trDepDate;
    if (!destCities.length || !trDays) return;
    setLoadingTrip(true); setGenTripErr('');
    setTripGenStage(0); setTripStreamDays([]);
    // Do NOT clear itinerary here — only update on success so failed generations preserve previous result

    // Admin bypasses server limit; regular users pass JWT + isItinerary:true
    // Server will check usage BEFORE calling Groq, consume ONLY on Groq success
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token || null;
    const isGuest = !accessToken;
    const itineraryOpts = isAdmin ? {} : isGuest ? { isItinerary: true, guestId: getGuestId() } : { isItinerary: true, accessToken };
    const al = T[lang].aiLang;
    const styleStr = Object.entries(trStyle).map(([k,v])=>`${k}:${v}%`).join(',');
    const modesStr = trModes.join('/')||'any';
    const companionMap = {
      solo: 'solo traveller (prefer counter-seating/single-diner restaurants like ramen or teishoku counters over group-style dining, favour niche/quiet spots such as bookstores, cafes, or hiking trails, pacing can be tighter with more stops per day since there is no group to coordinate)',
      friends: 'group of friends (prefer shareable-dish restaurants like yakiniku/nabe/izakaya over single-diner counters, include social/photogenic activities such as themed cafes, bar-hopping areas, or group activities like escape rooms, avoid solitary/quiet venues that do not suit a group)',
      family: 'family with young children (kid-friendly venues required)',
    };
    // 長者開關獨立於同行關係之外疊加：不管上面選個人/好友/親子，只要有長者
    // 同行，步調限制都要套用（例如親子遊+長者＝三代同遊，仍要放慢節奏）。
    const elderlyStr = trElderly
      ? ' An elderly member is part of this trip — reduce the number of stops per day, insert rest breaks, avoid attractions with long staircases or steep hikes (e.g. skip the hillside hiking portion of shrine trails), and prefer routes with elevators/escalators or flat, short walking distances between venues.'
      : '';
    const companionStr = (companionMap[trCompanion] || 'solo traveller') + elderlyStr;
    const dateStr = destDate ? ` starting ${destDate}` : '';
    const actStr = trActivities.length ? ` Include activities: ${trActivities.join(', ')}.` : '';
    // 自訂景點的 prompt 規則改為在下方「自訂景點」區塊按地理位置逐一指定日子後才組成
    let spotsStr = '';
    const sym = currSymbol;
    // Derived for legacy references below
    const _cities = destCities;
    const _country = destCountry;

    // ── Deterministic day→city schedule from the traveller's own per-destination
    // dates ─────────────────────────────────────────────────────────────────
    // Each `trDestinations[i].date` is "the day the traveller arrives in / switches
    // to this city" (shown to them as such in the trip summary — see the `d.date`
    // rendering elsewhere). Previously ONLY trDestinations[0].date was ever sent to
    // the AI (as `destDate` above) and the city list was flattened to a bare
    // "CityA+CityB" string — the model had zero information about which day each
    // city starts/ends on and had to guess, producing off-by-one (or worse) city
    // switches (e.g. traveller picks "move to Kyoto on the 25th", AI schedules it
    // on the 26th). Compute the true mapping here instead of trusting the model to
    // infer it, and force it onto the response afterward regardless of what the
    // model outputs (same "deterministic JS over LLM free choice" pattern already
    // used below for duplicate/geo swaps).
    const parseDateOnlyUTC = (s) => { // "YYYY-MM-DD" -> UTC-midnight ms; avoids local-TZ drift
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
      return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
    };
    const dayCityMap = {}; // 1-based day number -> city name
    {
      const destsWithCity = trDestinations.filter(d => d.city);
      const tripStartMs = parseDateOnlyUTC(destsWithCity[0]?.date) ?? parseDateOnlyUTC(trDepDate);
      const switches = (tripStartMs != null)
        ? destsWithCity
            .map(d => ({ city: d.city, dayIdx: parseDateOnlyUTC(d.date) != null ? Math.round((parseDateOnlyUTC(d.date) - tripStartMs) / 86400000) : null }))
            .filter(s => s.dayIdx != null && s.dayIdx >= 0)
            .sort((a, b) => a.dayIdx - b.dayIdx)
        : [];
      if (switches.length && destsWithCity.length > 1) {
        if (switches[0].dayIdx !== 0) switches.unshift({ city: destsWithCity[0].city, dayIdx: 0 });
        for (let day = 1; day <= trDays; day++) {
          let city = switches[0].city;
          for (const s of switches) { if (s.dayIdx <= day - 1) city = s.city; else break; }
          dayCityMap[day] = city;
        }
      } else if (destsWithCity.length > 1) {
        // Multiple cities but no per-destination date told us when each one
        // starts (previously this fell through to the single-city branch
        // below and forced EVERY day onto destsWithCity[0], silently
        // dropping the traveller's other cities entirely). Evenly split the
        // trip's days across the destinations in the order they were listed
        // instead — not perfect, but at least every chosen city gets days.
        const daysPerCity = Math.ceil(trDays / destsWithCity.length);
        for (let day = 1; day <= trDays; day++) {
          const idx = Math.min(destsWithCity.length - 1, Math.floor((day - 1) / daysPerCity));
          dayCityMap[day] = destsWithCity[idx].city;
        }
      } else if (destsWithCity.length === 1) {
        for (let day = 1; day <= trDays; day++) dayCityMap[day] = destsWithCity[0].city;
      }
    }
    const dayCityLines = Object.keys(dayCityMap).length
      ? Object.entries(dayCityMap).map(([day, city]) => `Day ${day}=${city}`).join(', ')
      : '';
    const dayCityRule = dayCityLines
      ? `FIXED DAY-BY-DAY CITY SCHEDULE — the traveller has ALREADY decided exactly which city they are in on each day, you MUST follow this exactly and never shift a city's start/end day: ${dayCityLines}.`
      : '';
    // When the trip crosses a country border mid-trip, find the day the switch
    // actually happens (per dayCityMap above) so the AI treats it as an
    // international transfer day, not a same-country city hop.
    const borderCrossingRule = (() => {
      if (!isMultiCountryTrip) return '';
      const destsWithCity = trDestinations.filter(d => d.city);
      // Compute the crossing day directly from THIS destination's own date
      // (now required — see validateTripDates) rather than reverse-searching
      // dayCityMap by city name: .find() there grabbed the FIRST day mapped
      // to that city, which mislabels the crossing when a later destination
      // shares a city name with an earlier one (e.g. the trip loops back
      // through the same city under a different country leg).
      const tripStartMs = parseDateOnlyUTC(destsWithCity[0]?.date) ?? parseDateOnlyUTC(trDepDate);
      const crossingDays = [];
      let prevCountry = destsWithCity[0]?.country;
      for (let i = 1; i < destsWithCity.length; i++) {
        const d = destsWithCity[i];
        if (d.country && d.country !== prevCountry) {
          const dMs = parseDateOnlyUTC(d.date);
          const day = (tripStartMs != null && dMs != null) ? Math.round((dMs - tripStartMs) / 86400000) + 1 : null;
          if (day != null && day >= 1 && day <= trDays) crossingDays.push(`Day ${day} (${prevCountry} -> ${d.country})`);
          prevCountry = d.country;
        }
      }
      const citiesByCountry = destCountriesUnique
        .map(c => `${c}: ${trDestinations.filter(d => d.country === c && d.city).map(d => d.city).join('/')}`)
        .join('; ');
      return `INTERNATIONAL MULTI-COUNTRY TRIP — this trip crosses country borders (${citiesByCountry}), NOT just cities within one country. ${crossingDays.length ? `Border-crossing day(s): ${crossingDays.join(', ')} — that day MUST include international flight/immigration travel time (treat it like a partial arrival/departure day: light morning or evening activity only, not a full 3-slot day).` : ''} Use each city's own country for currency, local customs, and venue suggestions — do not assume the whole trip is in one country.`;
    })();

    let allDays = [];
    let estimatedTotal = '';

    // ── Build a real-venue candidate pool from managed_attractions per city ──
    // The AI picks FROM this list instead of inventing places, so every chosen
    // venue carries a real lat/lng/place_id we can use for travel-time math and
    // duplicate detection. No candidates for a city → AI falls back to its own
    // knowledge for that city only (sparse-DB-city safety net).
    const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, '').trim();
    // Shared with the pre-generation outlet pre-selection below AND the
    // post-generation dedup pass further down — one definition, used both places.
    const OUTLET_NAME_RE = /outlet|暢貨|奧特萊斯|奥特莱斯|lalaport|aeon\s*mall|イオンモール/i;
    const isOutletCandidate = (v) => !!v && v.category === 'shopping' && OUTLET_NAME_RE.test(v.name || '');
    // The candidate list shown to the AI formats each venue as "Name(District)"
    // for readability — the AI frequently copies that whole string into its
    // "location"/restaurant "name" field instead of just the name, which then
    // fails an exact norm() match against the registry (keyed by plain name
    // only). Strip a trailing "(...)" as a fallback lookup, not the primary
    // key, so real venue names that legitimately end in parentheses still
    // register/match on their exact text first.
    const stripTrailingParen = (s) => (s || '').replace(/[（(][^）(]*[）)]\s*$/u, '');
    const candidateRegistry = new Map(); // normalized name -> {name, lat, lng, place_id, district, city, category}
    const candidateTextByCity = [];
    // 每城市全部有座標的餐飲景點（唔俾 AI 睇），用嚟喺後處理強制午餐／晚餐落到附近真實餐廳
    const foodPoolByCity = new Map();
    const attrPerCity = Math.max(15, Math.min(50, trDays * 6));
    // 中間日有午餐＋晚餐兩個餐飲時段，候選要夠分
    const foodPerCity = Math.max(10, Math.min(40, trDays * 4));

    // 這次生成已載入的城市資料。loadCityRows 第二次呼叫同一城市時會改讀 state，
    // 但本函式拿到的是按下生成那一刻的舊 state，剛載入的資料不在其中，會得到空清單；
    // 下方自訂景點等需要同一批資料的地方一律從這裡拿。
    const genCityRows = new Map();
    const buildCandidateBlockForCity = async (city, { limit, isNearby } = {}) => {
      const rows = await loadCityRows(city);
      if (!isNearby) genCityRows.set(city, rows);
      if (!rows.length) return;
      const byRating = byRankScore;
      // 溫泉一律不進一般候選名單：沒勾「溫泉」就不該安排；有勾的話只放旅客
      // 選定的那一個（下方 preselectedOnsen 另外登記），避免 AI 自己多塞幾個。
      const shaped = rows.map(toAppAttr).filter(a => !isOnsenVenue(a));
      const attrCap = limit ?? attrPerCity;
      const foodCap = limit ? Math.max(3, Math.round(limit / 2)) : foodPerCity;
      const topAttractions = shaped.filter(a => a.category !== 'food').sort(byRating).slice(0, attrCap);
      // Rating alone isn't a reliable cutoff — a lot of rows carry an inflated
      // AI-assigned 5.0, so a real landmark with an honest 4.5 (e.g. Universal
      // Studios Japan) can get crowded out of the top-N and never even reach
      // the AI's candidate list. Theme parks are rare enough per city that
      // guaranteeing all of them a slot costs little and fixes exactly this.
      const mustInclude = shaped.filter(a => a.category !== 'food' && a.specify_cat === '大型主題樂園/水上樂園');
      const seenKeys = new Set(topAttractions.map(a => norm(localName(a, lang) || a.name)));
      const attractions = isNearby ? topAttractions : [...topAttractions, ...mustInclude.filter(a => !seenKeys.has(norm(localName(a, lang) || a.name)))];
      const food = shaped.filter(a => a.category === 'food').sort(byRating).slice(0, foodCap);
      if (!isNearby) {
        foodPoolByCity.set(city, shaped
          .filter(a => a.category === 'food' && a.lat != null && a.lng != null)
          .map(a => ({ ...a, name: localName(a, lang) || a.name })));
      }
      [...attractions, ...food].forEach(a => {
        const name = localName(a, lang) || a.name;
        if (!name) return;
        const key = norm(name);
        if (!candidateRegistry.has(key)) {
          candidateRegistry.set(key, { name, lat: a.lat, lng: a.lng, place_id: a.place_id, district: a.district, city, category: a.category, specify_cat: a.specify_cat, rating: a.rating });
        }
      });
      if (attractions.length || food.length) {
        const line = (a) => `${localName(a, lang) || a.name}${a.district ? `(${a.district})` : ''}`;
        const label = isNearby ? `${city} (self-drive day-trip option, NOT the home city)` : city;
        candidateTextByCity.push(
          `${label} — attractions: ${attractions.map(line).join(', ') || 'none'}; food: ${food.map(line).join(', ') || 'none'}`
        );
      }
    };

    await Promise.all(_cities.map(city => buildCandidateBlockForCity(city)));
    __mark('candidate pool built');
    setTripGenStage(1);

    // Self-driving with only one city selected: figure out which OTHER real
    // cities are actually within day-trip driving range, using real
    // coordinates rather than a hand-curated (and inevitably incomplete)
    // adjacency list. A city with no managed_attractions rows at all simply
    // can't be measured, so it's skipped rather than guessed at — same
    // "verified data only" principle as everywhere else in this pipeline.
    const SELF_DRIVE_MAX_KM = 100; // ~1-1.5hr highway drive one-way, reasonable for a day trip
    const cityCentroid = async (cityEn) => {
      const { data } = await supabase.from('managed_attractions')
        .select('lat,lng').eq('is_active', true).eq('city', cityEn)
        .not('lat', 'is', null).not('lng', 'is', null).limit(5);
      if (!data?.length) return null;
      return { lat: data.reduce((s, r) => s + r.lat, 0) / data.length, lng: data.reduce((s, r) => s + r.lng, 0) / data.length };
    };
    const usesSelfDriveSingleCity = trModes.includes('self') && _cities.length <= 1;
    let nearby = [];
    if (usesSelfDriveSingleCity) {
      const homeCityEn = CITY_EN_MAP[_cities[0]] || _cities[0];
      const homeCentroid = await cityCentroid(homeCityEn);
      if (homeCentroid) {
        const countryEn = COUNTRY_TO_EN[_country] || _country;
        const candidateCities = getStaticCities(countryEn, al).en.filter(c => c.toLowerCase() !== homeCityEn.toLowerCase());
        const measured = (await Promise.all(candidateCities.map(async (c) => {
          const centroid = await cityCentroid(c);
          if (!centroid) return null; // no DB data for this city — don't guess, just skip it
          return { city: c, distKm: haversineKm(homeCentroid, centroid) };
        }))).filter(Boolean);
        nearby = measured.filter(x => x.distKm <= SELF_DRIVE_MAX_KM).sort((a, b) => a.distKm - b.distKm).slice(0, 3);
      }
    }
    if (nearby.length) {
      await Promise.all(nearby.map(({ city }) => buildCandidateBlockForCity(city, { limit: 10, isNearby: true })));
    }

    const candidateBlock = candidateTextByCity.length
      ? ` VERIFIED REAL VENUE LIST (grouped by city, "attractions" and "food" — pick "location" ONLY from names in this list, copied EXACTLY as written; each name may be used at most ONCE across the whole trip): ${candidateTextByCity.join(' | ')}. If a city has no list or the list runs out for a needed slot, you may fall back to your own knowledge of 100% real places for that city only.`
      : '';

    // ── Pre-select the outlet mall when the traveller explicitly asked for one ──
    // When "Outlet" is ticked in the activities picker, decide WHICH real outlet
    // to use right now, from the trip's own cities (via candidateRegistry, keyed
    // by the same raw city strings as trDestinations — no city-name mapping
    // needed) — deterministic, not a guess the model or a post-hoc rating
    // comparison has to make. The AI is then told to use exactly this venue,
    // scheduled on one of ITS city's own days per the day→city schedule above.
    const wantsOutlet = trActivities.includes('Outlet');
    let preselectedOutlet = null;
    if (wantsOutlet) {
      // Outlet malls often sit at a decent-but-unremarkable rating (~4.0) and
      // get crowded out of buildCandidateBlockForCity's per-city top-N cap by
      // hundreds of higher-rated shrines/cafes/experiences — so they can be
      // completely absent from candidateRegistry regardless of any prompt
      // wording asking the model to use one. Query for the real thing directly
      // instead of hoping it survived the generic cap. (Also doubles as the
      // fallback source when the traveller ticked "Outlet" but never actually
      // picked a card from trOutletOptions in the UI.)
      // Same fix as fetchOutletOptions: _cities can be localized names ("神戶")
      // straight from trDestinations, but the DB's city column is always
      // canonical English — one query across all trip cities instead of one
      // per city, fanning results back out to each raw city name afterward.
      const outletCityEnToRaw = new Map();
      _cities.forEach(city => {
        const cityEn = CITY_EN_MAP[city] || city;
        if (!outletCityEnToRaw.has(cityEn)) outletCityEnToRaw.set(cityEn, []);
        outletCityEnToRaw.get(cityEn).push(city);
      });
      const { data: outletData } = await supabase.from('managed_attractions')
        .select('name,name_en,lat,lng,place_id,district,category,specify_cat,rating,city')
        .eq('is_active', true).in('city', [...outletCityEnToRaw.keys()]).eq('category', 'shopping')
        .or('name.ilike.%outlet%,name.ilike.%暢貨%,name.ilike.%奧特萊斯%,name.ilike.%奥特莱斯%,name.ilike.%lalaport%,name.ilike.%aeon mall%')
        .order('rating', { ascending: false, nullsFirst: false }).limit(3 * outletCityEnToRaw.size);
      const outletHits = (outletData || []).flatMap(r =>
        (outletCityEnToRaw.get(r.city) || [r.city]).map(rawCity => ({ ...r, city: rawCity })) // keep raw city for downstream comparisons
      );
      outletHits.forEach(a => {
        const name = localName(a, lang) || a.name;
        if (!name) return;
        const key = norm(name);
        if (!candidateRegistry.has(key)) {
          candidateRegistry.set(key, { name, lat: a.lat, lng: a.lng, place_id: a.place_id, district: a.district, city: a.city, category: a.category, specify_cat: a.specify_cat, rating: a.rating });
        }
      });
      // The traveller's own explicit pick (trSelectedOutlet, from the card
      // picker shown once "Outlet" is ticked) is authoritative — make sure
      // it's registered too (it may not match anything above if its rating
      // changed or it's a name variant) and use it directly instead of the
      // auto highest-rated fallback below.
      if (trSelectedOutlet?.name) {
        const selKey = norm(trSelectedOutlet.name);
        if (!candidateRegistry.has(selKey)) candidateRegistry.set(selKey, { ...trSelectedOutlet, category: 'shopping' });
        preselectedOutlet = candidateRegistry.get(selKey);
      } else {
        const tripCities = new Set(_cities);
        preselectedOutlet = [...candidateRegistry.values()]
          .filter(v => isOutletCandidate(v) && tripCities.has(v.city))
          .sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0))[0] || null;
      }
    }
    // Decide WHICH day gets the pre-selected outlet before building day1Rule/
    // departureRule below — a scenario-testing pass found a same-shaped
    // "somewhere in these days" sentence got ignored by the model ~75% of
    // the time, unlike the day-specific imperative rules (day1Rule,
    // departureRule, dayCityRule) which it followed reliably every time
    // tested, so this needs to be a specific "DAY N IS X DAY" imperative.
    // Day 1 (arrival) and the last day (already governed by day1Rule/
    // departureRule) are both spoken for, so skip them when another day in
    // the same city exists. Also skip the outlet city's OWN arrival day (the
    // first day dayCityMap switches to it, e.g. day 4 of 8 when Kyoto starts
    // then) for anyone not self-driving — a traveller on public transit who
    // just switched cities wouldn't head straight to an outlet mall before
    // settling in; a self-driving traveller plausibly could (e.g. an outlet
    // en route). Needs to be known before departureRule below: when it lands
    // on an earlier day, the last day must NOT also demand an outlet mall.
    // A 1-day trip has no day to dedicate — day 1 is simultaneously arrival
    // AND departure, and day1Rule's single-day branch explicitly forbids a
    // full-day venue there (confirmed live: without this guard, the model
    // spent the ENTIRE only day at the outlet, arrival/departure and all).
    // Leave outletTargetDay null so outletRule falls back to the generic
    // "at most one, work it in reasonably" text instead of a forced full day.
    let outletTargetDay = null;
    if (preselectedOutlet && trDays > 1) {
      const cityDays = Object.entries(dayCityMap).filter(([, c]) => c === preselectedOutlet.city).map(([d]) => Number(d));
      const cityArrivalDay = cityDays.length ? Math.min(...cityDays) : null;
      const canUseArrivalDay = trModes.includes('self');
      const eligible = cityDays.filter(d => d !== 1 && d !== trDays && (canUseArrivalDay || d !== cityArrivalDay));
      // Relax the arrival-day exclusion before ever falling back to day 1 or
      // the last day — those two are excluded for a reason (day1Rule and
      // departureRule both forbid full-day venues there), and the previous
      // `?? cityDays[0]` fallback could still land on exactly one of them
      // when the outlet's city only spans arrival/departure days, producing
      // a forced outletDayRule that directly contradicts whichever rule
      // covers that day. Leave outletTargetDay null instead — outletRule
      // then falls back to its generic "at most one, work it in reasonably"
      // wording rather than a forced full day.
      const eligibleRelaxed = cityDays.filter(d => d !== 1 && d !== trDays);
      outletTargetDay = eligible[0] ?? eligibleRelaxed[0] ?? null;
    }

    // ── 溫泉：只有旅客勾了「溫泉」才安排 ──
    // 以旅客在選單挑的那間為準；沒挑就用同一份查詢取評分最高的。
    // 直接指定「第幾天下午」而不是「找一天放」，理由同 outletTargetDay（模糊指令常被忽略）。
    // 排除 Outlet 全天那天；盡量避開抵達日與回程日，實在沒有才放寬。
    const wantsOnsen = trActivities.includes('溫泉');
    let preselectedOnsen = null;
    let onsenTargetDay = null;
    let onsenDistKm = null;
    let onsenOvernight = false;
    let onsenFootbathNearby = []; // 選的是足湯時，一起逛的附近景點名稱
    if (wantsOnsen) {
      let pick = trSelectedOnsen?.name && _cities.includes(trSelectedOnsen.city) ? trSelectedOnsen : null;
      if (!pick) {
        try { pick = (await queryOnsenVenues(_cities, 1))[0] || null; }
        catch (e) { if (IS_DEV) console.error('[genItinerary onsen]', e); }
      }
      if (pick) {
        const name = localName(toAppAttr(pick), lang) || pick.name;
        const key = norm(name);
        if (!candidateRegistry.has(key)) {
          candidateRegistry.set(key, { name, lat: pick.lat, lng: pick.lng, place_id: pick.place_id, district: pick.district, city: pick.city, category: pick.category, specify_cat: pick.specify_cat, rating: pick.rating });
        }
        preselectedOnsen = candidateRegistry.get(key);
        onsenDistKm = pick.distKm ?? null;
        // 足湯要跟附近景點一起排：把那些景點也登記進候選名單，排程時才有真實座標
        if (isFootbath(pick)) {
          onsenFootbathNearby = (pick.nearby || []).map(a => {
            const nName = localName(toAppAttr(a), lang) || a.name;
            const nKey = norm(nName);
            if (!candidateRegistry.has(nKey)) {
              candidateRegistry.set(nKey, { name: nName, lat: a.lat, lng: a.lng, place_id: a.place_id, district: a.district, city: pick.city, category: a.category, specify_cat: a.specify_cat, rating: a.rating });
            }
            return nName;
          });
        }
        const cityDays = Object.entries(dayCityMap).filter(([, c]) => c === preselectedOnsen.city).map(([d]) => Number(d)).filter(d => d !== outletTargetDay);
        // 遠的溫泉（>30km）排兩天一夜：第 N 天下午出發並住溫泉區、第 N+1 天早上在溫泉區後返回。
        // N 與 N+1 都要在同一城市、都不是 Outlet 日，N+1 不能是回程日；盡量不用抵達日。
        // 行程太短排不下時，退回一般的「某天下午」安排。
        if (needsOvernight({ ...pick, distKm: onsenDistKm })) {
          const fits = (n) => cityDays.includes(n + 1) && n + 1 !== trDays;
          const n = cityDays.find(d => d !== 1 && fits(d)) ?? cityDays.find(fits);
          if (n != null) { onsenTargetDay = n; onsenOvernight = true; }
        }
        if (!onsenOvernight) {
          const preferred = cityDays.filter(d => d !== 1 && d !== trDays);
          onsenTargetDay = preferred[0] ?? cityDays.find(d => d !== trDays) ?? cityDays[0] ?? null;
        }
      }
    }

    // ── 自訂景點：按地理位置分「市內」與「城外即日來回」，生成前就決定排哪一天 ──
    // 以前只在 prompt 加一句「MUST include」，同一個 prompt 卻又要求「只能用候選名單」，
    // 不在名單內的城外景點（例如琵琶湖）經常被 AI 放棄；加上 Outlet 保險會整天覆蓋
    // 那天的內容，排在同一天的自訂景點會被一併蓋掉。
    // 規則（使用者要求 2026-10-05）：
    //   - 城外景點（行程城市的資料庫找不到，或距離該城市中心超過 CUSTOM_DAY_TRIP_KM）
    //     自己獨佔一天做即日來回，不可與 Outlet 日、溫泉日、抵達日、回程日、
    //     其他即日來回同一天。
    //   - 市內景點併入所屬城市的某一天（每天最多兩個），同樣不可排在 Outlet／溫泉／
    //     即日來回那天。
    // 直接指定「第幾天」而不是「找一天放」，理由同 outletTargetDay（模糊指令常被忽略）。
    const CUSTOM_DAY_TRIP_KM = 20;
    const customPlans = []; // { spot, kind: 'inCity'|'dayTrip', day: number|null, info: 登記資料|null, baseCity, trip? }
    const customDayTrips = []; // 每組城外景點一天：{ day, baseCity, plans, pool: { attractions, food }, anchor }
    // 動物園／海洋館／博物館選單挑的景點併入自訂景點：沿用同一套指定日子、避開
    // Outlet／溫泉日、AI 沒排就強制補上的機制，名稱是資料庫原名所以會精確比對到。
    const pickedVenueNames = Object.entries(trVenuePicks)
      .filter(([act]) => trActivities.includes(act))
      .flatMap(([, rows]) => rows)
      .filter(r => _cities.includes(r.city))
      .map(r => r.name);
    const spotsInput = [...new Set([...trCustomSpots, ...pickedVenueNames].map(s => s.trim()))].filter(s => norm(s));
    if (spotsInput.length) {
      const median = (arr) => { const s = [...arr].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)] : null; };
      const mostCommon = (arr) => { const m = new Map(); arr.filter(Boolean).forEach(x => m.set(x, (m.get(x) || 0) + 1)); return [...m.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null; };
      const rowsByCity = new Map(); // 旅客原本輸入的城市字串 -> 有座標的資料列
      await Promise.all(_cities.map(async c => {
        rowsByCity.set(c, (genCityRows.get(c) || await loadCityRows(c)).filter(r => r.lat != null && r.lng != null));
      }));
      const cityCenter = (c) => {
        const rs = rowsByCity.get(c) || [];
        return rs.length ? { lat: median(rs.map(r => r.lat)), lng: median(rs.map(r => r.lng)) } : null;
      };
      const rowNames = (r) => [r.name, r.name_en, r.name_zh_cn, r.name_ja, r.name_ko].filter(Boolean).map(norm);
      // 先找名稱完全相同的景點；找不到再當作「地區」（分區相同或名稱包含該字），
      // 用該地區景點座標的中位數代表位置，地點名稱保留旅客輸入的字（例如「六甲山」）。
      const rowInfo = (r, city) => ({ name: localName(toAppAttr(r), lang) || r.name, lat: r.lat, lng: r.lng, place_id: r.place_id, district: r.district, city, category: r.category, specify_cat: r.specify_cat, rating: r.rating });
      const byRatingDesc = (a, b) => (Number(b.r.rating) || 0) - (Number(a.r.rating) || 0);
      // 第三層比對只用於中日韓文：輸入的字按順序全部出現在名稱裡即算（例如「嵯峨小火車」
      // →「嵯峨野嵐山小火車」）。英文字母這樣比太寬鬆，所以不用；3 個字以下也容易誤配。
      const CJK_RE = /[぀-ヿ㐀-鿿가-힯]/;
      const fuzzyChars = (spot) => {
        const chars = Array.from(norm(spot));
        return chars.length >= 3 && CJK_RE.test(spot) ? chars : null;
      };
      const isSubsequence = (chars, name) => {
        let i = 0;
        for (const ch of name) { if (ch === chars[i]) i++; if (i === chars.length) return true; }
        return false;
      };
      const resolveSpot = (spot, rowsMap = rowsByCity) => {
        const q = norm(spot);
        const exact = [...rowsMap]
          .flatMap(([city, rows]) => rows.filter(r => r.category !== 'food' && rowNames(r).includes(q)).map(r => ({ r, city })))
          .sort(byRatingDesc)[0] || null;
        if (exact) return rowInfo(exact.r, exact.city);
        let best = null;
        for (const [city, rows] of rowsMap) {
          const hits = rows.filter(r => r.category !== 'food' && (norm(r.district) === q || rowNames(r).some(n => n.includes(q))));
          if (hits.length && (!best || hits.length > best.hits.length)) best = { hits, city };
        }
        if (!best) {
          const chars = fuzzyChars(spot);
          if (!chars) return null;
          // 名稱不可比輸入長太多（3 倍以內），避免長名稱碰巧包含這幾個字
          const fuzzy = [...rowsMap]
            .flatMap(([city, rows]) => rows.filter(r => r.category !== 'food'
              && rowNames(r).some(n => Array.from(n).length <= chars.length * 3 && isSubsequence(chars, n))).map(r => ({ r, city })))
            .sort(byRatingDesc)[0];
          return fuzzy ? rowInfo(fuzzy.r, fuzzy.city) : null;
        }
        const { hits, city } = best;
        if (hits.length === 1) return rowInfo(hits[0], city);
        return { name: spot, lat: median(hits.map(r => r.lat)), lng: median(hits.map(r => r.lng)), place_id: null, district: mostCommon(hits.map(r => r.district)), city, category: mostCommon(hits.map(r => r.category)) || 'nature', specify_cat: null, rating: null };
      };

      const resolved = spotsInput.map(spot => ({ spot, info: resolveSpot(spot) }));

      // 外地景點由最接近的行程城市出發
      const nearestTripCity = (pt) => _cities
        .map(c => ({ c, center: cityCenter(c) })).filter(x => x.center)
        .map(x => ({ c: x.c, km: haversineKm(x.center, pt) }))
        .sort((a, b) => a.km - b.km)[0] || null;

      // 第 1 層：行程城市找不到，就查整個資料庫（限同一國家）。例如京都行程輸入「奈良公園」，
      // 會找到 Nara 的資料，那天就用奈良的真實景點與餐廳。
      // 只接受距離最近行程城市 EXTERNAL_MAX_KM 以內的結果，避免同名地點配到很遠的地方
      // （例如輸入「白鬚」配到北海道的 Shirahige Waterfall，變成從京都即日來回北海道）。
      const EXTERNAL_MAX_KM = 150;
      const tripCountriesEn = [...new Set(trDestinations.map(d => COUNTRY_TO_EN[d.country] || d.country).filter(Boolean))];
      const externalRowsByCity = new Map(); // 資料庫城市（英文）-> 該城市全部有座標的資料列
      await Promise.all(resolved.filter(x => !x.info).map(async (x) => {
        const clean = x.spot.replace(/[%_,()."\\]/g, '');
        if (!clean) return;
        const search = async (pat) => {
          let query = supabase.from('managed_attractions')
            .select(MANAGED_FIELDS).eq('is_active', true).neq('category', 'food')
            .not('lat', 'is', null).not('lng', 'is', null)
            .or(`name.ilike.${pat},name_en.ilike.${pat},name_zh_cn.ilike.${pat},name_ja.ilike.${pat},district.ilike.${pat}`)
            .limit(200);
          if (tripCountriesEn.length) query = query.in('country', tripCountriesEn);
          const { data, error } = await query;
          return error ? [] : (data || []);
        };
        let data = await search(`%${clean}%`);
        // 連續字串找不到，改用「按順序出現」再找一次（同上方第三層比對）
        const chars = fuzzyChars(clean);
        if (!data.length && chars) data = await search(`%${chars.join('%')}%`);
        if (!data.length) return;
        const hitsByCity = new Map();
        data.forEach(r => {
          if ((nearestTripCity(r)?.km ?? Infinity) > EXTERNAL_MAX_KM) return;
          if (!hitsByCity.has(r.city)) hitsByCity.set(r.city, []);
          hitsByCity.get(r.city).push(r);
        });
        if (!hitsByCity.size) return;
        const info = resolveSpot(x.spot, hitsByCity);
        if (!info) return;
        x.info = { ...info, external: true };
        if (!externalRowsByCity.has(info.city)) {
          externalRowsByCity.set(info.city, (await loadCityRows(info.city)).filter(r => r.lat != null && r.lng != null));
        }
      }));

      const isDayTripInfo = (info) => {
        if (!info || info.external) return true;
        const center = cityCenter(info.city);
        return !!center && haversineKm(center, info) > CUSTOM_DAY_TRIP_KM;
      };
      const dayTripSpots = resolved.filter(x => isDayTripInfo(x.info));

      // 第 2 層：全庫都找不到（例如滋賀縣完全沒有資料的琵琶湖）。那天不指名任何景點或餐廳
      // （見下方 prompt 與後處理），避免出現未經核實的地點。
      // 問 AI 兩件事，都只用來排日子，不會顯示給旅客：
      //   - city：找不到的景點最接近哪個行程城市（決定由哪裡出發）；失敗就用剩餘空日最多的城市
      //   - group：哪些城外景點同一區、可以同一天去（例如琵琶湖＋白鬚神社）
      const unresolved = resolved.filter(x => !x.info).map(x => x.spot);
      const aiBaseCity = {};
      const aiGroup = {};
      // AI 給的大概座標：只用來決定「哪些景點同一天」和「從哪個行程城市出發」，
      // 不會顯示給旅客，也不當成景點資料（未登記進候選名單）。
      // 以前只看 AI 的 group 編號與 city 標籤，資料庫沒有的景點（琵琶湖、白鬚神社）
      // 只要 AI 給了不同編號或呼叫失敗，同一個湖區就被拆成兩天、還各自從不同城市出發。
      const aiPoint = {};
      // 資料庫沒有的景點先查 Wikipedia 座標（真實資料，搜尋排名反映知名度，
      // 例如「白鬚神社」排第 1 的就是琵琶湖畔那間，而 AI 完全不認識、OSM 只回一堆無名同名神社）。
      // 標題要與輸入吻合、離最近行程城市 EXTERNAL_MAX_KM 內才採用；查不到就退回下面 AI 給的座標。
      const wikiPoint = {};
      const wikiPointFor = async (spot) => {
        const q = spot.replace(/[()（）]/g, ' ').trim();
        for (const wl of ['zh', 'ja', 'en']) {
          try {
            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), 4000);
            const res = await fetch(`https://${wl}.wikipedia.org/w/api.php?action=query&generator=search&gsrsearch=${encodeURIComponent(q)}&gsrlimit=3&prop=coordinates&colimit=3&format=json&formatversion=2&origin=*`, { signal: ctrl.signal });
            clearTimeout(timer);
            if (!res.ok) continue;
            const pages = ((await res.json()).query?.pages || []).sort((a, b) => (a.index || 99) - (b.index || 99));
            const hit = pages.find(p => p.coordinates?.length && (norm(p.title).includes(norm(spot)) || norm(spot).includes(norm(p.title))));
            if (hit) return { lat: hit.coordinates[0].lat, lng: hit.coordinates[0].lon };
          } catch { /* 網路／逾時：換下一個語言版本 */ }
        }
        return null;
      };
      await Promise.all(resolved.filter(x => !x.info).map(async (x) => {
        const pt = await wikiPointFor(x.spot);
        if (pt && (nearestTripCity(pt)?.km ?? Infinity) <= EXTERNAL_MAX_KM) wikiPoint[norm(x.spot)] = pt;
      }));
      if ((unresolved.length && _cities.length > 1) || dayTripSpots.length >= 2) {
        const askGrouping = () => callAI(
          `The trip is in ${tripCountriesEn.join(' / ') || 'the destination country'}. Base cities: ${_cities.map(c => `"${c}"`).join(', ')}. Places: ${dayTripSpots.map(x => `"${x.spot}"`).join(', ')}. Place names can be ambiguous (many shrines, parks and temples share a name): always take the meaning that is the best-known TOURIST attraction with that name in that country — not an obscure local namesake, and not merely whichever one is nearest a base city. If one listed place is a landmark located at, on or around another listed place (e.g. a shrine on a famous lake's shore), they are in the same area: give them nearby coordinates and the same group. For each place give "lat" and "lng" = its approximate real-world coordinates in decimal degrees, "city" = the ONE base city it is geographically closest to (copied exactly from the list), and "group" = a number: give two places the SAME group number only if they are in the same area and close enough to visit together in one day trip (under about 1 hour apart), otherwise give them different numbers. Reply as a JSON array: [{"place":"<place exactly as given>","lat":35.0,"lng":135.0,"city":"<base city>","group":1}]`,
          'Reply ONLY with valid JSON. No explanation, no markdown.',
          600,
        );
        let r = await askGrouping();
        if (!Array.isArray(r)) r = await askGrouping(); // 偶爾回空或格式錯，重試一次
        if (Array.isArray(r)) r.forEach(x => {
          if (!x?.place) return;
          const k = norm(x.place);
          if (_cities.includes(x.city)) aiBaseCity[k] = x.city;
          if (x.group != null) aiGroup[k] = String(x.group);
          const lat = Number(x.lat), lng = Number(x.lng);
          if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
            // 離最近的行程城市超過 EXTERNAL_MAX_KM 視為 AI 亂編，不採用
            if ((nearestTripCity({ lat, lng })?.km ?? Infinity) <= EXTERNAL_MAX_KM) aiPoint[k] = { lat, lng };
          }
        });
      }
      Object.assign(aiPoint, wikiPoint); // Wikipedia 的真實座標優先於 AI 估的

      const canUseArrivalDay = trModes.includes('self');
      const allDayNums = Array.from({ length: trDays }, (_, i) => i + 1);
      const cityDaysOf = (c) => allDayNums.filter(d => dayCityMap[d] === c);
      const arrivalDayOf = (c) => cityDaysOf(c)[0];
      const isOnsenDay = (d) => onsenTargetDay != null && (d === onsenTargetDay || (onsenOvernight && d === onsenTargetDay + 1));
      const dayTripDays = new Set();
      const isFreeForDayTrip = (d) => d !== 1 && d !== trDays && d !== outletTargetDay && !isOnsenDay(d) && !dayTripDays.has(d);
      const pickDayTripDay = (baseCity) => {
        const own = cityDaysOf(baseCity);
        const tiers = [
          own.filter(d => isFreeForDayTrip(d) && (canUseArrivalDay || d !== arrivalDayOf(baseCity))),
          own.filter(isFreeForDayTrip),
          // 出發城市已經沒有空日，退而求其次用其他城市的空日
          allDayNums.filter(d => isFreeForDayTrip(d) && (canUseArrivalDay || d !== arrivalDayOf(dayCityMap[d]))),
          allDayNums.filter(isFreeForDayTrip),
        ];
        return tiers.find(t => t.length)?.[0] ?? null;
      };
      const fallbackBaseCity = () => [..._cities].reverse()
        .sort((a, b) => cityDaysOf(b).filter(isFreeForDayTrip).length - cityDaysOf(a).filter(isFreeForDayTrip).length)[0] || _cities[0];

      // 城外景點分組：同一區的放同一天（上午、下午各一個，所以每組最多兩個）。
      // 兩個都有座標就看實際距離；任何一個沒座標就看 AI 的分組。
      const MAX_SPOTS_PER_DAY_TRIP = 2;
      const SAME_DAY_TRIP_KM = 30;
      const groups = [];
      dayTripSpots.forEach(x => {
        const g = groups.find(g => g.length < MAX_SPOTS_PER_DAY_TRIP && g.every(m => {
          const pm = m.info || aiPoint[norm(m.spot)], px = x.info || aiPoint[norm(x.spot)];
          if (pm && px) return haversineKm(pm, px) <= SAME_DAY_TRIP_KM;
          const ga = aiGroup[norm(m.spot)];
          return ga != null && ga === aiGroup[norm(x.spot)];
        }));
        if (g) g.push(x); else groups.push([x]);
      });
      // 先排城外（每組獨佔一天），再排市內
      groups.forEach(members => {
        const lead = members.find(m => m.info) || members[0];
        const aiNearest = members.map(m => aiPoint[norm(m.spot)]).filter(Boolean).map(p => nearestTripCity(p)?.c).find(Boolean);
        const baseCity = (lead.info?.external ? nearestTripCity(lead.info)?.c : lead.info?.city) || aiNearest || aiBaseCity[norm(lead.spot)] || fallbackBaseCity();
        const day = pickDayTripDay(baseCity);
        if (day != null) dayTripDays.add(day);
        const trip = { day, baseCity, plans: [], pool: { attractions: [], food: [] }, anchor: null };
        members.forEach(m => {
          const p = { spot: m.spot, kind: 'dayTrip', day, info: m.info, baseCity, trip };
          trip.plans.push(p);
          customPlans.push(p);
        });
        customDayTrips.push(trip);
      });
      const inCityCount = {};
      resolved.forEach(({ spot, info }) => {
        if (customPlans.some(p => p.spot === spot)) return;
        const ok = (d) => !dayTripDays.has(d) && d !== outletTargetDay && !isOnsenDay(d) && (inCityCount[d] || 0) < 2;
        const own = cityDaysOf(info.city);
        // 已經有附近（5km 內）自訂景點的日子優先，同一區一齊行
        const nearDays = own.filter(d => ok(d) && customPlans.some(p => p.kind === 'inCity' && p.day === d && p.info && haversineKm(p.info, info) <= 5));
        const tiers = [
          nearDays,
          own.filter(d => ok(d) && d !== 1 && d !== trDays && (canUseArrivalDay || d !== arrivalDayOf(info.city))),
          own.filter(d => ok(d) && d !== 1 && d !== trDays),
          own.filter(ok),
        ];
        const cands = tiers.find(t => t.length) || [];
        const day = [...cands].sort((a, b) => (inCityCount[a] || 0) - (inCityCount[b] || 0) || a - b)[0] ?? null;
        if (day != null) inCityCount[day] = (inCityCount[day] || 0) + 1;
        customPlans.push({ spot, kind: 'inCity', day, info, baseCity: info.city });
      });

      // 登記進候選名單（標記 custom），後處理才有真實座標，並且不會被當成重複換走
      customPlans.forEach(p => {
        if (!p.info) return;
        const key = norm(p.info.name);
        p.info = { ...(candidateRegistry.get(key) || p.info), custom: true };
        candidateRegistry.set(key, p.info);
      });

      // 即日來回那天的候選池：該組有座標的景點周圍 DAY_TRIP_POOL_KM 內、資料庫已有的
      // 真實景點與餐廳（行程城市＋外地城市的資料都算），午餐／下午／晚餐只從這裡挑。
      const DAY_TRIP_POOL_KM = 15;
      const DAY_TRIP_POOL_SIZE = 8;
      const allRowsMaps = [...rowsByCity, ...externalRowsByCity];
      customDayTrips.forEach(trip => {
        const infos = trip.plans.map(p => p.info).filter(Boolean);
        trip.anchor = infos[0] || null;
        if (trip.day == null || !infos.length) return;
        const customKeys = new Set(infos.map(i => norm(i.name)));
        const near = [];
        allRowsMaps.forEach(([city, rows]) => rows.forEach(r => {
          const a = toAppAttr(r);
          if (isOnsenVenue(a)) return;
          if (!infos.some(i => haversineKm(i, r) <= DAY_TRIP_POOL_KM)) return;
          const name = localName(a, lang) || r.name;
          if (!name || customKeys.has(norm(name))) return;
          near.push({ name, lat: r.lat, lng: r.lng, place_id: r.place_id, district: r.district, city, category: r.category, specify_cat: r.specify_cat, rating: r.rating });
        }));
        const byRating = (a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0);
        const seen = new Set();
        const uniq = (v) => { const k = norm(v.name); if (seen.has(k)) return false; seen.add(k); return true; };
        trip.pool.attractions = near.filter(v => v.category !== 'food' && !isOutletCandidate(v)).sort(byRating).filter(uniq).slice(0, DAY_TRIP_POOL_SIZE);
        trip.pool.food = near.filter(v => v.category === 'food').sort(byRating).filter(uniq).slice(0, DAY_TRIP_POOL_SIZE);
        [...trip.pool.attractions, ...trip.pool.food].forEach(v => { if (!candidateRegistry.has(norm(v.name))) candidateRegistry.set(norm(v.name), v); });
      });

      // ── 自訂景點的「主要搭配景點」：從資料庫附近的真實景點中，由 AI 挑遊客通常會一起去的 ──
      // 使用者要求（2026-10-07）。只按評分排會挑到紀念品店、工作室甚至殯儀館（嵯峨野嵐山小火車
      // 1.5km 內評分最高的幾筆），真正的主要景點（天龍寺、竹林小徑）反而排在後面。
      // AI 只能按編號從清單裡選，編號不在清單內就丟棄——不會出現資料庫以外的地點。
      // 市內景點：當天另一個觀光時段從這裡選；城外一日遊：取代按評分排的下午候選。
      const COMPANION_KM = 1.5;
      const COMPANION_LIST_SIZE = 30;
      const COMPANION_PICKS = 3;
      const allCustomKeys = new Set(customPlans.flatMap(p => [norm(p.spot), p.info ? norm(p.info.name) : ''].filter(Boolean)));
      const companionTargets = []; // { label, anchor, radiusKm, candidates, apply(picks) }
      const nearbySights = (anchors, radiusKm) => {
        const seen = new Set();
        const out = [];
        allRowsMaps.forEach(([city, rows]) => rows.forEach(r => {
          if (r.category === 'food') return;
          const a = toAppAttr(r);
          if (isOnsenVenue(a)) return;
          const name = localName(a, lang) || r.name;
          const key = norm(name);
          if (!name || seen.has(key) || allCustomKeys.has(key)) return;
          const distKm = Math.min(...anchors.map(i => haversineKm(i, r)));
          if (distKm > radiusKm) return;
          const v = { name, lat: r.lat, lng: r.lng, place_id: r.place_id, district: r.district, city, category: r.category, specify_cat: r.specify_cat, rating: r.rating, distKm };
          if (isOutletCandidate(v)) return;
          seen.add(key);
          out.push(v);
        }));
        return out.sort((a, b) => a.distKm - b.distKm).slice(0, COMPANION_LIST_SIZE);
      };
      customPlans.filter(p => p.kind === 'inCity' && p.day != null && p.info).forEach(p => {
        p.companions = [];
        companionTargets.push({ label: p.info.name, candidates: nearbySights([p.info], COMPANION_KM), apply: (picks) => { p.companions = picks; } });
      });
      customDayTrips.filter(t => t.day != null && t.plans.length === 1 && t.anchor).forEach(trip => {
        companionTargets.push({ label: trip.anchor.name, candidates: nearbySights([trip.anchor], DAY_TRIP_POOL_KM), apply: (picks) => { trip.pool.attractions = picks; } });
      });
      const targetsWithCands = companionTargets.filter(t => t.candidates.length);
      if (targetsWithCands.length) {
        const r = await callAI(
          `For each requested place below, choose from ITS numbered candidate list up to ${COMPANION_PICKS} places that are the main, well-known sights visitors usually combine with it on the same visit, best first. Skip shops, studios, workshops, cafes, offices, funeral halls and anything that is not a real sightseeing spot. Only use numbers from that place's own list. Reply as a JSON array: [{"id":<requested place number>,"picks":[<candidate number>, ...]}]\n`
            + targetsWithCands.map((t, i) => `${i + 1}) "${t.label}" — candidates: ${t.candidates.map((v, j) => `${j + 1}. ${v.name}`).join('; ')}`).join('\n'),
          'Reply ONLY with valid JSON. No explanation, no markdown.',
          600,
        );
        const picksById = new Map();
        if (Array.isArray(r)) r.forEach(x => { if (Array.isArray(x?.picks)) picksById.set(Number(x.id), x.picks.map(Number)); });
        targetsWithCands.forEach((t, i) => {
          const picks = [...new Set(picksById.get(i + 1) || [])]
            .filter(n => Number.isInteger(n) && n >= 1 && n <= t.candidates.length)
            .slice(0, COMPANION_PICKS)
            .map(n => t.candidates[n - 1]);
          // AI 失敗時退而求其次：只取觀光類（文化／自然），按評分再按距離
          const fallback = t.candidates.filter(v => v.category === 'culture' || v.category === 'nature')
            .sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0) || a.distKm - b.distKm).slice(0, COMPANION_PICKS);
          const chosen = picks.length ? picks : fallback;
          if (!chosen.length) return;
          chosen.forEach(v => { if (!candidateRegistry.has(norm(v.name))) candidateRegistry.set(norm(v.name), v); });
          t.apply(chosen);
        });
      }

      const list = (arr) => arr.map(v => `"${v.name}"`).join(', ');
      const ruleParts = [];
      customDayTrips.forEach(trip => {
        const names = trip.plans.map(p => p.info?.name || p.spot);
        if (trip.day == null) {
          names.forEach(nm => ruleParts.push(`"${nm}": no free day is left for it — include it only where it genuinely fits without breaking the other day rules.`));
          return;
        }
        const [a, b] = names;
        const noData = trip.plans.filter(p => !p.info).map(p => `"${p.spot}"`);
        const afternoonPart = b ? `afternoon location MUST be "${b}"`
          : trip.pool.attractions.length ? `afternoon = one of these real sights nearby: ${list(trip.pool.attractions)}`
          : `afternoon location = "${a}" (activity: continue exploring the area, no specific venue name)`;
        const lunchPart = trip.pool.food.length
          ? `lunch = one of these real restaurants nearby: ${list(trip.pool.food)}`
          : `lunch location = "${a}" (activity: lunch in the area, no restaurant name)`;
        ruleParts.push(`DAY ${trip.day} IS A DAY TRIP TO ${names.map(n => `"${n}"`).join(' + ')} (leaving from and returning to ${trip.baseCity}): morning location MUST be "${a}", ${lunchPart}, ${afternoonPart}, evening = dinner ${trip.pool.food.length ? 'at another of the restaurants above or ' : ''}after returning to ${trip.baseCity}, from the VERIFIED REAL VENUE LIST. Do NOT put any ${trip.baseCity} city-centre attraction on this day, and do not use any place not listed here.${noData.length ? ` There is NO verified venue data around ${noData.join(', ')} — do NOT name any specific restaurant, shop or sight there.` : ''}`);
      });
      customPlans.filter(p => p.kind === 'inCity').forEach(p => {
        const nm = p.info?.name || p.spot;
        if (p.day == null) { ruleParts.push(`"${nm}": no free day is left for it — include it only where it genuinely fits without breaking the other day rules.`); return; }
        const companionPart = p.companions?.length
          ? ` If that day's other sightseeing slot is not another requested place, it must be one of these real main sights next to it: ${list(p.companions)} — never the same place twice.`
          : '';
        ruleParts.push(`DAY ${p.day} MUST include "${nm}" (write the location exactly as "${nm}") as its morning or afternoon sightseeing slot, and that day's other stops should be close to it${p.info?.district ? ` (around ${p.info.district})` : ''}.${companionPart}`);
      });
      spotsStr = ` (ALSO) TRAVELLER-REQUESTED PLACES — these override rule (1) and the VERIFIED REAL VENUE LIST restriction; never move them to a different day and never put them on the outlet-mall day: ${ruleParts.join(' ')}`;
      if (IS_DEV) console.log('[genItinerary] 自訂景點安排', customPlans.map(p => ({ spot: p.spot, kind: p.kind, day: p.day, base: p.baseCity, name: p.info?.name })));
    }
    // 某個地點是否屬於某個已排日子的自訂景點（名稱相同，或包含旅客輸入的字）。
    // 先找名稱完全相同的；只能用「包含」比對時取配對字數最長的——「嵯峨野嵐山小火車」
    // 也包含「嵐山」，以前按清單順序先配到嵐山，令小火車被當成沒排、反把真正的嵐山蓋掉。
    const customPlanOf = (loc) => {
      const k = norm(loc);
      if (!k) return null;
      const planned = customPlans.filter(p => p.day != null);
      const keysOf = (p) => [norm(p.spot), p.info ? norm(p.info.name) : ''].filter(Boolean);
      const exact = planned.find(p => keysOf(p).includes(k));
      if (exact) return exact;
      let best = null, bestLen = 0;
      planned.forEach(p => keysOf(p).forEach(key => {
        if (k.includes(key) && key.length > bestLen) { best = p; bestLen = key.length; }
      }));
      return best;
    };

    // Hotel/stay area, if the traveller gave one, anchors the arrival and
    // departure days to somewhere real instead of the AI inventing a nearby
    // place name that won't match any VERIFIED REAL VENUE LIST entry.
    const firstHotelArea = (trDestinations[0]?.hotelArea || '').trim();
    const lastHotelArea = (trDestinations[trDestinations.length - 1]?.hotelArea || '').trim();
    // A 1-day trip has Day 1 = the last day, so the arrival rule and the
    // departure rule both target the same day with genuinely contradictory
    // instructions (arrival wants a light morning; departure demands an
    // outlet mall morning) — confirmed via scenario testing this confuses
    // the model into violating BOTH "no full-day venues" rules at once
    // (a 7-day-park venue squeezed into a single afternoon slot). Use one
    // merged, single-day-aware rule instead of composing two conflicting ones.
    const isSingleDayTrip = trDays === 1;
    // 使用者要求（2026-09-25）：抵達日跟每次換城市的第一天都不要排登山步道／
    // 山上觀景台——剛下飛機或剛換城市體力/時間都比較緊，不適合爬山，除非是
    // 自駕遊（時間彈性較大）或整趟只有一天（day1Rule的single-day分支本來就
    // 已經很輕量，不需要另外疊加）。
    const restrictArrivalHiking = !trModes.includes('self') && !isSingleDayTrip;
    const NO_HIKE_CLAUSE = ' Do NOT schedule any hiking trail, mountain-climbing route, or mountaintop viewpoint/observation deck on this day (e.g. skip the hillside hiking portion of shrine/temple trails) — save those for a later day.';
    const day1Rule = isSingleDayTrip
      ? `DAY 1 IS THE ONLY DAY (arrival and departure both happen today) — morning=arrival/check-in or a short light activity, afternoon=ONE main sightseeing or shopping activity (must be light-to-medium, e.g. a shopping street or a single attraction — absolutely NOT a full-day venue like a theme park/water park/zoo since there's no time for one), evening=quick dinner then depart — NEVER schedule a full-day venue (rule 5) on a 1-day trip, there is not enough time.`
      : firstHotelArea
      ? `DAY 1 IS ARRIVAL DAY: the traveller is staying near "${firstHotelArea}" — morning=flight/travel/check-in near "${firstHotelArea}", afternoon=light nearby exploration or rest, evening=easy welcome dinner near "${firstHotelArea}" (prefer VERIFIED REAL VENUE LIST entries whose district matches or is close to this area) — NEVER schedule theme parks, zoos, or full-day venues on Day 1.${restrictArrivalHiking ? NO_HIKE_CLAUSE : ''}`
      : `DAY 1 IS ARRIVAL DAY: morning=flight/travel/check-in, afternoon=light nearby exploration or rest, evening=easy welcome dinner — NEVER schedule theme parks, zoos, or full-day venues on Day 1.${restrictArrivalHiking ? NO_HIKE_CLAUSE : ''}`;
    // 換城市當天（不含Day1，Day1已經由上面的day1Rule處理）第一天同樣不排登山/
    // 觀景台——條件跟抵達日一致（自駕/單日行程不受限）。
    const cityChangeNoHikeRule = (() => {
      if (!restrictArrivalHiking) return '';
      const dayNums = Object.keys(dayCityMap).map(Number).sort((a, b) => a - b);
      const changeDays = dayNums.filter((d, i) => i > 0 && dayCityMap[d] !== dayCityMap[dayNums[i - 1]]);
      return changeDays.length
        ? ` (ALSO) NO HIKING ON CITY-CHANGE DAYS: Day(s) ${changeDays.join(', ')} are the first day arriving in a new city within this trip — do NOT schedule any hiking trail, mountain-climbing route, or mountaintop viewpoint/observation deck on these days (save those for a later day in that city).`
        : '';
    })();
    // Several city groups have BOTH a real outlet mall genuinely close to
    // that city's airport AND one or more real outlets 45-90+ min away in
    // the same metro area — the AI has no way to tell them apart from the
    // name alone, so left to its own knowledge it has been picking the far
    // one for the departure-day morning (confirmed by user report: Kobe
    // Sanda Premium Outlets picked as "airport-convenient" for a KIX
    // departure day when it's actually 60-90 min away and belongs on a
    // separate Kobe-based shopping day instead). Verified against real
    // outlet coordinates in the DB — give the model the actual geography
    // per region rather than leaving it to guess from city name alone.
    const OUTLET_AIRPORT_FACTS = {
      // Kansai: KIX serves Osaka/Kyoto/Kobe/Nara alike
      Osaka: ` KANSAI OUTLET GEOGRAPHY (real distances — do not treat all Kansai outlets as interchangeable): 臨空 Outlet / Rinku Premium Outlets is the ONLY outlet mall genuinely close to Kansai Airport (KIX), a few minutes away — this is the ONLY outlet appropriate for a departure-day morning before a KIX flight. 三井 OUTLET PARK 大阪門真 (Osaka), 神戶三田 Premium Outlets (Kobe), and 三井アウトレットパーク 滋賀龍王 / Shiga Ryuoh Outlet (near Kyoto/Shiga) are each 45-90+ minutes from KIX and MUST NEVER be scheduled on the departure day — they belong only on a non-departure day as that region's dedicated shopping day for the matching city.`,
      Sapporo: ` SAPPORO OUTLET GEOGRAPHY (real distances): 三井 OUTLET PARK 札幌北廣島 is the outlet genuinely close to New Chitose Airport (CTS), about 30 min — this is the appropriate departure-day outlet. Kamaei Factory Outlet is in downtown Sapporo, over an hour from CTS, and MUST NEVER be scheduled on the departure day — it belongs only on a non-departure Sapporo sightseeing day.`,
      Nagoya: ` NAGOYA OUTLET GEOGRAPHY (real distances): Mitsui Outlet Park Jazz Dream Nagashima is the outlet genuinely close to Chubu Centrair Airport (NGO), about 20-30 min — this is the appropriate departure-day outlet. 土岐 PREMIUM OUTLETS is over an hour from NGO and MUST NEVER be scheduled on the departure day — it belongs only on a non-departure Nagoya-region day trip.`,
    };
    OUTLET_AIRPORT_FACTS.Kyoto = OUTLET_AIRPORT_FACTS.Kobe = OUTLET_AIRPORT_FACTS.Nara = OUTLET_AIRPORT_FACTS.Osaka;
    const tripCitiesEn = _cities.map(c => CITY_EN_MAP[c] || c);
    const kansaiOutletFacts = [...new Set(tripCitiesEn.map(c => OUTLET_AIRPORT_FACTS[c]).filter(Boolean))].join('');
    const departureRule = isSingleDayTrip
      ? '' // already folded into day1Rule above — do not compose a second, contradictory rule for the same day
      : lastHotelArea
      ? `DAY ${trDays} IS DEPARTURE DAY: the traveller is staying near "${lastHotelArea}" — morning=SOUVENIR (手信/伴手禮) SHOPPING near "${lastHotelArea}" — this is the day's main purpose, so pick a shopping-category venue from the VERIFIED REAL VENUE LIST whose district matches or is close to this area and suits buying gifts to take home (shopping street / 商店街, department-store food hall, local specialty or snack shop area, drugstore, station souvenir zone), and say in the activity/tip what to buy there — not sightseeing, afternoon/evening=check-out and travel to airport — NEVER schedule theme parks, full-day venues, or far attractions on the last day.${kansaiOutletFacts}`
      : (preselectedOutlet && outletTargetDay && outletTargetDay !== trDays)
      // The traveller's one requested outlet is already placed on an earlier
      // day (outletDayRule below) — do NOT also demand an outlet mall here,
      // or the model dutifully follows both instructions and the trip ends
      // up with two (confirmed live: this happened before this branch existed).
      ? `DAY ${trDays} IS DEPARTURE DAY: no hotel area was given — morning=SOUVENIR (手信/伴手禮) SHOPPING as the day's main purpose, at a non-outlet shopping-category venue from the VERIFIED REAL VENUE LIST convenient to the airport (shopping street / 商店街, department-store food hall, local specialty or snack shop area, drugstore, station souvenir zone) and say in the activity/tip what to buy there (the traveller's one outlet-mall visit is already scheduled on Day ${outletTargetDay}, do NOT add another outlet/shopping mall here), afternoon/evening=check-out and travel to airport — NEVER schedule theme parks, full-day venues, outlet malls, or far attractions on the last day.`
      : `DAY ${trDays} IS DEPARTURE DAY: no hotel area was given, so morning MUST be SOUVENIR (手信/伴手禮) SHOPPING — the day's main purpose (say in the activity/tip what to buy) — at an OUTLET MALL or large shopping venue from the VERIFIED REAL VENUE LIST that's GENUINELY close to the departure airport by real-world travel time (prefer a shopping-category entry whose name contains "Outlet", or otherwise a department store/mall near the airport) — do NOT pick an outlet mall just because it's in the same metro area if it's actually 60+ minutes from the airport; if no candidate is truly close to the airport, use a light nearby activity instead of a distant outlet — afternoon/evening=check-out and travel to airport — NEVER schedule theme parks, full-day venues, or far attractions on the last day.${kansaiOutletFacts}`;
    const outletDayRule = (preselectedOutlet && outletTargetDay)
      ? `DAY ${outletTargetDay} IS OUTLET DAY: dedicate the ENTIRE day to "${preselectedOutlet.name}" in ${preselectedOutlet.city} (per rule 5 — morning=arrival & explore, afternoon=continue, evening=dinner nearby) — this is the traveller's specifically requested outlet-mall visit. Do not schedule it on any other day, and do not add a different outlet/shopping mall anywhere else in the trip.`
      : null;
    // Multi-city trips often have an outlet mall candidate in EVERY city (they're
    // a common category in the VERIFIED REAL VENUE LIST) — left unconstrained the
    // model happily schedules one per city instead of once for the whole trip.
    // When the traveller explicitly ticked "Outlet", the day AND venue are both
    // already decided (outletDayRule above) — more direct than asking the model
    // to pick among several and then untangling duplicates after the fact, so it
    // takes priority over this generic multi-candidate rule.
    // A 1-day trip never gets an outletTargetDay (day1Rule already forbids a
    // full-day venue on the only day), so outletDayRule is always null here
    // and this generic text was the traveller's ONLY instruction about their
    // outlet request — it said nothing about being on a 1-day trip, leaving
    // it to silently negotiate against day1Rule's "light activity only"
    // constraint with no concrete guidance. Give it one.
    const singleDayOutletRule = (isSingleDayTrip && preselectedOutlet)
      ? `The traveller wants an outlet-mall visit, but this is a 1-day trip — treat "${preselectedOutlet.name}" in ${preselectedOutlet.city} as ONE regular activity slot (per day1Rule's afternoon slot), NOT a full-day dedication; use it there if it reasonably fits, otherwise substitute an ordinary shopping venue instead.`
      : null;
    const outletRule = outletDayRule || singleDayOutletRule || `OUTLET/SHOPPING MALLS: include AT MOST ONE outlet-mall or large factory-outlet-style shopping visit across the ENTIRE trip, even when several of the trip's cities each have their own outlet mall in the VERIFIED REAL VENUE LIST — never schedule two separate outlet-mall visits on different days/cities. If that one outlet-mall visit lands on the LAST day of the trip, prefer whichever candidate (an Outlet mall, else the nearest LaLaport, else the nearest AEON Mall, else any well-known shopping landmark) is geographically closest to the departure airport over one that is farther away.`;
    // 溫泉規則：有勾才安排，而且只安排旅客選定的那一個
    const onsenRule = !wantsOnsen
      ? ' (ALSO) HOT SPRINGS: the traveller did NOT ask for a hot spring — do NOT schedule any hot spring / onsen / public bath / spa / jjimjilbang visit anywhere in the trip.'
      : (preselectedOnsen && onsenTargetDay && onsenOvernight)
      ? ` (ALSO) DAYS ${onsenTargetDay}-${onsenTargetDay + 1} HOT SPRING OVERNIGHT (2 days, 1 night): "${preselectedOnsen.name}" is about ${onsenDistKm} km from central ${preselectedOnsen.city}, so it is a 2-day-1-night side trip, not a same-day visit. Day ${onsenTargetDay}: morning = one light activity in the city (or travel), AFTERNOON = travel to and soak at "${preselectedOnsen.name}", EVENING = dinner in the ${preselectedOnsen.district || preselectedOnsen.name} hot-spring area, and that night's "hotel" MUST be a hot-spring ryokan/inn in the ${preselectedOnsen.district || preselectedOnsen.name} area (not a city-centre hotel). Day ${onsenTargetDay + 1}: MORNING = "${preselectedOnsen.name}" again (morning bath / stroll around the hot-spring area), AFTERNOON = return to ${preselectedOnsen.city}. Never pair these slots with city-centre venues that are far from the hot spring, and do not add any other hot spring anywhere in the trip.`
      : (preselectedOnsen && onsenTargetDay && onsenFootbathNearby.length)
      ? ` (ALSO) DAY ${onsenTargetDay} FOOTBATH + NEARBY SIGHTS: "${preselectedOnsen.name}" is a footbath — a short 20-30 minute stop, never a standalone trip. On Day ${onsenTargetDay}, the AFTERNOON slot's location MUST be "${preselectedOnsen.name}" and the MORNING slot MUST be one of these sights within ${FOOTBATH_NEARBY_KM} km of it: ${onsenFootbathNearby.map(n => `"${n}"`).join(' / ')} — so the footbath is combined with sightseeing in the same area (say so in the activity/tip). Schedule the footbath exactly once, and do not add any other hot spring anywhere in the trip.`
      : (preselectedOnsen && onsenTargetDay)
      ? ` (ALSO) DAY ${onsenTargetDay} HOT SPRING: the AFTERNOON slot of Day ${onsenTargetDay} MUST be a visit to "${preselectedOnsen.name}" in ${preselectedOnsen.city} — the traveller's specifically requested hot spring / spa (say in the tip roughly how long to stay and what to bring).${onsenDistKm != null && onsenDistKm > ONSEN_OVERNIGHT_KM ? ` It is about ${onsenDistKm} km from the city centre, so keep that day's other stops light and near the route — no far-apart city-centre venues the same day.` : ''} Schedule it exactly once, and do not add any other hot spring anywhere in the trip.`
      : ' (ALSO) HOT SPRING: the traveller wants ONE hot-spring / spa visit — include exactly one real, well-known public hot spring / onsen (or, where the area has none, a well-known jjimjilbang / bathhouse spa) in or near one of the trip\'s cities as an afternoon slot, only if one genuinely exists there; never invent one.';
    // Public-transit travellers need a tighter same-day radius than a vague
    // "group nearby attractions" — a numeric round-trip cap gives the model
    // something concrete to reason against. Self-driving travellers get the
    // opposite adjustment: with a car, a single-city trip can reach nearby
    // towns/countryside for day trips, not just the home city.
    const usesPublicOnly = trModes.includes('public') && !trModes.includes('self');
    const transitRule = usesPublicOnly
      ? ` (ALSO) PUBLIC TRANSIT: the traveller is relying on public transit only — same-day activities (other than the evening restaurant) must stay close enough that a round trip by public transit totals under about 2 hours; do not pair same-day venues that require a long cross-town transit ride even if they're nominally in the same city. Full-day venues (rule 5) are exempt since the traveller isn't leaving that one place.`
      : '';
    const selfDriveRule = (usesSelfDriveSingleCity && nearby.length)
      ? ` (ALSO) SELF-DRIVE DAY TRIP: the traveller is self-driving with only one home city selected — for ONE day that doesn't need a full-day venue, prefer using the day-trip candidates listed under ${nearby.map(n => `${n.city} (~${Math.round(n.distKm)}km away)`).join('/')} in the VERIFIED REAL VENUE LIST above (they're marked "self-drive day-trip option") instead of another venue in the home city — this is a real day-trip opportunity a self-driving traveller would actually take, not a home-city-only trip.`
      : '';
    // 使用者要求（2026-09-29）：第 2 天至倒數第 2 天改為 4 個時段——上午景點 → 午餐 → 下午景點 → 晚餐；
    // 第 1 天與最後一天維持原本 3 個時段。午餐／晚餐一定要係 food 類別（後處理會再強制一次）。
    const hasMiddleDays = trDays >= 3;
    const middleDaysLabel = trDays === 3 ? 'Day 2' : `Days 2-${trDays - 1}`;
    const mealRule = ` (ALSO) MEALS: ${hasMiddleDays ? `${middleDaysLabel} MUST have FOUR slots in this order — morning = ONE sightseeing attraction, lunch = a restaurant for lunch, afternoon = ONE sightseeing attraction, evening = a restaurant for dinner. Day 1 and Day ${trDays} have NO "lunch" key at all (omit it). ` : 'Do NOT output a "lunch" key on any day. '}Every "lunch" slot and every dinner "evening" slot MUST use a venue from the "food" part of the VERIFIED REAL VENUE LIST (never an attraction, shopping venue, or viewpoint), close to the slot before it. The only evening exception is the departure day's check-out/airport transfer.`;
    const rules = `CRITICAL RULES: (1) Prefer the VERIFIED REAL VENUE LIST above all else; never repeat the same place (attraction or restaurant) on two different days. (2) Group nearby attractions on the same day — do not pair venues that are far apart across the city/region (use the district shown in the list as a guide). (3) Do not worry about travelToNext — leave it as a short placeholder; it will be recalculated separately from real coordinates. (4) TIME-SLOT LOGIC: morning=museums/parks/early markets/breakfast spots ONLY — NEVER night markets/bars/clubs in morning; evening=dinner at a restaurant (see MEALS rule) — NEVER early-morning venues at night. (5) FULL-DAY VENUES: if a venue needs a full day (theme park/outlet mall/water park/zoo/large market), dedicate the ENTIRE day to it — morning=arrival & explore, lunch (middle days) = a food-list restaurant nearest the venue, afternoon=continue, evening=dinner nearby — do NOT add other major attractions that day. (6) ${day1Rule}${departureRule ? ` (7) ${departureRule}` : ''} (8) ${outletRule}${dayCityRule ? ` (9) ${dayCityRule}` : ''}${borderCrossingRule ? ` (10) ${borderCrossingRule}` : ''}${transitRule}${selfDriveRule}${cityChangeNoHikeRule}${onsenRule}${mealRule} LANGUAGE: All text in JSON must be in ${al}.`;

    // 改動此公式時要同步修改 api/groq.js 的 GUEST_MAX_TOKENS（訪客 4 天上限）及 max_tokens 總上限
    const maxTokens = Math.min(9000, 2000 + trDays * 650);
    __mark('pre-AI prep done, calling AI');
    setTripGenStage(2);
    // For a multi-country trip, list each city with its own country instead of
    // appending only trDestinations[0]'s country to the whole city list.
    const destLabel = isMultiCountryTrip
      ? trDestinations.filter(d => d.city).map(d => `${d.city}(${d.country})`).join('+')
      : `${_cities.join('+')} ${_country}`;
    const userPrompt = `你必須用${al}回答。Plan a ${trDays}-day trip to ${destLabel}${dateStr}. Style:${styleStr}. Transport:${modesStr}. Traveller:${companionStr}. Currency:${currency}.${actStr}${candidateBlock} ${rules}${spotsStr}`;
    const systemPrompt = `Reply ONLY with valid JSON. Use ${sym} for prices. ALL text (titles, activities, tips, locations) MUST be in ${al}. No explanation, no markdown.
Format: {"estimatedTotal":"${sym}XXXX","days":[{"day":1,"title":"DAY_TITLE","city":"CITY","morning":{"activity":"ACT","location":"LOC","district":"DISTRICT","duration":"Xhr","cost":"${sym}XX","tip":"TIP","travelToNext":"15 min by subway"},"lunch":{"activity":"ACT","location":"FOOD_VENUE","district":"DISTRICT","duration":"1hr","cost":"${sym}XX","tip":"TIP","travelToNext":"10 min walk"},"afternoon":{"activity":"ACT","location":"LOC","district":"DISTRICT","duration":"Xhr","cost":"${sym}XX","tip":"TIP","travelToNext":"10 min walk"},"evening":{"activity":"ACT","location":"LOC","district":"DISTRICT","duration":"Xhr","cost":"${sym}XX","tip":"TIP"},"hotel":{"name":"HOTEL","stars":4,"pricePerNight":"${sym}XXX","area":"AREA"},"dailyCost":"${sym}XXX"}]}`;

    // Try streaming first so the loading screen can reveal finished days as
    // they're written — purely a perceived-speed win, never a correctness
    // trade-off: if the stream fails, produces nothing, or comes back short
    // (fewer complete day objects than requested — including a truncated
    // last day), fall through to the exact same non-streaming callAI() path
    // this always used, unchanged. Streaming always sends isItinerary+the
    // real JWT (unlike the non-streaming admin path, which omits both to
    // skip the usage-limit check) — the server recognizes ADMIN_EMAIL and
    // skips consuming a trip slot either way, so this works for admin too
    // as long as they're logged in (accessToken present).
    //
    // Re-enabled 2026-09-09 after root-causing the "gemini_stream_empty"
    // failures: api/groq.js's SSE parser was splitting records on the
    // literal 2-char string "\n\n", but Google's stream sends CRLF line
    // endings ("\r\n\r\n" between records) — that literal substring never
    // occurs inside "\r\n\r\n" (each '\n' has a '\r' right before it), so
    // the split never fired, the buffer just grew all stream long, and the
    // accumulated text stayed empty until the connection closed. Fixed
    // server-side to split on /\r?\n\r?\n/ instead. Left a diagnostic log
    // (raw byte count + tail) on the server for the empty case in case this
    // wasn't the only cause — watch Vercel logs after the next real test.
    const STREAM_ITINERARY_ENABLED = true;
    let d = null;
    let seenDayNums = new Set();
    // 記錄這次行程實際是哪個AI供應商回應的，接進下面P0驗證層的log，方便之後
    // 判斷違規率是否集中在特定供應商（Gemini/Groq遵守prompt規則的程度可能不同）。
    let usedProvider = null;
    if (STREAM_ITINERARY_ENABLED && accessToken) {
      const streamed = await streamAI(userPrompt, systemPrompt, maxTokens, accessToken, (fullText) => {
        const found = extractCompleteDayObjects(fullText);
        if (found.length !== seenDayNums.size) {
          seenDayNums = new Set(found.map(f => f.day));
          setTripStreamDays(found);
        }
      });
      if (streamed) {
        const parsedDays = extractCompleteDayObjects(streamed);
        if (parsedDays.length >= trDays) {
          const etMatch = streamed.match(/"estimatedTotal"\s*:\s*"([^"]+)"/);
          d = { days: parsedDays, estimatedTotal: etMatch ? etMatch[1] : '' };
          usedProvider = 'gemini-stream'; // streamAI/callGeminiStream只打Gemini，見api/groq.js註解
        } else {
          console.warn(`[genItinerary] stream produced ${parsedDays.length}/${trDays} days, falling back to non-streaming`);
        }
      }
    }
    if (!d) {
      d = await callAI(userPrompt, systemPrompt, maxTokens, GROQ_MODEL, { ...itineraryOpts, onProvider: (p) => { usedProvider = p; } });
    }
    __mark('AI call returned');
    setTripGenStage(3);
    if (d?.__guestLimit) {
      setLoadingTrip(false); setTripStreamDays([]);
      if (d.reason === 'guest_trial_used') markGuestTrialUsed();
      openGoogleGate(d.reason === 'guest_quota_full' ? 'quota_full' : d.reason === 'guest_days_limit' ? 'days' : 'trial_used');
      return;
    }
    if (d?.__dailyLimit) {
      trackEvent('trip_failed', 'daily_limit');
      setLoadingTrip(false); setTripStreamDays([]);
      setGenTripErr(`每日行程生成上限為 ${MAX_FREE_PER_DAY} 次，請明天再試。`);
      return;
    }
    if (d?.__rateLimited) {
      trackEvent('trip_failed', 'rate_limited');
      setLoadingTrip(false); setTripStreamDays([]);
      setGenTripErr((lang==='en'||lang==='ja'||lang==='ko') ? 'AI service busy — please wait 1 min and try again.' : 'AI 服務繁忙，請等候約 1 分鐘後重試。');
      return;
    }
    if (d?.days?.length) {
      // Dedup by day number (keep the first occurrence) and drop anything
      // outside 1..trDays — neither the streaming parser (which only checked
      // the raw COUNT of parsed day objects against trDays, so e.g. two
      // "day 3"s and no "day 5" could pass) nor the non-streaming path (which
      // had no such check at all) previously guaranteed the model actually
      // returned exactly days 1..trDays. estimatedTotal/dailyCost math below
      // would otherwise silently run on a duplicated or incomplete day list.
      const seenDayNos = new Set();
      allDays = d.days.filter(day => {
        const n = Number(day?.day);
        if (!n || n < 1 || n > trDays || seenDayNos.has(n)) return false;
        seenDayNos.add(n);
        return true;
      });
      if (allDays.length < trDays && IS_DEV) console.warn(`[genItinerary] AI returned ${allDays.length}/${trDays} valid unique days`);
      estimatedTotal = d.estimatedTotal || `${sym}${trDays * 800}`;
      // Force the traveller's own deterministic day→city schedule onto the
      // response — never trust the model's own `city` field for a day it
      // was explicitly told the answer to (see dayCityMap above).
      if (Object.keys(dayCityMap).length) {
        allDays.forEach(day => { if (dayCityMap[day.day]) day.city = dayCityMap[day.day]; });
      }
    } else {
      trackEvent('trip_failed', 'ai_failed');
      setLoadingTrip(false); setTripStreamDays([]);
      setGenTripErr((lang==='en'||lang==='ja'||lang==='ko') ? 'Generation failed — please try again.' : '行程生成失敗，請重新嘗試。');
      return;
    }

    // ── Post-process: match slots to real candidates, compute real travel
    // times, and programmatically drop any duplicate the AI slipped in anyway ──
    if (allDays.length > 0) {
      const usedKeys = new Set();
      const slots = ITIN_SLOTS;
      // 午餐只屬於中間日：第 1 天／最後一天刪走 AI 多加嘅 lunch；中間日冇 lunch 就預留空位，下面強制補餐廳
      allDays.forEach(day => {
        const isMiddleDay = day.day !== 1 && day.day !== trDays;
        if (!isMiddleDay) delete day.lunch;
        else if (!('lunch' in day)) day.lunch = null;
      });
      allDays.forEach(day => {
        let prevSlotName = null, prevCoord = null;
        const dayRawKeys = new Set();
        slots.forEach(slotName => {
          const slot = day[slotName];
          if (!slot?.location) return;
          const rawKey = norm(slot.location);
          // Full-day venues (theme parks, outlet malls, water parks — rule 5
          // in the prompt) are deliberately repeated across morning+afternoon
          // of the SAME day. Only treat a repeat as an AI mistake when it's
          // NOT the immediately preceding slot on the same day, otherwise this
          // swap-out fights the prompt's own "dedicate the entire day" rule.
          // 中間日多咗午餐夾喺上午同下午之間，所以改為「同一日之前出現過」而唔係「緊貼上一個時段」
          const isIntentionalSameDayRepeat = dayRawKeys.has(rawKey);
          dayRawKeys.add(rawKey);
          let key = rawKey;
          let match = candidateRegistry.get(key);
          if (!match) {
            const strippedKey = norm(stripTrailingParen(slot.location));
            const strippedMatch = strippedKey !== key ? candidateRegistry.get(strippedKey) : null;
            if (strippedMatch) { match = strippedMatch; key = strippedKey; slot.location = match.name; }
          }
          // 兩天一夜溫泉：第 N+1 天早上本來就要再出現同一間溫泉，不算重複
          const isOvernightOnsenRepeat = onsenOvernight && preselectedOnsen && day.day === onsenTargetDay + 1
            && norm(match?.name) === norm(preselectedOnsen.name);
          // 旅客指定、而且排在指定日子的自訂景點：不做重複替換或距離替換
          const isPlannedCustom = (slotName === 'morning' || slotName === 'afternoon') && customPlanOf(slot.location)?.day === day.day;
          if (match && usedKeys.has(key) && !isIntentionalSameDayRepeat && !isOvernightOnsenRepeat && !isPlannedCustom) {
            // AI reused a candidate already placed elsewhere — swap in the
            // best-rated unused candidate from the same category instead.
            // On a multi-city trip, match.city is whichever city the duplicate
            // name happened to be scraped under (not necessarily this day's
            // city — some junk venue names are duplicated verbatim across
            // cities), so prefer a replacement in THIS day's actual city first
            // and only fall back to match.city if this city has nothing left.
            // candidateRegistry.city is always the traveller's own raw
            // trDestinations string (whatever they typed/picked — English or
            // localized), never English-converted, so compare against day.city
            // as-is rather than through CITY_EN (which broke this comparison
            // for any city CITY_EN happens to cover, e.g. "京都"→"Kyoto" no
            // longer matching a registry entry stored under "京都").
            const replacement = [...candidateRegistry.entries()].find(([k, v]) =>
              !usedKeys.has(k) && v.city === day.city && v.category === match.category
            ) || [...candidateRegistry.entries()].find(([k, v]) =>
              !usedKeys.has(k) && v.city === match.city && v.category === match.category
            );
            if (replacement) {
              match = replacement[1];
              slot.location = match.name;
            } else {
              match = null; // no substitute available; leave AI's text as-is
            }
          }
          // The AI sees venue names + district text, not actual coordinates, so
          // it sometimes pairs same-day venues that are real but geographically
          // far apart (e.g. different cities lumped under one DB "city" value
          // like Fukuoka prefecture). Guard against that by swapping in a
          // nearer same-city/category candidate when one exists within range.
          const MAX_SAME_DAY_KM = 25;
          // 旅客指定的溫泉常在郊外（如黑川、有馬），不能因為離前一站遠就被換掉
          const isChosenOnsen = preselectedOnsen && norm(match?.name) === norm(preselectedOnsen.name);
          // 整天的地點（Outlet、主題樂園等，上午已排過、下午再出現）與旅客指定的 Outlet 也不換：
          // 之前午餐排在市區，下午的 Outlet 因離午餐超過 25km 被換成市區商店
          const isChosenOutlet = preselectedOutlet && norm(match?.name) === norm(preselectedOutlet.name);
          if (match && !isChosenOnsen && !isChosenOutlet && !isIntentionalSameDayRepeat && !isPlannedCustom && prevCoord && match.lat != null && match.lng != null) {
            const dist = haversineKm(prevCoord, { lat: match.lat, lng: match.lng });
            if (dist > MAX_SAME_DAY_KM) {
              const nearer = [...candidateRegistry.entries()]
                .filter(([k, v]) => !usedKeys.has(k) && v.city === match.city && v.category === match.category
                  && v.lat != null && v.lng != null && haversineKm(prevCoord, { lat: v.lat, lng: v.lng }) <= MAX_SAME_DAY_KM)
                .sort((a, b) => (Number(b[1].rating) || 0) - (Number(a[1].rating) || 0))[0];
              if (nearer) {
                match = nearer[1];
                slot.location = match.name;
              }
            }
          }
          if (match) {
            usedKeys.add(norm(match.name));
            slot.district = match.district || slot.district;
            slot.category = match.category || slot.category;
            slot.specify_cat = match.specify_cat || slot.specify_cat;
            slot._lat = match.lat;
            slot._lng = match.lng;
            slot._place_id = match.place_id;
          }
          const coord = match && match.lat != null && match.lng != null ? { lat: match.lat, lng: match.lng } : null;
          if (coord && prevCoord) {
            const est = estimateTravelBetween(prevCoord, coord, null, lang.startsWith('zh') ? 'zh' : 'en');
            if (est) day[prevSlotName].travelToNext = est;
          }
          if (coord) { prevCoord = coord; prevSlotName = slotName; }
        });
        // 每日另外推介嘅餐廳（restaurant）已取消：午餐／晚餐時段本身已經係餐廳
        delete day.restaurant;
      });

      // ── 自訂景點保險：AI 沒有照指定日子排，就強制放進去 ──────────────────
      // 同 Outlet／溫泉保險的做法：prompt 已指定日子，但 AI 不一定照做。
      // 排錯日子的同一景點會從那天移走（換成那天附近未用過的真實景點），避免重複。
      if (customPlans.some(p => p.day != null)) {
        const L = (en, ja, ko, cn, hk) => (lang === 'en' ? en : lang === 'ja' ? ja : lang === 'ko' ? ko : lang === 'zh-CN' ? cn : hk);
        const SIGHT_SLOTS = ['morning', 'afternoon'];
        const replaceMisplacedCustom = (d, sn) => {
          const order = daySlotOrder(d);
          const i = order.indexOf(sn);
          const anchor = [d[order[i - 1]], d[order[i + 1]]]
            .map(s => (s?._lat != null && s?._lng != null) ? { lat: s._lat, lng: s._lng } : null).find(Boolean);
          const pick = [...candidateRegistry.entries()]
            .filter(([k, v]) => !usedKeys.has(k) && !v.custom && v.city === d.city && v.category !== 'food'
              && !isOutletCandidate(v) && !(preselectedOnsen && k === norm(preselectedOnsen.name)) && v.lat != null && v.lng != null)
            .map(([k, v]) => ({ k, v, distKm: anchor ? haversineKm(anchor, v) : 0 }))
            .sort((a, b) => a.distKm - b.distKm || (Number(b.v.rating) || 0) - (Number(a.v.rating) || 0))[0];
          if (!pick) return;
          d[sn] = {
            ...d[sn],
            activity: L(`Visit ${pick.v.name}`, `${pick.v.name}を訪れる`, `${pick.v.name} 방문`, `游览${pick.v.name}`, `遊覽${pick.v.name}`),
            location: pick.v.name, district: pick.v.district || '', tip: '', cost: '',
            category: pick.v.category, specify_cat: pick.v.specify_cat,
            _lat: pick.v.lat, _lng: pick.v.lng, _place_id: pick.v.place_id,
          };
          usedKeys.add(pick.k);
        };
        customPlans.filter(p => p.day != null).forEach(p => {
          const isHit = (s) => !!s?.location && customPlanOf(s.location) === p;
          allDays.forEach(d => {
            if (d.day !== p.day) SIGHT_SLOTS.forEach(sn => { if (isHit(d[sn])) replaceMisplacedCustom(d, sn); });
          });
          if (p.kind !== 'inCity') return; // 城外景點下面按整組處理
          const dayEntry = allDays.find(d => d.day === p.day);
          if (!dayEntry || SIGHT_SLOTS.some(sn => isHit(dayEntry[sn]))) return;
          const nm = p.info?.name || p.spot;
          // 優先放下午；任何時段已經是另一個自訂景點就不可覆蓋（兩格都被佔就不放）
          const heldByOther = (slotName) => {
            const loc = dayEntry[slotName]?.location;
            const q = loc ? customPlanOf(loc) : null;
            return !!q && q !== p;
          };
          const sn = ['afternoon', 'morning'].find(s => !heldByOther(s));
          if (!sn) return;
          dayEntry[sn] = {
            ...(dayEntry[sn] || {}),
            activity: L(`Visit ${nm}`, `${nm}を訪れる`, `${nm} 방문`, `游览${nm}`, `遊覽${nm}`),
            location: nm, district: p.info?.district || '', tip: '', cost: '', // 原本那個地點的提示和費用已不適用
            category: p.info?.category || 'nature', specify_cat: p.info?.specify_cat,
            _lat: p.info?.lat, _lng: p.info?.lng, _place_id: p.info?.place_id,
          };
          usedKeys.add(norm(nm));
        });

        // 市內自訂景點那天的另一個觀光時段：與自訂景點是同一地點（重複）、或離它超過
        // COMPANION_MAX_KM，就換成 AI 從附近真實景點挑出的主要景點（第一個未用過的）。
        const COMPANION_MAX_KM = 3;
        customPlans.filter(p => p.kind === 'inCity' && p.day != null && p.info).forEach(p => {
          const d = allDays.find(x => x.day === p.day);
          if (!d) return;
          const own = SIGHT_SLOTS.find(sn => d[sn]?.location && customPlanOf(d[sn].location) === p);
          if (!own) return;
          const other = own === 'morning' ? 'afternoon' : 'morning';
          const s = d[other];
          if (!s?.location || isLogisticsSlot(s)) return; // 沒有這一格，或是回程日的退房／去機場
          const otherPlan = customPlanOf(s.location);
          if (otherPlan && otherPlan !== p) return; // 另一格是別的旅客指定景點
          const isDup = otherPlan === p;
          const isFar = s._lat != null && s._lng != null && haversineKm(p.info, { lat: s._lat, lng: s._lng }) > COMPANION_MAX_KM;
          if (!isDup && !isFar) return;
          const pick = (p.companions || []).find(v => !usedKeys.has(norm(v.name)));
          if (!pick) { if (isDup) replaceMisplacedCustom(d, other); return; }
          d[other] = {
            ...s,
            activity: L(`Visit ${pick.name}`, `${pick.name}を訪れる`, `${pick.name} 방문`, `游览${pick.name}`, `遊覽${pick.name}`),
            location: pick.name, district: pick.district || '', tip: '', cost: '',
            category: pick.category, specify_cat: pick.specify_cat,
            _lat: pick.lat, _lng: pick.lng, _place_id: pick.place_id,
          };
          usedKeys.add(norm(pick.name));
        });

        // 城外即日來回（每組一天）：上午＝第一個景點；下午＝第二個景點，只有一個時用附近候選池
        // 的真實景點，池是空的就寫「繼續遊覽一帶」。沒有資料的景點不指名任何店家，並附上提示。
        const NO_DATA_TIP = L(
          `We don't have verified venue data for this area yet — please check transport and restaurants before you go.`,
          `この地域の確認済みスポット情報はまだありません。交通手段や飲食店は事前にご確認ください。`,
          `이 지역의 검증된 장소 정보가 아직 없습니다. 교통편과 식당은 미리 확인해 주세요.`,
          `此地区暂未收录已核实的景点资料，建议出发前自行查阅交通及餐厅。`,
          `此地區暫未收錄已核實的景點資料，建議出發前自行查閱交通及餐廳。`,
        );
        customDayTrips.filter(t => t.day != null).forEach(trip => {
          const dayEntry = allDays.find(d => d.day === trip.day);
          if (!dayEntry) return;
          const [pa, pb] = trip.plans;
          const nameOf = (p) => p.info?.name || p.spot;
          const nmA = nameOf(pa);
          const isHitP = (s, p) => !!s?.location && customPlanOf(s.location) === p;
          const placeFields = (p) => ({
            location: nameOf(p), district: p.info?.district || '', tip: '', cost: '', // 原本那個地點的提示和費用已不適用
            _lat: p.info?.lat, _lng: p.info?.lng, _place_id: p.info?.place_id,
          });
          const setSight = (sn, p, activity) => {
            dayEntry[sn] = { ...(dayEntry[sn] || {}), activity, ...placeFields(p), category: p.info?.category || 'nature', specify_cat: p.info?.specify_cat };
            if (!p.info) dayEntry[sn].tip = NO_DATA_TIP;
          };
          const continueText = L(`Continue exploring the ${nmA} area`, `${nmA}周辺を散策`, `${nmA} 주변 둘러보기`, `继续游览${nmA}一带`, `繼續遊覽${nmA}一帶`);

          // 上午：第一個景點。沒有資料的一律改成不指名的文字（AI 可能在活動描述裡編了店名）
          if (!pa.info || !isHitP(dayEntry.morning, pa)) {
            setSight('morning', pa, L(`Day trip to ${nmA}`, `${nmA}へ日帰り旅行`, `${nmA} 당일치기 여행`, `前往${nmA}一日游`, `前往${nmA}一日遊`));
          }
          // 下午
          if (pb) {
            const nmB = nameOf(pb);
            if (!pb.info || !isHitP(dayEntry.afternoon, pb)) {
              setSight('afternoon', pb, L(`Visit ${nmB}`, `${nmB}を訪れる`, `${nmB} 방문`, `游览${nmB}`, `遊覽${nmB}`));
            }
          } else {
            const af = dayEntry.afternoon;
            const afInPool = !!af?.location && trip.pool.attractions.some(v => norm(v.name) === norm(af.location));
            if (!afInPool) {
              const pick = trip.anchor && trip.pool.attractions
                .filter(v => !usedKeys.has(norm(v.name)))
                .sort((a, b) => haversineKm(trip.anchor, a) - haversineKm(trip.anchor, b))[0];
              if (pick) {
                dayEntry.afternoon = {
                  ...(af || {}),
                  activity: L(`Visit ${pick.name}`, `${pick.name}を訪れる`, `${pick.name} 방문`, `游览${pick.name}`, `遊覽${pick.name}`),
                  location: pick.name, district: pick.district || '', tip: '', cost: '',
                  category: pick.category, specify_cat: pick.specify_cat,
                  _lat: pick.lat, _lng: pick.lng, _place_id: pick.place_id,
                };
                usedKeys.add(norm(pick.name));
              } else {
                setSight('afternoon', pa, continueText);
              }
            }
          }
          // 午晚餐：有餐廳池的交給下面「午餐／晚餐強制」步驟從池內挑；沒有的話
          // 午餐只寫「在某某一帶午餐」，晚餐要是回到出發城市後的真實餐廳，否則不指名。
          if (!trip.pool.food.length) {
            if ('lunch' in dayEntry) {
              dayEntry.lunch = {
                ...(dayEntry.lunch || {}),
                activity: L(`Lunch in the ${nmA} area`, `${nmA}周辺でランチ`, `${nmA} 주변에서 점심`, `在${nmA}一带午餐`, `在${nmA}一帶午餐`),
                ...placeFields(pa), category: 'food', specify_cat: undefined,
              };
            }
            const ev = dayEntry.evening;
            if (ev && !isLogisticsSlot(ev) && !candidateRegistry.has(norm(ev.location)) && !candidateRegistry.has(norm(stripTrailingParen(ev.location)))) {
              const bc = trip.baseCity;
              dayEntry.evening = {
                activity: L(`Dinner after returning to ${bc}`, `${bc}に戻って夕食`, `${bc}(으)로 돌아와 저녁 식사`, `返回${bc}后享用晚餐`, `返回${bc}後享用晚餐`),
                location: '', district: '', tip: '', cost: '', category: 'food',
              };
            }
          }
          trip.plans.forEach(p => usedKeys.add(norm(nameOf(p))));
        });
      }
      const dayTripByDay = new Map(customDayTrips.filter(t => t.day != null).map(t => [t.day, t]));

      // ── 同一天上午、下午是同一個地點 ──────────────────────────────────────
      // 只有真正需要一整天的地點（Outlet、大型主題樂園／水上樂園、動物園、旅客選的溫泉）
      // 才合理；其他（例如來回只要半小時的嵯峨野嵐山小火車）把下午換成附近 3km 內未用過的
      // 真實觀光景點（只取文化／自然類，避開商店、工作室等）。城外一日遊那天的
      // 「繼續遊覽某某一帶」是刻意重複，不處理。
      {
        const FULL_DAY_CATS = new Set(['大型主題樂園/水上樂園', '動植物園/生態農場']);
        const DUP_REPLACE_KM = 3;
        const L = (en, ja, ko, cn, hk) => (lang === 'en' ? en : lang === 'ja' ? ja : lang === 'ko' ? ko : lang === 'zh-CN' ? cn : hk);
        allDays.forEach(d => {
          if (dayTripByDay.has(d.day)) return;
          const m = d.morning, a = d.afternoon;
          if (!m?.location || !a?.location || norm(m.location) !== norm(a.location)) return;
          const v = candidateRegistry.get(norm(m.location)) || {};
          const isFullDay = isOutletCandidate(v) || FULL_DAY_CATS.has(v.specify_cat || m.specify_cat)
            || [preselectedOutlet, preselectedOnsen].some(x => x && norm(x.name) === norm(m.location));
          if (isFullDay || m._lat == null || m._lng == null) return;
          const anchor = { lat: m._lat, lng: m._lng };
          const pick = [...candidateRegistry.entries()]
            .filter(([k, c]) => !usedKeys.has(k) && !c.custom && c.city === d.city
              && (c.category === 'culture' || c.category === 'nature') && c.lat != null && c.lng != null
              && haversineKm(anchor, c) <= DUP_REPLACE_KM)
            .sort((x, y) => byRankScore(x[1], y[1]))[0];
          if (!pick) return;
          const [k, c] = pick;
          d.afternoon = {
            ...a,
            activity: L(`Visit ${c.name}`, `${c.name}を訪れる`, `${c.name} 방문`, `游览${c.name}`, `遊覽${c.name}`),
            location: c.name, district: c.district || '', tip: '', cost: '',
            category: c.category, specify_cat: c.specify_cat,
            _lat: c.lat, _lng: c.lng, _place_id: c.place_id,
          };
          usedKeys.add(k);
        });
      }

      // ── Outlet-mall dedup + "closest to airport" enforcement ──────────────
      // Rule 8 above asks the model for at most one outlet-mall visit per trip
      // (the exact venue when the traveller ticked "Outlet" — see
      // preselectedOutlet above — otherwise its own free choice, preferring the
      // one nearest the departure airport when it lands on the last day) — but
      // (same reason every other pass in this function exists) the model
      // doesn't reliably comply, so verify/correct it against real coordinates
      // instead of trusting the prompt alone.
      const outletSlotRefs = [];
      allDays.forEach(day => {
        slots.forEach(slotName => {
          const slot = day[slotName];
          if (!slot?.location) return;
          const match = candidateRegistry.get(norm(slot.location));
          if (isOutletCandidate(match)) outletSlotRefs.push({ day, slot, match });
        });
      });
      // A legitimate single-day outlet visit produces TWO entries here (morning
      // + afternoon, per rule 5's full-day-venue convention) — that's one
      // occurrence, not two. Group by day before deciding whether the trip
      // actually has more than one outlet-mall DAY; comparing at the slot level
      // previously treated a normal same-day morning/afternoon repeat as a false
      // "duplicate" and swapped the correct afternoon slot out to a different
      // real venue, which then (via the same-key rewrite below) clobbered the
      // still-correct morning slot too.
      const outletDayGroups = [];
      outletSlotRefs.forEach(ref => {
        let group = outletDayGroups.find(g => g.day.day === ref.day.day);
        if (!group) { group = { day: ref.day, match: ref.match, refs: [] }; outletDayGroups.push(group); }
        group.refs.push(ref);
      });
      if (outletDayGroups.length > 1) {
        // A pre-selected venue (traveller explicitly ticked "Outlet") is
        // authoritative — keep whichever day's occurrence actually is that
        // venue, no need to compare/guess among the others. Otherwise fall
        // back to: whichever occurrence is on the last day (matches the
        // prompt's own stated preference — the traveller never said the
        // outlet HAS to be on the last day, only that IF it lands there,
        // prefer the one nearest the airport); if none is on the last day,
        // keep the best-rated candidate rather than an arbitrary "whichever
        // the model happened to write first" — quality, not list order,
        // should decide which one survives.
        const preselectedIdx = preselectedOutlet ? outletDayGroups.findIndex(g => norm(g.match.name) === norm(preselectedOutlet.name)) : -1;
        const lastDayIdx = outletDayGroups.findIndex(g => g.day.day === trDays);
        const keepIdx = preselectedIdx !== -1 ? preselectedIdx
          : lastDayIdx !== -1 ? lastDayIdx
          : outletDayGroups.reduce((bestI, g, i) => (Number(g.match.rating) || 0) > (Number(outletDayGroups[bestI].match.rating) || 0) ? i : bestI, 0);
        outletDayGroups.forEach((group, i) => {
          if (i === keepIdx) return;
          const ref = group.refs[0];
          // candidateRegistry.city is the raw trDestinations string — see the
          // identical fix/comment on the non-outlet duplicate-swap above.
          const rawKeyOfRef = norm(ref.slot.location);
          const replacement = [...candidateRegistry.entries()].find(([k, v]) =>
            !usedKeys.has(k) && k !== rawKeyOfRef && v.city === ref.day.city && v.category === ref.match.category && !isOutletCandidate(v)
          );
          if (replacement) {
            const [replKey, repl] = replacement;
            // Full-day venues (rule 5) repeat the same text across morning+
            // afternoon — update every slot on this day still showing the old
            // outlet name, not just the one slot we happened to index.
            slots.forEach(sn => {
              const s = ref.day[sn];
              if (s?.location && norm(s.location) === rawKeyOfRef) {
                s.location = repl.name; s.district = repl.district || s.district;
                s.category = repl.category || s.category; s.specify_cat = repl.specify_cat || s.specify_cat;
                s._lat = repl.lat; s._lng = repl.lng; s._place_id = repl.place_id;
              }
            });
            usedKeys.add(replKey);
          }
        });
      }
      // Safety net: the traveller explicitly requested an outlet and outletDayRule
      // told the model exactly which day and venue to use, but scenario testing
      // found the model still skips it outright more often than not (~75% of the
      // time it just added nothing). Force it onto outletTargetDay rather than
      // silently failing the request — same "deterministic override" pattern as
      // forcing day.city from dayCityMap earlier in this function.
      if (preselectedOutlet && outletTargetDay && outletSlotRefs.length === 0) {
        const targetDayEntry = allDays.find(day => day.day === outletTargetDay);
        if (targetDayEntry) {
          ['morning', 'afternoon'].forEach(sn => {
            targetDayEntry[sn] = targetDayEntry[sn] || {};
            targetDayEntry[sn].activity = sn === 'morning'
              ? `前往${preselectedOutlet.name}購物`
              : `繼續於${preselectedOutlet.name}選購`;
            targetDayEntry[sn].location = preselectedOutlet.name;
            targetDayEntry[sn].district = preselectedOutlet.district || targetDayEntry[sn].district || '';
            targetDayEntry[sn].category = 'shopping';
            targetDayEntry[sn].specify_cat = preselectedOutlet.specify_cat || targetDayEntry[sn].specify_cat;
            targetDayEntry[sn]._lat = preselectedOutlet.lat;
            targetDayEntry[sn]._lng = preselectedOutlet.lng;
            targetDayEntry[sn]._place_id = preselectedOutlet.place_id;
          });
          usedKeys.add(norm(preselectedOutlet.name));
          // Evening: swap in the nearest not-yet-used real food candidate in the
          // outlet's own city, same proximity logic as the same-day-grouping pass
          // above, so dinner isn't left describing whatever the model originally
          // planned for a day that no longer has that content.
          if (preselectedOutlet.lat != null && preselectedOutlet.lng != null) {
            const nearestFood = [...candidateRegistry.entries()]
              .filter(([k, v]) => !usedKeys.has(k) && v.city === preselectedOutlet.city && v.category === 'food' && v.lat != null && v.lng != null)
              .map(([k, v]) => ({ k, v, distKm: haversineKm({ lat: preselectedOutlet.lat, lng: preselectedOutlet.lng }, { lat: v.lat, lng: v.lng }) }))
              .sort((a, b) => a.distKm - b.distKm)[0];
            if (nearestFood) {
              targetDayEntry.evening = targetDayEntry.evening || {};
              targetDayEntry.evening.activity = `${preselectedOutlet.name}附近晚餐`;
              targetDayEntry.evening.location = nearestFood.v.name;
              targetDayEntry.evening.district = nearestFood.v.district || targetDayEntry.evening.district || '';
              targetDayEntry.evening.category = 'food';
              targetDayEntry.evening._lat = nearestFood.v.lat;
              targetDayEntry.evening._lng = nearestFood.v.lng;
              targetDayEntry.evening._place_id = nearestFood.v.place_id;
              usedKeys.add(nearestFood.k);
            }
          }
        }
      }
      // 溫泉保險：旅客有勾溫泉、也指定了哪天，但 AI 沒照排 → 強制補上
      if (preselectedOnsen && onsenTargetDay) {
        const o = preselectedOnsen;
        const onsenKey = norm(o.name);
        const isOnsenSlot = (s) => !!s?.location && norm(s.location) === onsenKey;
        const onsenArea = o.district || o.name;
        const L = (en, ja, ko, cn, hk) => (lang === 'en' ? en : lang === 'ja' ? ja : lang === 'ko' ? ko : lang === 'zh-CN' ? cn : hk);
        const forceOnsen = (dayEntry, slotName, activity) => {
          const s = dayEntry[slotName] = dayEntry[slotName] || {};
          if (isOnsenSlot(s)) return;
          s.activity = activity;
          s.location = o.name;
          s.tip = ''; s.cost = ''; // 原本那個景點的提示和費用已不適用，不留錯資訊
          s.district = o.district || '';
          s.category = o.category;
          s.specify_cat = o.specify_cat;
          s._lat = o.lat; s._lng = o.lng; s._place_id = o.place_id;
        };
        const dayN = allDays.find(day => day.day === onsenTargetDay);
        if (onsenOvernight && dayN) {
          // 兩天一夜：N 天下午泡湯＋住溫泉區，N+1 天早上仍在溫泉區
          forceOnsen(dayN, 'afternoon', L(`Travel to ${o.name} for a hot-spring soak`, `${o.name}へ移動して温泉`, `${o.name}(으)로 이동해 온천`, `前往${o.name}泡温泉`, `前往${o.name}泡溫泉`));
          // 晚餐若排在離溫泉很遠的地方，改為溫泉區旅館晚餐；不指名任何店家，避免編造
          const ev = dayN.evening;
          const evFar = ev?._lat != null && o.lat != null && haversineKm({ lat: ev._lat, lng: ev._lng }, { lat: o.lat, lng: o.lng }) > 15;
          if (!ev?.location || evFar) {
            dayN.evening = {
              activity: L(`Dinner at your ryokan in the ${onsenArea} area`, `${onsenArea}の旅館で夕食`, `${onsenArea} 료칸에서 저녁 식사`, `在${onsenArea}的温泉旅馆享用晚餐`, `在${onsenArea}的溫泉旅館享用晚餐`),
              location: onsenArea, district: o.district || '', tip: '', cost: '',
              category: 'food', // 旅館晚餐都算餐飲時段，下面強制午晚餐嗰步唔好再換走
              _lat: o.lat, _lng: o.lng,
            };
          }
          // 當晚住宿以溫泉為中心（DayHotelOptions 會優先用 _stayLat/_stayLng 找附近真實旅館）
          dayN._stayLat = o.lat; dayN._stayLng = o.lng;
          if (dayN.hotel && !String(dayN.hotel.area || '').includes(onsenArea)) {
            dayN.hotel = { ...dayN.hotel, name: L(`Hot-spring ryokan in ${onsenArea}`, `${onsenArea}の温泉旅館`, `${onsenArea} 온천 료칸`, `${onsenArea}温泉旅馆`, `${onsenArea}溫泉旅館`), area: onsenArea };
          }
          const dayN1 = allDays.find(day => day.day === onsenTargetDay + 1);
          if (dayN1) forceOnsen(dayN1, 'morning', L(`Morning bath and stroll around ${o.name}`, `${o.name}で朝風呂と散策`, `${o.name}에서 아침 온천과 산책`, `在${o.name}晨间泡汤与散步`, `在${o.name}晨間泡湯與散步`));
          usedKeys.add(onsenKey);
        } else if (dayN && !allDays.some(day => slots.some(sn => isOnsenSlot(day[sn])))) {
          forceOnsen(dayN, 'afternoon', onsenFootbathNearby.length
            ? L(`Short footbath stop at ${o.name}`, `${o.name}で足湯`, `${o.name}에서 족욕`, `在${o.name}泡脚小憩`, `在${o.name}泡腳小憩`)
            : L(`Hot spring at ${o.name}`, `${o.name}で温泉`, `${o.name} 온천`, `前往${o.name}泡温泉`, `前往${o.name}泡溫泉`));
          usedKeys.add(onsenKey);
        }
        // 足湯不單獨成行：足湯那天若沒有任何附近景點，早上改排評分最高的附近景點
        if (onsenFootbathNearby.length) {
          const fbDay = allDays.find(day => slots.some(sn => isOnsenSlot(day[sn])));
          const nearbyKeys = onsenFootbathNearby.map(norm);
          const hasNearby = fbDay && slots.some(sn => fbDay[sn]?.location && nearbyKeys.includes(norm(fbDay[sn].location)));
          const aKey = nearbyKeys.find(k => !usedKeys.has(k) && candidateRegistry.has(k));
          const a = aKey && candidateRegistry.get(aKey);
          if (fbDay && !hasNearby && a && !isOnsenSlot(fbDay.morning)) {
            fbDay.morning = {
              ...(fbDay.morning || {}),
              activity: L(`Explore ${a.name} (near the footbath)`, `${a.name}を散策（足湯の近く）`, `${a.name} 둘러보기 (족욕 근처)`, `游览${a.name}（足汤附近）`, `遊覽${a.name}（足湯附近）`),
              location: a.name, district: a.district || '', tip: '', cost: '',
              category: a.category, specify_cat: a.specify_cat,
              _lat: a.lat, _lng: a.lng, _place_id: a.place_id,
            };
            usedKeys.add(aKey);
          }
        }
      }
      const lastDayEntry = allDays.find(day => day.day === trDays);
      // Skip this rerank when the traveller's single requested outlet visit was
      // already placed on an earlier day — upgrading whatever light hotel-area
      // shopping the AI put on the last day (per departureRule) to a real
      // outlet/LaLaport/AEON MALL would add a second mall-grade venue, defeating
      // the "at most one outlet total" rule. Only relevant when there's no
      // preselected outlet, or its designated day IS the last day.
      const outletAlreadyPlacedElsewhere = preselectedOutlet && outletTargetDay && outletTargetDay !== trDays;
      if (lastDayEntry && !outletAlreadyPlacedElsewhere) {
        // CITY_AIRPORTS is keyed by canonical English city names, unlike
        // candidateRegistry (keyed by the traveller's raw trDestinations
        // string) — need both forms here, not one substituted for the other.
        const lastDayCityEn = CITY_EN_MAP[lastDayEntry.city] || lastDayEntry.city;
        const airport = (CITY_AIRPORTS[lastDayCityEn] || [])[0];
        if (airport && airport.lat != null) {
          const rankOf = (v) => isOutletCandidate(v) ? 0 : /lalaport/i.test(v.name || '') ? 1 : /aeon\s*mall/i.test(v.name || '') ? 2 : 3;
          slots.forEach(slotName => {
            const slot = lastDayEntry[slotName];
            if (!slot?.location) return;
            const key = norm(slot.location);
            const current = candidateRegistry.get(key);
            if (!current || current.category !== 'shopping') return; // only re-rank an already-shopping slot, never invent one
            if (customPlanOf(slot.location)) return; // 旅客指定的景點不換
            const best = [...candidateRegistry.entries()]
              .filter(([k, v]) => v.city === lastDayEntry.city && v.category === 'shopping' && v.lat != null && v.lng != null && (k === key || !usedKeys.has(k)))
              .map(([k, v]) => ({ k, v, rank: rankOf(v), distKm: haversineKm(airport, { lat: v.lat, lng: v.lng }) }))
              .sort((a, b) => a.rank - b.rank || a.distKm - b.distKm)[0];
            if (best && best.k !== key) {
              slots.forEach(sn => {
                const s = lastDayEntry[sn];
                if (s?.location && norm(s.location) === key) {
                  s.location = best.v.name; s.district = best.v.district || s.district;
                  s.category = best.v.category || s.category; s.specify_cat = best.v.specify_cat || s.specify_cat;
                  s._lat = best.v.lat; s._lng = best.v.lng; s._place_id = best.v.place_id;
                }
              });
              usedKeys.delete(key); usedKeys.add(best.k);
            }
          });
        }
      }

      // ── 午餐／晚餐強制為餐飲類別 ──────────────────────────────────────────
      // prompt 已要求午晚餐用 food 名單，但 AI 唔一定跟（例如晚餐寫咗纜車站、
      // 或者自創一間對唔上資料庫嘅餐廳）。用真實座標揀前一站附近、未用過嘅
      // 真實餐廳取代；揀唔到就保留 AI 文字但標記為 food，換一批仍然只推餐飲。
      {
        const L = (en, ja, ko, cn, hk) => (lang === 'en' ? en : lang === 'ja' ? ja : lang === 'ko' ? ko : lang === 'zh-CN' ? cn : hk);
        const coordOfSlot = (s) => (s?._lat != null && s?._lng != null) ? { lat: s._lat, lng: s._lng } : null;
        allDays.forEach(day => {
          const order = daySlotOrder(day);
          ['lunch', 'evening'].forEach(mealKey => {
            if (!order.includes(mealKey)) return;
            const s = day[mealKey];
            if (s && isLogisticsSlot(s)) return; // 回程日晚上嘅退房／去機場
            if (mealKey === 'evening' && !s) return; // 晚上被刻意留空就唔補
            // 即日來回那天：午餐一定要是景點附近候選池的餐廳；晚餐可以是池內餐廳，
            // 或回到出發城市後的真實餐廳。池是空的（或第 2 層沒有資料）就不換，只標記為餐飲。
            const tripPlan = dayTripByDay.get(day.day);
            const tripFood = tripPlan?.pool?.food || [];
            // 整天的地點（上午、下午同一個地方，例如 Outlet、主題樂園）：午餐要在場地附近。
            // 之前 Outlet 那天午餐排在約 30km 外的市區，旅客要中途離場來回。
            const fullDayVenue = (!tripPlan && mealKey === 'lunch' && day.morning?.location
              && norm(day.morning.location) === norm(day.afternoon?.location)) ? day.morning : null;
            const fullDayCoord = coordOfSlot(fullDayVenue);
            const lunchCoord = coordOfSlot(s);
            const idx = order.indexOf(mealKey);
            // 餐廳雖然是餐飲類別，但離前一個行程點太遠（例如上午在嵐山、午餐卻是 8.7km 外祇園的店，
            // 活動文字還寫「在嵐山享用午餐」）。以前只有整天地點那天才檢查距離，其他日子只要
            // 類別是 food 就放行。判斷與挑選邏輯在 utils/meals.js（有單元測試）：換成前一站
            // MEAL_RADIUS_KM 內的真實餐廳；附近沒有收錄就維持原樣。
            const repick = mealRepickPlan({
              slotCoord: lunchCoord, isFood: s?.category === 'food', fullDayCoord,
              prevCoord: coordOfSlot(day[order[idx - 1]]), nextCoord: coordOfSlot(day[order[idx + 1]]),
            });
            const lunchTooFar = repick.lunchTooFar;
            if (tripPlan) {
              const inTripPool = !!s?.location && tripFood.some(v => norm(v.name) === norm(s.location));
              if (inTripPool || !tripFood.length || (mealKey === 'evening' && s?.category === 'food')) { if (s) s.category = 'food'; return; }
            } else if (repick.keep) return;
            const anchor = tripPlan
              ? { lat: tripPlan.anchor.lat, lng: tripPlan.anchor.lng }
              : repick.anchor;
            const pool = tripPlan ? tripFood : (foodPoolByCity.get(day.city) || []);
            const radiusKm = tripPlan ? Infinity // 候選池本身已限定在景點附近
              : repick.radiusKm;
            const pick = pickNearestFood({ anchor, radiusKm, pool, isUsed: v => usedKeys.has(norm(v.name)) });
            if (pick) {
              day[mealKey] = {
                ...(s || {}),
                activity: mealKey === 'lunch'
                  ? L(`Lunch at ${pick.name}`, `${pick.name}でランチ`, `${pick.name}에서 점심`, `在${pick.name}享用午餐`, `在${pick.name}享用午餐`)
                  : L(`Dinner at ${pick.name}`, `${pick.name}で夕食`, `${pick.name}에서 저녁 식사`, `在${pick.name}享用晚餐`, `在${pick.name}享用晚餐`),
                location: pick.name,
                district: pick.district || '',
                duration: s?.duration || (mealKey === 'lunch' ? '1hr' : '1.5hr'),
                tip: '', // 原本提示講緊另一個地方，唔留錯資訊
                category: 'food',
                specify_cat: pick.specify_cat,
                _lat: pick.lat, _lng: pick.lng, _place_id: pick.place_id,
              };
              usedKeys.add(norm(pick.name));
            } else if (lunchTooFar) {
              // 場地附近沒有收錄的餐廳：不指名店家，只寫在場地一帶午餐
              const venue = fullDayVenue.location;
              day.lunch = {
                ...(s || {}),
                activity: L(`Lunch at or near ${venue}`, `${venue}周辺でランチ`, `${venue} 주변에서 점심`, `在${venue}一带午餐`, `在${venue}一帶午餐`),
                location: venue, district: fullDayVenue.district || '',
                duration: s?.duration || '1hr', tip: '', cost: '',
                category: 'food', specify_cat: undefined,
                _lat: fullDayCoord.lat, _lng: fullDayCoord.lng, _place_id: undefined,
              };
            } else if (s) {
              s.category = 'food';
            }
          });
          // 午晚餐換咗之後，重新計相鄰時段嘅交通時間
          const langShort = lang.startsWith('zh') ? 'zh' : 'en';
          for (let i = 0; i < order.length - 1; i++) {
            const a = day[order[i]], b = day[order[i + 1]];
            const est = a && estimateTravelBetween(coordOfSlot(a), coordOfSlot(b), null, langShort);
            if (est) a.travelToNext = est;
          }
        });
      }

      // The AI's stated estimatedTotal routinely disagrees with its own
      // per-day breakdown (seen off by 60%-460% in scenario testing) — both
      // numbers are shown to the user on the same screen, so trust the sum of
      // the actual dailyCost values instead of the AI's separately-guessed total.
      const parseCost = (s) => Number(String(s || '').replace(/[^\d.]/g, '')) || 0;
      const sumDailyCost = allDays.reduce((sum, day) => sum + parseCost(day.dailyCost), 0);
      if (sumDailyCost > 0) estimatedTotal = `${sym}${Math.round(sumDailyCost).toLocaleString()}`;

      // P0摸底：只記錄違規率，不做自動修正（見上方 validateVenueInList 等函式的說明）
      {
        const candidateNames = new Set(candidateRegistry.keys());
        const venueIssues = validateVenueInList({ days: allDays }, candidateNames, norm);
        const dupIssues = validateNoDuplicates({ days: allDays }, norm);
        const slotIssues = validateTimeSlots({ days: allDays });
        const total = venueIssues.length + dupIssues.length + slotIssues.length;
        if (total > 0) {
          console.warn(`[itinerary-validation] ${total} 筆問題 — companion:${trCompanion}${trElderly ? '+elderly' : ''}, days:${trDays}, cities:${_cities.join('+')}, provider:${usedProvider || 'unknown'}`, { venueIssues, dupIssues, slotIssues });
        } else if (IS_DEV) {
          console.log(`[itinerary-validation] 通過，無問題 — companion:${trCompanion}${trElderly ? '+elderly' : ''}, days:${trDays}, provider:${usedProvider || 'unknown'}`);
        }
      }

      // Only increment localStorage count on successful generation (non-admin)
      // 訪客生成成功時記下行程參數（城市／天數／同行者），方便後台統計
      if (isGuest) { markGuestTrialUsed(); trackEvent('guest_trial_completed', JSON.stringify({ cities: _cities, days: trDays, companion: trCompanion, elderly: !!trElderly })); }
      else if (!isAdmin) incrementTripUsage();
      // 所有用戶（訪客／會員／管理員）生成成功都記一筆，後台「流量」分頁的行程統計用
      trackEvent('trip_generated', JSON.stringify({ user: isAdmin ? 'admin' : isGuest ? 'guest' : 'member', cities: _cities, countries: destCountriesUnique.length ? destCountriesUnique : [_country].filter(Boolean), days: trDays, companion: trCompanion, elderly: !!trElderly, provider: usedProvider || null }));
      setItinerary({ estimatedTotal, days: allDays });
      setActiveDay(0);
      setTripRating(null);
      setTripRatingComment('');
      setShowRatingPopup(false);
      setRatingDismissed(false);
      // Consume affiliate reward if this is an extended trip (8+ days)
      if (trDays > MAX_TRIP_DAYS && affiliateReward && user?.id) {
        supabase.from('affiliate_rewards')
          .update({ used_at: new Date().toISOString(), days_used: trDays })
          .eq('user_id', user.id)
          .then(() => setAffiliateReward(null));
      }
    } else {
      // Generation returned empty — preserve previous itinerary, just show error
      setGenTripErr((lang==='en'||lang==='ja'||lang==='ko') ? 'Generation failed — please try again.' : '行程生成失敗，請重新嘗試。');
    }
    setTripGenStage(4);
    setLoadingTrip(false);
    setTripStreamDays([]);
  };

  // PDF download — opens styled HTML page for browser Print → Save as PDF
  const downloadPDF = (savedTrip) => {
    const tripData = savedTrip
      ? { itinerary: savedTrip.itinerary, cities: savedTrip.cities, country: savedTrip.country }
      : { itinerary, cities: trDestinations.filter(d=>d.city).map(d=>d.city), country: trDestinations[0]?.country || trCountry };
    if (!tripData.itinerary) return;
    const days = tripData.itinerary.days || [];
    const daysHtml = days.map(d => `
      <div class="day-block">
        <div class="day-title">Day ${d.day}${d.title ? ' — ' + d.title : ''}</div>
        ${d.morning   ? `<div class="slot"><span class="slot-label">🌅 Morning</span><strong>${d.morning.activity || ''}</strong>${d.morning.location ? ' @ ' + d.morning.location : ''}<br/><small>${d.morning.note || ''}</small></div>` : ''}
        ${d.lunch ? `<div class="slot"><span class="slot-label">🍱 Lunch</span><strong>${d.lunch.activity || ''}</strong>${d.lunch.location ? ' @ ' + d.lunch.location : ''}<br/><small>${d.lunch.note || ''}</small></div>` : ''}
        ${d.afternoon ? `<div class="slot"><span class="slot-label">☀️ Afternoon</span><strong>${d.afternoon.activity || ''}</strong>${d.afternoon.location ? ' @ ' + d.afternoon.location : ''}<br/><small>${d.afternoon.note || ''}</small></div>` : ''}
        ${d.evening   ? `<div class="slot"><span class="slot-label">🌙 Evening</span><strong>${d.evening.activity || ''}</strong>${d.evening.location ? ' @ ' + d.evening.location : ''}<br/><small>${d.evening.note || ''}</small></div>` : ''}
      </div>`).join('');

    const logoUrl = `${window.location.origin}/ec-travel-logo.svg`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>EC Travel Itinerary</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; padding: 40px; background: #fff; }
  body::before {
    content: 'EC TRAVEL';
    position: fixed; top: 50%; left: 50%;
    transform: translate(-50%, -50%) rotate(-35deg);
    font-size: 100px; font-weight: 900; letter-spacing: 8px;
    color: rgba(245,158,11,0.08); pointer-events: none; z-index: 0; white-space: nowrap;
  }
  .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #f59e0b; padding-bottom: 16px; margin-bottom: 24px; }
  .logo { height: 52px; width: auto; display: block; }
  .meta { font-size: 12px; color: #64748b; text-align: right; }
  .trip-title { font-size: 20px; font-weight: 900; color: #0f172a; margin-bottom: 4px; }
  .trip-sub { font-size: 13px; color: #64748b; margin-bottom: 24px; }
  .day-block { background: #f8fafc; border-left: 4px solid #f59e0b; border-radius: 0 10px 10px 0; padding: 16px; margin-bottom: 16px; page-break-inside: avoid; position: relative; z-index: 1; }
  .day-title { font-size: 15px; font-weight: 900; color: #0f172a; margin-bottom: 10px; }
  .slot { font-size: 13px; margin-bottom: 6px; color: #334155; }
  .slot-label { display: inline-block; min-width: 110px; font-weight: 700; color: #64748b; }
  .slot small { color: #94a3b8; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; position: relative; z-index: 1; }
  @media print { body { padding: 20px; } }
</style></head><body>
<div class="header">
  <img class="logo" src="${logoUrl}" alt="EC Travel"/>
  <div class="meta">travel-ec.com<br/>${new Date().toLocaleDateString()}</div>
</div>
<div class="trip-title">${tripData.cities.join(' → ')}, ${tripData.country}</div>
${daysHtml}
<div class="footer">Generated by EC Travel · travel-ec.com · © 2026 EC Travel. All rights reserved.</div>
</body></html>`;

    // Use hidden iframe to print — avoids popup blocker entirely
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:0;opacity:0';
    document.body.appendChild(iframe);
    const idoc = iframe.contentDocument || iframe.contentWindow.document;
    idoc.open(); idoc.write(html); idoc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      try { iframe.contentWindow.print(); } catch(e) {}
      setTimeout(() => { try { document.body.removeChild(iframe); } catch(e) {} }, 2000);
    }, 800);
  };

  // ── BOOKING DATA ────────────────────────────────────────────────────────────
  const [bkType, setBkType] = useState(null);
  const [bkHotelMode, setBkHotelMode] = useState('trip'); // 住宿分頁：'trip' = Trip.com 小工具，'stay22' = 選城市後顯示 Stay22 地圖
  const [bkTicketCat, setBkTicketCat] = useState(null);
  const [bkEventYear, setBkEventYear] = useState(new Date().getFullYear());
  const [bkEventMonth, setBkEventMonth] = useState(null);
  const [bkRegion, setBkRegion] = useState('');
  const [bkCountry, setBkCountry] = useState('');
  const [bkCity, setBkCity] = useState('');
  const [bkCityCustom, setBkCityCustom] = useState('');
  const [bkDest, setBkDest] = useState('');
  const [bkCountries, setBkCountries] = useState([]);
  const [bkCities, setBkCities] = useState([]);
  const [bkCntMap, setBkCntMap] = useState({});
  const [bkCityMap, setBkCityMap] = useState({});
  const [loadingBkGeo, setLoadingBkGeo] = useState(false);
  const [bkResults, setBkResults] = useState(null);
  const [loadingBk, setLoadingBk] = useState(false);
  const [bkDepType, setBkDepType] = useState('station'); // local transport: 'station' | 'airport'
  const [bkLocalTransType, setBkLocalTransType] = useState(''); // local transport: rail/bus type
  const [bkAirport, setBkAirport] = useState('');        // local transport: selected airport code
  const [bkDepStation, setBkDepStation] = useState('');  // local transport: selected departure station
  const [bkDestStation, setBkDestStation] = useState(''); // local/cross-border: destination station
  const [bkCrossMode, setBkCrossMode] = useState('flight'); // cross-border: flight | train | bus
  const [bkCrossFold, setBkCrossFold] = useState('trip');   // 跨境機票／火車：目前展開的供應商列 'trip' | 'hopegoo' | ''
  const [bkTrainType, setBkTrainType] = useState('');
  // Cross-border destination country/city
  const [bkDestCountry, setBkDestCountry]       = useState('');
  const [bkDestCountries, setBkDestCountries]   = useState([]);
  const [bkDestCntMap, setBkDestCntMap]         = useState({});
  const [bkDestCities2, setBkDestCities2]       = useState([]);
  const [bkDestCityMap2, setBkDestCityMap2]     = useState({});
  const [loadingBkDest, setLoadingBkDest]       = useState(false);

  // Clear stale booking results when language changes
  useEffect(() => {
    setBkResults(null);
    setBkCountries([]); setBkCities([]); setBkRegion(''); setBkCountry(''); setBkCity(''); setBkDest('');
  }, [lang]);

  const fetchBkCountries = async (rgn, ln) => {
    setLoadingBkGeo(true); setBkCountry(''); setBkCity(''); setBkDest('');
    const al = T[ln].aiLang;
    const { en: sEn, map: sMap } = getStaticCountries(rgn, al);
    let { en: finalEn, map: finalMap } = applyCountryOverrides(rgn, sEn, sMap, al);
    // Hong Kong is a city under China elsewhere in the app, but dining is booked
    // by country and OpenRice is HK's main restaurant platform — expose it here only.
    if (bkType === 'dining' && rgn === 'Asia' && !finalEn.includes('Hong Kong')) {
      const hkName = { 'Traditional Chinese':'香港', 'Simplified Chinese':'香港', 'English':'Hong Kong', 'Japanese':'香港', 'Korean':'홍콩' }[al] || 'Hong Kong';
      const i = finalEn.indexOf('China');
      finalEn = [...finalEn.slice(0, i + 1), 'Hong Kong', ...finalEn.slice(i + 1)];
      finalMap = { ...finalMap, 'Hong Kong': hkName };
    }
    setBkCountries(finalEn); setBkCntMap(finalMap);
    setLoadingBkGeo(false);
  };

  const fetchBkCities = async (ctry, ln) => {
    setLoadingBkGeo(true); setBkCity(''); setBkDest('');
    const al = T[ln].aiLang;
    const { en: sEn, map: sMap } = getStaticCities(ctry, al);
    if (sEn.length > 0) {
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, sEn, sMap, al);
      setBkCities(finalEn); setBkCityMap(finalMap);
      setLoadingBkGeo(false); return;
    }
    const ck = `bk_cit_${ctry}_${ln}`;
    const cached = getAICache(ck);
    if (cached) { setBkCities(cached.en); setBkCityMap(cached.map); setLoadingBkGeo(false); return; }
    const d = await callAI(`List 20 tourist destinations in ${ctry}, including both famous cities and regional spots.`,
      `Reply ONLY JSON no markdown: {"cities":[{"en":"string","local":"string"}]} where "local" is TRANSLATED INTO ${al}`,
      900, GROQ_MODEL_FAST
    );
    if (d?.cities) {
      const en=d.cities.map(c=>c.en); const m={};
      d.cities.forEach(c=>{ m[c.en]=c.local||c.en; });
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, en, m, al);
      setAICache(ck, { en: finalEn, map: finalMap }, TTL_GEO);
      setBkCities(finalEn); setBkCityMap(finalMap);
    }
    setLoadingBkGeo(false);
  };

  const fetchBkDestCountries = async (rgn, ln) => {
    setLoadingBkDest(true); setBkDestCountry(''); setBkDestCities2([]); setBkDest('');
    const al = T[ln].aiLang;
    const { en: sEn, map: sMap } = getStaticCountries(rgn, al);
    const { en: finalEn, map: finalMap } = applyCountryOverrides(rgn, sEn, sMap, al);
    setBkDestCountries(finalEn); setBkDestCntMap(finalMap);
    setLoadingBkDest(false);
  };

  const fetchBkDestCities = async (ctry, ln) => {
    setLoadingBkDest(true); setBkDest(''); setBkDestStation('');
    const al = T[ln].aiLang;
    const { en: sEn, map: sMap } = getStaticCities(ctry, al);
    if (sEn.length > 0) {
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, sEn, sMap, al);
      setBkDestCities2(finalEn); setBkDestCityMap2(finalMap);
      setLoadingBkDest(false); return;
    }
    const ck = `bk_dcit_${ctry}_${ln}`;
    const cached = getAICache(ck);
    if (cached) { setBkDestCities2(cached.en); setBkDestCityMap2(cached.map); setLoadingBkDest(false); return; }
    const d = await callAI(`List 15 tourist destinations and transport hubs in ${ctry}.`,
      `Reply ONLY JSON no markdown: {"cities":[{"en":"string","local":"string"}]} where "local" is TRANSLATED INTO ${al}`, 700, GROQ_MODEL_FAST);
    if (d?.cities) {
      const en=d.cities.map(c=>c.en); const m={};
      d.cities.forEach(c=>{ m[c.en]=c.local||c.en; });
      const { en: finalEn, map: finalMap } = applyCityOverrides(ctry, en, m, al);
      setAICache(ck, { en: finalEn, map: finalMap }, TTL_GEO);
      setBkDestCities2(finalEn); setBkDestCityMap2(finalMap);
    }
    setLoadingBkDest(false);
  };

  const doBkSearch = async () => {
    if (!bkType) return;
    if (bkType==='dining') { if (!bkCountry) return; }
    else if (bkType !== 'esim' && !bkCity) return; // eSIM can run without city
    setLoadingBk(true); setBkResults(null);
    if (bkType==='localTransport'||bkType==='crossBorder') {
      if (bkType==='crossBorder') {
        if (bkDestCountry && bkCountry && (bkDestCountry===bkCountry || (bkCntMap[bkDestCountry]&&bkCntMap[bkDestCountry]===bkCntMap[bkCountry]))) {
          setBkResults([{ title:'⚠️ 請選擇不同國家', operator:'跨國交通需選擇與出發地不同的國家', duration:'—', price:'—', highlight:'例如：香港→日本、台灣→韓國、首爾→東京', link:'#', platform:'提示' }]);
          setLoadingBk(false); return;
        }
        const cityEn3    = CITY_EN[bkCity]||CITY_EN[bkCityMap[bkCity]]||bkCity;
        const cityDisp3  = bkCityMap[bkCity]||bkCity;
        const destEn3    = CITY_EN[bkDest]||CITY_EN[bkDestCityMap2[bkDest]]||bkDest;
        const destDisp3  = bkDestCityMap2[bkDest]||bkDest||bkDestCntMap[bkDestCountry]||bkDestCountry||'目的地';
        const origCntDisp= bkCntMap[bkCountry]||bkCountry;
        const destCntDisp= bkDestCntMap[bkDestCountry]||bkDestCountry||'';

        if (bkCrossMode==='flight') {
          const originCode = bkAirport || CITY_AIRPORTS[cityEn3]?.[0]?.code || '';
          const destCode   = CITY_AIRPORTS[destEn3]?.[0]?.code || '';
          const tripUrl    = originCode && destCode
            ? `https://www.trip.com/flights/${encodeURIComponent(cityDisp3.toLowerCase())}-${encodeURIComponent(destDisp3.toLowerCase())}/tickets/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}&dcity=${originCode}&acity=${destCode}`
            : `https://www.trip.com/flights/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}`;
          const stay22Url = stay22Link(destDisp3 || destEn3);
          setBkResults([
            { title:`✈️ ${originCode||cityDisp3} → ${destCode||destDisp3}`, operator:`${origCntDisp} → ${destCntDisp}`, duration:'視航班而定', price:'即時比價', highlight:originCode&&destCode?`${originCode} → ${destCode}`:'搜尋最優惠機票', link:tripUrl, platform:'Trip.com' },
            { title:`${cityDisp3} → ${destDisp3} 機票`, operator:'Klook 機票 & 旅遊套票', duration:'多家航空比較', price:'立即查價', highlight:'機票 + 旅遊套票', link:`https://www.klook.com/en-HK/flights/?Allianceid=${KLOOK_AID}&origin=${originCode||''}&destination=${destCode||''}`, platform:'Klook' },
            { title:`${destDisp3} 住宿地圖比價`, operator:'Stay22 即時住宿地圖', duration:'即時比價', price:'免費瀏覽', highlight:'地圖上直接比較附近飯店價格', link:stay22Url, platform:'Stay22' },
          ]);
          setLoadingBk(false); return;
        }

        // Train mode — pre-built booking cards
        const intlTrains3 = getTrainsForCountry().filter(tr => isIntlTrain(tr.k));
        const selTrain3   = bkTrainType ? intlTrains3.find(t=>t.k===bkTrainType) : null;
        const depSt3      = bkDepStation || cityDisp3;
        const destSt3     = bkDestStation || destDisp3;
        const routeLabel  = `${depSt3} → ${destSt3}`;
        const trainCard = (l, url) => url.includes('klook.com')
          ? { title:l, operator:routeLabel, duration:'查閱時刻表', price:'查閱優惠', highlight:routeLabel, platform:'交通資訊', noLink:true }
          : { title:l, operator:routeLabel, duration:'查閱時刻表', price:'查閱優惠', highlight:routeLabel, link:url, platform:url.includes('trip.com')?'Trip.com':url.includes('eurail')?'Eurail':url.includes('eurostar')?'Eurostar':url.includes('amtrak')?'Amtrak':'交通資訊' };
        const trainResults = selTrain3
          ? [trainCard(selTrain3.l, selTrain3.url),
             { title:`${cityDisp3} → ${destDisp3} 火車票`, operator:'Trip.com 一站比價', duration:'查閱時刻表', price:'即時比價', highlight:routeLabel, link:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}`, platform:'Trip.com' }]
          : intlTrains3.slice(0,3).map(tr=>trainCard(tr.l, tr.url));
        setBkResults(trainResults.length ? trainResults : [{ title:'💡 建議使用機票', operator:`${origCntDisp} → ${destCntDisp} 暫無直達跨境鐵路`, duration:'—', price:'—', highlight:'請切換至「✈️ 機票」模式', link:`https://www.trip.com/flights/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}`, platform:'Trip.com' }]);
        setLoadingBk(false); return;
      } else {
        const cityEn2 = CITY_EN[bkCity]||CITY_EN[bkCityMap[bkCity]]||bkCity;
        const dep     = bkDepStation||bkAirport||bkCity;
        const dest    = bkDestStation||'目的地';
        if (bkDepType==='airport') {
          const airportCode = bkAirport ? bkAirport.split(' · ')[0].trim() : null;
          const aeList = (AIRPORT_EXPRESS[cityEn2]||[]).filter(ae=>!airportCode||ae.airport===airportCode);
          const res = aeList.length
            ? aeList.map(ae=>({ title:ae.name, operator:`${ae.from} → ${ae.to}`, duration:ae.duration, price:ae.price, highlight:`停靠站：${ae.stops.join(' → ')}`, platform:'交通資訊', noLink:true }))
            : [{ title:`${cityEn2} 機場 → 市區`, operator:'請查閱各機場官網', duration:'約30-60分鐘', price:'視路線而定', highlight:'建議選擇機場快線前往市區', platform:'交通資訊', noLink:true }];
          setBkResults(res); setLoadingBk(false); return;
        }
        const transTypes = CITY_LOCAL_TRANSPORT[cityEn2]||[];
        const selTrans = bkLocalTransType ? transTypes.find(tt=>tt.k===bkLocalTransType) : null;
        const res2 = selTrans
          ? [{ title:selTrans.l, operator:`${dep} → ${dest}`, duration:'視路線而定', price:'查閱通票優惠', highlight:`${dep} → ${dest}`, platform:'交通資訊', noLink:true }]
          : transTypes.slice(0,4).map(tr=>({ title:tr.l, operator:`${cityEn2} 市內交通`, duration:'視路線而定', price:'查閱優惠', highlight:`${cityEn2} 鐵路一日票`, platform:'交通資訊', noLink:true }));
        setBkResults(res2.length ? res2 : [{ title:'本地交通', operator:cityEn2, duration:'—', price:'—', platform:'交通資訊', noLink:true }]);
        setLoadingBk(false); return;
      }
    } else if (bkType==='tickets') {
      const cat = bkTicketCat || 'tourist attractions';
      const tmClass = TM_CLASS[cat];

      // ── 演唱會 / 球賽 ─────────────────────────────────────────────────────
      if (tmClass) {
        const isHK = bkCity === 'Hong Kong' || bkCountry === 'Hong Kong';

        if (isHK) {
          // HK: LCSD + HK-specific platforms + Ticketmaster for international
          const [lcsdEvents, tmEvents] = await Promise.all([
            fetchLCSDEvents(cat),
            fetchTicketmasterEvents(bkCity, bkCountry, tmClass, bkEventYear, bkEventMonth),
          ]);
          const hkCards = generateHKCards(cat);
          const merged = [
            ...(tmEvents?.length ? tmEvents : []),
            ...(lcsdEvents?.length ? lcsdEvents : []),
            ...hkCards,
          ];
          setBkResults(merged.slice(0, 16));
          setLoadingBk(false); return;
        }

        // Non-HK: Ticketmaster + region-specific platform cards + AI fallback
        const events = await fetchTicketmasterEvents(bkCity, bkCountry, tmClass, bkEventYear, bkEventMonth);
        const concertCards = generateConcertCards(cat, bkCity, bkCountry, lang);

        if (events?.length) {
          // TM returned results — prepend region cards so user has local options too
          setBkResults([...events, ...concertCards].slice(0, 16));
          setLoadingBk(false); return;
        }

        // No TM data — show region-specific platform cards immediately
        if (concertCards.length) {
          setBkResults(concertCards.slice(0, 14));
          setLoadingBk(false); return;
        }

        // Used to fall back here to Groq inventing "top venues and events" with
        // fake prices and a fake ticketing link — same fabrication class removed
        // from doSearch 2026-09-27. No real per-venue data source is wired in for
        // this last-resort case, so show real search links instead of invented rows.
        const isConc = cat === 'concerts';
        setBkResults([{
          title: `🔍 在 ${isConc ? 'Songkick' : 'Viagogo'} 搜尋「${bkCity}」`,
          venue: bkCity,
          desc: `瀏覽 ${bkCity} 真實的${isConc ? '演唱會' : '球賽'}場次與售票資訊`,
          price: '查看所有結果',
          platform: isConc ? 'Songkick' : 'Viagogo',
          link: isConc
            ? `https://www.songkick.com/search?query=${encodeURIComponent(bkCity)}`
            : `https://www.viagogo.com/search?q=${encodeURIComponent(bkCity)}+sports`,
          highlight: '', duration: '',
        }]);
        setLoadingBk(false); return;
      }

      // ── 展覽 → Eventbrite ────────────────────────────────────────────────
      if (cat === 'exhibitions') {
        const evEvents = await fetchEventbriteEvents(bkCity, bkCountry, bkEventYear, bkEventMonth);
        const dbRes = VENUE_DB['exhibitions']?.[bkCity] || [];
        const merged = [
          ...(evEvents?.length ? evEvents : []),
          ...dbRes,
          ...generatePlatformCards(cat, bkCity, bkCountry, lang),
        ];
        setBkResults(merged.slice(0, 14));
        setLoadingBk(false); return;
      }

      // ── 景點門票 / 主題樂園 / 博物館 / 動物園 / 海洋館 → Viator + VENUE_DB + AI 備援 ─
      const RELATED_CATS = {
        'zoos':               ['theme parks'],
        'aquariums':          ['theme parks'],
        'exhibitions':        ['museums'],
        'tourist attractions':['theme parks','museums','zoos','aquariums'],
      };
      const primaryDB = VENUE_DB[cat]?.[bkCity] || VENUE_DB[cat]?.[bkCountry] || [];
      const relatedDB = (RELATED_CATS[cat] || []).flatMap(rc =>
        (VENUE_DB[rc]?.[bkCity] || VENUE_DB[rc]?.[bkCountry] || [])
          .filter(v => !primaryDB.some(p => p.venue === v.venue))
      );
      const platformCards = generatePlatformCards(cat, bkCity, bkCountry, lang);

      // Try Viator API first
      const viatorRes = await fetchViatorProducts(bkCity, bkCountry, cat, lang);

      if (viatorRes?.length) {
        // Viator returned results — merge with DB and platform cards
        const merged = [...primaryDB, ...relatedDB, ...viatorRes, ...platformCards];
        setBkResults(merged.slice(0, 14));
        setLoadingBk(false); return;
      }

      if (primaryDB.length || relatedDB.length) {
        // Have DB data — show it plus platform cards
        setBkResults([...primaryDB, ...relatedDB, ...platformCards].slice(0, 14));
        setLoadingBk(false); return;
      }

      // No DB/API data → used to ask Groq to invent "top 6 venues" with fake
      // ticket prices per row (same fabrication class removed from doSearch
      // 2026-09-27). platformCards (real Klook/KKday search links, defined
      // above) is the only real fallback left for this case.
      setBkResults(platformCards.slice(0, 14));
      setLoadingBk(false); return;
    } else if (bkType==='dining') {
      // Used to ask Groq to invent "top restaurant booking platforms" — plain text
      // completion with a fake "platform"/"link" per row, same fabrication class
      // audited and removed from the search page 2026-09-27 (see doSearch above).
      // Real search-result links to real platforms instead of invented rows.
      // Dining is booked by country (no city step), so bkCity is usually empty —
      // fall back to the country name or the titles read "搜尋「 餐廳」".
      const place = bkCity || bkCntMap[bkCountry] || bkCountry;
      const dq = encodeURIComponent(`${bkCity ? bkCity + ' ' : ''}${bkCountry} restaurants`);
      // Local restaurant platforms per country: verified-live entry points only
      // (no invented restaurants or prices). Ratings/booking happen on the platform.
      const LOCAL_DINING = {
        'Japan': [
          { platform:'Tabelog 食べログ', title:'Tabelog 食べログ 日本餐廳評分', desc:'日本餐廳評分與預約平台，有繁體中文版，可按地區／料理類型找餐廳', highlight:'⭐ 日本當地評分標準', link:'https://tabelog.com/tw/' },
          { platform:'TableCheck', title:'TableCheck 日本餐廳網上訂位', desc:'線上預約日本餐廳座位，有繁體中文介面，部分餐廳可預約', highlight:'🪑 可線上訂位', link:'https://www.tablecheck.com/zh-TW/japan/shops' },
        ],
        'Hong Kong': [
          { platform:'OpenRice 開飯', title:'OpenRice 開飯 香港餐廳', desc:'香港餐廳評價平台，可按地區、菜式找餐廳，部分餐廳可訂枱', highlight:'⭐ 本地食客評價', link:'https://www.openrice.com/zh/hongkong' },
        ],
        'Taiwan': [
          { platform:'愛食記 iFoodie', title:'愛食記 iFoodie 台灣餐廳', desc:'台灣美食評價與食記平台，可按縣市、料理類型找餐廳', highlight:'⭐ 在地食記', link:'https://ifoodie.tw/' },
          { platform:'inline', title:'inline 台灣餐廳訂位', desc:'台灣常見的餐廳線上訂位系統，部分餐廳可預約', highlight:'🪑 可線上訂位', link:'https://dining.inline.app/' },
        ],
        'South Korea': [
          { platform:'CatchTable', title:'CatchTable 韓國餐廳訂位', desc:'韓國餐廳預約平台，外國旅客需用 CatchTable Global 版預約，部分餐廳才開放', highlight:'🪑 可線上訂位', link:'https://www.catchtable.net/' },
        ],
      };
      setBkResults([
        { title:`🔍 在 Google 地圖搜尋「${place} 餐廳」`, venue:place, desc:`瀏覽 ${place} 的真實餐廳評價、營業時間與地址`, price:'免費查看', platform:'Google Maps', link:`https://www.google.com/maps/search/?api=1&query=${dq}`, highlight:'📍 真實評價與地址', duration:'' },
        ...(LOCAL_DINING[bkCountry === 'Korea' ? 'South Korea' : bkCountry] || []).map(p => ({ ...p, venue:place, price:'前往網站', duration:'' })),
        { title:`🔍 在 Klook 搜尋「${place} 美食體驗」`, venue:place, desc:`前往 Klook 搜尋頁面，瀏覽 ${place} 精選美食體驗與餐券`, price:'查看所有結果', platform:'Klook', link:klookSearch(place, '美食 food', lang), highlight:'⚡ 即時確認', duration:'' },
      ]);
      setLoadingBk(false); return;
    } else if (bkType==='esim') {
      // eSIM — Saily affiliate cards (no AI needed, static data)
      const esimL = lang==='en'||lang==='ja'||lang==='ko' ? lang : 'zh';
      const slug = ESIM_SLUGS[bkCountry] || null;
      const localTitle = lang==='en' ? `${bkCountry} Local eSIM`
        : lang==='ja' ? `${bkCountry} ローカル eSIM`
        : lang==='ko' ? `${bkCountry} 로컬 eSIM`
        : `${bkCountry} 本地 eSIM`;
      const localDesc = lang==='en' ? `Local mobile data in ${bkCity||bkCountry} — buy and use instantly, no physical SIM needed`
        : lang==='ja' ? `${bkCity||bkCountry}で使えるローカルデータ。即購入・即利用、物理SIM不要`
        : lang==='ko' ? `${bkCity||bkCountry}에서 사용하는 로컬 데이터 — 즉시 구매 및 사용, 실물 SIM 불필요`
        : `在 ${bkCity||bkCountry} 使用本地電話數據，即買即用，無需實體 SIM 卡`;
      const localHighlight = lang==='en' ? '📶 Instant activation · Works on iPhone & Android'
        : lang==='ja' ? '📶 即時有効化 · iPhone & Android対応'
        : lang==='ko' ? '📶 즉시 활성화 · 아이폰 & 안드로이드 지원'
        : '📶 即時激活 · 支援 iPhone & Android';
      const regionalSuffix = lang==='en' ? ', one eSIM for your whole trip'
        : lang==='ja' ? '、1枚のeSIMで複数国を周遊'
        : lang==='ko' ? ', eSIM 하나로 여러 국가 여행'
        : '，一張 eSIM 多國暢遊';
      const regionalHighlightFallback = lang==='en' ? '📶 Works across multiple countries'
        : lang==='ja' ? '📶 複数国で利用可能'
        : lang==='ko' ? '📶 여러 국가에서 사용 가능'
        : '📶 多國通用';
      const browseAllTitle = lang==='en' ? '🔍 Browse all Saily eSIM plans'
        : lang==='ja' ? '🔍 Saily の全eSIMプランを見る'
        : lang==='ko' ? '🔍 모든 Saily eSIM 요금제 보기'
        : '🔍 瀏覽所有 Saily eSIM 方案';
      const browseAllDesc = lang==='en' ? 'Multiple countries and regions worldwide — choose by duration and data'
        : lang==='ja' ? '世界中の複数の国・地域 — 日数とデータ量で選択可能'
        : lang==='ko' ? '전 세계 여러 국가 및 지역 — 기간과 데이터로 선택 가능'
        : '全球多個國家及地區，按天數和流量靈活選擇';
      const browseAllHighlight = lang==='en' ? '💳 Pay by card · Instant activation'
        : lang==='ja' ? '💳 カード決済 · 即時有効化'
        : lang==='ko' ? '💳 카드 결제 · 즉시 활성화'
        : '💳 信用卡付款 · 即時激活';
      const priceMultiple = lang==='en' ? 'Multiple price options'
        : lang==='ja' ? '複数の価格プラン'
        : lang==='ko' ? '다양한 요금제'
        : '多種價格方案';
      const cards = [];
      if (bkCountry && slug) {
        cards.push({
          title: localTitle,
          venue: `Saily · ${bkCountry}`,
          desc: localDesc,
          price: lang==='en'?'From USD 3.99':'約 HK$31 起',
          platform: 'Saily',
          link: sailyLink(slug),
          highlight: localHighlight,
          coupon: sailyCouponLabel(lang),
          duration: '',
        });
      }
      ESIM_REGIONAL.forEach(r => {
        const label = r.label[esimL];
        const desc = r.desc[esimL];
        const badge = r.badge[esimL];
        cards.push({
          title: `${r.icon} ${label} eSIM`,
          venue: `Saily · ${desc}`,
          desc: desc + regionalSuffix,
          price: lang==='en'?'From USD 4.99':'約 HK$39 起',
          platform: 'Saily',
          link: sailyLink(r.slug),
          highlight: badge ? `🌟 ${badge}` : regionalHighlightFallback,
          coupon: sailyCouponLabel(lang),
          duration: '',
        });
      });
      cards.push({
        title: browseAllTitle,
        venue: 'Saily',
        desc: browseAllDesc,
        price: priceMultiple,
        platform: 'Saily',
        link: sailyLink(),
        highlight: browseAllHighlight,
        coupon: sailyCouponLabel(lang),
        duration: '',
      });
      setBkResults(cards);
      setLoadingBk(false); return;
    }
    setLoadingBk(false);
  };

  // ── MENU PAGES — MenuPageShell, InfoCard imported from components/

  // ── 比較型攻略 ───────────────────────────────────────────────────────────────
  const COMPARE_TYPES = [
    { key:'transport',   label:'交通票券',  icon:'🚇', desc:'哪張 Pass 最划算？' },
    { key:'attractions', label:'景點套票',  icon:'🎡', desc:'周遊卡 vs 單買哪個好？' },
    { key:'area',        label:'住宿地區',  icon:'🏨', desc:'住哪個地區最方便？' },
    { key:'season',      label:'旅遊季節',  icon:'🌸', desc:'哪個季節最值得去？' },
  ];

  const fetchComparison = async () => {
    if (!compareQuery || !compareType) return;
    setCompareLoading(true); setCompareData(null);
    const typeLabel = COMPARE_TYPES.find(t => t.key === compareType)?.label || compareType;
    const cacheKey = `cmp_${compareQuery}_${compareType}`;
    const cached = getAICache(cacheKey);
    if (cached) { setCompareData(cached); setCompareLoading(false); return; }
    const d = await callAI(
      `比較 ${compareQuery} 的${typeLabel}選擇。${compareSituation ? `旅客情況：${compareSituation}` : ''}`,
      `你是專業旅遊分析師。以繁體中文回傳 JSON，格式如下：
{
  "title": "比較標題",
  "subtitle": "一句簡介",
  "options": [
    {
      "name": "選項名稱",
      "price": "價格說明",
      "coverage": ["包含項目"],
      "bestFor": "最適合誰",
      "pros": ["優點"],
      "cons": ["缺點"],
      "scores": { "性價比": 4, "靈活性": 3, "便利性": 5, "覆蓋度": 4 }
    }
  ],
  "recommendation": ${compareSituation ? '{ "for": "旅客情況描述", "pick": "推薦選項", "reason": "推薦原因" }' : 'null'},
  "quickPick": [
    { "type": "旅遊模式（如：純市區3日）", "best": "最佳選擇" }
  ]
}
選項最多 5 個，quickPick 最多 5 條，所有文字用繁體中文。`,
      2200, GROQ_MODEL
    );
    if (d) setAICache(cacheKey, d, TTL_WX);
    setCompareData(d);
    setCompareLoading(false);
  };

  const renderComparePage = () => {
    const ScoreBar = ({ label, score }) => (
      <div style={{ marginBottom:6 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
          <span style={{ fontSize:11, color:'#64748b', fontWeight:600 }}>{label}</span>
          <span style={{ fontSize:11, fontWeight:800, color:'#0f172a' }}>{score}/5</span>
        </div>
        <div style={{ height:6, borderRadius:99, background:'#f1f5f9', overflow:'hidden' }}>
          <div style={{ height:'100%', borderRadius:99, background:`hsl(${score*24},75%,50%)`, width:`${score/5*100}%`, transition:'width 0.4s ease' }}/>
        </div>
      </div>
    );

    return (
      <div style={{ minHeight:'100vh', background:'#f8fafc', paddingBottom:120 }}>
        {/* Hero */}
        <div style={{ background:'linear-gradient(135deg,#1e3a5f,#0f172a)', padding:'28px 20px 24px' }}>
          <button onClick={()=>setTab('home')} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'rgba(255,255,255,0.8)', borderRadius:10, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
            <ChevronLeft size={14}/> 返回
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
            <div style={{ width:48, height:48, borderRadius:16, background:'rgba(245,158,11,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Zap size={24} color='#f59e0b'/>
            </div>
            <div>
              <h1 style={{ margin:0, color:'#fff', fontSize:24, fontWeight:900 }}>比較攻略</h1>
              <p style={{ margin:0, color:'rgba(255,255,255,0.55)', fontSize:13 }}>AI 幫你做決定，不再糾結</p>
            </div>
          </div>

          {/* Search + Type selector */}
          <div style={{ display:'flex', gap:10, marginBottom:14 }}>
            <input value={compareQuery} onChange={e=>setCompareQuery(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&fetchComparison()}
              placeholder='目的地，如：大阪、東京、首爾…'
              style={{ flex:1, padding:'11px 14px', borderRadius:12, border:'none', fontSize:14, outline:'none' }}/>
            <button onClick={fetchComparison} disabled={compareLoading||!compareQuery||!compareType}
              style={{ background:'#f59e0b', color:'#0f172a', border:'none', borderRadius:12, padding:'11px 20px', fontWeight:900, fontSize:13, cursor:'pointer', opacity:(!compareQuery||!compareType||compareLoading)?0.5:1, whiteSpace:'nowrap' }}>
              {compareLoading ? <Loader2 size={16} style={{ animation:'spin 1s linear infinite' }}/> : '比較'}
            </button>
          </div>

          {/* Type pills */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {COMPARE_TYPES.map(type=>(
              <button key={type.key} onClick={()=>setCompareType(type.key)}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:99, border:'1.5px solid', borderColor:compareType===type.key?'#f59e0b':'rgba(255,255,255,0.2)', background:compareType===type.key?'#f59e0b':'rgba(255,255,255,0.08)', color:compareType===type.key?'#0f172a':'rgba(255,255,255,0.8)', fontSize:12, fontWeight:800, cursor:'pointer', transition:'all 0.15s' }}>
                <span>{type.icon}</span>{type.label}
                <span style={{ fontSize:10, opacity:0.7 }}>{type.desc}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth:720, margin:'0 auto', padding:'20px 16px' }}>
          {/* Situation input */}
          {compareType && !compareData && !compareLoading && (
            <div style={{ background:'#fff', borderRadius:14, padding:'16px', border:'1px solid #e2e8f0', marginBottom:20 }}>
              <p style={{ margin:'0 0 10px', fontSize:13, fontWeight:800, color:'#0f172a' }}>
                💬 說明你的情況（選填，讓推薦更準確）
              </p>
              <textarea value={compareSituation} onChange={e=>setCompareSituation(e.target.value)}
                placeholder={
                  compareType==='transport' ? '例如：住難波4天，計劃去京都一日、神戶半日' :
                  compareType==='area'       ? '例如：第一次去大阪，喜歡夜生活，預算中等' :
                  compareType==='attractions'? '例如：帶小朋友，喜歡歷史文化，不愛排隊' :
                  '例如：喜歡花季，怕熱，計劃去5天'
                }
                style={{ width:'100%', padding:'10px', borderRadius:10, border:'1.5px solid #e2e8f0', fontSize:13, resize:'vertical', minHeight:70, outline:'none', fontFamily:'system-ui', boxSizing:'border-box' }}/>
            </div>
          )}

          {/* Popular quick-starts */}
          {!compareData && !compareLoading && (
            <div style={{ marginBottom:20 }}>
              <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', marginBottom:12, textTransform:'uppercase', letterSpacing:1.5 }}>熱門比較</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  { q:'大阪', t:'transport',   label:'大阪交通票券比較',    emoji:'🚇' },
                  { q:'東京', t:'transport',   label:'東京交通 Pass 比較',  emoji:'🚆' },
                  { q:'大阪', t:'area',        label:'大阪住宿地區比較',    emoji:'🏨' },
                  { q:'首爾', t:'area',        label:'首爾住宿地區比較',    emoji:'🏘️' },
                  { q:'東京', t:'attractions', label:'東京景點套票比較',    emoji:'🎡' },
                  { q:'關西', t:'season',      label:'關西四季旅遊比較',    emoji:'🌸' },
                ].map((item,i)=>(
                  <button key={i} onClick={()=>{ setCompareQuery(item.q); setCompareType(item.t); }}
                    style={{ background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:12, padding:'12px 14px', textAlign:'left', cursor:'pointer', transition:'all 0.15s', display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={e=>{ e.currentTarget.style.borderColor='#f59e0b'; e.currentTarget.style.background='#fffbeb'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.background='#fff'; }}>
                    <span style={{ fontSize:22 }}>{item.emoji}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:'#0f172a', lineHeight:1.4 }}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {compareLoading && (
            <div style={{ textAlign:'center', padding:'60px 0', color:'#94a3b8' }}>
              <Loader2 size={36} style={{ animation:'spin 1s linear infinite', marginBottom:14 }}/>
              <p style={{ margin:0, fontSize:14, fontWeight:600 }}>AI 正在分析比較…</p>
              <p style={{ margin:'6px 0 0', fontSize:12, color:'#cbd5e1' }}>通常需要 10–15 秒</p>
            </div>
          )}

          {/* Results */}
          {compareData && !compareLoading && (
            <div>
              {/* Title */}
              <div style={{ marginBottom:20 }}>
                <h2 style={{ margin:'0 0 4px', fontSize:20, fontWeight:900, color:'#0f172a' }}>{compareData.title}</h2>
                <p style={{ margin:0, fontSize:13, color:'#64748b' }}>{compareData.subtitle}</p>
              </div>

              {/* Recommendation box */}
              {compareData.recommendation && (
                <div style={{ background:'linear-gradient(135deg,#0f172a,#1e3a5f)', borderRadius:16, padding:'18px 20px', marginBottom:20 }}>
                  <p style={{ margin:'0 0 6px', fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:1.5 }}>🎯 你的情況推薦</p>
                  <p style={{ margin:'0 0 4px', fontSize:12, color:'rgba(255,255,255,0.6)' }}>{compareData.recommendation.for}</p>
                  <p style={{ margin:'0 0 8px', fontSize:18, fontWeight:900, color:'#f59e0b' }}>{compareData.recommendation.pick}</p>
                  <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.75)', lineHeight:1.6 }}>{compareData.recommendation.reason}</p>
                </div>
              )}

              {/* Quick pick decision grid */}
              {compareData.quickPick?.length > 0 && (
                <div style={{ background:'#fff', borderRadius:14, padding:'16px', border:'1px solid #e2e8f0', marginBottom:20 }}>
                  <p style={{ margin:'0 0 12px', fontSize:13, fontWeight:800, color:'#0f172a' }}>⚡ 快速決定</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {compareData.quickPick.map((qp,i)=>(
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:'#f8fafc', borderRadius:10 }}>
                        <span style={{ fontSize:12, color:'#475569', fontWeight:600 }}>如果你是：{qp.type}</span>
                        <span style={{ fontSize:12, fontWeight:900, color:'#0f172a', background:'#fef3c7', padding:'3px 10px', borderRadius:6 }}>{qp.best}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Option cards */}
              <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', marginBottom:12, textTransform:'uppercase', letterSpacing:1.5 }}>詳細比較</p>
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {compareData.options?.map((opt,i)=>(
                  <div key={i} style={{ background:'#fff', borderRadius:16, border:'1.5px solid #e2e8f0', overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
                    {/* Header */}
                    <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <p style={{ margin:'0 0 3px', fontSize:16, fontWeight:900, color:'#0f172a' }}>{opt.name}</p>
                        <p style={{ margin:0, fontSize:13, fontWeight:700, color:'#f59e0b' }}>{opt.price}</p>
                      </div>
                      <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'4px 10px', textAlign:'center' }}>
                        <p style={{ margin:'0 0 1px', fontSize:10, color:'#16a34a', fontWeight:700, textTransform:'uppercase' }}>最適合</p>
                        <p style={{ margin:0, fontSize:11, color:'#0f172a', fontWeight:800, maxWidth:100 }}>{opt.bestFor}</p>
                      </div>
                    </div>

                    <div style={{ padding:'14px 16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                      {/* Coverage */}
                      {opt.coverage?.length > 0 && (
                        <div style={{ gridColumn:'1/-1' }}>
                          <p style={{ margin:'0 0 7px', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:1 }}>包含</p>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                            {opt.coverage.map((c,j)=>(
                              <span key={j} style={{ background:'#eff6ff', color:'#1d4ed8', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:6 }}>{c}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pros */}
                      <div>
                        <p style={{ margin:'0 0 6px', fontSize:11, fontWeight:700, color:'#16a34a', textTransform:'uppercase', letterSpacing:1 }}>✅ 優點</p>
                        {opt.pros?.map((p,j)=>(
                          <p key={j} style={{ margin:'0 0 3px', fontSize:12, color:'#374151' }}>• {p}</p>
                        ))}
                      </div>

                      {/* Cons */}
                      <div>
                        <p style={{ margin:'0 0 6px', fontSize:11, fontWeight:700, color:'#dc2626', textTransform:'uppercase', letterSpacing:1 }}>❌ 缺點</p>
                        {opt.cons?.map((c,j)=>(
                          <p key={j} style={{ margin:'0 0 3px', fontSize:12, color:'#374151' }}>• {c}</p>
                        ))}
                      </div>

                      {/* Score bars */}
                      {opt.scores && Object.keys(opt.scores).length > 0 && (
                        <div style={{ gridColumn:'1/-1' }}>
                          <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:1 }}>評分</p>
                          {Object.entries(opt.scores).map(([label,score])=>(
                            <ScoreBar key={label} label={label} score={score}/>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={()=>{ setCompareData(null); setCompareSituation(''); }}
                style={{ marginTop:20, width:'100%', background:'#f1f5f9', border:'none', borderRadius:12, padding:'14px', fontSize:13, fontWeight:700, color:'#64748b', cursor:'pointer' }}>
                重新比較
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // AboutPage — imported from components/pages/

  const fetchIntel = async (destination) => {
    if (!destination) return;
    setIntelLoading(true); setIntelData(null); setIntelHero(null);
    // Fetch hero photo via server-side proxy (keys not exposed to browser)
    fetch(`/api/photo?query=${encodeURIComponent(destination+' travel landscape')}`)
      .then(r=>r.json()).then(d=>{ if(d?.url) setIntelHero(d.url); }).catch(()=>{});
    // This used to ask Groq to write a full "Lonely Planet style" guide —
    // named attractions/restaurants/neighborhoods each with an invented price,
    // opening hours or address, for literally any destination typed in, no DB
    // or API check at all. Same fabrication class removed from doSearch and
    // the booking page 2026-09-27 (see those comments), just with no real
    // fallback data source available for this feature. Disabled rather than
    // left fabricating: `d` stays null, same as a failed/rate-limited call the
    // page already renders as "no data" — this feature needs a real data
    // source (e.g. the same managed_attractions DB + a real guide API) before
    // it can come back.
    const d = null;
    setIntelData(d);
    setIntelLoading(false);
  };

  // Map a Supabase row to the article format used in the UI
  const MOOD_REMAP = { '活力':'購物','美食':'飲食','冒險':'玩樂','文化':'體驗','shopping':'購物','food':'飲食','entertainment':'玩樂','experience':'體驗','relaxation':'放鬆' };
  const mapSupabaseRow = (row) => ({
    id:          row.id,
    destination: row.city,
    country:     row.country || '',
    mood:        MOOD_REMAP[row.mood] || row.mood,
    topic:       row.topic,
    title:       row.title,
    subtitle:    row.subtitle,
    teaser:      row.teaser,
    sections:    Array.isArray(row.sections) ? row.sections : [],
    tips:        Array.isArray(row.tips)     ? row.tips     : [],
    bestFor:     row.best_for,
    photoKw:     row.photo_kw,
    photoUrl:    row.photo_url || '',
    hasRealData: row.has_real_data,
  });

  // Load all articles from Supabase; fall back to localStorage + seeds if empty
  const loadArticlesFromSupabase = useCallback(async () => {
    setArticlesLoading(true);
    try {
      const { data, error } = await supabase
        .from('travel_articles')
        .select('*')
        .eq('published', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (!error && data) {
        const mapped = data.map(mapSupabaseRow);
        setArticles(mapped);
        // If URL was /intel/{id}, open that article now that data is loaded
        if (pendingArticleId.current) {
          const found = mapped.find(a => String(a.id) === pendingArticleId.current);
          if (found) {
            setSelectedArticle(found);
            pendingArticleId.current = null;
            // Update meta tags for direct URL access (important for Google)
            document.title = `${found.title} | EC Travel`;
            const desc = document.querySelector('meta[name="description"]');
            const ogTitle = document.querySelector('meta[property="og:title"]');
            const ogDesc = document.querySelector('meta[property="og:description"]');
            const teaser = found.teaser || found.subtitle || '';
            if (desc) desc.setAttribute('content', teaser.slice(0, 160));
            if (ogTitle) ogTitle.setAttribute('content', `${found.title} | EC Travel`);
            if (ogDesc) ogDesc.setAttribute('content', teaser.slice(0, 200));
          }
        }
      }
    } catch { /* network error — show nothing rather than stale cache */ }
    setArticlesLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload articles from Supabase on mount and every time user opens intel tab
  useEffect(() => {
    loadArticlesFromSupabase();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (tab === 'intel') loadArticlesFromSupabase();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Articles are authored in Traditional Chinese only — translate on the fly when reading in another UI language
  useEffect(() => {
    if (!selectedArticle || lang === 'zh-HK') { setArticleTranslation(null); setTranslatingArticle(false); return undefined; }
    let cancelled = false;
    setArticleTranslation(null);
    setTranslatingArticle(true);
    translateArticle(selectedArticle, lang).then(result => {
      if (cancelled) return;
      setArticleTranslation(result);
      setTranslatingArticle(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArticle?.id, lang]);

  // fetchDeals (HK credit card travel deals) removed 2026-09-27 — dead code,
  // never called from the UI (hence the eslint-disable this comment used to
  // sit above), and it was the same AI-fabrication class as everything else
  // audited that day: invented bank names, discounts and a "link" per deal.
  // dealsData/setDealsData/dealsLoading state is left in place in case a
  // real-data version of this feature gets built later.

  // eslint-disable-next-line no-unused-vars
  const renderIntelPage = () => {
    const CAT_COLOR = { 購物:'#db2777', 飲食:'#ea580c', 玩樂:'#7c3aed', 放鬆:'#059669', 體驗:'#0284c7', 娛樂:'#7c3aed', 景點:'#0284c7', 當地文化:'#b45309', 其他:'#64748b' };
    const moodColor = (mood) => CAT_COLOR[mood] || CAT_COLOR['其他'];
    const destConf = INTEL_DESTS.find(d => d.key === intelDest) || null;
    // eslint-disable-next-line no-unused-vars
    const isAdmin = user?.email === 'ecforwork1985@gmail.com';

    // Helper: does article belong to selected region?
    const inRegion = (a) => {
      if (!destConf) return true; // 全部
      if (destConf.cities?.includes(a.destination)) return true;
      return destConf.countryNames?.some(cn =>
        a.country && (a.country.toLowerCase().includes(cn.toLowerCase()) || cn.toLowerCase().includes(a.country.toLowerCase()))
      ) || false;
    };

    // Filter articles by current dest region + city + category
    const filtered = articles
      .filter(a => !intelDest || (inRegion(a) && (!cityFilter || cityFilter === '全部' || a.destination === cityFilter)))
      .filter(a => !articleFilter || articleFilter === '全部' || a.mood === articleFilter);
    // eslint-disable-next-line no-unused-vars
    const cityHasArticles = !cityFilter || cityFilter === '全部' || articles.some(a => a.destination === cityFilter);

    // ── Article Reader ────────────────────────────────────────────────────────
    if (selectedArticle) {
      const a = selectedArticle;
      // Prefer another article about the same destination (a different category
      // first, so "下一篇" reads as a real cross-link rather than a random pick),
      // falling back to same destination/same category, then any other article.
      const sameDest = articles.filter(x => x.id !== a.id && x.destination === a.destination);
      const next = sameDest.find(x => x.mood !== a.mood) || sameDest[0] || articles.find(x => x.id !== a.id);
      // Articles are written in Traditional Chinese only; show the AI-translated copy when reading in another language
      const tr = (articleTranslation && articleTranslation.id === a.id) ? articleTranslation : null;
      const disp = tr ? { ...a, ...tr } : a;
      return (
        <div style={{ minHeight:'100vh', background:'#f8fafc', paddingBottom:120 }}>
          {/* Hero */}
          <div style={{ position:'relative', height:280, overflow:'hidden', background:'#1e293b' }}>
            {a.photoUrl
              ? <CoverImage src={a.photoUrl} alt={disp.title} opacity={0.65} onErrorHide/>
              : <ArticlePhoto keyword={a.photoKw} destination={a.destination} style={{ opacity:0.65 }}/>
            }
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%)' }}/>
            <div style={{ position:'absolute', top:20, left:20, display:'flex', gap:8 }}>
              <button onClick={closeArticle} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(6px)', border:'none', color:'#fff', borderRadius:10, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                <ChevronLeft size={14}/> 返回
              </button>
              <button onClick={()=>{
                const citySlug = (a.destination || a.city || 'world').replace(/\s+/g, '-');
                const url = `${window.location.origin}/intel/${citySlug}/${a.id}`;
                const shareData = { title: disp.title, text: disp.teaser || disp.subtitle || '', url };
                if (navigator.share) {
                  navigator.share(shareData).catch(()=>{});
                } else {
                  navigator.clipboard?.writeText(url).then(()=>showShareToast('🔗 連結已複製到剪貼板！')).catch(()=>{});
                }
              }} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(6px)', border:'none', color:'#fff', borderRadius:10, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                <Share2 size={14}/> 分享
              </button>
            </div>
            <div style={{ position:'absolute', bottom:24, left:20, right:20 }}>
              <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                <span style={{ background:moodColor(a.mood), color:'#fff', fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:99 }}>{a.mood}</span>
                <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:11, padding:'3px 10px', borderRadius:99 }}>📍 {a.destination}</span>
                <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:11, padding:'3px 10px', borderRadius:99 }}>⏱ 約5分鐘</span>
                {a.hasRealData && <span style={{ background:'rgba(34,197,94,0.3)', color:'#bbf7d0', fontSize:10, padding:'3px 10px', borderRadius:99 }}>✓ 真實數據</span>}
              </div>
              <h1 style={{ margin:'0 0 6px', color:'#fff', fontSize:22, fontWeight:900, lineHeight:1.3 }}>{disp.title}</h1>
              <p style={{ margin:0, color:'rgba(255,255,255,0.75)', fontSize:13, fontStyle:'italic' }}>{disp.subtitle}</p>
            </div>
          </div>

          {/* Body */}
          <div style={{ maxWidth:680, margin:'0 auto', padding:'28px 20px' }}>
            {/* Translating notice — original (Traditional Chinese) shows until the AI translation lands */}
            {translatingArticle && !tr && lang !== 'zh-HK' && (
              <p style={{ margin:'0 0 16px', fontSize:11, color:'#94a3b8', fontStyle:'italic' }}>🌐 {T[lang]?.translating || 'Translating…'}</p>
            )}
            {/* Teaser */}
            <p style={{ fontSize:15, lineHeight:1.9, color:'#334155', fontWeight:500, margin:'0 0 28px', padding:'0 0 24px', borderBottom:'2px solid #f1f5f9', fontStyle:'italic' }}>{disp.teaser}</p>

            {/* Sections */}
            {disp.sections?.map((sec, i) => (
              <div key={i} style={{ marginBottom:44 }}>
                {sec.heading && <h3 style={{ fontSize:16, fontWeight:900, color:'#0f172a', margin:'0 0 12px', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ width:4, height:18, borderRadius:2, background:moodColor(a.mood), display:'inline-block', flexShrink:0 }}/>
                  {sec.heading}
                  {!sec.no_map && !sec.editor && (
                  <a href={sec.place_id ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sec.heading)}&query_place_id=${sec.place_id}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sec.heading)}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ display:'inline-flex', alignItems:'center', color:'#1a73e8', textDecoration:'none', flexShrink:0 }}
                    title="在 Google Maps 查看">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </a>
                  )}
                </h3>}
                {(() => {
                  const raw = (sec.content || sec.body || '').trimStart();
                  if (!raw) return null;
                  // Render as HTML if it contains HTML markup
                  if (raw.startsWith('<') || raw.includes('</') || raw.includes('<br')) {
                    return <div style={{ fontSize:14, lineHeight:1.9, color:'#374151', margin:'0 0 12px' }} className="ql-content" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(raw)}}/>;
                  }
                  return raw.split(/\n{2,}/).map((para, pi) => (
                    <p key={pi} style={{ fontSize:14, lineHeight:1.9, color:'#374151', margin:'0 0 12px' }}>
                      {para.split('\n').map((line, li, arr) => (
                        <React.Fragment key={li}>{line}{li < arr.length-1 && <br/>}</React.Fragment>
                      ))}
                    </p>
                  ));
                })()}
                {sec.content_source === 'wikipedia' && (
                  <p style={{ margin:'0 0 10px', fontSize:11, color:'#94a3b8' }}>
                    資料來源：<a href={`https://zh.wikipedia.org/wiki/${encodeURIComponent(sec.heading||'')}`} target="_blank" rel="noreferrer" style={{ color:'#94a3b8' }}>維基百科</a>（CC BY-SA 4.0）
                  </p>
                )}
                {/* Info box policy (user, 2026-07-18): only 100%-real info. photoSpot
                    and secret_tip removed; mustTry kept (fact-audited). transport is
                    machine-derived: nearest real station + walk estimate written into
                    sec.transport by enrich_article_transport.js, plus a Maps link.
                    openingHours/priceLevel (2026-08-26): scraped directly off the
                    spot's own Google Maps page by scrape_place_details.js — real
                    Google-sourced text, not AI-generated, same integrity bar as
                    transport. Absent when Google itself doesn't show one (e.g. a
                    public park/beach has no price level) — never backfilled/guessed. */}
                {sec.heading && !sec.editor && (sec.info?.length > 0 || !sec.no_map) && (
                  <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px', marginBottom:10, display:'flex', flexDirection:'column', gap:8 }}>
                    {/* 實用資訊 from the 主題研究 templates: each line is a source-quoted fact (see server_lib/researchTemplates.js). */}
                    {sec.info?.map((it, k) => (
                      <div key={k} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                        <span style={{ fontSize:15, flexShrink:0 }}>{({ '地址':'📍', '地點名稱':'📍', '營業時間':'🕐', '開放時間':'🕐', '門票費用':'💰', '售價':'💰', '交通方式':'🚇', '交通指南':'🚇', '商品名稱':'🏷️', '販售通路':'🛒', '優惠活動':'🎁', '注意事項':'⚠️', '預約':'📅', '公休日':'🚫', '休園日':'🚫', '價位':'💰', '費用':'💰', '票券種類':'🎟️', '訂位':'📞', '預約規則':'📅', '免稅':'🧾', '主要品牌':'🏬', '體驗與預約':'📅', '設施':'🚻', '安全注意':'⚠️', '建議停留時間':'⏱️' })[it.label] || '📌'}</span>
                        <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.7 }}><b>{it.label}：</b>{it.text}</p>
                      </div>
                    ))}
                    {sec.mustTry && (
                      <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                        <span style={{ fontSize:15, flexShrink:0 }}>🎯</span>
                        <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.7 }}><b>必做：</b>{sec.mustTry}</p>
                      </div>
                    )}
                    {/* 拍照位（2026-10-12 恢復）：只顯示主題研究從來源原文核對過的拍照位，
                        即附有 sources 的新格式段落；舊文章的 photoSpot 未查證，仍不顯示。 */}
                    {sec.photoSpot && sec.sources?.length > 0 && (
                      <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                        <span style={{ fontSize:15, flexShrink:0 }}>📸</span>
                        <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.7 }}><b>拍照位：</b>{sec.photoSpot}</p>
                      </div>
                    )}
                    {(sec.openingHours || sec.priceLevel) && (
                      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                        {sec.openingHours && (
                          <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                            <span style={{ fontSize:15, flexShrink:0 }}>🕐</span>
                            <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.7 }}><b>營業時間：</b>{sec.openingHours}</p>
                          </div>
                        )}
                        {sec.priceLevel && (
                          <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                            <span style={{ fontSize:15, flexShrink:0 }}>💰</span>
                            <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.7 }}><b>價位：</b>{sec.priceLevel}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {!sec.no_map && (
                    <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                      <span style={{ fontSize:15, flexShrink:0 }}>🚇</span>
                      <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.7 }}>
                        <b>交通：</b>
                        {sec.transport_station && <>{sec.transport_station}　</>}
                        <a href={sec.place_id
                            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(sec.heading)}&destination_place_id=${sec.place_id}`
                            : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${sec.heading} ${a.city||''}`.trim())}`}
                          target="_blank" rel="noreferrer" style={{ color:'#2563eb', fontWeight:700 }}>
                          在 Google Maps 規劃路線 →
                        </a>
                      </p>
                    </div>
                    )}
                  </div>
                )}
                {sec.images?.length > 0 && (
                  <>
                  <div style={{ display:'grid', gridTemplateColumns:sec.images.length===1?'1fr':'1fr 1fr', gap:8 }}>
                    {sec.images.map((img, j) => (
                      <div key={j} style={{ borderRadius:12, overflow:'hidden', aspectRatio:'16/9', background:'#f1f5f9' }}>
                        <img src={img.url} alt={sec.heading||''} loading="lazy"
                          style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                          onError={e=>e.target.style.display='none'}/>
                      </div>
                    ))}
                  </div>
                  {sec.caption && <p style={{ margin:'6px 0 0', fontSize:12, color:'#64748b', lineHeight:1.6 }}>{sec.caption}</p>}
                  </>
                )}
              </div>
            ))}

            {/* Tips */}
            {disp.tips?.length > 0 && (
              <div style={{ background:`${moodColor(a.mood)}10`, border:`1.5px solid ${moodColor(a.mood)}30`, borderRadius:16, padding:'18px 20px', marginTop:8, marginBottom:28 }}>
                <p style={{ margin:'0 0 12px', fontSize:13, fontWeight:900, color:moodColor(a.mood) }}>✦ 旅行貼士</p>
                {disp.tips.map((tip,i) => (
                  <div key={i} style={{ display:'flex', gap:10, marginBottom:i<disp.tips.length-1?10:0 }}>
                    <span style={{ fontSize:13, fontWeight:900, color:moodColor(a.mood), flexShrink:0 }}>{i+1}.</span>
                    <p style={{ margin:0, fontSize:13, color:'#374151', lineHeight:1.7 }}>{tip}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Best for */}
            {disp.bestFor && (
              <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 16px', marginBottom:28, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:16 }}>👤</span>
                <p style={{ margin:0, fontSize:13, color:'#475569' }}>最適合：<b style={{ color:'#0f172a' }}>{disp.bestFor}</b></p>
              </div>
            )}

            {/* Next article */}
            {next && (
              <div>
                <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', marginBottom:12, textTransform:'uppercase', letterSpacing:1.5 }}>下一篇</p>
                <button onClick={()=>openArticle(next)} style={{ width:'100%', background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:16, overflow:'hidden', cursor:'pointer', textAlign:'left', transition:'all 0.15s', display:'flex', gap:0 }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor=moodColor(next.mood); e.currentTarget.style.transform='translateY(-1px)'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.transform='none'; }}>
                  <div style={{ width:90, flexShrink:0, position:'relative', overflow:'hidden' }}>
                    {next.photoUrl
                      ? <CoverImage src={next.photoUrl} alt={next.title} onErrorHide/>
                      : <ArticlePhoto keyword={next.photoKw} destination={next.destination}/>
                    }
                  </div>
                  <div style={{ padding:'14px', flex:1 }}>
                    <span style={{ background:moodColor(next.mood), color:'#fff', fontSize:10, fontWeight:800, padding:'2px 8px', borderRadius:99, display:'inline-block', marginBottom:6 }}>{next.mood}</span>
                    <p style={{ margin:0, fontSize:13, fontWeight:800, color:'#0f172a', lineHeight:1.4 }}>{next.title}</p>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── Magazine Hub ──────────────────────────────────────────────────────────
    const AC = { info:'#3b82f6', warning:'#f59e0b', danger:'#ef4444' };
    const AB = { info:'#eff6ff', warning:'#fffbeb', danger:'#fef2f2' };
    const SectionHead = ({ label }) => (
      <div style={{ display:'flex', alignItems:'center', gap:10, margin:'28px 0 14px' }}>
        <div style={{ flex:1, height:1, background:'#e2e8f0' }}/>
        <span style={{ fontSize:11, fontWeight:900, color:'#94a3b8', textTransform:'uppercase', letterSpacing:2, whiteSpace:'nowrap' }}>{label}</span>
        <div style={{ flex:1, height:1, background:'#e2e8f0' }}/>
      </div>
    );

    // ── Magazine Hub (new destination-based navigation) ─────────────────────────
    if (intelMode === 'magazine' && !intelData) {
      return (
        <div style={{ minHeight:'100vh', background:'#f8fafc', paddingBottom:120 }}>
          {/* Header */}
          <div style={{ background:'linear-gradient(135deg,#0f172a,#1e3a5f)', padding:'24px 20px 16px' }}>
            <button onClick={()=>setTab('home')} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'rgba(255,255,255,0.8)', borderRadius:10, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
              <ChevronLeft size={14}/> 返回
            </button>
            <h1 style={{ margin:'0 0 3px', color:'#fff', fontSize:24, fontWeight:900 }}>旅遊情報</h1>
            <p style={{ margin:0, color:'rgba(255,255,255,0.45)', fontSize:12 }}>
              AI · 即時搜尋 · YouTube 旅遊達人 · 旅遊故事精選
            </p>
          </div>

          {/* ── Destination region tabs ── */}
          <div style={{ background:'#fff', borderBottom:'1.5px solid #f1f5f9', padding:'10px 16px', display:'flex', gap:8, overflowX:'auto', scrollbarWidth:'none' }}>
            {INTEL_DESTS.map(dest=>{
              const matchCountry = (a) =>
                dest.cities?.includes(a.destination) ||
                dest.countryNames?.some(cn => a.country && (
                  a.country.toLowerCase().includes(cn.toLowerCase()) ||
                  cn.toLowerCase().includes(a.country.toLowerCase())
                ));
              const hasArticles = articles.some(matchCountry);
              if (!dest.active && !hasArticles) return null;
              return (
                <button key={dest.key}
                  onClick={()=>{ setIntelDest(dest.key); setCityFilter('全部'); setArticleFilter('全部'); }}
                  style={{ padding:'7px 14px', borderRadius:99, border:'1.5px solid', borderColor:intelDest===dest.key?'#0f172a':'#e2e8f0', background:intelDest===dest.key?'#0f172a':'#fff', color:intelDest===dest.key?'#fff':'#334155', fontSize:12, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                  {dest.label}
                </button>
              );
            })}
          </div>

          {/* ── City sub-tabs (only when dest selected) ── */}
          {destConf && intelDest && (
            <div style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0', padding:'8px 16px', display:'flex', gap:6, overflowX:'auto', scrollbarWidth:'none' }}>
              {(() => {
                const preset = destConf.cities || [];
                const fromArticles = [...new Set(articles.filter(inRegion).map(a=>a.destination))];
                const allCities = [...new Set([...preset, ...fromArticles])];
                return allCities.map(city=>(
                  <button key={city} onClick={()=>setCityFilter(city)}
                    style={{ padding:'5px 12px', borderRadius:99, border:'1.5px solid', borderColor:cityFilter===city?'#f59e0b':'#e2e8f0', background:cityFilter===city?'#f59e0b':'#fff', color:cityFilter===city?'#0f172a':'#64748b', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                    {city}
                  </button>
                ));
              })()}
            </div>
          )}

          {/* ── Category tabs ── */}
          <div style={{ background:'#fff', borderBottom:'1px solid #f1f5f9', padding:'8px 16px', display:'flex', gap:6, overflowX:'auto', scrollbarWidth:'none' }}>
            {INTEL_CATS.map(cat=>(
              <button key={cat.key} onClick={()=>setArticleFilter(cat.key)}
                style={{ padding:'5px 12px', borderRadius:99, border:'1.5px solid', borderColor:articleFilter===cat.key?cat.color:'#e2e8f0', background:articleFilter===cat.key?cat.color:'#fff', color:articleFilter===cat.key?'#fff':'#64748b', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                {cat.icon} {cat.key}
              </button>
            ))}
          </div>

          {/* generate button removed — articles come from AdminPanel only */}

          <div style={{ padding:'14px 16px 0' }}>
            {/* Loading */}
            {articlesLoading && (
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:12, padding:'10px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
                <Loader2 size={16} color="#3b82f6" style={{ animation:'spin 1s linear infinite', flexShrink:0 }}/>
                <p style={{ margin:0, fontSize:12, fontWeight:700, color:'#1e40af' }}>
                  正在從雲端載入文章…
                </p>
              </div>
            )}

            {/* Empty state */}
            {!articlesLoading && filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:'48px 24px' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>✍️</div>
                <p style={{ margin:'0 0 6px', fontSize:15, fontWeight:800, color:'#0f172a' }}>
                  {intelDest && cityFilter !== '全部' ? `暫無「${cityFilter}」的${articleFilter !== '全部' ? articleFilter : ''}文章` : '暫無文章'}
                </p>
                <p style={{ margin:0, fontSize:13, color:'#64748b' }}>即將推出更多旅遊情報，敬請期待</p>
              </div>
            )}

            {/* Article grid — uniform 2-col cards */}
            {!articlesLoading && filtered.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
                {filtered.map((a,i)=>(
                  <button key={a.id||i} onClick={()=>openArticle(a)}
                    style={{ borderRadius:16, overflow:'hidden', background:'#fff', border:'1.5px solid #e2e8f0', cursor:'pointer', textAlign:'left', transition:'all 0.2s', padding:0, display:'block' }}
                    onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 18px rgba(0,0,0,0.09)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
                    <div style={{ aspectRatio:'3/2', position:'relative', overflow:'hidden', background:'#f1f5f9' }}>
                      {a.photoUrl
                        ? <CoverImage src={a.photoUrl} alt={a.title} onErrorHide/>
                        : <ArticlePhoto keyword={a.photoKw} destination={a.destination}/>
                      }
                      <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)' }}/>
                      <div style={{ position:'absolute', top:7, left:7, display:'flex', gap:4 }}>
                        <span style={{ background:moodColor(a.mood), color:'#fff', fontSize:9, fontWeight:900, padding:'2px 7px', borderRadius:99 }}>{a.mood}</span>
                        {a.hasYoutube && <span style={{ background:'rgba(255,0,0,0.85)', color:'#fff', fontSize:9, fontWeight:800, padding:'2px 6px', borderRadius:99 }}>▶</span>}
                        {a.hasRealData && <span style={{ background:'rgba(34,197,94,0.85)', color:'#fff', fontSize:9, fontWeight:800, padding:'2px 6px', borderRadius:99 }}>✓</span>}
                      </div>
                      <div style={{ position:'absolute', bottom:7, left:8, right:8 }}>
                        <span style={{ fontSize:9, color:'rgba(255,255,255,0.75)' }}>📍 {a.destination}</span>
                      </div>
                    </div>
                    <div style={{ padding:'10px 12px 13px' }}>
                      <p style={{ margin:0, fontSize:14, fontWeight:800, color:'#0f172a', lineHeight:1.45, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{a.title}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{ minHeight:'100vh', background:'#f8fafc', paddingBottom:120 }}>
        {/* Destination Guide Hero / Search */}
        {!intelData ? (
          <div style={{ position:'relative', minHeight:260, background:'linear-gradient(135deg,#0f172a,#1e3a5f)', display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:'24px 20px' }}>
            {intelHero && <CoverImage src={intelHero} alt="旅遊目的地背景圖" opacity={0.35}/>}
            <button onClick={()=>{ setIntelMode('magazine'); setIntelData(null); setIntelHero(null); setIntelQuery(''); }} style={{ position:'absolute', top:20, left:20, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:10, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, zIndex:2 }}>
              <ChevronLeft size={14}/> 旅遊情報
            </button>
            <div style={{ position:'relative', zIndex:2 }}>
              <h1 style={{ margin:'0 0 6px', color:'#fff', fontSize:26, fontWeight:900 }}>目的地查詢</h1>
              <p style={{ margin:'0 0 18px', color:'rgba(255,255,255,0.65)', fontSize:13 }}>簽證、交通、美食、景點一次掌握</p>
              <div style={{ display:'flex', gap:10 }}>
                <input value={intelQuery} onChange={e=>setIntelQuery(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetchIntel(intelQuery)}
                  placeholder='東京、大阪、巴黎、峇里島…'
                  style={{ flex:1, padding:'12px 16px', borderRadius:12, border:'none', fontSize:14, outline:'none' }}/>
                <button onClick={()=>fetchIntel(intelQuery)} disabled={intelLoading||!intelQuery}
                  style={{ background:'#f59e0b', color:'#0f172a', border:'none', borderRadius:12, padding:'12px 20px', fontWeight:900, fontSize:13, cursor:'pointer', opacity:(!intelQuery||intelLoading)?0.5:1 }}>
                  {intelLoading?<Loader2 size={16} style={{ animation:'spin 1s linear infinite' }}/>:'搜尋'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Magazine Hero */
          <div style={{ position:'relative', height:300, background:'linear-gradient(135deg,#0f172a,#1e3a5f)', overflow:'hidden' }}>
            {intelHero && <CoverImage src={intelHero} alt={intelData.destination} opacity={0.5}/>}
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%)' }}/>
            <button onClick={()=>{setIntelData(null);setIntelHero(null);setIntelQuery('');setIntelMode('magazine');}} style={{ position:'absolute', top:20, left:20, background:'rgba(255,255,255,0.15)', backdropFilter:'blur(6px)', border:'none', color:'#fff', borderRadius:10, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, zIndex:2 }}>
              <ChevronLeft size={14}/> 返回
            </button>
            <div style={{ position:'absolute', bottom:24, left:20, right:20, zIndex:2 }}>
              <p style={{ margin:'0 0 4px', color:'rgba(255,255,255,0.6)', fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:2 }}>{intelData.country}</p>
              <h1 style={{ margin:'0 0 6px', color:'#fff', fontSize:32, fontWeight:900, lineHeight:1.1 }}>{intelData.destination}</h1>
              <p style={{ margin:0, color:'rgba(255,255,255,0.75)', fontSize:14, fontStyle:'italic' }}>{intelData.tagline}</p>
            </div>
          </div>
        )}

        {/* Hot picks */}
        {!intelData && !intelLoading && (
          <div style={{ padding:'20px 20px 0' }}>
            <p style={{ fontSize:11, fontWeight:900, color:'#94a3b8', marginBottom:12, textTransform:'uppercase', letterSpacing:1.5 }}>熱門目的地</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {['東京','大阪','首爾','曼谷','峇里島','新加坡','倫敦','巴黎','紐約','杜拜','台北','京都'].map(d=>(
                <button key={d} onClick={()=>{ setIntelQuery(d); fetchIntel(d); }}
                  style={{ background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:99, padding:'8px 16px', fontSize:13, fontWeight:700, color:'#334155', cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {intelLoading && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'#94a3b8' }}>
            <Loader2 size={36} style={{ animation:'spin 1s linear infinite', marginBottom:14 }}/>
            <p style={{ margin:0, fontSize:14, fontWeight:600 }}>正在生成旅遊情報…</p>
            <p style={{ margin:'6px 0 0', fontSize:12, color:'#cbd5e1' }}>通常需要 10–15 秒</p>
          </div>
        )}

        {intelData && !intelLoading && (
          <div style={{ maxWidth:720, margin:'0 auto', padding:'0 16px' }}>
            {/* Quick info pills */}
            {intelData.quickInfo && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, padding:'20px 0 4px' }}>
                {[
                  { label:'最佳時間', val:intelData.quickInfo.bestTime },
                  { label:'貨幣', val:intelData.quickInfo.currency },
                  { label:'簽證', val:intelData.quickInfo.visa },
                  { label:'時區', val:intelData.quickInfo.timezone },
                  { label:'語言', val:intelData.quickInfo.language },
                  { label:'每日預算', val:intelData.quickInfo.budget },
                ].map((item,i)=>item.val?(
                  <div key={i} style={{ background:'#fff', borderRadius:12, padding:'12px', border:'1px solid #e2e8f0', textAlign:'center' }}>
                    <p style={{ margin:'0 0 4px', fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:1 }}>{item.label}</p>
                    <p style={{ margin:0, fontSize:12, fontWeight:800, color:'#0f172a', lineHeight:1.3 }}>{item.val}</p>
                  </div>
                ):null)}
              </div>
            )}

            {/* Alerts */}
            {intelData.alerts?.length > 0 && (
              <div style={{ marginTop:16 }}>
                {intelData.alerts.map((a,i)=>(
                  <div key={i} style={{ background:AB[a.level]||'#f8fafc', borderLeft:`4px solid ${AC[a.level]||'#94a3b8'}`, borderRadius:10, padding:'12px 14px', marginBottom:8 }}>
                    <p style={{ margin:'0 0 3px', fontSize:13, fontWeight:800, color:AC[a.level]||'#334155' }}>{a.title}</p>
                    <p style={{ margin:0, fontSize:12, color:'#475569' }}>{a.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Intro */}
            {intelData.intro && (
              <p style={{ fontSize:14, lineHeight:1.85, color:'#334155', margin:'20px 0 0', padding:'0 0 4px' }}>{intelData.intro}</p>
            )}

            {/* Transport */}
            {intelData.transport && (
              <>
                <SectionHead label='交通攻略'/>
                <div style={{ background:'#fff', borderRadius:14, border:'1px solid #e2e8f0', overflow:'hidden' }}>
                  {intelData.transport.fromHK?.length > 0 && (
                    <div style={{ padding:'14px 16px', borderBottom:'1px solid #f1f5f9' }}>
                      <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:800, color:'#0f172a' }}>✈️ 如何前往</p>
                      {intelData.transport.fromHK.map((t,i)=><p key={i} style={{ margin:'0 0 4px', fontSize:12, color:'#475569' }}>• {t}</p>)}
                    </div>
                  )}
                  {intelData.transport.local?.length > 0 && (
                    <div style={{ padding:'14px 16px', borderBottom: intelData.transport.tip?'1px solid #f1f5f9':'none' }}>
                      <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:800, color:'#0f172a' }}>🚇 當地交通</p>
                      {intelData.transport.local.map((t,i)=><p key={i} style={{ margin:'0 0 4px', fontSize:12, color:'#475569' }}>• {t}</p>)}
                    </div>
                  )}
                  {intelData.transport.tip && (
                    <div style={{ padding:'12px 16px', background:'#fffbeb' }}>
                      <p style={{ margin:0, fontSize:12, color:'#92400e', fontWeight:600 }}>💡 {intelData.transport.tip}</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Attractions */}
            {intelData.attractions?.length > 0 && (
              <>
                <SectionHead label='必遊景點'/>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {intelData.attractions.map((a,i)=>(
                    <div key={i} style={{ position:'relative' }}>
                      <IntelAttractionCard name={a.name} city={intelData.destination} desc={a.desc} tip={a.tip} category={a.category} index={i} nameEn={a.photoKw||a.nameEn||''}/>
                      {(a.price||a.openHours) && (
                        <div style={{ display:'flex', gap:6, padding:'6px 12px 10px', background:'#fff', borderTop:'1px solid #f1f5f9' }}>
                          {a.openHours && <span style={{ fontSize:10, color:'#64748b' }}>🕐 {a.openHours}</span>}
                          {a.price && <span style={{ fontSize:10, fontWeight:700, color:'#f59e0b' }}>{a.price}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Food */}
            {intelData.food?.length > 0 && (
              <>
                <SectionHead label='美食指南'/>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {intelData.food.map((f,i)=>(
                    <div key={i} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', border:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                          <span style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>{f.name}</span>
                          <span style={{ fontSize:10, background:'#f1f5f9', color:'#64748b', borderRadius:6, padding:'2px 7px', fontWeight:700 }}>{f.type}</span>
                        </div>
                        <p style={{ margin:'0 0 4px', fontSize:12, color:'#475569' }}>{f.desc}</p>
                        {f.where && <p style={{ margin:0, fontSize:11, color:'#94a3b8' }}>📍 {f.where}</p>}
                      </div>
                      {f.price && <span style={{ fontSize:13, fontWeight:900, color:'#f59e0b', whiteSpace:'nowrap', flexShrink:0 }}>{f.price}</span>}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Neighborhoods */}
            {intelData.neighborhoods?.length > 0 && (
              <>
                <SectionHead label='特色地區'/>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {intelData.neighborhoods.map((n,i)=>(
                    <div key={i} style={{ background:'#fff', borderRadius:14, padding:'14px', border:'1px solid #e2e8f0' }}>
                      <p style={{ margin:'0 0 4px', fontSize:14, fontWeight:800, color:'#0f172a' }}>{n.name}</p>
                      <p style={{ margin:'0 0 6px', fontSize:12, color:'#64748b' }}>{n.vibe}</p>
                      {n.for && <p style={{ margin:0, fontSize:11, color:'#f59e0b', fontWeight:700 }}>適合 {n.for}</p>}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Shopping */}
            {intelData.shopping?.length > 0 && (
              <>
                <SectionHead label='購物指南'/>
                {intelData.shopping.map((s,i)=>(
                  <div key={i} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', border:'1px solid #e2e8f0', marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                      <span style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>{s.area}</span>
                      {s.for && <span style={{ fontSize:11, color:'#64748b', textAlign:'right', maxWidth:'55%' }}>{s.for}</span>}
                    </div>
                    <p style={{ margin:0, fontSize:12, color:'#475569' }}>{s.desc}</p>
                  </div>
                ))}
              </>
            )}

            {/* Day trips */}
            {intelData.dayTrips?.length > 0 && (
              <>
                <SectionHead label='一日遊推薦'/>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {intelData.dayTrips.map((d,i)=>(
                    <div key={i} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', border:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <p style={{ margin:'0 0 3px', fontSize:14, fontWeight:800, color:'#0f172a' }}>{d.dest}</p>
                        <p style={{ margin:0, fontSize:12, color:'#64748b' }}>{d.how} · {d.highlight}</p>
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, color:'#f59e0b', whiteSpace:'nowrap', flexShrink:0 }}>{d.duration}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Tips */}
            {intelData.tips?.length > 0 && (
              <>
                <SectionHead label='旅行貼士'/>
                <div style={{ background:'#fff', borderRadius:14, padding:'16px', border:'1px solid #e2e8f0', marginBottom:12 }}>
                  {intelData.tips.map((tip,i)=>(
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:i<intelData.tips.length-1?10:0 }}>
                      <span style={{ fontSize:13, fontWeight:900, color:'#f59e0b', flexShrink:0 }}>{i+1}.</span>
                      <p style={{ margin:0, fontSize:13, color:'#334155', lineHeight:1.6 }}>{tip}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            <button onClick={()=>{setIntelData(null);setIntelHero(null);setIntelQuery('');}}
              style={{ width:'100%', background:'#f1f5f9', border:'none', borderRadius:12, padding:'14px', fontSize:13, fontWeight:700, color:'#64748b', cursor:'pointer', marginBottom:20 }}>
              🔍 查詢其他目的地
            </button>
          </div>
        )}
      </div>
    );
  };

  // DealsPage — imported from components/pages/

  // FeedbackPage, ContactPage — imported from components/pages/

  // ── RENDERS ─────────────────────────────────────────────────────────────────

  // HOME
  const renderHome = () => (
    <div style={S.page}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.45}} @keyframes spin{to{transform:rotate(360deg)}} ::-webkit-scrollbar{display:none}`}</style>

      {/* Geolocation banner */}
      {geo.status==='ok' && geoCity && (
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', borderRadius:14, padding:'10px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
          <MapPin size={14} color="#f59e0b"/>
          <p style={{ fontSize:12, color:'rgba(255,255,255,0.85)', margin:0, fontWeight:700 }}>
            {lang==='en'
              ? `Showing results near ${geoCity}${geoDistrict ? `, ${geoDistrict}` : ''}`
              : lang==='ja'
              ? `${geoCity}${geoDistrict ? ` · ${geoDistrict}` : ''}周辺の情報を表示中`
              : lang==='ko'
              ? `${geoCity}${geoDistrict ? ` · ${geoDistrict}` : ''} 근처 정보 표시 중`
              : `正在顯示 ${geoCity}${geoDistrict ? ` · ${geoDistrict}` : ''} 附近資訊`}
          </p>
        </div>
      )}
      {geo.status==='denied' && (
        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:14, padding:'10px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
          <MapPin size={14} color="#f59e0b"/>
          <p style={{ fontSize:12, color:'#92400e', margin:0, fontWeight:700 }}>
            {(lang==='en'||lang==='ja'||lang==='ko') ? 'Location access denied — showing Hong Kong' : '未獲取位置權限 — 顯示香港資訊'}
          </p>
        </div>
      )}

      {/* City Banner */}
      <div style={{ marginBottom:16 }}>
        <CityBanner cityKey={CITY_EN_MAP[homeCity] || homeCity} displayCity={homeCity} displayCountry={homeCountry} displayDistrict={homeDistrict || geoDistrict} height={320}>
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.15)', backdropFilter:'blur(8px)', borderRadius:10, padding:'6px 12px' }}>
              <Clock size={13} color="white"/>
              <span style={{ color:'white', fontSize:13, fontWeight:900, fontVariantNumeric:'tabular-nums' }}>{localTime}</span>
            </div>
          </div>
          {homeWeather?.length>0 && <WeatherStrip data={homeWeather}/>}
        </CityBanner>
      </div>

      {/* City intro card */}
      {(homeInfo?.intro || homeInfoLoading) && (
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e3a5f)', borderRadius:18, padding:'18px 20px', margin:'0 0 12px', color:'white' }}>
          {homeInfoLoading ? (
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Loader2 size={16} color="#f59e0b" style={{ animation:'spin 1s linear infinite' }}/>
              <span style={{ fontSize:12, color:'rgba(255,255,255,0.5)' }}>載入城市簡介…</span>
            </div>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <span style={{ fontSize:18 }}>✈️</span>
                <span style={{ fontSize:11, fontWeight:900, color:'#f59e0b', textTransform:'uppercase', letterSpacing:1.5 }}>{homeCity} · {homeCountry}</span>
              </div>
              <p style={{ margin:0, fontSize:14, fontWeight:500, color:'rgba(255,255,255,0.88)', lineHeight:1.8 }}>{cleanAIText(homeInfo.intro||'')}</p>
            </>
          )}
        </div>
      )}
      {/* Unlike doSearch's result page, this used to fail silently: both AI
          providers down (or one flaky) left homeInfo null forever with no
          error shown and no way to retry short of a full page reload. */}
      {!homeInfoLoading && !homeInfo && (
        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:12 }}>
          <p style={{ margin:0, fontSize:12, color:'#92400e', fontWeight:700 }}>
            {(lang==='en'||lang==='ja'||lang==='ko') ? 'City info unavailable. Please retry.' : '城市資訊載入失敗，請重試。'}
          </p>
          <button onClick={()=>loadHomeInfo(homeCity, homeCountry, lang, currency)} style={{ background:'#f59e0b', color:'#0f172a', border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:900, cursor:'pointer', whiteSpace:'nowrap' }}>
            {(lang==='en'||lang==='ja'||lang==='ko') ? 'Retry' : '重試'}
          </button>
        </div>
      )}
      {/* Info cards */}
      <InfoCards cityInfo={homeInfo} t={t} loading={homeInfoLoading}/>
      <TaxRefundCard country={homeCountry} city={homeCity} lang={lang}/>
      <TravelExtras info={homeInfo} weather={homeWeather} lang={lang}/>

      {/* eSIM affiliate banner */}
      <div style={{ marginBottom:12 }}>
        {(() => {
          const esimSlug = ESIM_SLUGS[homeCountry] || null;
          const esimLink = sailyLink(esimSlug);
          const label = lang==='en'
            ? `Travelling to ${homeCountry}? Get your eSIM ready before you go`
            : lang==='ja' ? `${homeCountry}へ旅行？出発前にeSIMを準備しよう`
            : lang==='ko' ? `${homeCountry} 여행? 출발 전 eSIM을 준비하세요`
            : `前往${homeCountry}？提前買好 eSIM，落機即用`;
          const sub = lang==='en'
            ? `Saily · Local & Regional plans · Instant activation · From USD 3.99`
            : lang==='ja' ? `Saily · ローカル＆地域プラン · 即時有効化 · USD 3.99〜`
            : lang==='ko' ? `Saily · 현지 및 지역 플랜 · 즉시 활성화 · USD 3.99부터`
            : `Saily · 本地及地區方案 · 即時激活 · 約 HK$31 起`;
          const btn = lang==='en' ? 'View Plans →' : lang==='ja' ? 'プランを見る →' : lang==='ko' ? '플랜 보기 →' : '查看方案 →';
          return (
            <a href={esimLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', display:'block' }}>
              <div style={{ background:'linear-gradient(135deg,#0f172a,#1a2e50)', border:'1.5px solid #334155', borderRadius:16, padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
                  <span style={{ fontSize:28, flexShrink:0 }}>📱</span>
                  <div style={{ minWidth:0 }}>
                    <p style={{ margin:0, fontSize:13, fontWeight:800, color:'white', lineHeight:1.4 }}>{label}</p>
                    <p style={{ margin:'2px 0 0', fontSize:11, color:'rgba(255,255,255,0.55)', lineHeight:1.4 }}>{sub}</p>
                    <p style={{ margin:'4px 0 0', fontSize:11, fontWeight:800, color:'#fbbf24', lineHeight:1.4 }}>🎟️ {sailyCouponLabel(lang)}</p>
                  </div>
                </div>
                <div style={{ background:'#f59e0b', color:'#0f172a', borderRadius:10, padding:'8px 14px', fontSize:12, fontWeight:900, whiteSpace:'nowrap', flexShrink:0 }}>{btn}</div>
              </div>
            </a>
          );
        })()}
      </div>

      {/* Radical Storage — homepage entry card, links straight out to the affiliate URL */}
      <RadicalStorageCard lang={lang}/>

      <div style={{ height:8 }}/>

      {/* Nearby attractions — temporarily hidden pending better data source */}

      {/* Hot attractions — district filter + grid */}
      {homeLoading ? (
        <div style={{ textAlign:'center', padding:'20px 0' }}>
          <Loader2 size={28} color="#f59e0b" style={{ animation:'spin 1s linear infinite' }}/>
          <p style={{ color:'#94a3b8', fontSize:13, marginTop:10 }}>{t.loading}</p>
        </div>
      ) : (() => {
          const managed = getManagedForCity(homeCity);
          const mergedHot = [...managed.hot, ...homeAttractions.filter(a => !managed.hot.some(m => m.name === a.name))];
          const districts = homeDistricts;
          const cityEnHome = CITY_EN_MAP[homeCity] || homeCity;
          const dbFiltered = homeDistrict ? mergedHot.filter(a => matchesDistrict(a.district, homeDistrict)) : mergedHot;
          const apiKey     = homeDistrict ? `${cityEnHome}:${homeDistrict}` : '';
          const apiPlaces  = apiKey ? districtResults[apiKey] : null;
          // Thin district data (older rows have district=null, enrichment
          // mid-way) gets topped up with the API district search instead of
          // showing e.g. 3 nature-only cards for Ikebukuro.
          const apiMapped = apiPlaces ? apiPlaces
            .filter(p => !dbFiltered.some(a => a.name === p.name))
            .map(p=>({ name:p.name, desc:p.address, category:p.category, rating:p.rating, imageUrl:p.imageUrl, photoKw:p.name, district:homeDistrict })) : [];
          const filteredHot = dbFiltered.length >= 8 ? dbFiltered : [...dbFiltered, ...apiMapped];
          const isDistrictLoading = homeDistrict && districtLoading && apiPlaces === null && dbFiltered.length === 0;
          const homeQ = homeAttrQuery.trim().toLowerCase();
          const homeAttrMatches = homeQ ? mergedHot.filter(a =>
            (a.name||'').toLowerCase().includes(homeQ) ||
            (localName(a,lang)||'').toLowerCase().includes(homeQ) ||
            (a.district||'').toLowerCase().includes(homeQ)
          ).slice(0, 30) : [];
          const homeAttrSearchBar = (
            <div style={{ marginTop:4, marginBottom:10 }}>
              <div style={{ position:'relative' }}>
                <Search size={15} color="#94a3b8" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
                <input value={homeAttrQuery} onChange={e=>setHomeAttrQuery(e.target.value)}
                  placeholder={lang==='en'?'Search attractions or districts…':'搜尋景點或地區…'}
                  style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:12, padding:'10px 34px 10px 36px', fontSize:13, fontWeight:600, outline:'none', color:'#1e293b', boxSizing:'border-box' }}
                  onFocus={e=>e.target.style.borderColor='#f59e0b'} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                {homeAttrQuery && (
                  <button onClick={()=>setHomeAttrQuery('')} title="清除"
                    style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:16, lineHeight:1, padding:2 }}>×</button>
                )}
              </div>
              {homeQ && (
                <div style={{ marginTop:8, background:'white', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden' }}>
                  {homeAttrMatches.length === 0 ? (
                    <p style={{ margin:0, padding:'14px', fontSize:12, color:'#94a3b8', textAlign:'center' }}>{lang==='en'?'No matches':'找不到相關景點'}</p>
                  ) : homeAttrMatches.map((a,i) => (
                    <button key={i} onClick={()=>{setSelAttraction({...a,cityKey:homeCity});fetchAttractionDetail(a,homeCity);setHomeAttrQuery('');}}
                      style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px', background:'none', border:'none', borderTop:i>0?'1px solid #f1f5f9':'none', cursor:'pointer', textAlign:'left' }}>
                      {a.district && <span style={{ flexShrink:0, background:'#fef3c7', color:'#92400e', borderRadius:8, padding:'2px 8px', fontSize:10, fontWeight:800 }}>{a.district}</span>}
                      <span style={{ fontSize:13, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{localName(a,lang)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
          // 地圖只顯示已核實嘅地區，少過兩個就唔出地圖（一粒圖釘睇唔到分佈）
          const homeMapPins = getDistrictPins(cityEnHome, districts);
          const homeDistrictFilter = districts.length > 0 ? (
            <div style={{ marginTop:4, marginBottom:10 }}>
              <p style={{ fontSize:12, fontWeight:700, color:'#64748b', margin:'0 0 8px', textTransform:'uppercase', letterSpacing:1 }}>{t.districtFilterHome}</p>
              {homeMapPins.length >= 2 && (
                <div style={{ marginBottom:10 }}>
                  <DistrictMap pins={homeMapPins} selected={homeDistrict} onSelect={setHomeDistrict} lang={lang}
                    labelOf={(d)=>districtLabels[d]?.[lang] || d}/>
                </div>
              )}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {districts.map(d=>(
                  <button key={d} onClick={()=>setHomeDistrict(d)} style={{ padding:'5px 14px', borderRadius:20, border:'1.5px solid', borderColor:homeDistrict===d?'#f59e0b':'#e2e8f0', background:homeDistrict===d?'#fef3c7':'white', color:homeDistrict===d?'#92400e':'#475569', fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}>{districtLabels[d]?.[lang] || d}</button>
                ))}
              </div>
            </div>
          ) : null;
          return (
            <>
              <RecommendCard attractions={filteredHot} lang={lang} gps={geo.gps} status={geo.status} onRequestGps={geo.request} onSelect={(a)=>{setSelAttraction({...a,cityKey:homeCity});fetchAttractionDetail(a,homeCity);}}/>
              {homeAttrSearchBar}
              {homeDistrictFilter}
              {isDistrictLoading ? (
                <div style={{ textAlign:'center', padding:'40px 0', color:'#94a3b8', fontSize:13 }}>搜尋 {homeDistrict} 景點中…</div>
              ) : (
                <>
                  <RainyDayPicks weather={homeWeather} attractions={filteredHot} lang={lang}
                    onSelect={(a)=>{setSelAttraction({...a,cityKey:homeCity});fetchAttractionDetail(a,homeCity);}}/>
                  <ThemeEntryStrip lang={lang} attractions={filteredHot} activeTheme={homeActiveTheme}
                    onThemeSelect={(k)=>setHomeActiveTheme(prev=>prev===k?null:k)}/>
                  {homeActiveTheme && (
                    <CategorySection catKey={homeActiveTheme} attractions={filteredHot} lang={lang} t={t} cityKey={homeCity}
                      favorites={favorites} onToggleFav={toggleFav}
                      onSelect={(a,ck)=>{setSelAttraction({...a,cityKey:ck});fetchAttractionDetail(a,ck);}} districtKey={homeDistrict}/>
                  )}
                </>
              )}
              {/* 深度體驗 — follows the same district filter as the hot grid
                  above; a district with no deep picks just shows nothing
                  (DeepStrip returns null when its list is empty). */}
              <DeepStrip attractions={homeDistrict ? managed.deep.filter(a => matchesDistrict(a.district, homeDistrict)) : managed.deep} cityKey={homeCity} t={t} favorites={favorites} onToggleFav={toggleFav} onSelect={(a,ck)=>{setSelAttraction({...a,cityKey:ck});fetchAttractionDetail(a,ck);}} districtKey={homeDistrict}/>
            </>
          );
        })()}

      {/* Promo codes below hot attractions */}
      {!homeLoading && <RelevantCoupons city={homeCity} country={homeCountry} lang={lang} hideMerchants={['saily','radicalstorage']}/>}

      {/* Klook dynamic widget */}
      <div style={{ marginTop:24 }}>
        <p style={{ fontSize:17, fontWeight:900, color:'#0f172a', margin:'0 0 12px' }}>{t.deals}</p>
        <KlookDynamicWidget/>
      </div>
    </div>
  );

  // SEARCH
  const renderSearch = () => (
    <div style={S.page}>
      <p style={{ fontSize:22, fontWeight:900, color:'#0f172a', margin:'0 0 16px' }}>{t.search}</p>


      {/* Progressive search: pick region → country → city, each tap advances automatically */}
      <div style={{ ...S.card, display:'flex', flexDirection:'column', gap:14, marginBottom:16 }}>
        {/* Breadcrumb of picks so far — click one to jump back and change it */}
        {(srRegion || srCountry || srCity) && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {srRegion && (
              <button onClick={()=>{setSrRegion('');setSrCountry('');setSrCity('');setSrCountries([]);setSrCities([]);setSrCityCustom('');}}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20, border:'1.5px solid #f59e0b', background:'#fffbeb', color:'#92400e', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                <Globe size={12}/>{t.regions[srRegion]||srRegion}<span style={{ opacity:0.6, marginLeft:2 }}>×</span>
              </button>
            )}
            {srCountry && (
              <button onClick={()=>{setSrCountry('');setSrCity('');setSrCities([]);setSrCityCustom('');}}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20, border:'1.5px solid #f59e0b', background:'#fffbeb', color:'#92400e', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                <Compass size={12}/>{srCntMap[srCountry]||srCountry}<span style={{ opacity:0.6, marginLeft:2 }}>×</span>
              </button>
            )}
            {srCity && (
              <button onClick={()=>{setSrCity('');setSrCityCustom('');}}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20, border:'1.5px solid #f59e0b', background:'#fffbeb', color:'#92400e', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                <MapPin size={12}/>{srCityMap[srCity]||srCity}<span style={{ opacity:0.6, marginLeft:2 }}>×</span>
              </button>
            )}
          </div>
        )}

        {/* Step 1: region */}
        {!srRegion && (
          <div>
            <p style={S.label}>{t.region}</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {REGIONS.map(r=>(
                <button key={r} onClick={()=>{setSrRegion(r);fetchSrCountries(r,lang);}}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  <Globe size={14} color="#f59e0b"/>{t.regions[r]||r}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: country */}
        {srRegion && !srCountry && (
          <div>
            <p style={S.label}>{t.country}</p>
            {loadingGeo ? (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', color:'#94a3b8', fontSize:13 }}>
                <Loader2 size={14} style={{ animation:'spin 1s linear infinite' }}/>{t.loading}
              </div>
            ) : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {srCountries.map(c=>(
                  <button key={c} onClick={()=>{setSrCountry(c);fetchSrCities(c,lang);}}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                    <Compass size={14} color="#f59e0b"/>{srCntMap[c]||c}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: city */}
        {srRegion && srCountry && !srCity && (
          <div>
            <p style={S.label}>{t.city}</p>
            {loadingGeo ? (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', color:'#94a3b8', fontSize:13 }}>
                <Loader2 size={14} style={{ animation:'spin 1s linear infinite' }}/>{t.loading}
              </div>
            ) : (
              <>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {srCities.map(c=>(
                    <button key={c} onClick={()=>setSrCity(c)}
                      style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                      <MapPin size={14} color="#f59e0b"/>{srCityMap[c]||c}
                    </button>
                  ))}
                </div>
                <input value={srCityCustom} onChange={e=>setSrCityCustom(e.target.value)}
                  onKeyDown={e=>{ if(e.key==='Enter' && srCityCustom.trim()) setSrCity(srCityCustom.trim()); }}
                  placeholder={lang==='en'?'Or type another city…':'或輸入其他城市…'}
                  style={{ marginTop:10, width:'100%', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'9px 12px', fontSize:13, fontWeight:600, outline:'none', color:'#1e293b', boxSizing:'border-box' }}/>
              </>
            )}
          </div>
        )}

        {/* City picked: shows spinner while auto-search kicks in (button kept for retry) */}
        {srRegion && srCountry && srCity && (
          <button onClick={doSearch} disabled={srLoading}
            style={{ ...S.btnAmber, justifyContent:'center' }}>
            {srLoading ? <><Loader2 size={16} style={{ animation:'spin 1s linear infinite' }}/>{t.searching}</> : <><Search size={16}/>{t.searchBtn}</>}
          </button>
        )}
      </div>

      {/* Search error */}
      {srError && !srLoading && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'flex-start', gap:10 }}>
          <AlertTriangle size={16} color="#dc2626" style={{ flexShrink:0, marginTop:1 }}/>
          <div>
            <p style={{ margin:'0 0 4px', fontSize:13, fontWeight:900, color:'#dc2626' }}>
              {(lang==='en'||lang==='ja'||lang==='ko')?'Search failed — please try again in a moment':'搜尋失敗，請稍後再試'}
            </p>
            <p style={{ margin:0, fontSize:11, color:'#ef4444' }}>
              {(lang==='en'||lang==='ja'||lang==='ko')?'AI service is temporarily busy (rate limit). Wait 30 seconds and retry.':'AI 服務暫時繁忙（超出每分鐘限額），等待 30 秒後重試。'}
            </p>
            <button onClick={doSearch} style={{ marginTop:8, background:'#dc2626', color:'white', border:'none', borderRadius:8, padding:'6px 14px', fontSize:12, fontWeight:900, cursor:'pointer' }}>
              {(lang==='en'||lang==='ja'||lang==='ko')?'Retry':'重試'}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {srResult && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <CityBanner cityKey={srResult.city} displayCity={srResult.cityDisplay} displayCountry={srResult.countryDisplay} displayDistrict={srDistrict} height={280}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.15)', backdropFilter:'blur(8px)', borderRadius:10, padding:'6px 12px' }}>
                <Clock size={13} color="white"/>
                <span style={{ color:'white', fontSize:13, fontWeight:900 }}>{destTime || localTime}</span>
              </div>
            </div>
            <WeatherStrip data={srResult.weather}/>
          </CityBanner>
          {srResult.info?.intro && (
            <div style={{ background:'linear-gradient(135deg,#0f172a,#1e3a5f)', borderRadius:18, padding:'18px 20px', color:'white' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <span style={{ fontSize:18 }}>✈️</span>
                <span style={{ fontSize:11, fontWeight:900, color:'#f59e0b', textTransform:'uppercase', letterSpacing:1.5 }}>{srResult.cityDisplay} · {srResult.countryDisplay}</span>
              </div>
              <p style={{ margin:0, fontSize:14, fontWeight:500, color:'rgba(255,255,255,0.88)', lineHeight:1.8 }}>{cleanAIText(srResult.info?.intro||'')}</p>
            </div>
          )}
          {(() => {
            const info = srResult.info;
            // Still mid-retry (fetchD1 can take a few seconds across up to 3
            // attempts) — show a loading skeleton, not a false "failed" box.
            if (srResult.infoLoading) return <InfoCards cityInfo={null} t={t} loading={true}/>;
            const hasData = info && ['currency','voltage','transport','tips'].some(k => info[k]);
            if (!hasData) return (
              <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
                <p style={{ margin:0, fontSize:12, color:'#92400e', fontWeight:700 }}>
                  {(lang==='en'||lang==='ja'||lang==='ko') ? 'City info unavailable. Please retry.' : '城市資訊載入失敗，請重試。'}
                </p>
                <button onClick={doSearch} style={{ background:'#f59e0b', color:'#0f172a', border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:900, cursor:'pointer', whiteSpace:'nowrap' }}>
                  {(lang==='en'||lang==='ja'||lang==='ko') ? 'Retry' : '重試'}
                </button>
              </div>
            );
            return <InfoCards cityInfo={info} t={t} loading={false}/>;
          })()}
          <TaxRefundCard country={srResult.country} city={srResult.city} lang={lang}/>
          <TravelExtras info={srResult.info} weather={srResult.weather} lang={lang}/>
          {/* eSIM affiliate banner */}
          {(() => {
            const esimSlug = ESIM_SLUGS[srResult.country] || null;
            const esimLink = sailyLink(esimSlug);
            const countryLabel = srResult.countryDisplay || srResult.country;
            const label = lang==='en'
              ? `Travelling to ${countryLabel}? Get your eSIM ready before you go`
              : lang==='ja' ? `${countryLabel}へ旅行？出発前にeSIMを準備しよう`
              : lang==='ko' ? `${countryLabel} 여행? 출발 전 eSIM을 준비하세요`
              : `前往${countryLabel}？提前買好 eSIM，落機即用`;
            const sub = lang==='en'
              ? `Saily · Local & Regional plans · Instant activation · From USD 3.99`
              : lang==='ja' ? `Saily · ローカル＆地域プラン · 即時有効化 · USD 3.99〜`
              : lang==='ko' ? `Saily · 현지 및 지역 플랜 · 즉시 활성화 · USD 3.99부터`
              : `Saily · 本地及地區方案 · 即時激活 · 約 HK$31 起`;
            const btn = lang==='en' ? 'View Plans →' : lang==='ja' ? 'プランを見る →' : lang==='ko' ? '플랜 보기 →' : '查看方案 →';
            return (
              <a href={esimLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', display:'block' }}>
                <div style={{ background:'linear-gradient(135deg,#0f172a,#1a2e50)', border:'1.5px solid #334155', borderRadius:16, padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
                    <span style={{ fontSize:28, flexShrink:0 }}>📱</span>
                    <div style={{ minWidth:0 }}>
                      <p style={{ margin:0, fontSize:13, fontWeight:800, color:'white', lineHeight:1.4 }}>{label}</p>
                      <p style={{ margin:'2px 0 0', fontSize:11, color:'rgba(255,255,255,0.55)', lineHeight:1.4 }}>{sub}</p>
                      <p style={{ margin:'4px 0 0', fontSize:11, fontWeight:800, color:'#fbbf24', lineHeight:1.4 }}>🎟️ {sailyCouponLabel(lang)}</p>
                    </div>
                  </div>
                  <div style={{ background:'#f59e0b', color:'#0f172a', borderRadius:10, padding:'8px 14px', fontSize:12, fontWeight:900, whiteSpace:'nowrap', flexShrink:0 }}>{btn}</div>
                </div>
              </a>
            );
          })()}
          <div style={{ height:12 }}/>
          <RadicalStorageCard lang={lang}/>
          {/* Hot attractions — district filter + grid */}
          {(() => {
            const managed = getManagedForCity(srResult.cityDisplay || srResult.city);
            const mergedHot = [...managed.hot, ...(srResult.attractions||[]).filter(a => !managed.hot.some(m => m.name === a.name))];
            const districts = srDistricts;
            const srCityDisplay = srResult.cityDisplay || srResult.city;
            const cityEnSr   = CITY_EN_MAP[srCityDisplay] || srCityDisplay;
            const dbFilteredSr = srDistrict ? mergedHot.filter(a => matchesDistrict(a.district, srDistrict)) : mergedHot;
            const apiKeySr   = srDistrict ? `${cityEnSr}:${srDistrict}` : '';
            const apiPlacesSr = apiKeySr ? districtResults[apiKeySr] : null;
            const apiMappedSr = apiPlacesSr ? apiPlacesSr
              .filter(p => !dbFilteredSr.some(a => a.name === p.name))
              .map(p=>({ name:p.name, desc:p.address, category:p.category, rating:p.rating, imageUrl:p.imageUrl, photoKw:p.name, district:srDistrict })) : [];
            const filteredHot = dbFilteredSr.length >= 8 ? dbFilteredSr : [...dbFilteredSr, ...apiMappedSr];
            const isSrDistrictLoading = srDistrict && districtLoading && apiPlacesSr === null && dbFilteredSr.length === 0;
            const srQ = srAttrQuery.trim().toLowerCase();
            const srAttrMatches = srQ ? mergedHot.filter(a =>
              (a.name||'').toLowerCase().includes(srQ) ||
              (localName(a,lang)||'').toLowerCase().includes(srQ) ||
              (a.district||'').toLowerCase().includes(srQ)
            ).slice(0, 30) : [];
            const srAttrSearchBar = (
              <div style={{ marginTop:4, marginBottom:10 }}>
                <div style={{ position:'relative' }}>
                  <Search size={15} color="#94a3b8" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
                  <input value={srAttrQuery} onChange={e=>setSrAttrQuery(e.target.value)}
                    placeholder={lang==='en'?'Search attractions or districts…':'搜尋景點或地區…'}
                    style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:12, padding:'10px 34px 10px 36px', fontSize:13, fontWeight:600, outline:'none', color:'#1e293b', boxSizing:'border-box' }}
                    onFocus={e=>e.target.style.borderColor='#f59e0b'} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  {srAttrQuery && (
                    <button onClick={()=>setSrAttrQuery('')} title="清除"
                      style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:16, lineHeight:1, padding:2 }}>×</button>
                  )}
                </div>
                {srQ && (
                  <div style={{ marginTop:8, background:'white', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden' }}>
                    {srAttrMatches.length === 0 ? (
                      <p style={{ margin:0, padding:'14px', fontSize:12, color:'#94a3b8', textAlign:'center' }}>{lang==='en'?'No matches':'找不到相關景點'}</p>
                    ) : srAttrMatches.map((a,i) => (
                      <button key={i} onClick={()=>{setSelAttraction({...a,cityKey:srResult.city});fetchAttractionDetail(a,srResult.city);setSrAttrQuery('');}}
                        style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px', background:'none', border:'none', borderTop:i>0?'1px solid #f1f5f9':'none', cursor:'pointer', textAlign:'left' }}>
                        {a.district && <span style={{ flexShrink:0, background:'#fef3c7', color:'#92400e', borderRadius:8, padding:'2px 8px', fontSize:10, fontWeight:800 }}>{a.district}</span>}
                        <span style={{ fontSize:13, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{localName(a,lang)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
            const srMapPins = getDistrictPins(cityEnSr, districts);
            const srDistrictFilter = districts.length > 0 ? (
              <div style={{ marginTop:4, marginBottom:10 }}>
                <p style={{ fontSize:12, fontWeight:700, color:'#64748b', margin:'0 0 8px', textTransform:'uppercase', letterSpacing:1 }}>{t.districtFilter}</p>
                {srMapPins.length >= 2 && (
                  <div style={{ marginBottom:10 }}>
                    <DistrictMap pins={srMapPins} selected={srDistrict} onSelect={setSrDistrict} lang={lang}
                      labelOf={(d)=>districtLabels[d]?.[lang] || d}/>
                  </div>
                )}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {districts.map(d=>(
                    <button key={d} onClick={()=>setSrDistrict(d)} style={{ padding:'5px 14px', borderRadius:20, border:'1.5px solid', borderColor:srDistrict===d?'#f59e0b':'#e2e8f0', background:srDistrict===d?'#fef3c7':'white', color:srDistrict===d?'#92400e':'#475569', fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}>{districtLabels[d]?.[lang] || d}</button>
                  ))}
                </div>
              </div>
            ) : null;
            return (
              <>
                {srAttrSearchBar}
                {srDistrictFilter}
                {isSrDistrictLoading ? (
                  <div style={{ textAlign:'center', padding:'40px 0', color:'#94a3b8', fontSize:13 }}>搜尋 {srDistrict} 景點中…</div>
                ) : (
                  <>
                    <RainyDayPicks weather={srResult.weather} attractions={filteredHot} lang={lang}
                      onSelect={(a)=>{setSelAttraction({...a,cityKey:srResult.city});fetchAttractionDetail(a,srResult.city);}}/>
                    <ThemeEntryStrip lang={lang} attractions={filteredHot} activeTheme={srActiveTheme}
                      onThemeSelect={(k)=>setSrActiveTheme(prev=>prev===k?null:k)}/>
                    {srActiveTheme && (
                      <CategorySection catKey={srActiveTheme} attractions={filteredHot} lang={lang} t={t} cityKey={srResult.city}
                        favorites={favorites} onToggleFav={toggleFav}
                        onSelect={(a,ck)=>{setSelAttraction({...a,cityKey:ck});fetchAttractionDetail(a,ck);}} districtKey={srDistrict}/>
                    )}
                  </>
                )}
              </>
            );
          })()}
          {/* Deep Experience — last, scoped to the same district filter as the hot grid above */}
          {(srResult.deep||[]).length > 0 && (
            <DeepStrip attractions={srDistrict ? (srResult.deep||[]).filter(a => matchesDistrict(a.district, srDistrict)) : srResult.deep} cityKey={srResult.city} t={t} favorites={favorites} onToggleFav={toggleFav} onSelect={(a,ck)=>{setSelAttraction({...a,cityKey:ck});fetchAttractionDetail(a,ck);}} districtKey={srDistrict}/>
          )}
          <RelevantCoupons city={srResult.city} country={srResult.country} lang={lang} hideMerchants={['saily','radicalstorage']}/>
        </div>
      )}
    </div>
  );

  // TRIP
  const toggleMode = m => setTrModes(p=>p.includes(m)?p.filter(x=>x!==m):[...p,m]);
  const showTripToast = (msg, isError=false) => {
    setTripToast({ msg, isError });
    setTimeout(() => setTripToast(null), 3500);
  };

  const renderTrip = () => (
    <div style={{ maxWidth:700, margin:'0 auto', padding:'14px 14px 96px' }}>
      {/* 訪客試用提示：未登入可免費生成 1 次，之後以 Google 登入繼續 */}
      {!isLoggedIn && !itinerary && (() => {
        const used = isGuestTrialUsed();
        const gi = {
          'zh-HK': used ? ['你已用過 1 次免費試用', '用 Google 登入即可每日免費生成 3 次，並儲存行程'] : ['🎁 無需登入，免費試用 1 次（最多 4 天）', '填好目的地和日期就可以生成行程。之後用 Google 登入即可規劃最多 7 天，每日免費生成 3 次'],
          'zh-CN': used ? ['你已用过 1 次免费试用', '用 Google 登录即可每日免费生成 3 次，并储存行程'] : ['🎁 无需登录，免费试用 1 次（最多 4 天）', '填好目的地和日期即可生成行程。之后用 Google 登录即可规划最多 7 天，每日免费生成 3 次'],
          'en':    used ? ["You've used your free try", 'Continue with Google to get 3 free itineraries a day and save your trips'] : ['🎁 Try it free once — no sign-up (up to 4 days)', 'Pick your destination and dates to generate a trip. Continue with Google afterwards for trips up to 7 days, 3 free itineraries a day'],
          'ja':    used ? ['無料お試しは使用済みです', 'Google でログインすると 1 日 3 回まで無料で生成でき、旅程も保存できます'] : ['🎁 ログイン不要で 1 回無料お試し（最大 4 日）', '目的地と日付を入力するだけで旅程を作成。その後 Google でログインすると最大 7 日、1 日 3 回まで無料'],
          'ko':    used ? ['무료 체험을 이미 사용했습니다', 'Google로 계속하면 하루 3회 무료 생성 및 일정 저장이 가능합니다'] : ['🎁 로그인 없이 1회 무료 체험（최대 4일）', '목적지와 날짜만 입력하면 일정을 생성합니다. 이후 Google로 계속하면 최대 7일, 하루 3회 무료'],
        };
        const [h, sub] = gi[lang] || gi['en'];
        return (
          <div style={{ background:'linear-gradient(135deg,#fffbeb,#fef3c7)', border:'1.5px solid #fde68a', borderRadius:14, padding:'12px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <div style={{ flex:'1 1 220px' }}>
              <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#92400e' }}>{h}</p>
              <p style={{ margin:'2px 0 0', fontSize:11.5, color:'#78350f', lineHeight:1.6 }}>{sub}</p>
            </div>
            {used && (
              <button onClick={()=>openGoogleGate('trial_used')}
                style={{ background:'#f59e0b', color:'#0f172a', border:'none', borderRadius:10, padding:'9px 14px', fontWeight:900, fontSize:12, cursor:'pointer', flexShrink:0 }}>
                {lang==='en'?'Continue with Google':lang==='ja'?'Google でログイン':lang==='ko'?'Google로 계속':lang==='zh-CN'?'用 Google 登录':'用 Google 登入'}
              </button>
            )}
          </div>
        );
      })()}
      {!itinerary ? (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, margin:'0 0 16px' }}>
            <p style={{ fontSize:22, fontWeight:900, color:'#0f172a', margin:0 }}>{t.planTrip}</p>
            <button onClick={()=>{ trackEvent('trip_guide_opened'); setShowTripGuide(true); }}
              style={{ display:'flex', alignItems:'center', gap:6, background:'white', border:'1.5px solid #fde68a', borderRadius:50, padding:'8px 14px', fontSize:12, fontWeight:900, color:'#b45309', cursor:'pointer', flexShrink:0 }}>
              📖 {lang==='en'?'How it works':lang==='ja'?'使い方':lang==='ko'?'사용 방법':lang==='zh-CN'?'使用教学':'使用教學'}
            </button>
          </div>
          <div style={{ ...S.card, display:'flex', flexDirection:'column', gap:16 }}>
            {/* Step progress — 3步驟：1必填 2選填細節 3風格與出行 */}
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <p style={{ margin:0, fontSize:12, color:'#94a3b8', fontWeight:700 }}>
                  {tripStep===1 ? (lang==='en'?'Required basics':lang==='ja'?'必須項目':lang==='ko'?'필수 항목':'必填，先填最基本的')
                    : tripStep===2 ? (lang==='en'?'Optional — you can skip':lang==='ja'?'任意・スキップ可':lang==='ko'?'선택・건너뛰기 가능':'選填，可直接跳過')
                    : (lang==='en'?'Last step':lang==='ja'?'最後のステップ':lang==='ko'?'마지막 단계':'最後一步')}
                </p>
                <p style={{ margin:0, fontSize:12, color:'#94a3b8', fontWeight:700 }}>
                  {lang==='en'?`Step ${tripStep} / 3`:lang==='ja'?`ステップ ${tripStep} / 3`:lang==='ko'?`단계 ${tripStep} / 3`:`步驟 ${tripStep} / 3`}
                </p>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                {[1,2,3].map(s=>(
                  <div key={s} style={{ flex:1, height:5, borderRadius:3, background: s<=tripStep?'#0f172a':'#e2e8f0' }}/>
                ))}
              </div>
            </div>
            {tripStep===1 && <>
            {/* Multi-destination rows */}
            <div>
              <p style={S.label}>{t.destination} * <span style={{ fontSize:10, color:'#94a3b8', fontWeight:600 }}>({t.multiDestHint})</span></p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {trDestinations.map((dest, idx) => (
                  <div key={dest.id} style={{ background:'#f8fafc', borderRadius:14, padding:'10px 12px', border:'1.5px solid #e2e8f0' }}>
                    <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr auto', gap:8, alignItems:'end' }}>
                      {/* Country */}
                      <div style={{ position:'relative' }}>
                        <p style={{ ...S.label, margin:'0 0 4px' }}>{t.country}</p>
                        <input list={`cnt-${dest.id}`} value={dest.country}
                          onChange={e=>{ const v=e.target.value; setTrDestinations(p=>p.map((d,i)=>i===idx?{...d,country:v,city:''}:d)); if(v.length>=2) fetchDestCities(v); }}
                          placeholder={lang==='en'?'e.g. Japan':lang==='ja'?'例：日本':lang==='ko'?'예: 일본':'例：日本'}
                          style={{ width:'100%',border:'1.5px solid #e2e8f0',borderRadius:10,padding:'9px 28px 9px 11px',fontSize:12,outline:'none',background:'white',boxSizing:'border-box' }}
                          onFocus={e=>{ e.target.style.borderColor='#f59e0b'; e.target.select(); }} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                        {dest.country && <button onClick={()=>setTrDestinations(p=>p.map((d,i)=>i===idx?{...d,country:'',city:''}:d))}
                          style={{ position:'absolute',right:8,top:'calc(50% + 10px)',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:14,fontWeight:700,padding:'2px 4px',lineHeight:1 }} title="清除">×</button>}
                        <datalist id={`cnt-${dest.id}`}>{(POPULAR_COUNTRIES_LOCALIZED[lang]||POPULAR_COUNTRIES_LOCALIZED['zh-HK']).map(c=><option key={c} value={c}/>)}</datalist>
                      </div>
                      {/* City */}
                      <div style={{ position:'relative' }}>
                        <p style={{ ...S.label, margin:'0 0 4px' }}>{t.city}</p>
                        <input list={`cit-${dest.id}`} value={dest.city}
                          onChange={e=>setTrDestinations(p=>p.map((d,i)=>i===idx?{...d,city:e.target.value}:d))}
                          placeholder={lang==='en'?'e.g. Tokyo':lang==='ja'?'例：東京':lang==='ko'?'예: 도쿄':'例：東京'}
                          style={{ width:'100%',border:'1.5px solid #e2e8f0',borderRadius:10,padding:'9px 28px 9px 11px',fontSize:12,outline:'none',background:'white',boxSizing:'border-box' }}
                          onFocus={e=>{ e.target.style.borderColor='#f59e0b'; e.target.select(); }}
                          onBlur={e=>{ e.target.style.borderColor='#e2e8f0'; if (trActivities.includes('Outlet')) fetchOutletOptions(); if (trActivities.includes('溫泉')) fetchOnsenOptions(); }}/>
                        {dest.city && <button onClick={()=>{
                            setTrDestinations(p=>p.map((d,i)=>i===idx?{...d,city:''}:d));
                            if (trActivities.includes('Outlet')) fetchOutletOptions();
                            if (trActivities.includes('溫泉')) fetchOnsenOptions();
                          }}
                          style={{ position:'absolute',right:8,top:'calc(50% + 10px)',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:14,fontWeight:700,padding:'2px 4px',lineHeight:1 }} title="清除">×</button>}
                        <datalist id={`cit-${dest.id}`}>
                          {(trDestCityData[dest.country]?.cities||[]).map(c=>(
                            <option key={c} value={trDestCityData[dest.country]?.map?.[c]||c}/>
                          ))}
                        </datalist>
                      </div>
                      {/* Date — required: drives genItinerary's deterministic day->city schedule.
                          Flagged inline (not just in the one message near the submit button)
                          so with several destinations the traveller can see exactly WHICH
                          row is missing a date instead of hunting for it. */}
                      {(() => {
                        const missingDate = !!dest.city && !dest.date;
                        const prevDate = idx > 0 ? trDestinations[idx-1]?.date : null;
                        // 用瀏覽器本地日期作最早可選日，UTC 會令香港用戶早上 8 點前可選到昨天
                        const todayStr = new Date().toLocaleDateString('en-CA');
                        let minDate = todayStr;
                        if (prevDate) {
                          const d = new Date(prevDate + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + 1);
                          const nextAfterPrev = d.toISOString().split('T')[0];
                          minDate = nextAfterPrev > todayStr ? nextAfterPrev : todayStr;
                        }
                        return (
                          <div>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:6, margin:'0 0 4px' }}>
                              <p style={{ ...S.label, margin:0 }}>{t.depDate} {dest.city && <span style={{ color:'#ef4444' }}>*</span>}</p>
                              {missingDate && (
                                <span style={{ fontSize:10, color:'#dc2626', fontWeight:700, whiteSpace:'nowrap' }}>
                                  {lang==='en' ? 'Required' : lang==='ja' ? '必須' : lang==='ko' ? '필수' : '必填'}
                                </span>
                              )}
                            </div>
                            <input type="date" value={dest.date} min={minDate}
                              onChange={e=>{
                                const v = e.target.value;
                                setTrDestinations(p=>p.map((d,i)=>i===idx?{...d,date:v}:d));
                                if (idx===0 && trReturnDate) {
                                  const n = calcTripDays(v, trReturnDate);
                                  setTrDays(n && n <= getTripMaxDays() ? n : null);
                                }
                              }}
                              style={{ width:'100%',border:`1.5px solid ${missingDate?'#fca5a5':'#e2e8f0'}`,borderRadius:10,padding:'9px 11px',fontSize:12,outline:'none',background:missingDate?'#fef2f2':'white',boxSizing:'border-box',color:dest.date?'#1e293b':'#94a3b8' }}/>
                          </div>
                        );
                      })()}
                      {/* Remove */}
                      {trDestinations.length > 1 && (
                        <button aria-label="移除目的地" onClick={()=>setTrDestinations(p=>p.filter((_,i)=>i!==idx))}
                          style={{ background:'#fef2f2',border:'none',borderRadius:9,width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#ef4444',flexShrink:0,alignSelf:'flex-end' }}>
                          <X size={13}/>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {trDestinations.length < 5 && (
                  <button onClick={()=>setTrDestinations(p=>[...p,{id:Date.now(),country:p[p.length-1]?.country||'',city:'',date:'',hotelArea:''}])}
                    style={{ border:'1.5px dashed #e2e8f0',borderRadius:12,padding:'10px',background:'white',color:'#94a3b8',fontSize:12,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6 }}>
                    <Plus size={13}/> {t.addDest}
                  </button>
                )}
              </div>
            </div>
            {/* Return date — optional, auto-calculates the day-count below once both this and the first destination's date are set */}
            <div>
              <p style={{ ...S.label, margin:'0 0 4px' }}>
                {t.returnDate}
                <span style={{ fontWeight:600, color:'#94a3b8' }}> ({lang==='en'?'optional':lang==='ja'?'任意':lang==='ko'?'선택':'選填'})</span>
              </p>
              <input type="date" value={trReturnDate} min={trDestinations[0]?.date || new Date().toISOString().split('T')[0]}
                onChange={e=>{
                  const v = e.target.value;
                  setTrReturnDate(v);
                  const startDate = trDestinations[0]?.date;
                  // 清空回程日期就保留現有天數，讓用戶改用下面的按鈕手動揀
                  if (startDate && v) {
                    const n = calcTripDays(startDate, v);
                    setTrDays(n && n <= getTripMaxDays() ? n : null);
                  }
                }}
                style={{ width:'100%',border:'1.5px solid #e2e8f0',borderRadius:10,padding:'9px 11px',fontSize:12,outline:'none',background:'white',boxSizing:'border-box',color:trReturnDate?'#1e293b':'#94a3b8' }}/>
              {!(trDestinations[0]?.date && trReturnDate) && (
                <p style={{ margin:'4px 0 0', fontSize:10, color:'#94a3b8', fontWeight:600 }}>{t.autoDaysNeedDate}</p>
              )}
            </div>
            {/* Days: 1–7 normal, 1–14 with affiliate reward (or the admin test account, always) */}
            {(() => {
              const isAdmin = user?.email === ADMIN_EMAIL;
              const maxDays = getTripMaxDays();
              // 出發同回程日期都有 → 天數由日期決定，只顯示結果，唔再俾用戶揀按鈕，避免兩邊打架
              const autoDays = calcTripDays(trDestinations[0]?.date, trReturnDate);
              if (autoDays) {
                const over = autoDays > maxDays;
                return (
                  <div>
                    <p style={{ ...S.label, margin:'0 0 4px' }}>{t.days}</p>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap', background:over?'#fef2f2':'#fffbeb', border:`1.5px solid ${over?'#fecaca':'#fde68a'}`, borderRadius:12, padding:'10px 14px' }}>
                      <div>
                        <p style={{ margin:0, fontSize:15, fontWeight:900, color:over?'#dc2626':'#92400e' }}>
                          {lang==='en' ? `${autoDays} days` : lang==='ja' ? `${autoDays}日間` : lang==='ko' ? `${autoDays}일` : lang==='zh-CN' ? `共 ${autoDays} 天` : `共 ${autoDays} 天`}
                        </p>
                        <p style={{ margin:'2px 0 0', fontSize:10, fontWeight:600, color:over?'#dc2626':'#b45309' }}>{t.autoDaysHint}</p>
                      </div>
                      <button onClick={()=>setTrReturnDate('')}
                        style={{ background:'white', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'7px 12px', fontSize:11, fontWeight:800, color:'#64748b', cursor:'pointer', flexShrink:0 }}>
                        {lang==='en'?'Pick days manually':lang==='ja'?'日数を手動で選ぶ':lang==='ko'?'일수 직접 선택':lang==='zh-CN'?'改为手动选天数':'改為手動選天數'}
                      </button>
                    </div>
                  </div>
                );
              }
              // 訪客照樣顯示 1–7 天，5–7 天加鎖，按下即提示用 Google 登入
              const shownDays = isLoggedIn ? maxDays : MAX_TRIP_DAYS;
              const expDate = affiliateReward ? new Date(affiliateReward.expires_at).toLocaleDateString('zh-HK',{year:'numeric',month:'short',day:'numeric'}) : '';
              return (
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <p style={{ ...S.label, margin:0 }}>{t.days} *</p>
                    {affiliateReward && (
                      <span style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:6, padding:'1px 7px', fontSize:9, fontWeight:900, color:'#92400e' }}>
                        🎁 最多{AFFILIATE_MAX_DAYS}天 · 有效至{expDate}
                      </span>
                    )}
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto' }}>
                      {!trDays && (
                        <span style={{ fontSize:10, color:'#dc2626', fontWeight:700, whiteSpace:'nowrap' }}>
                          {lang==='en' ? 'Required' : lang==='ja' ? '必須' : lang==='ko' ? '필수' : '必填'}
                        </span>
                      )}
                      <span style={{ fontSize:10, color:'#94a3b8', fontWeight:600, whiteSpace:'nowrap' }}>
                        {isLoggedIn ? `（最多${maxDays}天）`
                          : lang==='en' ? `(Up to ${GUEST_MAX_DAYS} days · ${MAX_TRIP_DAYS} after sign-in)`
                          : lang==='ja' ? `（最大${GUEST_MAX_DAYS}日・ログイン後${MAX_TRIP_DAYS}日）`
                          : lang==='ko' ? `(최대 ${GUEST_MAX_DAYS}일 · 로그인 후 ${MAX_TRIP_DAYS}일)`
                          : lang==='zh-CN' ? `（最多${GUEST_MAX_DAYS}天 · 登录后${MAX_TRIP_DAYS}天）`
                          : `（最多${GUEST_MAX_DAYS}天 · 登入後${MAX_TRIP_DAYS}天）`}
                      </span>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:7 }}>
                    {Array.from({length:shownDays},(_,i)=>i+1).map(n=>{
                      const locked = n > maxDays;
                      return (
                        <button key={n} onClick={()=>{ if (locked) { openGoogleGate('days'); return; } setTrDays(n); }}
                          style={{ padding:'10px 0', borderRadius:12, border:trDays===n?'none':'1.5px solid #e2e8f0', background:trDays===n?'#f59e0b':locked?'#f8fafc':'white', color:trDays===n?'#0f172a':locked?'#cbd5e1':'#64748b', fontSize:13, fontWeight:900, cursor:'pointer', opacity: n>MAX_TRIP_DAYS&&!affiliateReward&&!isAdmin?0.3:1 }}>
                          {locked ? `🔒${n}` : n}
                        </button>
                      );
                    })}
                  </div>
                  {/* 已登入但未有禮遇：喺天數上限旁邊提示點樣解鎖 14 天（訪客先要登入，由上面 🔒 處理） */}
                  {isLoggedIn && !affiliateReward && !isAdmin && (
                    <p style={{ margin:'6px 0 0', fontSize:10.5, color:'#047857', fontWeight:700, lineHeight:1.5 }}>🎁 {affPerkText()}</p>
                  )}
                </div>
              );
            })()}
            {(() => {
              // 缺日期已在每行標示「必填」，呢度只顯示日期／天數互相矛盾的情況
              const missing = !trDestinations.some(d=>d.city) || trDestinations.some(d=>d.city&&!d.date);
              const step1Err = missing ? null : validateTripDates();
              const blocked = missing || !trDays || !!step1Err;
              return <>
                {step1Err && (
                  <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                    <AlertTriangle size={14} color="#dc2626" style={{ flexShrink:0 }}/>
                    <p style={{ margin:0, fontSize:12, color:'#dc2626', fontWeight:700 }}>{step1Err}</p>
                  </div>
                )}
                <button onClick={()=>setTripStep(2)} disabled={blocked}
                  style={{ ...S.btnDark, justifyContent:'center', display:'flex', alignItems:'center', gap:8, opacity:blocked?0.5:1 }}>
                  {lang==='en'?'Next: Trip Details →':lang==='ja'?'次へ：旅程の詳細 →':lang==='ko'?'다음: 여행 세부사항 →':lang==='zh-CN'?'下一步：行程细节 →':'下一步：行程細節 →'}
                </button>
              </>;
            })()}
            </>}
            {tripStep===2 && <>
            {/* Hotel area — optional, anchors arrival/departure-day slots to somewhere real
                instead of the AI guessing a nearby place name that won't match any real venue.
                由第 1 步搬過嚟，第 1 步只留必填項目。 */}
            <div>
              <p style={S.label}>
                {lang==='en'?'Hotel / stay area':lang==='zh-CN'?'酒店/住宿地区':lang==='ja'?'ホテル/滞在エリア':lang==='ko'?'호텔/숙박 지역':'住宿地區 / 酒店名稱'}
                <span style={{ fontWeight:600, color:'#94a3b8' }}> ({lang==='en'?'optional':lang==='ja'?'任意':lang==='ko'?'선택':'選填'})</span>
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {trDestinations.map((dest, idx) => dest.city ? (
                  <div key={dest.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:12, fontWeight:800, color:'#475569', minWidth:64, flexShrink:0 }}>{dest.city}</span>
                    <input value={dest.hotelArea||''}
                      onChange={e=>setTrDestinations(p=>p.map((d,i)=>i===idx?{...d,hotelArea:e.target.value}:d))}
                      placeholder={lang==='en'?'e.g. Shinjuku, or hotel name':lang==='ja'?'例：新宿、またはホテル名':lang==='ko'?'예: 신주쿠 또는 호텔명':'例：新宿區 或 酒店名稱'}
                      style={{ flex:1, minWidth:0, border:'1.5px solid #e2e8f0',borderRadius:10,padding:'9px 11px',fontSize:12,outline:'none',background:'white',boxSizing:'border-box' }}
                      onFocus={e=>{ e.target.style.borderColor='#f59e0b'; }} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  </div>
                ) : null)}
              </div>
            </div>
            {/* Custom attractions */}
            <div>
              <p style={S.label}>{lang==='en'?'Custom Attractions (optional)':lang==='ja'?'行きたいスポット（任意）':lang==='ko'?'가고 싶은 명소 (선택)':lang==='zh-CN'?'指定景点（选填）':'指定景點（選填）'}</p>
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <input value={trCustomInput} onChange={e=>setTrCustomInput(e.target.value)}
                  onKeyDown={e=>{ if(e.key==='Enter'&&trCustomInput.trim()){ const v=trCustomInput.trim(); if(!trCustomSpots.includes(v)) setTrCustomSpots(p=>[...p,v]); setTrCustomInput(''); }}}
                  placeholder={lang==='en'?'e.g. Shibuya Crossing':lang==='ja'?'例：渋谷スクランブル交差点':lang==='ko'?'예: 시부야 스크램블 교차로':lang==='zh-CN'?'例如：涩谷十字路口':'例如：澀谷十字路口'}
                  style={{ flex:1, border:'1.5px solid #e2e8f0', borderRadius:12, padding:'10px 13px', fontSize:13, outline:'none', background:'white' }}
                  onFocus={e=>e.target.style.borderColor='#f59e0b'} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                <button onClick={()=>{ const v=trCustomInput.trim(); if(v&&!trCustomSpots.includes(v)){ setTrCustomSpots(p=>[...p,v]); setTrCustomInput(''); }}}
                  style={{ padding:'10px 16px', borderRadius:12, border:'none', background:'#f59e0b', color:'#0f172a', fontWeight:900, fontSize:13, cursor:'pointer', flexShrink:0 }}>+</button>
              </div>
              {trCustomSpots.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                  {trCustomSpots.map(s=>(
                    <div key={s} style={{ display:'flex', alignItems:'center', gap:5, background:'#fef3c7', border:'1.5px solid #fde68a', borderRadius:50, padding:'5px 10px 5px 12px' }}>
                      <MapPin size={10} color="#f59e0b"/>
                      <span style={{ fontSize:12, fontWeight:700, color:'#92400e' }}>{s}</span>
                      <button onClick={()=>setTrCustomSpots(p=>p.filter(x=>x!==s))}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#f59e0b', padding:'0 0 0 2px', display:'flex', alignItems:'center', lineHeight:1 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Activities */}
            <div>
              <p style={S.label}>{lang==='en'?'Activities (optional)':lang==='ja'?'アクティビティ（任意）':lang==='ko'?'액티비티 (선택)':lang==='zh-CN'?'活动选项（选填）':'活動選項（選填）'}</p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                {(t.activities||['主題樂園','海洋館','動物園','博物館','寵物Café','溫泉','室內遊樂場','當地文化體驗','音樂 Live Bar','Outlet']).map((act,ai)=>{
                  const zhAct=['主題樂園','海洋館','動物園','博物館','寵物Café','溫泉','室內遊樂場','當地文化體驗','音樂 Live Bar','Outlet'][ai];
                  const sel=trActivities.includes(zhAct);
                  return <button key={zhAct} onClick={()=>{
                      setTrActivities(p=>sel?p.filter(x=>x!==zhAct):[...p,zhAct]);
                      if (zhAct==='Outlet') {
                        if (sel) { setTrOutletOptions([]); setTrSelectedOutlet(null); }
                        else fetchOutletOptions();
                      }
                      if (zhAct==='溫泉') {
                        if (sel) { setTrOnsenOptions([]); setTrSelectedOnsen(null); }
                        else fetchOnsenOptions();
                      }
                      if (VENUE_PICKERS[zhAct]) toggleVenueAct(zhAct, sel);
                    }}
                    style={{ padding:'7px 13px',borderRadius:50,border:sel?'none':'1.5px solid #e2e8f0',background:sel?'#f59e0b':'white',color:sel?'white':'#64748b',fontSize:12,fontWeight:700,cursor:'pointer' }}>
                    {sel&&'✓ '}{act}
                  </button>;
                })}
              </div>
              {/* Real outlet-mall picker — appears once "Outlet" is ticked, so
                  the traveller chooses the actual venue instead of the generator
                  silently auto-selecting the highest-rated one. */}
              {trActivities.includes('Outlet') && (
                <div style={{ marginTop:10, background:'#fffbeb', border:'1.5px solid #fde68a', borderRadius:12, padding:'10px 12px' }}>
                  <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:800, color:'#92400e' }}>
                    {lang==='en'?'Choose which outlet mall:':lang==='ja'?'訪れるアウトレットを選択：':lang==='ko'?'방문할 아울렛 선택:':'選擇要去的 Outlet：'}
                  </p>
                  {loadingOutlets ? (
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#92400e' }}>
                      <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }}/>
                      {lang==='en'?'Looking up real outlets…':'查詢真實 Outlet 資料中…'}
                    </div>
                  ) : trOutletOptions.length ? (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                      {trOutletOptions.map(o => {
                        const oSel = trSelectedOutlet && (trSelectedOutlet.place_id ? trSelectedOutlet.place_id === o.place_id : trSelectedOutlet.name === o.name);
                        return (
                          <button key={`${o.city}-${o.name}`} onClick={()=>setTrSelectedOutlet(o)}
                            style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:2, padding:'7px 12px', borderRadius:12, border:oSel?'none':'1.5px solid #fde68a', background:oSel?'#f59e0b':'white', color:oSel?'white':'#78350f', fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'left' }}>
                            <span>{oSel?'✓ ':''}{o.name}</span>
                            <span style={{ fontSize:10, fontWeight:600, opacity:0.85 }}>{o.city}{o.rating ? ` · ★${o.rating}` : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ margin:0, fontSize:12, color:'#92400e' }}>
                      {lang==='en'?'No real outlet mall found in this trip\'s cities yet — fill in a city first.':'目前這趟行程的城市裡查不到真實 Outlet 資料，請先填好城市。'}
                    </p>
                  )}
                </div>
              )}
              {/* 溫泉選單 — 勾「溫泉」後出現，列出已選城市在資料庫中的真實溫泉 */}
              {trActivities.includes('溫泉') && (
                <div style={{ marginTop:10, background:'#fff1f2', border:'1.5px solid #fecdd3', borderRadius:12, padding:'10px 12px' }}>
                  <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:800, color:'#9f1239' }}>
                    {lang==='en'?'Choose a hot spring / spa:':lang==='ja'?'訪れる温泉・スパを選択：':lang==='ko'?'방문할 온천·스파 선택:':lang==='zh-CN'?'选择要去的温泉・SPA：':'選擇要去的溫泉・SPA：'}
                  </p>
                  {loadingOnsens ? (
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#9f1239' }}>
                      <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }}/>
                      {lang==='en'?'Looking up real hot springs…':lang==='zh-CN'?'查询真实温泉资料中…':'查詢真實溫泉資料中…'}
                    </div>
                  ) : trOnsenOptions.length ? (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                      {trOnsenOptions.map(o => {
                        const oSel = trSelectedOnsen && (trSelectedOnsen.place_id ? trSelectedOnsen.place_id === o.place_id : trSelectedOnsen.name === o.name);
                        const oName = localName(toAppAttr(o), lang) || o.name;
                        return (
                          <button key={`${o.city}-${o.place_id || o.name}`} onClick={()=>setTrSelectedOnsen(o)}
                            style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:2, padding:'7px 12px', borderRadius:12, border:oSel?'none':'1.5px solid #fecdd3', background:oSel?'#e11d48':'white', color:oSel?'white':'#881337', fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'left' }}>
                            <span>{oSel?'✓ ':''}{oName}</span>
                            <span style={{ fontSize:10, fontWeight:600, opacity:0.85 }}>{o.city}{o.district && o.district !== o.city ? ` · ${o.district}` : ''}{o.rating ? ` · ★${o.rating}` : ''}</span>
                            {o.distKm != null && (
                              <span style={{ fontSize:10, fontWeight:600, opacity:0.85 }}>
                                {lang==='en'?`~${o.distKm} km from centre`:lang==='ja'?`中心部から約${o.distKm}km`:lang==='ko'?`중심부에서 약 ${o.distKm}km`:lang==='zh-CN'?`距市中心约 ${o.distKm} 公里`:`距市中心約 ${o.distKm} 公里`}
                                {needsOvernight(o) && (lang==='en'?' · 2-day, 1-night trip':lang==='ja'?' · 1泊2日推奨':lang==='ko'?' · 1박 2일 추천':lang==='zh-CN'?' · 建议两天一夜':' · 建議兩天一夜')}
                              </span>
                            )}
                            {o.nearby?.length > 0 && (
                              <span style={{ fontSize:10, fontWeight:600, opacity:0.85 }}>
                                {lang==='en'?'Combine with: ':lang==='ja'?'近くの観光地：':lang==='ko'?'함께 둘러보기: ':lang==='zh-CN'?'可顺游：':'可順遊：'}
                                {o.nearby.map(a => localName(toAppAttr(a), lang) || a.name).join(lang==='en'?', ':'、')}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ margin:0, fontSize:12, color:'#9f1239' }}>
                      {lang==='en'?'No hot spring in our database for this trip\'s cities yet — fill in a city first, or the planner will only add one if a real, well-known one exists nearby.':lang==='zh-CN'?'目前资料库里查不到这趟行程城市的温泉（请先填好城市）；生成时只会在附近确实有知名温泉时才安排。':'目前資料庫裡查不到這趟行程城市的溫泉（請先填好城市）；生成時只會在附近確實有知名溫泉時才安排。'}
                    </p>
                  )}
                </div>
              )}
              {/* 動物園／海洋館／博物館選單 — 勾選後列出真實景點；多城市先選城市；預設只顯示前 6 筆 */}
              {Object.keys(VENUE_PICKERS).filter(act => trActivities.includes(act)).map(act => {
                const cfg = VENUE_PICKERS[act];
                const cities = venueTripCities();
                const city = trVenueCity[act] && cities.includes(trVenueCity[act]) ? trVenueCity[act] : (cities.length === 1 ? cities[0] : null);
                const rowsRaw = city ? trVenueRows[`${act}|${city}`] : null;
                const rows = Array.isArray(rowsRaw) ? rowsRaw : [];
                const qText = (trVenueQuery[act] || '').trim().toLowerCase();
                const filtered = qText ? rows.filter(r => [r.name, r.name_en, r.name_zh_cn, r.name_ja, r.name_ko, r.district].filter(Boolean).some(n => n.toLowerCase().includes(qText))) : rows;
                const showAll = !!trVenueMore[act] || !!qText;
                const shown = showAll ? filtered : filtered.slice(0, VENUE_PREVIEW_COUNT);
                const picks = trVenuePicks[act] || [];
                const [bg, bd, tx, ac] = cfg.color;
                const L5 = (en, ja, ko, cn, hk) => (lang==='en'?en:lang==='ja'?ja:lang==='ko'?ko:lang==='zh-CN'?cn:hk);
                const actLabel = L5({ '動物園':'zoo', '海洋館':'aquarium', '博物館':'museum' }[act], { '動物園':'動物園', '海洋館':'水族館', '博物館':'博物館・美術館' }[act], { '動物園':'동물원', '海洋館':'수족관', '博物館':'박물관·미술관' }[act], { '動物園':'动物园', '海洋館':'海洋馆', '博物館':'博物馆・美术馆' }[act], { '動物園':'動物園', '海洋館':'海洋館', '博物館':'博物館・美術館' }[act]);
                return (
                  <div key={act} style={{ marginTop:10, background:bg, border:`1.5px solid ${bd}`, borderRadius:12, padding:'10px 12px' }}>
                    <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:800, color:tx }}>
                      {L5(`Choose a ${actLabel} (up to ${VENUE_PICK_LIMIT}):`, `訪れる${actLabel}を選択（最大${VENUE_PICK_LIMIT}件）：`, `방문할 ${actLabel} 선택 (최대 ${VENUE_PICK_LIMIT}곳):`, `选择要去的${actLabel}（最多 ${VENUE_PICK_LIMIT} 间）：`, `選擇要去的${actLabel}（最多 ${VENUE_PICK_LIMIT} 間）：`)}
                    </p>
                    {cities.length > 1 && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                        {cities.map(c => {
                          const cSel = city === c;
                          const nPick = picks.filter(x => x.city === c).length;
                          return (
                            <button key={c} onClick={()=>pickVenueCity(act, c)}
                              style={{ padding:'5px 12px', borderRadius:50, border:cSel?'none':`1.5px solid ${bd}`, background:cSel?ac:'white', color:cSel?'white':tx, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                              {c}{nPick ? ` · ✓${nPick}` : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {cities.length === 0 ? (
                      <p style={{ margin:0, fontSize:12, color:tx }}>{L5('Fill in a city first.', '先に都市を入力してください。', '먼저 도시를 입력해 주세요.', '请先填好城市。', '請先填好城市。')}</p>
                    ) : !city ? (
                      <p style={{ margin:0, fontSize:12, color:tx }}>{L5('Pick a city above to see its list.', '上の都市を選ぶと一覧が表示されます。', '위에서 도시를 선택하면 목록이 표시됩니다.', '请先在上方选择城市。', '請先在上方選擇城市。')}</p>
                    ) : rowsRaw === 'loading' || rowsRaw == null ? (
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:tx }}>
                        <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }}/>
                        {L5('Looking up real venues…', '実在の施設を検索中…', '실제 장소 조회 중…', '查询真实资料中…', '查詢真實資料中…')}
                      </div>
                    ) : !rows.length ? (
                      <p style={{ margin:0, fontSize:12, color:tx }}>
                        {lang==='en' ? cfg.emptyHint.en : cfg.emptyHint.zh}
                      </p>
                    ) : (
                      <>
                        {cfg.search && rows.length > VENUE_PREVIEW_COUNT && (
                          <input value={trVenueQuery[act] || ''} onChange={e=>setTrVenueQuery(p=>({ ...p, [act]: e.target.value }))}
                            placeholder={L5('Search by name or area', '名前・エリアで検索', '이름·지역 검색', '输入名称或地区搜索', '輸入名稱或地區搜尋')}
                            style={{ width:'100%', boxSizing:'border-box', padding:'7px 10px', marginBottom:8, borderRadius:10, border:`1.5px solid ${bd}`, fontSize:12, outline:'none' }}/>
                        )}
                        <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                          {shown.map(o => {
                            const oSel = picks.some(x => sameVenue(x, o));
                            const full = !oSel && picks.length >= VENUE_PICK_LIMIT;
                            const oName = localName(toAppAttr(o), lang) || o.name;
                            return (
                              <button key={`${o.city}-${o.place_id || o.name}`} onClick={()=>toggleVenuePick(act, o)} disabled={full}
                                style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:2, padding:'7px 12px', borderRadius:12, border:oSel?'none':`1.5px solid ${bd}`, background:oSel?ac:'white', color:oSel?'white':tx, fontSize:12, fontWeight:700, cursor:full?'not-allowed':'pointer', opacity:full?0.45:1, textAlign:'left' }}>
                                <span>{oSel?'✓ ':''}{oName}</span>
                                <span style={{ fontSize:10, fontWeight:600, opacity:0.85 }}>{o.city}{o.district && o.district !== o.city ? ` · ${o.district}` : ''}{o.rating ? ` · ★${o.rating}` : ''}</span>
                              </button>
                            );
                          })}
                        </div>
                        {qText && !filtered.length && (
                          <p style={{ margin:'6px 0 0', fontSize:12, color:tx }}>{L5('No match.', '該当なし。', '검색 결과 없음.', '没有符合的结果。', '沒有符合的結果。')}</p>
                        )}
                        {!qText && filtered.length > VENUE_PREVIEW_COUNT && (
                          <button onClick={()=>setTrVenueMore(p=>({ ...p, [act]: !p[act] }))}
                            style={{ marginTop:8, background:'none', border:'none', padding:0, cursor:'pointer', fontSize:12, fontWeight:700, color:ac, textDecoration:'underline' }}>
                            {trVenueMore[act]
                              ? L5('Show less', '閉じる', '접기', '收起', '收起')
                              : L5(`Show more (${filtered.length - VENUE_PREVIEW_COUNT})`, `もっと見る（${filtered.length - VENUE_PREVIEW_COUNT}）`, `더 보기 (${filtered.length - VENUE_PREVIEW_COUNT})`, `显示更多（${filtered.length - VENUE_PREVIEW_COUNT}）`, `顯示更多（${filtered.length - VENUE_PREVIEW_COUNT}）`)}
                          </button>
                        )}
                      </>
                    )}
                    {picks.length > 0 && (
                      <p style={{ margin:'8px 0 0', fontSize:11, fontWeight:700, color:tx }}>
                        {L5('Selected: ', '選択中：', '선택됨: ', '已选：', '已選：')}{picks.map(x => localName(toAppAttr(x), lang) || x.name).join(lang==='en'?', ':'、')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setTripStep(1)}
                style={{ flex:1, padding:'13px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#64748b', fontWeight:900, fontSize:13, cursor:'pointer' }}>
                {lang==='en'?'← Back':lang==='ja'?'← 戻る':lang==='ko'?'← 이전':'← 上一步'}
              </button>
              <button onClick={()=>setTripStep(3)}
                style={{ flex:2, ...S.btnDark, justifyContent:'center', display:'flex', alignItems:'center', gap:8 }}>
                {lang==='en'?'Next: Style & Travel Type →':lang==='ja'?'次へ：スタイルと旅行タイプ →':lang==='ko'?'다음: 스타일 및 여행 유형 →':lang==='zh-CN'?'下一步：风格偏好 →':'下一步：風格偏好 →'}
              </button>
            </div>
            </>}
            {tripStep===3 && <>
            {/* Travel style — card multi-select */}
            {(() => {
              const PRESETS = {
                culture:       { culture:50, shopping:10, entertainment:10, nature:15, food:15 },
                food:          { culture:10, shopping:10, entertainment:15, nature:10, food:55 },
                shopping:      { culture:10, shopping:55, entertainment:15, nature:5,  food:15 },
                nature:        { culture:10, shopping:5,  entertainment:10, nature:55, food:20 },
                entertainment: { culture:10, shopping:15, entertainment:55, nature:10, food:10 },
                balanced:      { culture:20, shopping:20, entertainment:20, nature:20, food:20 },
              };
              const CARD_META = {
                culture:       { icon:'🏛️', color:'#1e3a5f', accent:'#93c5fd', zh:'文化深度遊', zhCN:'文化深度游', en:'Culture',       ja:'文化体験',        ko:'문화 체험',    sub:{ zh:'博物館・古蹟・傳統', zhCN:'博物馆・古迹・传统', en:'Museums · Heritage', ja:'博物館・遺跡', ko:'박물관・유적' } },
                food:          { icon:'🍜', color:'#c2410c', accent:'#fed7aa', zh:'美食之旅',   zhCN:'美食之旅',   en:'Food & Dining', ja:'グルメ',          ko:'미식 여행',    sub:{ zh:'餐廳・街食・夜市', zhCN:'餐厅・街食・夜市', en:'Restaurants · Street Food', ja:'レストラン・屋台', ko:'레스토랑・길거리 음식' } },
                shopping:      { icon:'🛍️', color:'#9f1239', accent:'#fda4af', zh:'購物天堂',   zhCN:'购物天堂',   en:'Shopping',      ja:'ショッピング',    ko:'쇼핑',        sub:{ zh:'商場・市集・品牌', zhCN:'商场・市集・品牌', en:'Malls · Markets · Brands', ja:'モール・マーケット', ko:'쇼핑몰・마켓' } },
                nature:        { icon:'🌿', color:'#14532d', accent:'#4ade80', zh:'自然放鬆',   zhCN:'自然放松',   en:'Nature & Relax',ja:'自然・リラックス', ko:'자연 힐링',    sub:{ zh:'公園・海灘・山野', zhCN:'公园・海滩・山野', en:'Parks · Beaches · Hiking', ja:'公園・ビーチ', ko:'공원・해변' } },
                entertainment: { icon:'🎡', color:'#4c1d95', accent:'#c4b5fd', zh:'娛樂玩樂',   zhCN:'娱乐玩乐',   en:'Entertainment', ja:'エンタメ',        ko:'엔터테인먼트', sub:{ zh:'主題樂園・夜生活', zhCN:'主题乐园・夜生活', en:'Theme Parks · Nightlife', ja:'テーマパーク・夜', ko:'테마파크・나이트라이프' } },
                balanced:      { icon:'⭐', color:'#78350f', accent:'#fbbf24', zh:'均衡之旅',   zhCN:'均衡之旅',   en:'Balanced',      ja:'バランス',        ko:'균형 여행',    sub:{ zh:'各類型均衡體驗', zhCN:'各类型均衡体验', en:'A bit of everything', ja:'バランスよく', ko:'모든 종류 균형' } },
              };
              const lk = lang==='zh-CN'?'zhCN':lang==='en'?'en':lang==='ja'?'ja':lang==='ko'?'ko':'zh';
              const styleLabel = lang==='en'?'Travel Style':lang==='ja'?'旅行スタイル':lang==='ko'?'여행 스타일':lang==='zh-CN'?'旅游风格':'旅遊風格';
              const styleHint  = lang==='en'?'Choose up to 3 (tap to toggle)':lang==='ja'?'最大3つ選択':lang==='ko'?'최대 3개 선택':lang==='zh-CN'?'可選1-3個':'可選1-3個';
              const toggleCard = (key) => {
                setTrStyleCards(prev => {
                  let next;
                  if (prev.includes(key)) {
                    next = prev.length > 1 ? prev.filter(k => k !== key) : prev;
                  } else if (prev.length < 3) {
                    next = [...prev, key];
                  } else {
                    next = [...prev.slice(1), key];
                  }
                  const combined = { culture:0, shopping:0, entertainment:0, nature:0, food:0 };
                  next.forEach(k => { Object.entries(PRESETS[k]).forEach(([cat,v]) => { combined[cat] += v; }); });
                  const count = next.length;
                  const raw = {}; let sum = 0;
                  Object.entries(combined).forEach(([cat,v]) => { raw[cat] = Math.round(v / count / 5) * 5; sum += raw[cat]; });
                  const diff = 100 - sum;
                  if (diff !== 0) { const top = Object.entries(raw).sort(([,a],[,b])=>b-a)[0][0]; raw[top] += diff; }
                  setTrStyle(raw);
                  return next;
                });
              };
              return (
                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                    <p style={{ ...S.label, margin:0 }}>{styleLabel} <span style={{ color:'#ef4444' }}>*</span></p>
                    <span style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>{styleHint}</span>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                    {Object.entries(CARD_META).map(([key, m]) => {
                      const sel = trStyleCards.includes(key);
                      return (
                        <button key={key} onClick={() => toggleCard(key)}
                          style={{ borderRadius:16, border: sel ? `2.5px solid ${m.color}` : '1.5px solid #e2e8f0',
                            background: sel ? m.color : 'white',
                            padding:'14px 8px 12px', cursor:'pointer', display:'flex', flexDirection:'column',
                            alignItems:'center', gap:5, transition:'all 0.18s',
                            boxShadow: sel ? `0 4px 14px ${m.color}40` : 'none',
                            position:'relative', minHeight:100 }}>
                          {sel && (
                            <div style={{ position:'absolute', top:7, right:7, width:16, height:16, borderRadius:'50%', background:m.accent, display:'flex', alignItems:'center', justifyContent:'center' }}>
                              <span style={{ fontSize:9, fontWeight:900, color:m.color, lineHeight:1 }}>✓</span>
                            </div>
                          )}
                          <span style={{ fontSize:26, lineHeight:1 }}>{m.icon}</span>
                          <span style={{ fontSize:12, fontWeight:900, color: sel ? 'white' : '#0f172a', lineHeight:1.2, textAlign:'center' }}>{m[lk]||m.zh}</span>
                          <span style={{ fontSize:9, fontWeight:600, color: sel ? `${m.accent}cc` : '#94a3b8', lineHeight:1.4, textAlign:'center' }}>{m.sub[lk]||m.sub.zh}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            {/* Transport modes */}
            <div>
              <p style={S.label}>{t.travelMode}</p>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {[{k:'public',l:t.publicTransport,ic:Bus},{k:'self',l:t.selfDrive,ic:Car}].map(({k,l,ic:Ic})=>{
                  const sel=trModes.includes(k);
                  return <button key={k} onClick={()=>toggleMode(k)}
                    style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 16px',borderRadius:50,border:sel?'none':'1.5px solid #e2e8f0',background:sel?'#0f172a':'white',color:sel?'white':'#64748b',fontSize:12,fontWeight:900,cursor:'pointer' }}>
                    <Ic size={13}/>{l}
                  </button>;
                })}
              </div>
            </div>
            {/* Travel companion */}
            <div>
              <p style={S.label}>{lang==='en'?'Travel Type':lang==='ja'?'同行者':lang==='ko'?'동행 유형':lang==='zh-CN'?'出行方式':'出行方式'}</p>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {[
                  { k:'solo',    icon:'🧍', zh:'個人遊',   zhCN:'个人游',  en:'Solo',          ja:'一人旅',          ko:'혼자 여행' },
                  { k:'friends', icon:'👫', zh:'好友同遊', zhCN:'好友同游',en:'With Friends',   ja:'友人と旅行',      ko:'친구와 여행' },
                  { k:'family',  icon:'👨‍👩‍👧', zh:'親子遊',   zhCN:'亲子游',  en:'Family & Kids', ja:'家族・子連れ',    ko:'가족 여행' },
                ].map(({ k, icon, zh, zhCN, en: enL, ja, ko: koL }) => {
                  const label = lang==='en'?enL:lang==='ja'?ja:lang==='ko'?koL:lang==='zh-CN'?zhCN:zh;
                  const sel = trCompanion === k;
                  return (
                    <button key={k} onClick={() => setTrCompanion(k)}
                      style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 16px',borderRadius:50,
                        border: sel?'none':'1.5px solid #e2e8f0',
                        background: sel?'#0f172a':'white',
                        color: sel?'white':'#64748b',
                        fontSize:12,fontWeight:900,cursor:'pointer' }}>
                      <span>{icon}</span>{label}
                    </button>
                  );
                })}
              </div>
              {/* 長者同行開關：獨立於上面的同行關係，勾了就疊加放慢節奏的限制 */}
              <button onClick={() => setTrElderly(v => !v)}
                style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 16px',borderRadius:50,marginTop:8,
                  border: trElderly?'none':'1.5px solid #e2e8f0',
                  background: trElderly?'#0f172a':'white',
                  color: trElderly?'white':'#64748b',
                  fontSize:12,fontWeight:900,cursor:'pointer' }}>
                <span>🦯</span>
                {lang==='en'?'Elderly traveller(s) in the group':lang==='ja'?'高齢者が同行':lang==='ko'?'고령자 동반':lang==='zh-CN'?'有长者同行':'有長者同行'}
              </button>
            </div>
            {/* Summary */}
            {trDestinations.some(d=>d.city) && trDays && (
              <div style={{ background:'#fffbeb', border:'1.5px solid #fde68a', borderRadius:12, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                <MapPin size={13} color="#f59e0b"/>
                <p style={{ fontSize:12, fontWeight:700, color:'#92400e', margin:0 }}>
                  {trDestinations.filter(d=>d.city).map(d=>`${d.city}${d.date?' ('+d.date+')':''}`).join(' → ')} · {trDays}{t.daysSuffix||t.dayUnit||'天'}
                </p>
              </div>
            )}
            {/* Usage hint */}
            {(() => {
              if (user?.email === ADMIN_EMAIL) return null;
              const u = getTripUsage();
              return u.count >= MAX_FREE_PER_DAY ? (
                <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:16 }}>🚫</span>
                  <p style={{ margin:0, fontSize:12, color:'#dc2626', fontWeight:700 }}>
                    {lang==='en' ? `Today's limit of ${MAX_FREE_PER_DAY} itineraries reached. Come back tomorrow!`
                    : lang==='ja' ? `本日の上限（${MAX_FREE_PER_DAY}回）に達しました。`
                    : lang==='ko' ? `오늘 한도(${MAX_FREE_PER_DAY}회)에 도달했습니다.`
                    : `今日行程生成次數（${MAX_FREE_PER_DAY}次）已達上限，請明天再試。`}
                  </p>
                </div>
              ) : (
                <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:16 }}>📢</span>
                  <p style={{ margin:0, fontSize:12, color:'#92400e', fontWeight:700 }}>
                    {lang==='en' ? `Each generation requires a 10-sec sponsor ad. Today: ${u.count}/${MAX_FREE_PER_DAY} used.`
                    : lang==='ja' ? `毎回10秒の広告視聴が必要です。本日：${u.count}/${MAX_FREE_PER_DAY}回使用済み。`
                    : lang==='ko' ? `매 생성 시 10초 광고 시청 필요. 오늘: ${u.count}/${MAX_FREE_PER_DAY}회 사용.`
                    : `每次生成均需觀看10秒廣告。今日已使用 ${u.count}/${MAX_FREE_PER_DAY} 次。`}
                  </p>
                </div>
              );
            })()}
            {/* Disabled-button hint — every condition that disables the generate
                button below now gets a visible reason. Without this, the button
                disables silently and validateTripDates() inside handleGenItinerary
                never even runs (the button can't be clicked), leaving the
                traveller with no clue why nothing happens when they tap it. */}
            {(() => {
              if (loadingTrip) return null;
              const noCity = !trDestinations.some(d=>d.city);
              const noDate = !noCity && trDestinations.some(d=>d.city && !d.date);
              const noDays = !noCity && !noDate && !trDays;
              if (!noCity && !noDate && !noDays) return null;
              const isError = noDate; // missing date after already picking a city is a real validation error; the other two are just "form not finished yet"
              const msg = noCity
                ? (lang==='en' ? 'Please choose at least one destination city.'
                  : lang==='ja' ? '目的地の都市を1つ以上選択してください。'
                  : lang==='ko' ? '목적지 도시를 하나 이상 선택해 주세요.'
                  : '請至少選擇一個目的地城市。')
                : noDate
                ? (lang==='en' ? 'Please pick a date for every destination.'
                  : lang==='ja' ? 'すべての目的地に日付を入力してください。'
                  : lang==='ko' ? '모든 목적지에 날짜를 입력해 주세요.'
                  : '請為每個目的地選擇日期。')
                : (lang==='en' ? 'Please select how many days your trip is.'
                  : lang==='ja' ? '旅行日数を選択してください。'
                  : lang==='ko' ? '여행 일수를 선택해 주세요.'
                  : '請選擇行程天數。');
              return (
                <div style={{ background: isError?'#fef2f2':'#fffbeb', border:`1px solid ${isError?'#fecaca':'#fde68a'}`, borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                  <AlertTriangle size={14} color={isError?'#dc2626':'#f59e0b'}/>
                  <p style={{ margin:0, fontSize:12, color:isError?'#dc2626':'#92400e', fontWeight:700 }}>{msg}</p>
                </div>
              );
            })()}
            {!loadingTrip && (
              <button onClick={()=>setTripStep(2)}
                style={{ padding:'11px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#64748b', fontWeight:900, fontSize:13, cursor:'pointer' }}>
                {lang==='en'?'← Back':lang==='ja'?'← 戻る':lang==='ko'?'← 이전':'← 上一步'}
              </button>
            )}
            <button onClick={()=>{ setGenTripErr(''); handleGenItinerary(); }}
              disabled={loadingTrip||!trDestinations.some(d=>d.city)||!trDays||trDestinations.some(d=>d.city&&!d.date)}
              style={{ ...S.btnDark, justifyContent:'center', display:'flex', alignItems:'center', gap:10, opacity:(!trDestinations.some(d=>d.city)||!trDays||trDestinations.some(d=>d.city&&!d.date))?0.5:1 }}>
              {loadingTrip?<><Loader2 size={17} style={{ animation:'spin 1s linear infinite' }}/>{t.loading}</>:<><Sparkles size={17}/>{t.genItinerary}</>}
            </button>
            {/* Generation progress — real pipeline stages (tripGenStage), with
                finished days (tripStreamDays) revealed as the streaming AI
                response writes them, so an 8-14 day trip's ~20-30s wait shows
                visible progress instead of a bare spinner. */}
            {loadingTrip && (() => {
              const stageLabels = lang==='en'
                ? ['Reading your trip details','Matching real venues in our database','AI is planning each day','Calculating travel times & finalizing']
                : lang==='ja'
                ? ['旅程情報を確認中','実在するスポットと照合中','AIが毎日の旅程を作成中','移動時間を計算・仕上げ中']
                : lang==='ko'
                ? ['여행 정보 확인 중','실제 명소 데이터와 대조 중','AI가 일정을 짜는 중','이동 시간 계산 및 마무리 중']
                : ['整理你的行程需求','比對真實景點資料庫','AI 正在編排每日行程','計算交通時間並排版'];
              return (
                <div style={{ background:'white', border:'1.5px solid #e2e8f0', borderRadius:16, padding:'16px 14px', display:'flex', flexDirection:'column', gap:14 }}>
                  <style>{`@keyframes shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}`}</style>
                  <div style={{ display:'flex', flexDirection:'column', gap:11 }}>
                    {stageLabels.map((label, i) => {
                      const state = tripGenStage > i ? 'done' : tripGenStage === i ? 'active' : 'pending';
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:20, height:20, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                            background: state==='done' ? '#16a34a' : 'white',
                            border: state==='active' ? '2px solid #f59e0b' : state==='done' ? 'none' : '1.5px solid #e2e8f0' }}>
                            {state==='done' && <CheckCircle2 size={13} color="white" style={{ strokeWidth:3 }}/>}
                            {state==='active' && <div style={{ width:7, height:7, borderRadius:'50%', background:'#f59e0b', animation:'pulse 1.1s ease-in-out infinite' }}/>}
                          </div>
                          <span style={{ fontSize:12.5, fontWeight: state==='pending' ? 500 : 700, color: state==='pending' ? '#94a3b8' : '#0f172a' }}>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {tripGenStage >= 2 && trDays > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6, borderTop:'1px solid #f1f5f9', paddingTop:12 }}>
                      {Array.from({ length: trDays }, (_, i) => i + 1).map(dayNum => {
                        const found = tripStreamDays.find(d => d.day === dayNum);
                        return (
                          <div key={dayNum} style={{ display:'grid', gridTemplateColumns:'28px 1fr', gap:10, alignItems:'center' }}>
                            <div style={{ width:28, height:28, borderRadius:8, background: found ? '#fffbeb' : '#f8fafc', color: found ? '#92400e' : '#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800 }}>
                              {t.dayLabel}{dayNum}
                            </div>
                            {found ? (
                              <p style={{ margin:0, fontSize:12, fontWeight:700, color:'#0f172a', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{found.title || found.city}</p>
                            ) : (
                              <div style={{ height:10, borderRadius:5, background:'linear-gradient(90deg,#f1f5f9 25%,#f8fafc 37%,#f1f5f9 63%)', backgroundSize:'400% 100%', animation:'shimmer 1.6s ease infinite', width: `${45 + (dayNum * 13) % 35}%` }}/>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            {genTripErr && (
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:12, padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:18 }}>⛔</span>
                <p style={{ margin:0, fontSize:13, fontWeight:700, color:'#dc2626' }}>{genTripErr}</p>
              </div>
            )}
            </>}
          </div>
          {/* 訪客才顯示：表單卡片下方的登入按鈕，登入後返回行程分頁 */}
          {!isLoggedIn && (
            <button onClick={()=>openLogin('trips')}
              style={{ width:'100%', marginTop:12, padding:'13px 0', borderRadius:14, border:'1.5px solid #f59e0b', background:'white', color:'#b45309', fontSize:13, fontWeight:900, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              {lang==='en' ? `Sign in · unlock up to ${MAX_TRIP_DAYS}-day trips`
                : lang==='ja' ? `ログイン・最大${MAX_TRIP_DAYS}日の旅程を解放`
                : lang==='ko' ? `로그인 · 최대 ${MAX_TRIP_DAYS}일 일정 이용`
                : lang==='zh-CN' ? `登录 · 解锁最多${MAX_TRIP_DAYS}天行程`
                : `登入 · 解鎖最多${MAX_TRIP_DAYS}天行程`}
            </button>
          )}
        </>
      ) : itinerary?.error ? (
        <div style={{ textAlign:'center', padding:'40px 20px' }}>
          <div style={{ width:56,height:56,background:'#fef2f2',borderRadius:18,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
            <AlertTriangle size={28} color="#dc2626"/>
          </div>
          <p style={{ fontSize:15,fontWeight:900,color:'#0f172a',margin:'0 0 8px' }}>{t.noResults}</p>
          <p style={{ fontSize:12,color:'#64748b',margin:'0 0 20px' }}>{t.retryTxt||'請重試'}</p>
          <button onClick={resetTripForm} style={S.btnDark}>{t.redo}</button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
          <div style={{ display:'flex', alignItems:isMobile?'flex-start':'center', justifyContent:'space-between', flexDirection:isMobile?'column':'row', gap:isMobile?10:0 }}>
            <div>
              <p style={{ fontSize:19, fontWeight:900, color:'#0f172a', margin:0 }}>{trDestinations.filter(d=>d.city).map(d=>d.city).join(' · ')||trSelCities.join(' · ')}</p>
              <p style={{ fontSize:12, color:'#64748b', margin:'3px 0 0' }}>
                {trDestinations[0]?.date && <span style={{ color:'#94a3b8' }}>{trDestinations[0].date}</span>}
              </p>
            </div>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
              {/* Share button */}
              <button onClick={()=>{
                const txt = `🗺 EC Travel 行程\n📍 ${trDestinations.filter(d=>d.city).map(d=>d.city).join(' → ')}\n${trDestinations[0]?.date?`📅 ${trDestinations[0].date}\n\n`:'\n'}`+
                  (itinerary.days||[]).map(d=>`Day ${d.day}: ${d.title}\n  ☀️ ${d.morning?.activity||''}${d.lunch?.activity?`\n  🍱 ${d.lunch.activity}`:''}\n  🌤 ${d.afternoon?.activity||''}\n  🌙 ${d.evening?.activity||''}`).join('\n\n');
                if (navigator.share) {
                  navigator.share({ title:'EC Travel 行程', text:txt }).catch(()=>{});
                } else {
                  navigator.clipboard?.writeText(txt).then(()=>showTripToast(t.copied||'行程已複製到剪貼板！')).catch(()=>showTripToast(txt.slice(0,200)+'...'));
                }
              }} style={{ ...S.btnAmber, padding:'8px 13px', fontSize:11 }}><Share2 size={13}/>{t.share}</button>
              <button onClick={()=>downloadPDF()} style={{ padding:'8px 13px', background:'#f1f5f9', border:'none', borderRadius:11, fontSize:11, fontWeight:900, cursor:'pointer', color:'#475569', display:'flex', alignItems:'center', gap:5 }}><Download size={13}/>{t.downloadPDF}</button>
              {/* Save trip button */}
              <button onClick={()=>{
                // 訪客：先用 Google 登入，登入後自動儲存這個行程（見 pendingSaveAfterLoginRef 的 useEffect）
                if (!isLoggedIn) { pendingSaveAfterLoginRef.current = true; openGoogleGate('save'); return; }
                if (savedTrips.length >= MAX_SAVED_TRIPS) { showTripToast((t.maxTrips||'最多只能儲存 {n} 個行程，請先刪除舊行程').replace('{n}',MAX_SAVED_TRIPS), true); return; }
                saveCurrentTrip();
                showTripToast(t.tripSaved||'✅ 行程已儲存！可在「我的 → 儲存行程」查看');
              }} style={{ padding:'8px 13px', background:'#f0fdf4', border:'1.5px solid #bbf7d0', borderRadius:11, fontSize:11, fontWeight:900, cursor:'pointer', color:'#16a34a', display:'flex', alignItems:'center', gap:5 }}>
                💾 {t.saveTrip||'儲存行程'}
              </button>
              {/* 重新規劃放最後，未儲存的行程先確認，避免誤撳清走行程又浪費一次生成次數 */}
              <button onClick={()=>{
                const isSaved = savedTrips.some(s => s.itinerary === itinerary);
                if (!isSaved) {
                  const msg = lang==='en' ? 'This itinerary hasn\'t been saved. Start over and discard it?'
                    : lang==='ja' ? 'この旅程はまだ保存されていません。破棄して最初からやり直しますか？'
                    : lang==='ko' ? '이 일정은 아직 저장되지 않았습니다. 삭제하고 다시 계획할까요?'
                    : lang==='zh-CN' ? '这个行程还未储存，确定要放弃并重新规划吗？'
                    : '這個行程還未儲存，確定要放棄並重新規劃嗎？';
                  if (!window.confirm(msg)) return;
                }
                resetTripForm();
              }} style={{ padding:'8px 13px', background:'white', border:'1.5px solid #e2e8f0', borderRadius:11, fontSize:11, fontWeight:900, cursor:'pointer', color:'#94a3b8' }}>{t.redo}</button>
            </div>
          </div>
          {/* Trip toast notification */}
          {tripToast && (
            <div style={{ background:tripToast.isError?'#fef2f2':'#f0fdf4', border:`1px solid ${tripToast.isError?'#fecaca':'#bbf7d0'}`, borderRadius:12, padding:'11px 16px', fontSize:12, fontWeight:700, color:tripToast.isError?'#dc2626':'#16a34a' }}>
              {tripToast.msg}
            </div>
          )}
          {/* Day tabs */}
          <div style={{ display:'flex', gap:7, overflowX:'auto', paddingBottom:3 }}>
            {itinerary.days?.map((d,i)=>{
              const lastIdx = (itinerary.days?.length||1)-1;
              return (
                <button key={i} onClick={()=>{
                  setActiveDay(i);
                  if (i===lastIdx && !tripRating && !ratingDismissed) {
                    setTimeout(()=>setShowRatingPopup(true), 600);
                  }
                }}
                  style={{ flexShrink:0, padding:'9px 16px', borderRadius:13, border:activeDay===i?'none':'1.5px solid #e2e8f0', background:activeDay===i?'#0f172a':'white', color:activeDay===i?'white':'#64748b', fontSize:12, fontWeight:900, cursor:'pointer' }}>
                  {t.dayLabel}{d.day}{t.dayUnit}
                </button>
              );
            })}
          </div>
          {/* 行程地圖：預設顯示每日地區（當日高亮），可切換做當日景點路線；只用有資料庫座標嘅時段 */}
          {(() => {
            const mapDays = (itinerary.days || []).map(d => {
              const cityKey = CITY_EN_MAP[d.city] || d.city || '';
              const cityPins = Object.fromEntries(getDistrictPins(cityKey).map(p => [p.name, { lat:p.lat, lng:p.lng }]));
              // 資料庫分區可能比地區按鈕細（例如中環屬中西區），對應返按鈕名先搵圖釘
              const chipOf = (dist) => !dist ? null : cityPins[dist] ? dist : Object.keys(cityPins).find(c => matchesDistrict(dist, c, cityKey)) || null;
              const stops = ['morning','lunch','afternoon','evening'].map(k => d[k]).concat(d.extras || [])
                .filter(s => s && s._lat != null && s._lng != null)
                .map(s => ({ name: s.location || s.activity, lat: s._lat, lng: s._lng, district: chipOf(s.district) }));
              return { cityPins, stops };
            });
            const hasAreas = mapDays.some(d => d.stops.some(s => s.district && d.cityPins[s.district]));
            const hasRoute = (mapDays[activeDay]?.stops.length || 0) > 0;
            if (!hasAreas && !hasRoute) return null;
            const mode = tripMapMode === 'route' || !hasAreas ? 'route' : 'areas';
            const modeLabels = {
              areas: lang==='en'?'Areas by day':lang==='zh-CN'?'每日区域':lang==='ja'?'日ごとのエリア':lang==='ko'?'일별 지역':'每日地區',
              route: lang==='en'?'Day route':lang==='zh-CN'?'当日路线':lang==='ja'?'当日のルート':lang==='ko'?'당일 경로':'當日路線',
            };
            return (
              <div style={{ background:'white', borderRadius:16, border:'1.5px solid #f1f5f9', padding:12, display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
                  <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#0f172a', display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:10, height:10, borderRadius:3, background:dayColor(activeDay) }}/>
                    {lang==='en'?'Trip map':lang==='zh-CN'?'行程地图':lang==='ja'?'旅程マップ':lang==='ko'?'여행 지도':'行程地圖'}
                  </p>
                  {hasAreas && hasRoute && (
                    <div style={{ display:'flex', gap:6 }}>
                      {['areas','route'].map(m => (
                        <button key={m} onClick={()=>setTripMapMode(m)} aria-pressed={mode===m}
                          style={{ fontSize:11, fontWeight:800, padding:'6px 11px', borderRadius:10, cursor:'pointer', border:`1.5px solid ${mode===m?'#0f172a':'#e2e8f0'}`, background:'white', color:mode===m?'#0f172a':'#64748b' }}>
                          {modeLabels[m]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <TripMap days={mapDays} mode={mode} activeDay={activeDay} lang={lang} labelOf={(d)=>districtLabels[d]?.[lang] || d}/>
              </div>
            );
          })()}
          {/* Day detail */}
          {itinerary.days?.[activeDay]&&(()=>{
            const day=itinerary.days[activeDay];
            const SESSION_META = [
              { key:'morning',   time:'09:00', label:t.morning,   color:'#f59e0b', bg:'#fffbeb' },
              { key:'lunch',     time:'12:00', label:t.lunch,     color:'#10b981', bg:'#ecfdf5' },
              { key:'afternoon', time:'lunch' in day ? '14:00' : '13:00', label:t.afternoon, color:'#3b82f6', bg:'#eff6ff' },
              { key:'evening',   time:'18:30', label:t.evening,   color:'#8b5cf6', bg:'#f5f3ff' },
            ];
            // 午餐只喺中間日有（有 lunch key 先顯示，清空咗都保留空位俾佢重新揀）
            const sessions = SESSION_META.filter(m => m.key !== 'lunch' || 'lunch' in day).map(m => ({ ...m, d: day[m.key] }));
            const activeSessions = sessions.filter(s => s.d);
            const daySummary = activeSessions.map(s => s.d.activity).filter(Boolean).join(' · ');
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:11 }}>
                <div style={{ background:'linear-gradient(135deg,#f59e0b,#ef4444)', borderRadius: tripRobotOpen ? '16px 16px 0 0' : 16, padding:'14px 18px', color:'white' }}>
                  <p style={{ fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:2, opacity:0.7, margin:'0 0 2px' }}>{t.dayLabel}{day.day}{t.dayUnit}{day.city?` · ${day.city}`:''}</p>
                  <p style={{ fontSize:17, fontWeight:900, margin:'0 0 4px' }}>{day.title}</p>
                  {daySummary&&<p style={{ fontSize:11, opacity:0.75, margin:'0 0 10px', lineHeight:1.5 }}>📍 {daySummary}</p>}
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  {(() => {
                    // 連續重複嘅地點（例如上午同午餐都喺同一個商場）合併做一個站
                    const locs = [day.morning?.location, day.lunch?.location, day.afternoon?.location, day.evening?.location]
                      .filter(Boolean).filter((l, i, arr) => i === 0 || l !== arr[i - 1]);
                    if (locs.length < 2) return null;
                    const ctx = day.city || '';
                    const tagged = locs.map(l => encodeURIComponent(ctx ? `${l} ${ctx}` : l));
                    const origin = tagged[0];
                    const dest   = tagged[tagged.length - 1];
                    const wpts   = tagged.slice(1, -1).join('|');
                    // Google Maps 大眾運輸唔支援中途站，有中途站會顯示「無法規劃路線」，所以多站改用駕車模式顯示成條路線
                    const mode   = wpts ? 'driving' : 'transit';
                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${wpts?`&waypoints=${wpts}`:''}&travelmode=${mode}`;
                    const label = lang==='en'?'View Route on Google Maps':lang==='zh-CN'?'在 Google 地图查看路线':lang==='ja'?'Google マップでルートを見る':lang==='ko'?'Google 지도에서 경로 보기':'在 Google Maps 查看路線';
                    return (
                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.18)', border:'1.5px solid rgba(255,255,255,0.35)', borderRadius:10, padding:'7px 13px', color:'white', textDecoration:'none', fontSize:11, fontWeight:900 }}>
                        🗺 {label}
                      </a>
                    );
                  })()}
                  <TripRobotTrigger open={tripRobotOpen} onToggle={() => { if (!isLoggedIn) { openGoogleGate('robot'); return; } setTripRobotOpen(o => !o); }} lang={lang} />
                  </div>
                </div>
                {tripRobotOpen && (
                  <TripRobotPanel
                    itinerary={itinerary}
                    lang={lang}
                    onUpdateItinerary={(updated) => setItinerary(prev => reconcileTweakedItinerary(updated, prev))}
                    onClose={() => setTripRobotOpen(false)}
                  />
                )}
                {/* Vertical timeline */}
                <div style={{ background:'white', borderRadius:16, border:'1.5px solid #f1f5f9', padding:'16px 14px' }}>
                {sessions.map(({key, time, label:slotLabel, color, bg, d:sd}, si) => {
                  if (!sd) {
                    // Deliberately empty (cleared, or the AI never filled it) —
                    // still offer the SAME 換一批/瀏覽分類 pair as a filled slot
                    // (anchored on a sibling slot's coordinates via
                    // swapCandidates' fallback) so re-filling it stays within
                    // the day's original area instead of starting from scratch.
                    const cityRaw = day.city || trDestinations[0]?.city || trCountry || '';
                    const shown = swapBatchShown(day, key, cityRaw);
                    const browseKey = `${day.day}_${key}`;
                    const browsing = categoryBrowseKey === browseKey;
                    const label = lang==='en'?'🔄 Swap for another batch':lang==='zh-CN'?'🔄 换一批':lang==='ja'?'🔄 別の候補に入れ替え':lang==='ko'?'🔄 다른 후보로 교체':'🔄 換一批';
                    const browseLabel = lang==='en'?'📂 Browse categories':lang==='zh-CN'?'📂 浏览分类':lang==='ja'?'📂 カテゴリを見る':lang==='ko'?'📂 카테고리 보기':'📂 瀏覽分類';
                    const detailLabel = lang==='en'?'Details':lang==='ja'?'詳細':lang==='ko'?'상세':'詳情';
                    const pickLabel = lang==='en'?'Pick':lang==='ja'?'選択':lang==='ko'?'선택':'選擇';
                    return (
                      <div key={key} style={{ display:'flex', gap:12, marginBottom:14 }}>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:44, flexShrink:0, paddingTop:2 }}>
                          <span style={{ fontSize:11, fontWeight:800, color:'#cbd5e1', lineHeight:1, marginBottom:8, whiteSpace:'nowrap' }}>{time}</span>
                          <div style={{ width:12, height:12, borderRadius:'50%', border:'2px dashed #cbd5e1', flexShrink:0 }}/>
                        </div>
                        <div style={{ flex:1 }}>
                          <p style={{ fontSize:10, fontWeight:900, color:'#cbd5e1', margin:'0 0 6px', textTransform:'uppercase', letterSpacing:1 }}>{slotLabel}</p>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                            {shown.length > 0 && (
                              <button onClick={() => swapNextBatch(day, key)}
                                style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#475569' }}>
                                {label}
                              </button>
                            )}
                            <button onClick={() => setCategoryBrowseKey(browsing ? null : browseKey)}
                              style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#475569' }}>
                              {browseLabel}
                            </button>
                          </div>
                          {browsing ? (
                            <ActivityPicker
                              favorites={favorites}
                              onToggleFav={toggleFav}
                              attractions={managedAttractions.filter(a => cityAliasesFor(cityRaw).has((a.city || '').toLowerCase()))}
                              lang={lang}
                              onPick={(a) => { swapSlot(day, key, a, cityRaw); setCategoryBrowseKey(null); }}
                              onDetail={(a) => { setSelAttraction({ ...a, cityKey: cityRaw }); fetchAttractionDetail(a, cityRaw); }}
                            />
                          ) : (
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(104px, 1fr))', gap:8 }}>
                              {shown.map((a, ai) => (
                                <div key={ai} style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:5 }}>
                                  <div style={{ width:'100%', height:88, borderRadius:7, overflow:'hidden', background:'#f1f5f9', position:'relative' }}>
                                    <SwapCandidateThumb name={a.name} city={cityRaw}/>
                                    <FavHeartButton name={a.name} cityKey={cityRaw} favorites={favorites} onToggleFav={toggleFav}/>
                                    {a.rating && (
                                      <span style={{ position:'absolute', bottom:3, right:3, background:'rgba(15,23,42,0.75)', color:'#fbbf24', fontSize:10, fontWeight:800, borderRadius:5, padding:'2px 5px' }}>
                                        ★{Number(a.rating).toFixed(1)}
                                      </span>
                                    )}
                                  </div>
                                  <p title={localName(a, lang)} style={{ fontSize:11, fontWeight:700, color:'#475569', margin:'6px 0 6px', lineHeight:1.3, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                                    {localName(a, lang)}
                                  </p>
                                  <div style={{ display:'flex', gap:4 }}>
                                    <button onClick={() => { setSelAttraction({ ...a, cityKey: cityRaw }); fetchAttractionDetail(a, cityRaw); }}
                                      style={{ flex:1, fontSize:11, fontWeight:700, border:'none', background:'#f0fdf4', color:'#15803d', borderRadius:6, padding:'8px 0', minHeight:32, cursor:'pointer' }}>
                                      {detailLabel}
                                    </button>
                                    <button onClick={() => swapSlot(day, key, a, cityRaw)}
                                      style={{ flex:1, fontSize:11, fontWeight:700, border:'none', background:'#eff6ff', color:'#1677ff', borderRadius:6, padding:'8px 0', minHeight:32, cursor:'pointer' }}>
                                      {pickLabel}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  const ck = day.city || trDestinations[0]?.city || trCountry || '';
                  const attrName = sd.location || sd.activity;
                  const openDetail = () => { setSelAttraction({name:attrName,cityKey:ck,photoKw:attrName,category:sd.category,lat:sd._lat ?? null,lng:sd._lng ?? null}); fetchAttractionDetail({name:attrName},ck); };
                  const activeIdx = activeSessions.findIndex(s => s.key === key);
                  const isLastActive = activeIdx === activeSessions.length - 1;
                  return (
                    <React.Fragment key={key}>
                      <div style={{ display:'flex', gap:12 }}>
                        {/* Timeline column: time + dot + line */}
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:44, flexShrink:0, paddingTop:2 }}>
                          <span style={{ fontSize:11, fontWeight:800, color, lineHeight:1, marginBottom:8, whiteSpace:'nowrap' }}>{time}</span>
                          <div style={{ width:12, height:12, borderRadius:'50%', background:color, flexShrink:0, boxShadow:`0 0 0 3px ${color}33` }}/>
                          {!isLastActive&&<div style={{ width:2, flex:1, background:'#e2e8f0', minHeight:28, marginTop:3 }}/>}
                        </div>
                        {/* Content */}
                        <div style={{ flex:1, paddingBottom: isLastActive ? 0 : 18 }}>
                          <p style={{ fontSize:10, fontWeight:900, color, margin:'0 0 6px', textTransform:'uppercase', letterSpacing:1 }}>{slotLabel}</p>
                          <div style={{ background:bg, borderRadius:12, padding:'12px 14px', border:`1px solid ${color}22` }}>
                            <button onClick={openDetail}
                              style={{ fontWeight:900,color:'#0f172a',margin:'0 0 4px',fontSize:13,background:'none',border:'none',padding:0,cursor:'pointer',textAlign:'left',textDecoration:'underline',textDecorationStyle:'dotted',textDecorationColor:'#94a3b8',textUnderlineOffset:3,display:'block',width:'100%' }}>
                              {sd.activity}
                            </button>
                            <p style={{ fontSize:11,color:'#64748b',margin:'0 0 4px' }}>
                              📍 {sd.location}
                              {sd.district&&sd.district!==sd.location&&<span style={{ marginLeft:6,background:'rgba(255,255,255,0.7)',borderRadius:6,padding:'1px 6px',fontSize:10,color:'#94a3b8',fontWeight:700 }}>{sd.district}</span>}
                            </p>
                            <div style={{ display:'flex',gap:10,marginBottom:sd.tip?4:0 }}>
                              <span style={{ fontSize:10,color:'#94a3b8',display:'flex',alignItems:'center',gap:3 }}><Clock size={10}/>{sd.duration}</span>
                              <span style={{ fontSize:10,color:'#f59e0b',fontWeight:700 }}>{sd.cost}</span>
                            </div>
                            {sd.tip&&<p style={{ fontSize:10,color:'#475569',background:'rgba(255,255,255,0.7)',borderRadius:9,padding:'6px 10px',margin:0,border:'1px solid #fde68a' }}>💡 {sd.tip}</p>}
                          </div>
                          <div style={{ display:'flex', gap:6, marginTop:8 }}>
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((sd.activity||'')+(sd.location?' '+sd.location:'')+(day.city?' '+day.city:''))}`}
                              target="_blank" rel="noopener noreferrer"
                              style={{ display:'flex',alignItems:'center',gap:4,fontSize:10,color:'#1677ff',fontWeight:700,textDecoration:'none',background:'#eff6ff',borderRadius:7,padding:'4px 8px' }}>
                              <MapPin size={10}/>{lang==='en'?'Map':lang==='ja'?'地図':lang==='ko'?'지도':'地圖'}
                            </a>
                            <button onClick={openDetail}
                              style={{ display:'flex',alignItems:'center',gap:4,fontSize:10,fontWeight:700,border:'none',background:'#f0fdf4',color:'#15803d',borderRadius:7,padding:'4px 8px',cursor:'pointer' }}>
                              ℹ️ {lang==='en'?'Details':lang==='ja'?'詳細':lang==='ko'?'상세':'詳情'}
                            </button>
                            <button onClick={()=>toggleFav(sd.activity, day.city||trCountry)}
                              style={{ display:'flex',alignItems:'center',gap:4,fontSize:10,fontWeight:700,border:'none',background:favorites.includes(sd.activity)?'#fef2f2':'#f1f5f9',color:favorites.includes(sd.activity)?'#ef4444':'#64748b',borderRadius:7,padding:'4px 8px',cursor:'pointer' }}>
                              <Heart size={10} fill={favorites.includes(sd.activity)?'#ef4444':'none'} color={favorites.includes(sd.activity)?'#ef4444':'#64748b'}/>
                              {favorites.includes(sd.activity)
                                ?(lang==='en'?'Saved':lang==='ja'?'保存済み':lang==='ko'?'저장됨':'已收藏')
                                :(lang==='en'?'Save':lang==='ja'?'保存':lang==='ko'?'저장':'收藏')}
                            </button>
                          </div>
                          {/* Nearby real DB venues, filtered to a compatible category — always shown as
                              a 5-card row; 換一批 cycles to the next page of the pool. */}
                          {(() => {
                            if (isLogisticsSlot(sd)) return null;
                            const cityRaw = day.city || trDestinations[0]?.city || trCountry || '';
                            const shown = swapBatchShown(day, key, cityRaw);
                            const browseKey = `${day.day}_${key}`;
                            const browsing = categoryBrowseKey === browseKey;
                            const label = lang==='en'?'🔄 Swap for another batch':lang==='zh-CN'?'🔄 换一批':lang==='ja'?'🔄 別の候補に入れ替え':lang==='ko'?'🔄 다른 후보로 교체':'🔄 換一批';
                            const browseLabel = lang==='en'?'📂 Browse categories':lang==='zh-CN'?'📂 浏览分类':lang==='ja'?'📂 カテゴリを見る':lang==='ko'?'📂 카테고리 보기':'📂 瀏覽分類';
                            const clearLabel = lang==='en'?'✕ Clear':lang==='zh-CN'?'✕ 清空':lang==='ja'?'✕ 空にする':lang==='ko'?'✕ 비우기':'✕ 清空';
                            const detailLabel = lang==='en'?'Details':lang==='ja'?'詳細':lang==='ko'?'상세':'詳情';
                            const pickLabel = lang==='en'?'Pick':lang==='ja'?'選択':lang==='ko'?'선택':'選擇';
                            return (
                              <div style={{ marginTop:10, paddingTop:8, borderTop:'1px dashed #e2e8f0' }}>
                                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                                  {shown.length > 0 && (
                                    <button onClick={() => swapNextBatch(day, key)}
                                      style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#475569', display:'flex', alignItems:'center', gap:4 }}>
                                      {label}{sd.district?` · ${sd.district}`:''}
                                    </button>
                                  )}
                                  <button onClick={() => setCategoryBrowseKey(browsing ? null : browseKey)}
                                    style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#475569', display:'flex', alignItems:'center', gap:4 }}>
                                    {browseLabel}
                                  </button>
                                  <button onClick={() => clearSlot(day, key)}
                                    style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #fecaca', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#dc2626', display:'flex', alignItems:'center', gap:4 }}>
                                    {clearLabel}
                                  </button>
                                </div>
                                {browsing ? (
                                  <ActivityPicker
                                    favorites={favorites}
                                    onToggleFav={toggleFav}
                                    attractions={managedAttractions.filter(a => cityAliasesFor(cityRaw).has((a.city || '').toLowerCase()))}
                                    lang={lang}
                                    onPick={(a) => { swapSlot(day, key, a, cityRaw); setCategoryBrowseKey(null); }}
                                    onDetail={(a) => { setSelAttraction({ ...a, cityKey: cityRaw }); fetchAttractionDetail(a, cityRaw); }}
                                  />
                                ) : (
                                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(104px, 1fr))', gap:8 }}>
                                    {shown.map((a, ai) => (
                                      <div key={ai}
                                        style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:5 }}>
                                        <div style={{ width:'100%', height:88, borderRadius:7, overflow:'hidden', background:'#f1f5f9', position:'relative' }}>
                                          <SwapCandidateThumb name={a.name} city={cityRaw}/>
                                          <FavHeartButton name={a.name} cityKey={cityRaw} favorites={favorites} onToggleFav={toggleFav}/>
                                          {a.rating && (
                                            <span style={{ position:'absolute', bottom:3, right:3, background:'rgba(15,23,42,0.75)', color:'#fbbf24', fontSize:10, fontWeight:800, borderRadius:5, padding:'2px 5px' }}>
                                              ★{Number(a.rating).toFixed(1)}
                                            </span>
                                          )}
                                        </div>
                                        <p title={localName(a, lang)} style={{ fontSize:11, fontWeight:700, color:'#475569', margin:'6px 0 6px', lineHeight:1.3, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                                          {localName(a, lang)}
                                        </p>
                                        <div style={{ display:'flex', gap:4 }}>
                                          <button onClick={() => { setSelAttraction({ ...a, cityKey: cityRaw }); fetchAttractionDetail(a, cityRaw); }}
                                            style={{ flex:1, fontSize:11, fontWeight:700, border:'none', background:'#f0fdf4', color:'#15803d', borderRadius:6, padding:'8px 0', minHeight:32, cursor:'pointer' }}>
                                            {detailLabel}
                                          </button>
                                          <button onClick={() => swapSlot(day, key, a, cityRaw)}
                                            style={{ flex:1, fontSize:11, fontWeight:700, border:'none', background:'#eff6ff', color:'#1677ff', borderRadius:6, padding:'8px 0', minHeight:32, cursor:'pointer' }}>
                                            {pickLabel}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      {/* Travel to next：每段獨立開大眾運輸（鐵路＋巴士）路線，因為 Google Maps 大眾運輸唔支援中途站 */}
                      {!isLastActive&&(() => {
                        const nextLoc = activeSessions[activeIdx + 1]?.d?.location;
                        const legOk = sd.location && nextLoc && nextLoc !== sd.location;
                        if (!sd.travelToNext && !legOk) return null;
                        const withCity = l => encodeURIComponent(day.city ? `${l} ${day.city}` : l);
                        const legLabel = lang==='en'?'Train / bus route':lang==='zh-CN'?'铁路／巴士路线':lang==='ja'?'電車・バスのルート':lang==='ko'?'열차·버스 경로':'鐵路／巴士路線';
                        return (
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', paddingLeft:56, paddingBottom:4 }}>
                            {sd.travelToNext&&(
                              <span style={{ fontSize:10,color:'#94a3b8',fontWeight:700,display:'flex',alignItems:'center',gap:4,background:'#f8fafc',borderRadius:8,padding:'3px 10px',border:'1px solid #e2e8f0' }}>
                                🚶 {sd.travelToNext}
                              </span>
                            )}
                            {legOk&&(
                              <a href={`https://www.google.com/maps/dir/?api=1&origin=${withCity(sd.location)}&destination=${withCity(nextLoc)}&travelmode=transit`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ fontSize:10,color:'#1677ff',fontWeight:700,display:'flex',alignItems:'center',gap:4,background:'#eff6ff',borderRadius:8,padding:'3px 10px',border:'1px solid #bfdbfe',textDecoration:'none' }}>
                                🚆 {legLabel}
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
                </div>
                {/* Extra activities — genuinely additive, up to MAX_EXTRAS,
                    on top of the fixed morning/afternoon/evening slots
                    (which never get replaced by adding one of these). */}
                {(() => {
                  const cityRaw = day.city || trDestinations[0]?.city || trCountry || '';
                  const extras = day.extras || [];
                  const attractionsForCity = managedAttractions.filter(a => cityAliasesFor(cityRaw).has((a.city || '').toLowerCase()));
                  const extrasTitle = lang==='en'?'Extra activities':lang==='zh-CN'?'额外新增':lang==='ja'?'追加のアクティビティ':lang==='ko'?'추가 활동':'額外新增';
                  const addExtraLabel = (lang==='en'?'+ Add activity':lang==='zh-CN'?'+ 添加活动':lang==='ja'?'+ アクティビティを追加':lang==='ko'?'+ 활동 추가':'+ 新增景點') + ` (${extras.length}/${MAX_EXTRAS})`;
                  const browseLabel = lang==='en'?'📂 Browse categories':lang==='zh-CN'?'📂 浏览分类':lang==='ja'?'📂 カテゴリを見る':lang==='ko'?'📂 카테고리 보기':'📂 瀏覽分類';
                  const removeLabel = lang==='en'?'🗑 Remove':lang==='zh-CN'?'🗑 移除':lang==='ja'?'🗑 削除':lang==='ko'?'🗑 삭제':'🗑 移除';
                  const addExtraKey = `${day.day}_extra_new`;
                  return (
                    <div style={{ marginTop:16 }}>
                      {extras.length > 0 && (
                        <p style={{ fontSize:10, fontWeight:900, color:'#94a3b8', textTransform:'uppercase', letterSpacing:1, margin:'0 0 8px' }}>{extrasTitle}</p>
                      )}
                      {extras.map((ex, idx) => {
                        const extraKey = `${day.day}_extra_${idx}`;
                        const browsing = categoryBrowseKey === extraKey;
                        return (
                          <div key={idx} style={{ ...S.card, padding:'12px 14px', marginBottom:10 }}>
                            <p style={{ fontWeight:900, color:'#0f172a', margin:'0 0 4px', fontSize:13 }}>{ex.activity}</p>
                            <p style={{ fontSize:11, color:'#64748b', margin:'0 0 8px' }}>
                              📍 {ex.location}
                              {ex.district && ex.district!==ex.location && <span style={{ marginLeft:6, background:'#f1f5f9', borderRadius:6, padding:'1px 6px', fontSize:10, color:'#94a3b8', fontWeight:700 }}>{ex.district}</span>}
                            </p>
                            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                              <button onClick={() => setCategoryBrowseKey(browsing ? null : extraKey)}
                                style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #e2e8f0', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#475569' }}>
                                {browseLabel}
                              </button>
                              <button onClick={() => removeExtraActivity(day, idx)}
                                style={{ fontSize:11, fontWeight:800, background:'white', border:'1px solid #fecaca', borderRadius:10, padding:'6px 11px', cursor:'pointer', color:'#dc2626' }}>
                                {removeLabel}
                              </button>
                            </div>
                            {browsing && (
                              <ActivityPicker
                                favorites={favorites}
                                onToggleFav={toggleFav}
                                attractions={attractionsForCity}
                                lang={lang}
                                onPick={(a) => { swapExtraActivity(day, idx, a); setCategoryBrowseKey(null); }}
                                onDetail={(a) => { setSelAttraction({ ...a, cityKey: cityRaw }); fetchAttractionDetail(a, cityRaw); }}
                              />
                            )}
                          </div>
                        );
                      })}
                      {extras.length < MAX_EXTRAS && (
                        <div>
                          <button onClick={() => setCategoryBrowseKey(categoryBrowseKey === addExtraKey ? null : addExtraKey)}
                            style={{ width:'100%', border:'1.5px dashed #e2e8f0', background:'#f8fafc', borderRadius:12, padding:'12px 14px', color:'#94a3b8', fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'left' }}>
                            {addExtraLabel}
                          </button>
                          {categoryBrowseKey === addExtraKey && (
                            <ActivityPicker
                              favorites={favorites}
                              onToggleFav={toggleFav}
                              attractions={attractionsForCity}
                              lang={lang}
                              onPick={(a) => { addExtraActivity(day, a); setCategoryBrowseKey(null); }}
                              onDetail={(a) => { setSelAttraction({ ...a, cityKey: cityRaw }); fetchAttractionDetail(a, cityRaw); }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* 目的地專屬優惠碼 — 放在住宿推薦之上 */}
                <RelevantCoupons
                  city={trDestinations.filter(d=>d.city).map(d=>d.city).join(' ')}
                  country={trDestinations[0]?.country || ''}
                  lang={lang}/>
                {day.hotel&&(
                  <div style={S.card}>
                    <p style={{ fontSize:9,fontWeight:900,textTransform:'uppercase',letterSpacing:2,color:'#94a3b8',margin:'0 0 8px' }}>🏨 {t.hotel}</p>
                    <DayHotelOptions hotel={day.hotel} city={day.city} day={day} lang={lang} currency={currency} t={t} />
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Affiliate Booking Section ── */}
          {(() => {
            // Collect top venues from VENUE_DB per city
            const getVenues = (cityEn, max=4) => {
              const out=[];
              for (const cities of Object.values(VENUE_DB)) {
                if (cities[cityEn]) out.push(...cities[cityEn]);
                if (out.length>=max) break;
              }
              return out.slice(0,max);
            };

            const cities = trDestinations.filter(d=>d.city).map(d=>({ key:d.city, display:d.city, en:CITY_EN[d.city]||d.city }));
            const primary = cities[0];
            const primaryLinks = primary ? getAffiliateLinks(primary.en) : {};

            return (
              <div style={{ display:'flex', flexDirection:'column', gap:12, marginTop:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <Ticket size={16} color="#f59e0b"/>
                  <p style={{ fontSize:16, fontWeight:900, color:'#0f172a', margin:0 }}>
                    {lang==='en'?'Book Your Trip':lang==='ja'?'旅行を予約':lang==='ko'?'여행 예약':'預訂你的行程'}
                  </p>
                </div>

                {/* 🏨 Klook 酒店優惠 — 只在目的地有優惠時出現，放在 Stay22 地圖上面 */}
                {cities.map(city => <KlookHotelDealsCard key={city.key} city={city.key} lang={lang}/>)}

                {/* 🏨 住宿 — live Stay22 map for the primary destination, real listings instead of an AI guess */}
                {primary && (
                  <div style={{ borderRadius:16, border:'1.5px solid #e2e8f0', overflow:'hidden' }}>
                    <div style={{ background:'linear-gradient(135deg,#0f9488,#14b8a6)', padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:14 }}>🏨</span>
                      <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#fff' }}>
                        {(lang==='en'||lang==='ja'||lang==='ko')?`Hotels near ${primary.display}`:`${primary.display} 附近住宿`}
                      </p>
                    </div>
                    <iframe
                      src={`https://www.stay22.com/embed/gm?aid=${STAY22_AID}&address=${encodeURIComponent(primary.en)}&currency=${currency}`}
                      style={{ width:'100%', height:380, border:'none', display:'block' }}
                      loading="lazy"
                      title="Stay22 hotel map"
                    />
                  </div>
                )}

                {/* 🎡 景點體驗 — from VENUE_DB per city */}
                {cities.map(city=>{
                  const venues = getVenues(city.en, 4);
                  if (!venues.length) return null;
                  const cl = getAffiliateLinks(city.en);
                  return (
                    <div key={city.key} style={{ borderRadius:16, border:'1.5px solid #e2e8f0', overflow:'hidden' }}>
                      <div style={{ background:'linear-gradient(135deg,#ff5a00,#ff8c42)', padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:14 }}>🎡</span>
                        <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#fff' }}>
                          {city.display} {(lang==='en'||lang==='ja'||lang==='ko')?'Top Experiences':'熱門景點體驗'}
                        </p>
                      </div>
                      <div style={{ background:'#fff', padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
                        {venues.map((v,i)=>(
                          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, paddingBottom:i<venues.length-1?10:0, borderBottom:i<venues.length-1?'1px solid #f1f5f9':'none' }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <p style={{ margin:'0 0 2px', fontSize:12, fontWeight:900, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.title}</p>
                              <p style={{ margin:0, fontSize:10, color:'#64748b' }}>{v.highlight} · <span style={{ color:'#f59e0b', fontWeight:700 }}>{v.price}</span></p>
                            </div>
                            <a href={v.link||cl.klook} target="_blank" rel="sponsored noopener noreferrer"
                              style={{ textDecoration:'none', background:v.platform==='Klook'?'#ff5a00':v.platform==='KKday'?'#00b300':'#0f172a', color:(v.platform==='Klook'||v.platform==='KKday')?'#0f172a':'#fff', borderRadius:8, padding:'6px 11px', fontSize:10, fontWeight:900, whiteSpace:'nowrap', flexShrink:0 }}>
                              {v.platform==='Klook'?'Klook':v.platform==='KKday'?'KKday':'立即購票'}
                            </a>
                          </div>
                        ))}
                        <div style={{ display:'flex', gap:8, paddingTop:4 }}>
                          <a href={cl.klook} target="_blank" rel="sponsored noopener noreferrer"
                            style={{ flex:1, textDecoration:'none', border:'1.5px solid #ff5a00', color:'#c24400', borderRadius:8, padding:'7px 0', textAlign:'center', fontSize:11, fontWeight:800 }}>Klook 更多 →</a>
                          <a href={cl.kkday} target="_blank" rel="sponsored noopener noreferrer"
                            style={{ flex:1, textDecoration:'none', border:'1.5px solid #00b300', color:'#00b300', borderRadius:8, padding:'7px 0', textAlign:'center', fontSize:11, fontWeight:800 }}>KKday 更多 →</a>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* ✈️ 機票套餐 */}
                {primary && (
                  <a href={primaryLinks.trip} target="_blank" rel="sponsored noopener noreferrer"
                    style={{ textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'space-between', background:'linear-gradient(135deg,#1677ff,#40a9ff)', borderRadius:16, padding:'14px 18px', gap:12 }}>
                    <div>
                      <p style={{ margin:'0 0 2px', fontSize:11, color:'rgba(255,255,255,0.7)', fontWeight:700 }}>✈️ {(lang==='en'||lang==='ja'||lang==='ko')?'Flights & Packages':'機票 + 酒店套餐'}</p>
                      <p style={{ margin:0, fontSize:14, fontWeight:900, color:'#fff' }}>Trip.com — {(lang==='en'||lang==='ja'||lang==='ko')?'Hong Kong':'香港'} → {primary.display}</p>
                    </div>
                    <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff', borderRadius:10, padding:'8px 14px', fontSize:12, fontWeight:900, flexShrink:0 }}>
                      {(lang==='en'||lang==='ja'||lang==='ko')?'Search →':'搜尋套餐 →'}
                    </span>
                  </a>
                )}

                {/* 🧳 行李寄放 — Radical Storage affiliate link + coupon (no iframe/API access yet, flat link for every city) */}
                {primary && (
                  <div style={{ borderRadius:16, border:'1.5px solid #e2e8f0', overflow:'hidden' }}>
                    <div style={{ background:'linear-gradient(135deg,#0f9488,#14b8a6)', padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ background:'#fff', borderRadius:8, padding:'3px 8px', display:'flex', alignItems:'center' }}>
                        <img src="/Radical_Storage.png" alt="Radical Storage" style={{ height:14, width:'auto' }}/>
                      </div>
                      <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#fff' }}>
                        {(lang==='en'||lang==='ja'||lang==='ko')?'Luggage Storage':'行李寄放'}
                      </p>
                    </div>
                    <div style={{ background:'#fff', padding:'14px 16px', display:'flex', flexDirection:'column', gap:12 }}>
                      <p style={{ margin:0, fontSize:12, color:'#64748b', lineHeight:1.6 }}>
                        {(lang==='en'||lang==='ja'||lang==='ko')
                          ? `Store your bags before exploring ${primary.display} — trusted local partners near stations and city centers.`
                          : `在${primary.display}逛街前先把行李寄放好，合作店家遍布車站與市區。`}
                      </p>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'#fff7e6', border:'1.5px dashed #f59e0b', borderRadius:10, padding:'9px 12px' }}>
                        <span style={{ fontSize:11, fontWeight:800, color:'#92400e' }}>
                          {(lang==='en'||lang==='ja'||lang==='ko')?'Coupon code — 10% off':'優惠碼 — 享10%折扣'}
                        </span>
                        <span style={{ fontFamily:'ui-monospace, monospace', fontSize:12, fontWeight:800, background:'#fff', border:'1px solid #f59e0b', color:'#92400e', borderRadius:7, padding:'4px 9px' }}>
                          ECTRAVEL
                        </span>
                      </div>
                      <a href={radicalStorageLink()} target="_blank" rel="sponsored noopener noreferrer"
                        style={{ textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center', gap:8, background:'#0f9488', color:'#fff', borderRadius:12, padding:'11px', fontSize:13, fontWeight:900 }}>
                        {(lang==='en'||lang==='ja'||lang==='ko')?'Find Storage Near You →':'前往預訂寄放點 →'}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Already-rated badge */}
          {tripRating && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'10px 16px', background:'#f0fdf4', borderRadius:14, border:'1.5px solid #bbf7d0' }}>
              <span style={{ fontSize:14 }}>{'★'.repeat(tripRating)}{'☆'.repeat(5-tripRating)}</span>
              <span style={{ fontSize:12, fontWeight:700, color:'#15803d' }}>
                {lang==='zh-HK'?`感謝您的評分！`:lang==='zh-CN'?`感谢您的评分！`:lang==='ja'?`評価ありがとう！`:lang==='ko'?`평가 감사합니다!`:`Thanks for your rating!`}
              </span>
              <button onClick={()=>{ setTripRating(null); setTripRatingComment(''); setRatingDismissed(false); setShowRatingPopup(true); }}
                style={{ background:'none', border:'none', fontSize:11, color:'#94a3b8', cursor:'pointer', marginLeft:4, fontWeight:700 }}>
                {lang==='zh-HK'||lang==='zh-CN'?'重新評分':lang==='ja'?'再評価':lang==='ko'?'다시 평가':'Re-rate'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // BOOKING
  const bkTypes = [
    {k:'localTransport',l:t.localTransport,ic:Bus},
    {k:'crossBorder',l:t.crossBorder,ic:Train},
    {k:'tickets',l:t.tickets,ic:Ticket},
    {k:'dining',l:t.dining,ic:Utensils},
    {k:'accommodation',l:t.accommodation,ic:Hotel},
    {k:'esim',l:lang==='en'?'eSIM':lang==='ja'?'eSIM':'📱 eSIM',ic:Smartphone},
  ];

  // Local transport types per city
  const CITY_LOCAL_TRANSPORT = {
    'Hong Kong':  [{ k:'mtr',    l:'🚇 港鐵 MTR',        url:`https://www.klook.com/en-HK/search/result/?query=Hong+Kong+MTR+pass&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 電車 Tram',       url:`https://www.klook.com/en-HK/search/result/?query=Hong+Kong+tram&aid=${KLOOK_AID}` }],
    'Tokyo':      [{ k:'shinkansen',l:'🚄 新幹線 Shinkansen',url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+ticket&aid=${KLOOK_AID}` },{ k:'jr_pass',l:'🎫 JR Pass',          url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass&aid=${KLOOK_AID}` },{ k:'metro',  l:'🚇 東京Metro',       url:`https://www.klook.com/en-HK/search/result/?query=Tokyo+Metro+pass&aid=${KLOOK_AID}` },{ k:'jr',     l:'🚃 JR山手線',        url:`https://www.klook.com/en-HK/search/result/?query=Tokyo+JR+pass&aid=${KLOOK_AID}` },{ k:'toei',   l:'🚇 都營地鐵',        url:`https://www.klook.com/en-HK/search/result/?query=Tokyo+Toei+subway&aid=${KLOOK_AID}` }],
    'Osaka':      [{ k:'shinkansen',l:'🚄 新幹線 Shinkansen',url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+ticket+osaka&aid=${KLOOK_AID}` },{ k:'jr_pass',l:'🎫 JR Pass',          url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass+osaka&aid=${KLOOK_AID}` },{ k:'metro',  l:'🚇 大阪Metro',       url:`https://www.klook.com/en-HK/search/result/?query=Osaka+Metro+pass&aid=${KLOOK_AID}` },{ k:'jr',     l:'🚃 JR大阪環状線',    url:`https://www.klook.com/en-HK/search/result/?query=Osaka+JR&aid=${KLOOK_AID}` },{ k:'hankyu', l:'🚃 阪急電鐵',        url:`https://www.klook.com/en-HK/search/result/?query=Osaka+Hankyu&aid=${KLOOK_AID}` },{ k:'hanshin',l:'🚃 阪神電鐵',        url:`https://www.klook.com/en-HK/search/result/?query=Osaka+Hanshin&aid=${KLOOK_AID}` }],
    'Seoul':      [{ k:'metro',  l:'🚇 首爾地鐵',        url:`https://www.klook.com/en-HK/search/result/?query=Seoul+subway+pass&aid=${KLOOK_AID}` },{ k:'korail', l:'🚃 KORAIL',          url:`https://www.klook.com/en-HK/search/result/?query=Seoul+KORAIL&aid=${KLOOK_AID}` }],
    'Bangkok':    [{ k:'bts',    l:'🚇 BTS Skytrain',   url:`https://www.klook.com/en-HK/search/result/?query=Bangkok+BTS&aid=${KLOOK_AID}` },{ k:'mrt',    l:'🚇 MRT 地鐵',        url:`https://www.klook.com/en-HK/search/result/?query=Bangkok+MRT&aid=${KLOOK_AID}` }],
    'Singapore':  [{ k:'mrt',    l:'🚇 MRT',             url:`https://www.klook.com/en-HK/search/result/?query=Singapore+MRT+pass&aid=${KLOOK_AID}` },{ k:'lrt',    l:'🚃 LRT',             url:`https://www.klook.com/en-HK/search/result/?query=Singapore+LRT&aid=${KLOOK_AID}` }],
    'Taipei':     [{ k:'mrt',    l:'🚇 台北捷運 MRT',    url:`https://www.klook.com/en-HK/search/result/?query=Taipei+MRT+pass&aid=${KLOOK_AID}` },{ k:'tra',    l:'🚃 台鐵 TRA',        url:`https://www.klook.com/en-HK/search/result/?query=Taipei+TRA&aid=${KLOOK_AID}` }],
    'Kyoto':      [{ k:'shinkansen',l:'🚄 新幹線 Shinkansen',url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+ticket+kyoto&aid=${KLOOK_AID}` },{ k:'jr_pass',l:'🎫 JR Pass',          url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass+kyoto&aid=${KLOOK_AID}` },{ k:'jr',     l:'🚃 JR嵯峨野線',      url:`https://www.klook.com/en-HK/search/result/?query=Kyoto+JR&aid=${KLOOK_AID}` },{ k:'keihan', l:'🚃 京阪電鐵',        url:`https://www.klook.com/en-HK/search/result/?query=Kyoto+Keihan&aid=${KLOOK_AID}` },{ k:'kintetsu',l:'🚃 近鐵',            url:`https://www.klook.com/en-HK/search/result/?query=Kyoto+Kintetsu&aid=${KLOOK_AID}` }],
    'London':     [{ k:'tube',   l:'🚇 Underground',    url:`https://www.klook.com/en-HK/search/result/?query=London+Oyster+card&aid=${KLOOK_AID}` },{ k:'eliz',   l:'🚃 Elizabeth Line', url:`https://www.klook.com/en-HK/search/result/?query=London+Elizabeth+Line&aid=${KLOOK_AID}` },{ k:'dlr',    l:'🚃 DLR',             url:`https://www.klook.com/en-HK/search/result/?query=London+DLR&aid=${KLOOK_AID}` },{ k:'overground',l:'🚃 Overground',    url:`https://www.klook.com/en-HK/search/result/?query=London+Overground&aid=${KLOOK_AID}` }],
    'Paris':      [{ k:'metro',  l:'🚇 Métro',          url:`https://www.klook.com/en-HK/search/result/?query=Paris+Metro+pass&aid=${KLOOK_AID}` },{ k:'rer',    l:'🚃 RER',             url:`https://www.klook.com/en-HK/search/result/?query=Paris+RER&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tramway',         url:`https://www.klook.com/en-HK/search/result/?query=Paris+tram&aid=${KLOOK_AID}` }],
    // 中國
    'Beijing':    [{ k:'subway', l:'🚇 北京地鐵',         url:`https://www.klook.com/en-HK/search/result/?query=Beijing+subway+pass&aid=${KLOOK_AID}` },{ k:'cap_exp', l:'🚄 首都機場快線',     url:`https://www.klook.com/en-HK/search/result/?query=Beijing+airport+express&aid=${KLOOK_AID}` }],
    'Shanghai':   [{ k:'metro',  l:'🚇 上海地鐵',         url:`https://www.klook.com/en-HK/search/result/?query=Shanghai+Metro+pass&aid=${KLOOK_AID}` },{ k:'maglev', l:'🚄 磁浮列車 Maglev',  url:`https://www.klook.com/en-HK/search/result/?query=Shanghai+Maglev&aid=${KLOOK_AID}` }],
    'Guangzhou':  [{ k:'metro',  l:'🚇 廣州地鐵',         url:`https://www.klook.com/en-HK/search/result/?query=Guangzhou+Metro&aid=${KLOOK_AID}` }],
    'Shenzhen':   [{ k:'metro',  l:'🚇 深圳地鐵',         url:`https://www.klook.com/en-HK/search/result/?query=Shenzhen+Metro&aid=${KLOOK_AID}` }],
    // 澳門
    'Macau':      [{ k:'lrt_mac',l:'🚃 澳門輕軌 MLM',    url:`https://www.klook.com/en-HK/search/result/?query=Macau+light+rail&aid=${KLOOK_AID}` }],
    // 韓國
    'Busan':      [{ k:'metro',  l:'🚇 釜山地鐵',         url:`https://www.klook.com/en-HK/search/result/?query=Busan+subway+pass&aid=${KLOOK_AID}` },{ k:'ktx',    l:'🚄 KTX',             url:`https://www.klook.com/en-HK/search/result/?query=KTX+Busan&aid=${KLOOK_AID}` }],
    // 馬來西亞
    'Kuala Lumpur':[{ k:'lrt_kl', l:'🚇 LRT',            url:`https://www.klook.com/en-HK/search/result/?query=Kuala+Lumpur+LRT&aid=${KLOOK_AID}` },{ k:'mrt_kl', l:'🚇 MRT',             url:`https://www.klook.com/en-HK/search/result/?query=Kuala+Lumpur+MRT&aid=${KLOOK_AID}` },{ k:'ktm_kl', l:'🚃 KTM Komuter',     url:`https://www.klook.com/en-HK/search/result/?query=KTM+Komuter+KL&aid=${KLOOK_AID}` }],
    // 日本 其他城市
    'Hiroshima':  [{ k:'shinkansen',l:'🚄 新幹線 Shinkansen',url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+hiroshima&aid=${KLOOK_AID}` },{ k:'jr_pass',l:'🎫 JR Pass',       url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass&aid=${KLOOK_AID}` },{ k:'hiroden', l:'🚃 廣島電鐵 Hiroden', url:`https://www.klook.com/en-HK/search/result/?query=Hiroshima+tram&aid=${KLOOK_AID}` }],
    'Fukuoka':    [{ k:'shinkansen',l:'🚄 新幹線 Shinkansen',url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+fukuoka&aid=${KLOOK_AID}` },{ k:'jr_pass',l:'🎫 JR Pass',       url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass&aid=${KLOOK_AID}` },{ k:'subway_fk',l:'🚇 福岡市地鐵',     url:`https://www.klook.com/en-HK/search/result/?query=Fukuoka+subway&aid=${KLOOK_AID}` }],
    'Sapporo':    [{ k:'subway_sp',l:'🚇 札幌市地鐵',     url:`https://www.klook.com/en-HK/search/result/?query=Sapporo+subway&aid=${KLOOK_AID}` },{ k:'jr_pass',l:'🎫 JR Pass',         url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass+Hokkaido&aid=${KLOOK_AID}` }],
    // 歐洲 其他城市
    'Rome':       [{ k:'metro',  l:'🚇 Metro',            url:`https://www.klook.com/en-HK/search/result/?query=Rome+Metro&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Rome+tram&aid=${KLOOK_AID}` }],
    'Milan':      [{ k:'metro',  l:'🚇 Metro',            url:`https://www.klook.com/en-HK/search/result/?query=Milan+Metro&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Milan+tram&aid=${KLOOK_AID}` },{ k:'s_rail', l:'🚃 Passante',        url:`https://www.klook.com/en-HK/search/result/?query=Milan+train+pass&aid=${KLOOK_AID}` }],
    'Barcelona':  [{ k:'metro',  l:'🚇 Metro',            url:`https://www.klook.com/en-HK/search/result/?query=Barcelona+Metro+pass&aid=${KLOOK_AID}` },{ k:'fgc',    l:'🚃 FGC',             url:`https://www.klook.com/en-HK/search/result/?query=Barcelona+FGC&aid=${KLOOK_AID}` },{ k:'renfe_c',l:'🚃 Rodalies Renfe',  url:`https://www.klook.com/en-HK/search/result/?query=Barcelona+Rodalies&aid=${KLOOK_AID}` }],
    'Berlin':     [{ k:'u_bahn', l:'🚇 U-Bahn',           url:`https://www.klook.com/en-HK/search/result/?query=Berlin+U-Bahn+pass&aid=${KLOOK_AID}` },{ k:'s_bahn', l:'🚃 S-Bahn',          url:`https://www.klook.com/en-HK/search/result/?query=Berlin+S-Bahn&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Berlin+tram&aid=${KLOOK_AID}` }],
    'Amsterdam':  [{ k:'metro',  l:'🚇 Metro',            url:`https://www.klook.com/en-HK/search/result/?query=Amsterdam+Metro+pass&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Amsterdam+tram&aid=${KLOOK_AID}` }],
    'Vienna':     [{ k:'u_bahn', l:'🚇 U-Bahn',           url:`https://www.klook.com/en-HK/search/result/?query=Vienna+U-Bahn+pass&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Vienna+tram&aid=${KLOOK_AID}` },{ k:'s_bahn_v',l:'🚃 S-Bahn',         url:`https://www.klook.com/en-HK/search/result/?query=Vienna+S-Bahn&aid=${KLOOK_AID}` }],
    'Prague':     [{ k:'metro',  l:'🚇 Metro',            url:`https://www.klook.com/en-HK/search/result/?query=Prague+Metro&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Prague+tram&aid=${KLOOK_AID}` }],
    'Brussels':   [{ k:'metro',  l:'🚇 Metro',            url:`https://www.klook.com/en-HK/search/result/?query=Brussels+Metro+pass&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Tram',            url:`https://www.klook.com/en-HK/search/result/?query=Brussels+tram&aid=${KLOOK_AID}` },{ k:'s_rail_b',l:'🚃 S-Train',        url:`https://www.klook.com/en-HK/search/result/?query=Brussels+train&aid=${KLOOK_AID}` }],
    // 中東
    'Dubai':      [{ k:'metro',  l:'🚇 Dubai Metro',      url:`https://www.klook.com/en-HK/search/result/?query=Dubai+Metro+pass&aid=${KLOOK_AID}` },{ k:'tram',   l:'🚃 Dubai Tram',      url:`https://www.klook.com/en-HK/search/result/?query=Dubai+Tram&aid=${KLOOK_AID}` }],
    // 美洲
    'New York':   [{ k:'subway', l:'🚇 NYC Subway',       url:`https://www.klook.com/en-HK/search/result/?query=New+York+Subway+pass&aid=${KLOOK_AID}` }],
    'Los Angeles':[{ k:'metro',  l:'🚇 LA Metro',         url:`https://www.klook.com/en-HK/search/result/?query=Los+Angeles+Metro&aid=${KLOOK_AID}` }],
  };

  // Airport express / airport-to-city rail per city
  const AIRPORT_EXPRESS = {
    'Hong Kong': [
      { airport:'HKG', name:'機場快線 Airport Express', from:'香港國際機場', to:'香港站 / 九龍站 / 青衣站', duration:'24分鐘', price:'HK$115', stops:['機場站','青衣站','九龍站','香港站'], url:`https://www.klook.com/en-HK/search/result/?query=Hong+Kong+Airport+Express&aid=${KLOOK_AID}` },
    ],
    'Tokyo': [
      { airport:'NRT', name:"成田特快 N'EX", from:'成田國際機場', to:'東京站 / 新宿站 / 橫濱站', duration:'60分鐘', price:'¥3,070', stops:['成田機場站','東京站','新宿站','橫濱站'], url:`https://www.klook.com/en-HK/search/result/?query=Narita+Express+NEX&aid=${KLOOK_AID}` },
      { airport:'NRT', name:'京成特快 Keisei Skyliner', from:'成田國際機場', to:'上野站 / 日暮里站', duration:'41分鐘', price:'¥2,520', stops:['成田機場站','日暮里站','上野站'], url:`https://www.klook.com/en-HK/search/result/?query=Keisei+Skyliner&aid=${KLOOK_AID}` },
      { airport:'HND', name:'京急電鐵 Keikyu Line', from:'羽田機場', to:'品川站 / 新橋站', duration:'20分鐘', price:'¥610', stops:['羽田機場國際線站','品川站','新橋站','淺草站'], url:`https://www.klook.com/en-HK/search/result/?query=Haneda+airport+train&aid=${KLOOK_AID}` },
    ],
    'Osaka': [
      { airport:'KIX', name:'はるか特急 Haruka Express', from:'關西國際機場', to:'天王寺站 / 新大阪站 / 京都站', duration:'50分鐘', price:'¥1,910', stops:['關西機場站','天王寺站','新大阪站','京都站'], url:`https://www.klook.com/en-HK/search/result/?query=Haruka+Express+Osaka&aid=${KLOOK_AID}` },
      { airport:'KIX', name:'南海電鐵 Nankai Rapi:t', from:'關西國際機場', to:'難波站', duration:'38分鐘', price:'¥1,450', stops:['關西機場站','泉佐野站','難波站'], url:`https://www.klook.com/en-HK/search/result/?query=Nankai+Rapit+Osaka&aid=${KLOOK_AID}` },
    ],
    'Seoul': [
      { airport:'ICN', name:'仁川機場鐵路 AREX 直達', from:'仁川國際機場', to:'首爾站', duration:'43分鐘', price:'₩9,500', stops:['仁川機場2航廈站','仁川機場1航廈站','首爾站'], url:`https://www.klook.com/en-HK/search/result/?query=Seoul+AREX+airport+express&aid=${KLOOK_AID}` },
      { airport:'ICN', name:'仁川機場鐵路 AREX 一般', from:'仁川國際機場', to:'弘大站 / 首爾站', duration:'66分鐘', price:'₩4,250', stops:['仁川機場站','金浦機場站','弘大站','首爾站'], url:`https://www.klook.com/en-HK/search/result/?query=Seoul+airport+train&aid=${KLOOK_AID}` },
    ],
    'Bangkok': [
      { airport:'BKK', name:'素萬那普機場快線 ARL', from:'素萬那普機場', to:'瑪卡三站', duration:'28分鐘', price:'฿45', stops:['素萬那普機場站','拉卡邦站','華馬克站','瑪卡三站'], url:`https://www.klook.com/en-HK/search/result/?query=Bangkok+Airport+Rail+Link&aid=${KLOOK_AID}` },
      { airport:'DMK', name:'廊曼機場列車 SRT', from:'廊曼機場', to:'帕亞泰站', duration:'45分鐘', price:'฿45', stops:['廊曼站','帕亞泰站'], url:`https://www.klook.com/en-HK/search/result/?query=Don+Mueang+airport+train&aid=${KLOOK_AID}` },
    ],
    'Singapore': [
      { airport:'SIN', name:'樟宜機場 MRT 東西線', from:'樟宜機場', to:'市區 / 烏節路', duration:'30分鐘', price:'S$2.10', stops:['樟宜機場站','丹戎巴葛站','武吉士站','烏節站'], url:`https://www.klook.com/en-HK/search/result/?query=Singapore+Changi+MRT&aid=${KLOOK_AID}` },
    ],
    'London': [
      { airport:'LHR', name:'希思路快線 Heathrow Express', from:'希思路機場', to:'帕丁頓站', duration:'15分鐘', price:'£25', stops:['希思路機場站','帕丁頓站'], url:`https://www.klook.com/en-HK/search/result/?query=Heathrow+Express+ticket&aid=${KLOOK_AID}` },
      { airport:'LGW', name:'蓋威克快線 Gatwick Express', from:'蓋威克機場', to:'維多利亞站', duration:'30分鐘', price:'£19.90', stops:['蓋威克機場站','維多利亞站'], url:`https://www.klook.com/en-HK/search/result/?query=Gatwick+Express+ticket&aid=${KLOOK_AID}` },
      { airport:'STN', name:'斯坦斯特快線 Stansted Express', from:'斯坦斯特機場', to:'利物浦街站', duration:'47分鐘', price:'£21.50', stops:['斯坦斯特機場站','托特納姆黑爾站','利物浦街站'], url:`https://www.klook.com/en-HK/search/result/?query=Stansted+Express&aid=${KLOOK_AID}` },
    ],
    'Paris': [
      { airport:'CDG', name:'戴高樂機場 RER B線', from:'戴高樂機場', to:'北站 / 沙特萊站', duration:'35分鐘', price:'€11.80', stops:['戴高樂機場站','北站','沙特萊-大堂站','聖米歇爾站'], url:`https://www.klook.com/en-HK/search/result/?query=Paris+CDG+airport+train&aid=${KLOOK_AID}` },
      { airport:'ORY', name:'奧利機場 OrlyVal + RER B', from:'奧利機場', to:'北站', duration:'35分鐘', price:'€12.10', stops:['奧利機場站','安東尼站','北站'], url:`https://www.klook.com/en-HK/search/result/?query=Paris+Orly+airport+train&aid=${KLOOK_AID}` },
    ],
    'Taipei': [
      { airport:'TPE', name:'桃園機場捷運 MRT', from:'桃園國際機場', to:'台北站', duration:'35分鐘', price:'NT$160', stops:['桃園機場站','新北產業園區站','台北站'], url:`https://www.klook.com/en-HK/search/result/?query=Taoyuan+Airport+MRT&aid=${KLOOK_AID}` },
    ],
    'Kyoto': [
      { airport:'KIX', name:'はるか特急 Haruka Express', from:'關西國際機場', to:'京都站', duration:'75分鐘', price:'¥2,900', stops:['關西機場站','天王寺站','新大阪站','京都站'], url:`https://www.klook.com/en-HK/search/result/?query=Haruka+Express+Kyoto&aid=${KLOOK_AID}` },
    ],
    'Beijing': [
      { airport:'PEK', name:'首都機場快線 Capital Airport Express', from:'北京首都機場', to:'東直門站 / 三元橋站', duration:'19分鐘', price:'¥35', stops:['北京首都機場T2站','北京首都機場T3站','三元橋站','東直門站'], url:`https://www.klook.com/en-HK/search/result/?query=Beijing+Capital+Airport+Express&aid=${KLOOK_AID}` },
    ],
    'Shanghai': [
      { airport:'PVG', name:'磁浮列車 Maglev', from:'上海浦東機場', to:'龍陽路站', duration:'8分鐘', price:'¥55', stops:['浦東機場站','龍陽路站'], url:`https://www.klook.com/en-HK/search/result/?query=Shanghai+Maglev+train&aid=${KLOOK_AID}` },
      { airport:'SHA', name:'上海地鐵 10號線', from:'虹橋機場', to:'南京路站', duration:'40分鐘', price:'¥6', stops:['虹橋火車站','虹橋機場1航廈站','南京路站'], url:`https://www.klook.com/en-HK/search/result/?query=Shanghai+airport+metro&aid=${KLOOK_AID}` },
    ],
    'Busan': [
      { airport:'PUS', name:'釜山地鐵 2號線', from:'金海國際機場', to:'西面站 / 釜山站', duration:'35分鐘', price:'₩1,700', stops:['金海機場站','沙上站','西面站','釜山站'], url:`https://www.klook.com/en-HK/search/result/?query=Busan+airport+subway&aid=${KLOOK_AID}` },
    ],
    'Kuala Lumpur': [
      { airport:'KUL', name:'KLIA Ekspres 快線', from:'吉隆坡國際機場', to:'KL Sentral', duration:'28分鐘', price:'RM55', stops:['吉隆坡機場站','KL Sentral站'], url:`https://www.klook.com/en-HK/search/result/?query=KLIA+Ekspres+Kuala+Lumpur&aid=${KLOOK_AID}` },
    ],
    'Fukuoka': [
      { airport:'FUK', name:'福岡市地鐵空港線', from:'福岡機場', to:'天神站 / 博多站', duration:'11分鐘', price:'¥260', stops:['福岡機場站','博多站','天神站'], url:`https://www.klook.com/en-HK/search/result/?query=Fukuoka+airport+subway&aid=${KLOOK_AID}` },
    ],
    'Sapporo': [
      { airport:'CTS', name:'JR 快速エアポート', from:'新千歲機場', to:'札幌站', duration:'37分鐘', price:'¥1,290', stops:['新千歲機場站','南千歲站','白石站','札幌站'], url:`https://www.klook.com/en-HK/search/result/?query=Sapporo+New+Chitose+airport+train&aid=${KLOOK_AID}` },
    ],
    'Rome': [
      { airport:'FCO', name:'菲烏米奇諾機場快線 Leonardo Express', from:'菲烏米奇諾機場', to:'特米尼站', duration:'32分鐘', price:'€14', stops:['菲烏米奇諾機場站','特米尼站'], url:`https://www.klook.com/en-HK/search/result/?query=Rome+Fiumicino+Leonardo+Express&aid=${KLOOK_AID}` },
    ],
    'Milan': [
      { airport:'MXP', name:'馬爾彭薩快線 Malpensa Express', from:'馬爾彭薩機場', to:'米蘭中央站', duration:'50分鐘', price:'€13', stops:['馬爾彭薩機場T1站','馬爾彭薩機場T2站','加里波第站','米蘭中央站'], url:`https://www.klook.com/en-HK/search/result/?query=Milan+Malpensa+Express&aid=${KLOOK_AID}` },
    ],
    'Barcelona': [
      { airport:'BCN', name:'Aerobus 快線巴士 / Renfe', from:'巴塞隆拿機場', to:'加泰隆尼亞廣場', duration:'35分鐘', price:'€6.75', stops:['巴塞隆拿機場T1站','巴塞隆拿機場T2站','加泰隆尼亞廣場站'], url:`https://www.klook.com/en-HK/search/result/?query=Barcelona+airport+train&aid=${KLOOK_AID}` },
    ],
    'Berlin': [
      { airport:'BER', name:'柏林機場 S-Bahn S9/S45', from:'柏林布蘭登堡機場', to:'柏林中央站', duration:'30分鐘', price:'€4.40', stops:['BER機場站','柏林東站','亞歷山大廣場站','柏林中央站'], url:`https://www.klook.com/en-HK/search/result/?query=Berlin+BER+airport+train&aid=${KLOOK_AID}` },
    ],
    'Amsterdam': [
      { airport:'AMS', name:'史基浦機場 Intercity Direct', from:'史基浦機場', to:'阿姆斯特丹中央站', duration:'17分鐘', price:'€6', stops:['史基浦機場站','阿姆斯特丹中央站'], url:`https://www.klook.com/en-HK/search/result/?query=Amsterdam+Schiphol+airport+train&aid=${KLOOK_AID}` },
    ],
    'Vienna': [
      { airport:'VIE', name:'維也納機場快線 CAT', from:'維也納國際機場', to:'維也納米特站', duration:'16分鐘', price:'€14.90', stops:['維也納機場站','維也納米特站'], url:`https://www.klook.com/en-HK/search/result/?query=Vienna+CAT+airport+express&aid=${KLOOK_AID}` },
    ],
    'Dubai': [
      { airport:'DXB', name:'迪拜地鐵紅線', from:'迪拜國際機場', to:'聯合廣場站 / 市中心', duration:'20分鐘', price:'AED 6', stops:['迪拜機場T1站','迪拜機場T3站','包哈里夫站','聯合廣場站'], url:`https://www.klook.com/en-HK/search/result/?query=Dubai+Metro+airport&aid=${KLOOK_AID}` },
    ],
    'New York': [
      { airport:'JFK', name:'AirTrain JFK + Subway', from:'甘迺迪國際機場', to:'曼哈頓', duration:'60分鐘', price:'$9.75', stops:['JFK機場站','牙買加站','時代廣場站'], url:`https://www.klook.com/en-HK/search/result/?query=JFK+AirTrain+New+York&aid=${KLOOK_AID}` },
    ],
    'Brussels': [
      { airport:'BRU', name:'布魯塞爾機場快線', from:'布魯塞爾機場', to:'布魯塞爾中央站', duration:'17分鐘', price:'€13.70', stops:['布魯塞爾機場站','北站','中央站','南站'], url:`https://www.klook.com/en-HK/search/result/?query=Brussels+airport+train&aid=${KLOOK_AID}` },
    ],
  };

  // Official transport booking links per city
  const OFFICIAL_TRANSPORT = {
    'Hong Kong': [
      { name:'港鐵 MTR 單程票／旅遊票', icon:'🚇', color:'#e0161a', url:'https://www.mtr.com.hk/ch/customer/tickets/index.html', desc:'港鐵車票 · 機場快線 · 旅遊日票' },
      { name:'八達通 Octopus Card',     icon:'💳', color:'#ff6600', url:'https://www.octopus.com.hk/tc/personal/using-octopus/transport/index.html', desc:'港鐵 · 巴士 · 小巴 · 渡輪 · 電車' },
      { name:'天星小輪 Star Ferry',     icon:'⛴️', color:'#1e6db5', url:'https://www.starferry.com.hk/tc/fare.html', desc:'中環 ↔ 尖沙咀渡輪票' },
    ],
    'Tokyo': [
      { name:'Suica Card (JR East)',    icon:'🚃', color:'#2ecc71', url:'https://www.jreast.co.jp/multi/zh-CHT/pass/suica.html', desc:'JR電車 · 地鐵 · 巴士全線通用' },
      { name:'東京Metro一日乘車券',      icon:'🚇', color:'#b5001b', url:'https://www.tokyometro.jp/tcn/ticket/value/index.html', desc:'東京Metro全線任乘' },
      { name:'都營地鐵一日券',           icon:'🚇', color:'#00549b', url:'https://www.kotsu.metro.tokyo.jp/ch_h/services/tickets_subway.html', desc:'都營地鐵全線任乘' },
    ],
    'Osaka': [
      { name:'大阪Metro一日乘車券',      icon:'🚇', color:'#e41221', url:'https://subway.osakametro.co.jp/guide/page/enjoy-eco.php', desc:'大阪地鐵全線任乘' },
      { name:'大阪周遊卡 Osaka Pass',   icon:'🎫', color:'#ff8c00', url:'https://www.osaka-info.jp/osp/tc/', desc:'地鐵無限乘 + 40+景點免費' },
      { name:'ICOCA (JR West)',         icon:'💳', color:'#0070c0', url:'https://www.westjr.co.jp/global/tc/howto/icoca/', desc:'JR電車 · 私鐵 · 巴士' },
    ],
    'Kyoto': [
      { name:'京都市巴士一日券',         icon:'🚌', color:'#cc3300', url:'https://www2.city.kyoto.lg.jp/kotsu/webguide/zh-TW/passes/', desc:'京都市巴士全線任乘' },
      { name:'京都觀光一日乘車券',       icon:'🎫', color:'#006600', url:'https://www2.city.kyoto.lg.jp/kotsu/webguide/zh-TW/passes/', desc:'巴士 + 地鐵一日通票' },
    ],
    'Seoul': [
      { name:'T-money Card',           icon:'💳', color:'#ff6600', url:'https://www.t-money.co.kr/', desc:'地鐵 · 巴士 · 計程車全線通用' },
      { name:'首爾地鐵官網訂票',         icon:'🚇', color:'#0056a8', url:'https://www.seoulmetro.co.kr/en/index.do', desc:'首爾地鐵票 · 一日票 · 套票' },
    ],
    'Taipei': [
      { name:'悠遊卡 EasyCard',         icon:'💳', color:'#009900', url:'https://www.easycard.com.tw/', desc:'捷運 · 公車 · YouBike' },
      { name:'台北捷運一日票',           icon:'🚇', color:'#cc0000', url:'https://www.metro.taipei/cp.aspx?n=485', desc:'台北捷運全線任乘' },
    ],
    'Singapore': [
      { name:'EZ-Link Card',           icon:'💳', color:'#1e90ff', url:'https://www.ezlink.com.sg/', desc:'MRT · 巴士 · 輕軌' },
      { name:'Singapore Tourist Pass', icon:'🎫', color:'#cc0000', url:'https://www.thesingaporetouristpass.com.sg/', desc:'MRT + 巴士無限乘 1-3日' },
    ],
    'London': [
      { name:'TfL Oyster Card',        icon:'💳', color:'#0019a8', url:'https://tfl.gov.uk/fares/how-to-pay-and-where-to-buy-tickets-and-oyster/pay-as-you-go/oyster-pay-as-you-go', desc:'地鐵 · 巴士 · DLR · 過境快線' },
      { name:'Travelcard 旅遊票',       icon:'🎫', color:'#e32017', url:'https://tfl.gov.uk/fares/find-fares/tube-and-rail-fares/travelcards', desc:'倫敦全區交通一日/周票' },
    ],
    'Paris': [
      { name:'RATP 巴黎交通票',         icon:'💳', color:'#00a1e4', url:'https://www.ratp.fr/titres-et-tarifs', desc:'地鐵 · 巴士 · RER 車票' },
      { name:'Paris Visite Pass',      icon:'🎫', color:'#0055a4', url:'https://www.ratp.fr/titres-et-tarifs/paris-visite', desc:'全區無限乘 1-5日' },
    ],
    'Sydney': [
      { name:'Opal Card',              icon:'💳', color:'#00a0e9', url:'https://transportnsw.info/tickets-opal/opal', desc:'火車 · 巴士 · 渡輪 · 輕軌' },
      { name:'NSW Transport 訂票',     icon:'🚌', color:'#005e97', url:'https://www.transportnsw.info/', desc:'悉尼公共交通官方網站' },
    ],
    'Melbourne': [
      { name:'myki Card',              icon:'💳', color:'#0072bc', url:'https://www.ptv.vic.gov.au/tickets/myki/', desc:'電車 · 火車 · 巴士全線通用' },
      { name:'PTV 交通查詢',            icon:'🚌', color:'#00567a', url:'https://www.ptv.vic.gov.au/', desc:'墨爾本公共交通官方網站' },
    ],
    'Bangkok': [
      { name:'BTS Skytrain 票',        icon:'🚃', color:'#009e49', url:'https://www.bts.co.th/eng/services/rabbit_cards.html', desc:'BTS空鐵 · Rabbit Card' },
      { name:'MRT 地鐵票',              icon:'🚇', color:'#003f8a', url:'https://www.mrta.co.th/en/', desc:'曼谷地鐵全線車票' },
    ],
    'Macau': [
      { name:'澳門巴士票',              icon:'🚌', color:'#990000', url:'https://www.dsedj.gov.mo/carris/index_c.html', desc:'澳門公共巴士路線及票價' },
      { name:'澳門輕軌 LRT',           icon:'🚇', color:'#006699', url:'https://www.mlm.com.mo/', desc:'氹仔線 · 媽閣線票務' },
    ],
  };

  // Airport map: English city name → [{code, name}]
  const CITY_AIRPORTS = {
    'Hong Kong':  [{ code:'HKG', name:'香港國際機場', lat:22.3080, lng:113.9185 }],
    'Tokyo':      [{ code:'NRT', name:'成田國際機場', lat:35.7720, lng:140.3929 }, { code:'HND', name:'羽田機場', lat:35.5494, lng:139.7798 }],
    'Osaka':      [{ code:'KIX', name:'關西國際機場', lat:34.4347, lng:135.2441 }, { code:'ITM', name:'伊丹機場', lat:34.7855, lng:135.4382 }],
    'Seoul':      [{ code:'ICN', name:'仁川國際機場', lat:37.4602, lng:126.4407 }, { code:'GMP', name:'金浦機場', lat:37.5583, lng:126.7906 }],
    'Bangkok':    [{ code:'BKK', name:'素萬那普機場', lat:13.6900, lng:100.7501 }, { code:'DMK', name:'廊曼機場', lat:13.9126, lng:100.6068 }],
    'Bali':       [{ code:'DPS', name:'峇里島恩古拉萊機場', lat:-8.7482, lng:115.1672 }],
    'Singapore':  [{ code:'SIN', name:'樟宜機場', lat:1.3644, lng:103.9915 }],
    'Taipei':     [{ code:'TPE', name:'桃園國際機場', lat:25.0797, lng:121.2342 }, { code:'TSA', name:'松山機場', lat:25.0694, lng:121.5522 }],
    'Kyoto':      [{ code:'KIX', name:'關西國際機場（最近）', lat:34.4347, lng:135.2441 }],
    'Kobe':       [{ code:'UKB', name:'神戶機場', lat:34.6328, lng:135.2238 }, { code:'KIX', name:'關西國際機場（最近）', lat:34.4347, lng:135.2441 }],
    'Nara':       [{ code:'KIX', name:'關西國際機場（最近）', lat:34.4347, lng:135.2441 }],
    'Macau':         [{ code:'MFM', name:'澳門國際機場', lat:22.1496, lng:113.5915 }],
    'London':        [{ code:'LHR', name:'希思路機場', lat:51.4700, lng:-0.4543 }, { code:'LGW', name:'蓋威克機場', lat:51.1537, lng:-0.1821 }, { code:'STN', name:'斯坦斯特機場', lat:51.8860, lng:0.2389 }],
    'Paris':         [{ code:'CDG', name:'戴高樂機場', lat:49.0097, lng:2.5479 }, { code:'ORY', name:'奧利機場', lat:48.7233, lng:2.3794 }],
    'Beijing':       [{ code:'PEK', name:'北京首都國際機場', lat:40.0799, lng:116.6031 }, { code:'PKX', name:'北京大興國際機場', lat:39.5098, lng:116.4105 }],
    'Shanghai':      [{ code:'PVG', name:'上海浦東國際機場', lat:31.1443, lng:121.8083 }, { code:'SHA', name:'上海虹橋機場', lat:31.1979, lng:121.3363 }],
    'Guangzhou':     [{ code:'CAN', name:'廣州白雲國際機場', lat:23.3924, lng:113.2988 }],
    'Shenzhen':      [{ code:'SZX', name:'深圳寶安國際機場', lat:22.6393, lng:113.8107 }],
    'Busan':         [{ code:'PUS', name:'金海國際機場', lat:35.1795, lng:128.9382 }],
    'Kuala Lumpur':  [{ code:'KUL', name:'吉隆坡國際機場 KLIA', lat:2.7456, lng:101.7099 }, { code:'SZB', name:'蘇邦機場 KLIA2', lat:3.1305, lng:101.5493 }],
    'Hiroshima':     [{ code:'HIJ', name:'廣島機場', lat:34.4361, lng:132.9194 }],
    'Fukuoka':       [{ code:'FUK', name:'福岡機場', lat:33.5859, lng:130.4506 }],
    'Sapporo':       [{ code:'CTS', name:'新千歲機場', lat:42.7752, lng:141.6923 }],
    'Rome':          [{ code:'FCO', name:'菲烏米奇諾機場', lat:41.8003, lng:12.2389 }, { code:'CIA', name:'錢皮諾機場', lat:41.7994, lng:12.5949 }],
    'Milan':         [{ code:'MXP', name:'馬爾彭薩機場', lat:45.6306, lng:8.7281 }, { code:'LIN', name:'利納特機場', lat:45.4451, lng:9.2767 }],
    'Barcelona':     [{ code:'BCN', name:'巴塞隆拿-埃爾普拉特機場', lat:41.2974, lng:2.0833 }],
    'Berlin':        [{ code:'BER', name:'柏林布蘭登堡機場', lat:52.3667, lng:13.5033 }],
    'Amsterdam':     [{ code:'AMS', name:'史基浦機場', lat:52.3105, lng:4.7683 }],
    'Vienna':        [{ code:'VIE', name:'維也納國際機場', lat:48.1103, lng:16.5697 }],
    'Prague':        [{ code:'PRG', name:'瓦茨拉夫·哈維爾機場', lat:50.1008, lng:14.2600 }],
    'Brussels':      [{ code:'BRU', name:'布魯塞爾機場', lat:50.9014, lng:4.4844 }],
    'Dubai':         [{ code:'DXB', name:'迪拜國際機場', lat:25.2532, lng:55.3657 }, { code:'DWC', name:'阿勒馬克圖姆機場', lat:24.8967, lng:55.1614 }],
    'New York':      [{ code:'JFK', name:'甘迺迪國際機場', lat:40.6413, lng:-73.7781 }, { code:'LGA', name:'拉瓜地亞機場', lat:40.7769, lng:-73.8740 }, { code:'EWR', name:'紐瓦克機場', lat:40.6895, lng:-74.1745 }],
    'Los Angeles':   [{ code:'LAX', name:'洛杉磯國際機場', lat:33.9416, lng:-118.4085 }],
  };

  // Major stations per city
  const CITY_STATIONS = {
    'Hong Kong':  ['香港站','九龍站','西九龍站','旺角東站','紅磡站','尖沙咀站','中環站'],
    'Tokyo':      ['新宿站','澀谷站','東京站','品川站','上野站','秋葉原站','池袋站','羽田機場站'],
    'Osaka':      ['大阪站','新大阪站','難波站','梅田站','天王寺站','心齋橋站'],
    'Seoul':      ['首爾站','東大門站','弘大站','明洞站','江南站','水西站'],
    'Bangkok':    ['廊曼站','曼谷站 (Hua Lamphong)','BTS 阿索克站','BTS 暹羅站','MRT 素坤逸站'],
    'Singapore':  ['裕廊東站','政府大廈站','烏節站','海灣舫站','丹戎巴葛站'],
    'Taipei':     ['台北站','板橋站','松山站','南港站','台中站'],
    'Kyoto':      ['京都站','二条站','円町站','丹波橋站'],
    'Macau':         ['機場站','氹仔站','東亞運站','蓮花口岸站','媽閣站','氹仔碼頭','外港碼頭'],
    'London':        ['維多利亞站','帕丁頓站','滑鐵盧站','倫敦橋站','尤斯頓站','聖潘克拉斯站','金融城站'],
    'Paris':         ['北站 (Gare du Nord)','里昂站','東站','聖拉撒路站','蒙帕納斯站','沙特萊站'],
    'Bali':          ['Sanur 碼頭','吉安亞爾','庫塔','水明漾','烏布'],
    'Beijing':       ['北京站','北京南站','北京西站','北京北站','首都機場站','天安門東站','王府井站'],
    'Shanghai':      ['上海火車站','上海南站','虹橋火車站','浦東機場站','人民廣場站','南京東路站','陸家嘴站'],
    'Guangzhou':     ['廣州南站','廣州站','廣州東站','天河城站','珠江新城站','白雲機場站'],
    'Shenzhen':      ['深圳北站','福田站','羅湖站','寶安站','前海灣站','深圳機場站'],
    'Busan':         ['釜山站','西面站','南浦洞站','海雲台站','金海機場站'],
    'Kuala Lumpur':  ['吉隆坡中央站','武吉免登站','安邦站','孟沙站','KL Sentral站'],
    'Hiroshima':     ['廣島站','新白島站','原爆圓頂館前站','宮島口站'],
    'Fukuoka':       ['博多站','天神站','中洲川端站','福岡機場站'],
    'Sapporo':       ['札幌站','大通站','薄野站','新千歲機場站'],
    'Rome':          ['特米尼站','梵蒂岡站','西班牙廣場站','科洛西歐站','共和廣場站'],
    'Milan':         ['米蘭中央站','主教座堂廣場站','斯福爾扎城堡站','加里波第站'],
    'Barcelona':     ['巴塞隆拿聖拿站','加泰隆尼亞廣場站','帕塞奧格雷西亞站','薩格拉達聖家堂站'],
    'Berlin':        ['柏林中央站','亞歷山大廣場站','腓特烈街站','波茨坦廣場站','動物園站'],
    'Amsterdam':     ['阿姆斯特丹中央站','水壩廣場站','博物館廣場站','阿姆斯特丹南站'],
    'Vienna':        ['維也納中央站','斯蒂芬廣場站','卡爾廣場站','歌劇院站','西站'],
    'Prague':        ['瓦茨拉夫廣場站','老城廣場站','共和廣場站','安德爾站'],
    'Brussels':      ['布魯塞爾中央站','布魯塞爾北站','布魯塞爾南站','大廣場站'],
    'Dubai':         ['迪拜機場站','聯合廣場站','迪拜購物中心站','棕櫚島站','傑伯阿里站'],
    'New York':      ['時代廣場站','中央公園站','大中央站','世貿中心站','布魯克林橋站'],
    'Los Angeles':   ['聯合站','好萊塢/藤街站','威爾郡站','科爾弗城站','機場站'],
  };

  // Stations per rail line per city (for filtered station lists)
  const RAIL_STATIONS = {
    'Hong Kong': {
      mtr:  ['香港站','九龍站','旺角東站','紅磡站','尖沙咀站','中環站','灣仔站','銅鑼灣站','天后站','鑽石山站','觀塘站','荃灣站','屯門站','元朗站','沙田站','大埔墟站','羅湖站','落馬洲站'],
      tram: ['上環站','中環站','金鐘站','灣仔站','銅鑼灣站','跑馬地站','天后站','筲箕灣站'],
    },
    'Tokyo': {
      metro:      ['澀谷站','新宿站','池袋站','上野站','銀座站','表參道站','六本木站','赤坂站','大手町站','新橋站'],
      jr:         ['新宿站','澀谷站','東京站','品川站','上野站','秋葉原站','池袋站','高田馬場站','田端站','鶯谷站','日暮里站','西日暮里站'],
      toei:       ['新宿站','神保町站','馬喰橫山站','淺草站','押上站','三田站','大門站'],
      shinkansen: ['東京站','品川站','新橫濱站','靜岡站','濱松站','名古屋站','京都站','新大阪站','新神戶站','岡山站','廣島站','小倉站','博多站','仙台站','新青森站'],
      jr_pass:    ['東京站','品川站','新橫濱站','名古屋站','京都站','新大阪站','廣島站','博多站','仙台站','札幌站'],
    },
    'Osaka': {
      metro:      ['梅田站','難波站','天王寺站','心齋橋站','本町站','新大阪站','天神橋筋六丁目站'],
      jr:         ['大阪站','新大阪站','難波站','天王寺站','京橋站','森之宮站','西九条站'],
      hankyu:     ['梅田站','十三站','淡路站','石橋阪大前站','嵐山站'],
      hanshin:    ['梅田站','尼崎站','甲子園站','西宮站','神戶三宮站'],
      shinkansen: ['新大阪站','京都站','新神戶站','岡山站','廣島站','博多站','名古屋站','東京站'],
      jr_pass:    ['新大阪站','京都站','廣島站','博多站','名古屋站','東京站','奈良'],
    },
    'Seoul': {
      metro:  ['首爾站','東大門站','弘大站','明洞站','江南站','新村站','乙支路站','鐘路三街站'],
      korail: ['首爾站','龍山站','水西站','清涼里站','仁川站'],
    },
    'Bangkok': {
      bts: ['阿索克站','暹羅站','奇隆站','沙潘塔克辛站','烏泰站','廊曼站','勝利紀念碑站','安努站'],
      mrt: ['素坤逸站','席隆站','廊漢站','旺威亞站','地鐵回路站','曼谷站'],
    },
    'Singapore': {
      mrt: ['裕廊東站','政府大廈站','烏節站','海灣舫站','丹戎巴葛站','武吉士站','多美歌站','勞明達站'],
      lrt: ['武吉班讓站','鳳山站','巴西班讓站'],
    },
    'Taipei': {
      mrt: ['台北站','板橋站','松山站','南港站','大安站','信義安和站','市政府站','台北101/世貿站'],
      tra: ['台北站','板橋站','松山站','南港站','汐止站'],
    },
    'Kyoto': {
      jr:         ['京都站','二条站','花園站','嵯峨嵐山站'],
      keihan:     ['三条站','丹波橋站','伏見稲荷站','藤森站','深草站'],
      kintetsu:   ['近鐵京都站','東寺站','竹田站','伏見站','丹波橋站'],
      shinkansen: ['京都站','新大阪站','名古屋站','東京站','廣島站','博多站','新神戶站'],
      jr_pass:    ['京都站','新大阪站','奈良','東京站','廣島站','博多站'],
    },
    'London': {
      tube:       ['維多利亞站','帕丁頓站','滑鐵盧站','倫敦橋站','尤斯頓站','聖潘克拉斯站','金融城站','銀行站','利物浦街站','牛津圓環站','皮卡迪利廣場站','柯芬園站'],
      eliz:       ['帕丁頓站','龐德街站','托特納姆宮路站','法靈頓站','利物浦街站','白教堂站'],
      dlr:        ['金融城站','金絲雀碼頭站','斯特拉特福站','伍利治兵工廠站'],
      overground: ['達爾斯頓嫩芽站','海克尼中央站','帕丁頓站','尤斯頓站'],
    },
    'Paris': {
      metro: ['北站','沙特萊站','羅浮宮站','聖拉撒路站','里昂站','東站','巴士底站','共和國廣場站'],
      rer:   ['北站','沙特萊-大堂站','里昂站','聖日耳曼昂萊站','凡爾賽站','戴高樂機場站'],
      tram:  ['聖德尼站','薩克雷路站','維爾納依站'],
    },
    // 中國
    'Beijing': {
      subway:  ['天安門東站','王府井站','西直門站','北京站','北京南站','朝陽站','國貿站','中關村站','北京北站','清河站'],
      cap_exp: ['北京首都機場T3站','北京首都機場T2站','三元橋站','東直門站'],
    },
    'Shanghai': {
      metro:  ['人民廣場站','南京東路站','陸家嘴站','靜安寺站','徐家匯站','虹橋火車站','虹橋機場站','上海火車站','浦東機場站'],
      maglev: ['上海浦東機場站','龍陽路站'],
    },
    'Guangzhou': {
      metro: ['廣州南站','廣州站','廣州東站','天河城站','珠江新城站','體育西路站','公園前站','白雲機場站'],
    },
    'Shenzhen': {
      metro: ['深圳北站','福田站','羅湖站','寶安站','前海灣站','深圳機場站','科技園站','龍華站'],
    },
    // 澳門
    'Macau': {
      lrt_mac: ['機場站','氹仔站','東亞運站','蓮花口岸站','媽閣站','澳門半島'],
    },
    // 韓國
    'Busan': {
      metro: ['釜山站','西面站','南浦洞站','海雲台站','廣安站','釜山大學站','金海機場站'],
      ktx:   ['釜山站','東大邱站','大田站','水原站','首爾站'],
    },
    // 馬來西亞
    'Kuala Lumpur': {
      lrt_kl:  ['吉隆坡中央站','敦拉薩站','安邦站','武吉免登站','KL Sentral站'],
      mrt_kl:  ['吉隆坡中央站','孟沙站','大學站','Muzium Negara站','KL Sentral站'],
      ktm_kl:  ['吉隆坡中央站','吉隆坡站','班底站','巴生站','蒲種站'],
    },
    // 日本其他城市
    'Hiroshima': {
      shinkansen: ['廣島站','新山口站','博多站','岡山站','新神戶站','新大阪站','京都站','東京站'],
      jr_pass:    ['廣島站','宮島口站','岡山站','新大阪站','東京站'],
      hiroden:    ['廣島站','紙屋町西站','原爆圓頂館前站','宮島口站','本通站'],
    },
    'Fukuoka': {
      shinkansen: ['博多站','小倉站','新山口站','廣島站','岡山站','新大阪站','東京站'],
      jr_pass:    ['博多站','小倉站','廣島站','新大阪站','東京站'],
      subway_fk:  ['博多站','天神站','中洲川端站','福岡機場站','姪浜站'],
    },
    'Sapporo': {
      subway_sp: ['大通站','さっぽろ站','南平岸站','真駒內站','宮の沢站','栄町站'],
      jr_pass:   ['札幌站','小樽站','函館站','旭川站','新千歲機場站'],
    },
    // 歐洲其他城市
    'Rome': {
      metro: ['特米尼站','梵蒂岡-聖彼得站','西班牙廣場站','巴貝里尼站','共和廣場站','科洛西歐站','波爾圖佩利亞站'],
      tram:  ['特米尼站','韋內托大道站','特拉斯提弗列站','孔科爾達廣場站'],
    },
    'Milan': {
      metro:  ['米蘭中央站','主教座堂廣場站','斯福爾扎城堡站','加里波第站','馬真塔廣場站','林南特站'],
      tram:   ['斯卡拉廣場站','布宜諾斯艾利斯大道站','科爾索布宜諾斯艾利斯站'],
      s_rail: ['米蘭中央站','加里波第站','羅戈雷多站','蘭拿特站'],
    },
    'Barcelona': {
      metro:   ['巴塞隆拿站','加泰隆尼亞廣場站','格蘭維亞站','帕塞奧格雷西亞站','迪亞哥那爾站','薩格拉達聖家堂站','巴塞隆拿聖拿站'],
      fgc:     ['加泰隆尼亞廣場站','格拉西亞站','薩里亞站','聖安德列斯站'],
      renfe_c: ['巴塞隆拿聖拿站','帕塞奧格雷西亞站','巴達洛納站','加瓦站'],
    },
    'Berlin': {
      u_bahn: ['亞歷山大廣場站','波茨坦廣場站','動物園站','選帝侯大道站','柏林中央站','諾霍爾德堡站','勝利紀念柱站'],
      s_bahn: ['柏林中央站','亞歷山大廣場站','腓特烈街站','波茨坦廣場站','動物園站','斯潘道站','奧斯特班霍夫站'],
      tram:   ['亞歷山大廣場站','羅莎盧森堡廣場站','哈克市場站','普倫茨勞貝格站'],
    },
    'Amsterdam': {
      metro: ['中央站','科爾納廣場站','比約梅爾站','阿姆斯特丹南站','羅金站','維特林霍倫站'],
      tram:  ['中央站','水壩廣場站','博物館廣場站','萊頓廣場站','阿姆斯特爾站','林布蘭廣場站'],
    },
    'Vienna': {
      u_bahn:   ['維也納中央站','斯蒂芬廣場站','卡爾廣場站','歌劇院站','城市公園站','普拉特站','薩爾茨托爾橋站'],
      tram:     ['維也納國家歌劇院站','卡爾廣場站','熊布朗宮站','百年紀念柱站'],
      s_bahn_v: ['維也納中央站','維也納米特站','維也納西站','法洛西德勒路站'],
    },
    'Prague': {
      metro: ['瓦茨拉夫廣場站','共和廣場站','老城廣場站','馬薩里克火車站','安德爾站','弗羅倫茨站'],
      tram:  ['瓦茨拉夫廣場站','查理大橋站','城堡區站','小城廣場站'],
    },
    'Brussels': {
      metro:    ['中央站','布魯塞爾北站','布魯塞爾南站','藝術山站','羅歇站','歐洲議會站'],
      tram:     ['中央站','大廣場站','薩布隆站','路易絲站'],
      s_rail_b: ['布魯塞爾中央站','布魯塞爾北站','布魯塞爾南站','歐洲區站'],
    },
    // 中東
    'Dubai': {
      metro: ['迪拜機場T1站','迪拜機場T3站','聯合廣場站','包哈里夫站','迪拜購物中心站','棕櫚島站','傑伯阿里站','博覽城站'],
      tram:  ['朱美拉湖塔站','迪拜碼頭站','朱美拉海灘住宅站','媒體城站'],
    },
    // 美洲
    'New York': {
      subway: ['時代廣場站','中央公園站','哥倫布圓環站','大中央站','世貿中心站','布魯克林橋站','法拉盛站','14街站','86街站'],
    },
    'Los Angeles': {
      metro: ['聯合站','好萊塢/藤街站','諾曼底站','威爾郡/韋斯特湖站','科爾弗城站','洛杉磯機場站'],
    },
  };

  // Train types by region
  // Country-based train types (matches bkCountry or bkCntMap values)
  const COUNTRY_TRAINS = {
    // 日本
    'Japan':          [{ k:'shinkansen', l:'🚄 新幹線 Shinkansen', url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+ticket&aid=${KLOOK_AID}` },{ k:'jr_pass', l:'🎫 JR Pass', url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass&aid=${KLOOK_AID}` },{ k:'jr_local', l:'🚂 JR 普通列車', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    '日本':           [{ k:'shinkansen', l:'🚄 新幹線 Shinkansen', url:`https://www.klook.com/en-HK/search/result/?query=shinkansen+ticket&aid=${KLOOK_AID}` },{ k:'jr_pass', l:'🎫 JR Pass', url:`https://www.klook.com/en-HK/search/result/?query=JR+Pass&aid=${KLOOK_AID}` },{ k:'jr_local', l:'🚂 JR 普通列車', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    // 韓國
    'South Korea':    [{ k:'ktx', l:'🚄 KTX', url:`https://www.klook.com/en-HK/search/result/?query=KTX+ticket&aid=${KLOOK_AID}` },{ k:'srt', l:'🚄 SRT', url:`https://www.klook.com/en-HK/search/result/?query=SRT+Korea&aid=${KLOOK_AID}` },{ k:'itx', l:'🚂 ITX 青春', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    'Korea':          [{ k:'ktx', l:'🚄 KTX', url:`https://www.klook.com/en-HK/search/result/?query=KTX+ticket&aid=${KLOOK_AID}` },{ k:'srt', l:'🚄 SRT', url:`https://www.klook.com/en-HK/search/result/?query=SRT+Korea&aid=${KLOOK_AID}` },{ k:'itx', l:'🚂 ITX 青春', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    '韓國':           [{ k:'ktx', l:'🚄 KTX', url:`https://www.klook.com/en-HK/search/result/?query=KTX+ticket&aid=${KLOOK_AID}` },{ k:'srt', l:'🚄 SRT', url:`https://www.klook.com/en-HK/search/result/?query=SRT+Korea&aid=${KLOOK_AID}` }],
    // 中國
    'China':          [{ k:'g_train', l:'🚄 高鐵 G-Train', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'d_train', l:'🚄 動車 D-Train', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'c_train', l:'🚂 城際 C-Train', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    '中國':           [{ k:'g_train', l:'🚄 高鐵 G-Train', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'d_train', l:'🚄 動車 D-Train', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    // 台灣
    'Taiwan':         [{ k:'hsr', l:'🚄 台灣高鐵 HSR', url:`https://www.klook.com/en-HK/search/result/?query=Taiwan+HSR&aid=${KLOOK_AID}` },{ k:'tra', l:'🚂 台鐵 TRA', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    '台灣':           [{ k:'hsr', l:'🚄 台灣高鐵 HSR', url:`https://www.klook.com/en-HK/search/result/?query=Taiwan+HSR&aid=${KLOOK_AID}` },{ k:'tra', l:'🚂 台鐵 TRA', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    // 法國
    'France':         [{ k:'tgv', l:'🚄 TGV / Thalys', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'eurostar', l:'🚄 Eurostar', url:'https://www.eurostar.com/' },{ k:'eurail', l:'🎫 Eurail Pass', url:'https://www.eurail.com/' },{ k:'intercites', l:'🚂 Intercités', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    // 英國
    'United Kingdom': [{ k:'nat_rail', l:'🚂 National Rail', url:`https://www.klook.com/en-HK/search/result/?query=UK+train+pass&aid=${KLOOK_AID}` },{ k:'eurostar', l:'🚄 Eurostar', url:'https://www.eurostar.com/' }],
    'UK':             [{ k:'nat_rail', l:'🚂 National Rail', url:`https://www.klook.com/en-HK/search/result/?query=UK+train+pass&aid=${KLOOK_AID}` },{ k:'eurostar', l:'🚄 Eurostar', url:'https://www.eurostar.com/' }],
    // 德國
    'Germany':        [{ k:'ice', l:'🚄 ICE', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'ic', l:'🚂 IC/EC', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'eurail', l:'🎫 Eurail Pass', url:'https://www.eurail.com/' }],
    // 意大利
    'Italy':          [{ k:'frecciarossa', l:'🚄 Frecciarossa', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'italo', l:'🚄 Italo', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'intercity', l:'🚂 Intercity', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'eurail', l:'🎫 Eurail Pass', url:'https://www.eurail.com/' }],
    // 西班牙
    'Spain':          [{ k:'ave', l:'🚄 AVE 高速', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'renfe', l:'🚂 Renfe', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'eurail', l:'🎫 Eurail Pass', url:'https://www.eurail.com/' }],
    // 瑞士
    'Switzerland':    [{ k:'sbb', l:'🚂 SBB Swiss Rail', url:`https://www.klook.com/en-HK/search/result/?query=Switzerland+rail+pass&aid=${KLOOK_AID}` },{ k:'eurail', l:'🎫 Eurail Pass', url:'https://www.eurail.com/' }],
    // 美國
    'United States':  [{ k:'amtrak', l:'🚂 Amtrak', url:'https://www.amtrak.com/' }],
    'USA':            [{ k:'amtrak', l:'🚂 Amtrak', url:'https://www.amtrak.com/' }],
    // 泰國
    'Thailand':       [{ k:'bts', l:'🚇 BTS Skytrain', url:`https://www.klook.com/en-HK/search/result/?query=Bangkok+BTS&aid=${KLOOK_AID}` },{ k:'mrt_bkk', l:'🚇 MRT 曼谷', url:`https://www.klook.com/en-HK/search/result/?query=Bangkok+MRT&aid=${KLOOK_AID}` },{ k:'srb', l:'🚂 泰國國鐵 SRB', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` }],
    // 默認
    'default':        [{ k:'hsr', l:'🚄 高鐵 HSR', url:`https://www.trip.com/trains/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },{ k:'bus', l:'🚌 跨境巴士', url:`https://www.klook.com/en-HK/search/result/?query=cross+border+bus&aid=${KLOOK_AID}` },{ k:'eurail', l:'🎫 Eurail Pass', url:'https://www.eurail.com/' }],
  };
  // Helper: get trains for current booking country
  const getTrainsForCountry = () => COUNTRY_TRAINS[bkCntMap[bkCountry]] || COUNTRY_TRAINS[bkCountry] || COUNTRY_TRAINS['default'];
  // Filter to only trains that genuinely cross international borders
  const isIntlTrain = (k) => {
    const r = TRAIN_ROUTES[k];
    if (!r) return true;
    const cts = r.countries || [];
    if (!cts.length) return true; // generic (bus etc.)
    const uniq = [...new Set(cts.map(c => c==='Korea'?'South Korea':c==='UK'?'United Kingdom':c))];
    return uniq.length > 1;
  };

  // Countries/cities supported by each train type (for destination filtering)
  const TRAIN_ROUTES = {
    shinkansen:   { countries:['Japan'],                                                         cities:{ Japan:['Tokyo','Osaka','Kyoto','Hiroshima','Nagoya','Fukuoka','Sapporo','Sendai'] } },
    jr_pass:      { countries:['Japan'],                                                         cities:{ Japan:['Tokyo','Osaka','Kyoto','Hiroshima','Nagoya','Fukuoka','Sapporo','Nara','Hakone'] } },
    jr_local:     { countries:['Japan'],                                                         cities:{} },
    ktx:          { countries:['South Korea','Korea'],                                           cities:{ 'South Korea':['Seoul','Busan','Daegu','Daejeon','Gwangju'] } },
    srt:          { countries:['South Korea','Korea'],                                           cities:{ 'South Korea':['Seoul','Busan'] } },
    itx:          { countries:['South Korea','Korea'],                                           cities:{} },
    g_train:      { countries:['China'],                                                         cities:{ China:['Beijing','Shanghai','Guangzhou','Shenzhen','Hangzhou','Chengdu','Xi\'an','Nanjing'] } },
    d_train:      { countries:['China'],                                                         cities:{ China:['Beijing','Shanghai','Guangzhou','Chengdu','Kunming'] } },
    c_train:      { countries:['China'],                                                         cities:{} },
    hsr:          { countries:['Taiwan'],                                                        cities:{ Taiwan:['Taipei','Taichung','Tainan','Kaohsiung','Hsinchu'] } },
    tra:          { countries:['Taiwan'],                                                        cities:{} },
    tgv:          { countries:['France','Belgium','Netherlands'],                               cities:{ France:['Paris','Lyon','Marseille','Bordeaux','Lille','Nice','Strasbourg'], Belgium:['Brussels'], Netherlands:['Amsterdam'] } },
    eurostar:     { countries:['United Kingdom','France','Belgium'],                             cities:{ 'United Kingdom':['London'], France:['Paris','Lille'], Belgium:['Brussels'] } },
    nat_rail:     { countries:['United Kingdom'],                                                cities:{} },
    ice:          { countries:['Germany','Austria','Switzerland'],                               cities:{} },
    ic:           { countries:['Germany','Austria','Switzerland','Netherlands'],                 cities:{} },
    eurail:       { countries:['France','Germany','Italy','Spain','Switzerland','Netherlands','Austria','Belgium','Portugal','Greece','Denmark','Sweden','Norway'], cities:{} },
    frecciarossa: { countries:['Italy'],                                                         cities:{ Italy:['Rome','Milan','Florence','Venice','Naples','Turin'] } },
    italo:        { countries:['Italy'],                                                         cities:{ Italy:['Rome','Milan','Florence','Venice','Naples'] } },
    intercity:    { countries:['Italy'],                                                         cities:{} },
    intercites:   { countries:['France'],                                                        cities:{} },
    ave:          { countries:['Spain'],                                                         cities:{ Spain:['Madrid','Barcelona','Seville','Valencia','Bilbao'] } },
    renfe:        { countries:['Spain'],                                                         cities:{} },
    sbb:          { countries:['Switzerland','Germany','Austria','France','Italy'],               cities:{} },
    amtrak:       { countries:['United States'],                                                 cities:{} },
    bts:          { countries:['Thailand'],                                                      cities:{ Thailand:['Bangkok'] } },
    mrt_bkk:      { countries:['Thailand'],                                                      cities:{ Thailand:['Bangkok'] } },
    srb:          { countries:['Thailand'],                                                      cities:{} },
    bus:          { countries:[], cities:{} }, // generic — no filter
  };

  const renderBooking = () => (
    <div style={{ maxWidth:900, margin:'0 auto', padding:'14px 14px 96px', display:'flex', gap:16, alignItems:'flex-start' }}>
      {/* Klook affiliate sidebar */}
      <div className="klook-sidebar" style={{ width:160, flexShrink:0, position:'sticky', top:108 }}>
        <KlookAffBanner/>
      </div>
      {/* Main content */}
      <div style={{ flex:1, minWidth:0 }}>
      <p style={{ fontSize:22, fontWeight:900, color:'#0f172a', margin:'0 0 16px' }}>{t.booking}</p>
      {/* 14 天禮遇提示：未有禮遇的用戶先顯示 */}
      {!affiliateReward && user?.email !== ADMIN_EMAIL && (
        <div style={{ background:'#ecfdf5', border:'1.5px solid #a7f3d0', borderRadius:12, padding:'10px 14px', marginBottom:14, fontSize:12, fontWeight:700, color:'#047857', lineHeight:1.6 }}>
          🎁 {affPerkText()}
        </div>
      )}
      {/* Type selector */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:8, marginBottom:18 }}>
        {bkTypes.map(({k,l,ic:Ic})=>(
          <button key={k} onClick={()=>{setBkType(k);setBkTicketCat(null);setBkResults(null);setBkRegion('');setBkCountry('');setBkCity('');setBkDest('');setBkCountries([]);setBkCities([]);}}
            style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:7,padding:'14px 8px',borderRadius:14,border:bkType===k?'none':'1.5px solid #e2e8f0',background:bkType===k?'#0f172a':'white',color:bkType===k?'white':'#64748b',cursor:'pointer',transition:'all 0.2s' }}>
            <Ic size={20}/><span style={{ fontSize:10,fontWeight:900,textAlign:'center',lineHeight:1.2 }}>{l}</span>
          </button>
        ))}
      </div>

      {/* Ticket sub-categories */}
      {bkType==='tickets'&&(
        <div style={{ marginBottom:14 }}>
          <p style={{ fontSize:12, fontWeight:700, color:'#64748b', margin:'0 0 10px' }}>選擇門票類別</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:12 }}>
            {[
              {k:'theme parks',        l:'主題樂園', e:'🎢'},
              {k:'museums',            l:'博物館',   e:'🏛️'},
              {k:'zoos',               l:'動物園',   e:'🦁'},
              {k:'aquariums',          l:'海洋館',   e:'🐠'},
              {k:'exhibitions',        l:'展覽館',   e:'🖼️'},
              {k:'concerts',           l:'演唱會',   e:'🎤'},
              {k:'sports matches',     l:'球賽',     e:'⚽'},
              {k:'tourist attractions',l:'景點',     e:'📍'},
            ].map(({k,l,e})=>(
              <button key={k} onClick={()=>{ setBkTicketCat(k); setBkEventMonth(null); }}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'10px 4px', borderRadius:12, border: bkTicketCat===k?'none':'1.5px solid #e2e8f0', background:bkTicketCat===k?'#f59e0b':'#fff', color:bkTicketCat===k?'#fff':'#64748b', cursor:'pointer', transition:'all 0.15s' }}>
                <span style={{ fontSize:18 }}>{e}</span>
                <span style={{ fontSize:10, fontWeight:800, textAlign:'center', lineHeight:1.2 }}>{l}</span>
              </button>
            ))}
          </div>
          {/* Year / Month selector — concerts & sports only */}
          {(bkTicketCat==='concerts'||bkTicketCat==='sports matches')&&(
            <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px', border:'1px solid #e2e8f0' }}>
              <p style={{ margin:'0 0 10px', fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:1 }}>
                {bkTicketCat==='concerts'?'🎤 演唱會':'⚽ 球賽'} 日期篩選
              </p>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {/* Year */}
                <div>
                  <p style={{ margin:'0 0 6px', fontSize:11, color:'#94a3b8', fontWeight:700 }}>年份</p>
                  <div style={{ display:'flex', gap:6 }}>
                    {[2026,2027,2028].map(y=>(
                      <button key={y} onClick={()=>setBkEventYear(y)}
                        style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid', borderColor:bkEventYear===y?'#0f172a':'#e2e8f0', background:bkEventYear===y?'#0f172a':'#fff', color:bkEventYear===y?'#fff':'#64748b', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Month */}
                <div style={{ flex:1 }}>
                  <p style={{ margin:'0 0 6px', fontSize:11, color:'#94a3b8', fontWeight:700 }}>月份（可選）</p>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                    <button onClick={()=>setBkEventMonth(null)}
                      style={{ padding:'5px 10px', borderRadius:8, border:'1.5px solid', borderColor:bkEventMonth===null?'#0f172a':'#e2e8f0', background:bkEventMonth===null?'#0f172a':'#fff', color:bkEventMonth===null?'#fff':'#64748b', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                      全年
                    </button>
                    {['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'].map((m,i)=>(
                      <button key={i} onClick={()=>setBkEventMonth(i+1)}
                        style={{ padding:'5px 10px', borderRadius:8, border:'1.5px solid', borderColor:bkEventMonth===i+1?'#f59e0b':'#e2e8f0', background:bkEventMonth===i+1?'#f59e0b':'#fff', color:bkEventMonth===i+1?'#fff':'#64748b', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cross-border: flight / train / bus mode selector（巴士只有 HopeGoo 港陸路線連結，沒有 Trip.com 嵌入框） */}
      {bkType==='crossBorder' && (
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {[{k:'flight',l:'✈️ 機票',},{k:'train',l:'🚄 火車'},{k:'bus',l:'🚌 巴士'}].map(m=>(
            <button key={m.k} onClick={()=>{ setBkCrossMode(m.k); setBkCrossFold(m.k==='train' ? 'hopegoo' : 'trip'); setBkTrainType(''); setBkResults(null); }}
              style={{ flex:1, padding:'10px 0', borderRadius:12, border:'none', background:bkCrossMode===m.k?'#0f172a':'#f1f5f9', color:bkCrossMode===m.k?'#fff':'#64748b', fontSize:13, fontWeight:900, cursor:'pointer', transition:'all 0.15s' }}>
              {m.l}
            </button>
          ))}
        </div>
      )}

      {bkType&&(
        <div style={{ ...S.card, display:'flex', flexDirection:'column', gap:14 }}>
          {/* 住宿：Trip.com 小工具 / Stay22 多平台比價 切換 */}
          {bkType==='accommodation' && (
            <div style={{ display:'flex', gap:6, background:'#f1f5f9', borderRadius:12, padding:4 }}>
              {[['trip','Trip.com'],['stay22',lang==='en'?'Compare platforms':'多平台比價']].map(([k,label])=>(
                <button key={k} onClick={()=>setBkHotelMode(k)}
                  style={{ flex:1, padding:'8px 12px', borderRadius:9, border:'none', background:bkHotelMode===k?'white':'transparent', color:bkHotelMode===k?'#0f172a':'#64748b', fontSize:13, fontWeight:800, cursor:'pointer', boxShadow:bkHotelMode===k?'0 1px 3px rgba(15,23,42,0.12)':'none' }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* Cascade: region → country → city, or Trip.com iframe for cross-border */}
          {bkType==='crossBorder' ? (() => {
            // 機票／火車：Trip.com 與 HopeGoo 各收成一行摺疊列，一次只開一行。
            // 火車預設展開 HopeGoo 高鐵熱門路線（香港用戶最常用），機票預設展開 Trip.com
            if (bkCrossMode!=='flight' && bkCrossMode!=='train') return null;
            const en = lang==='en'||lang==='ja'||lang==='ko';
            const sc = lang==='zh-CN';
            const fold = (k) => ({ open: bkCrossFold===k, onToggle: () => setBkCrossFold(f => f===k ? '' : k) });
            const tripRow = bkCrossMode==='flight' ? (
              <FoldRow key="trip" {...fold('trip')} keepMounted source="Trip.com" sourceColor="#2f6bff"
                title={en ? '✈️ Search flights' : sc ? '✈️ 搜索机票' : '✈️ 搜尋機票'}
                subtitle={en ? 'Enter origin, destination and date' : sc ? '输入出发地、目的地及日期' : '輸入出發地、目的地及日期'}>
                <iframe
                  src="https://hk.trip.com/partners/ad/S16741727?Allianceid=8171962&SID=309371812&trip_sub1="
                  style={{ width:'100%', height:465, border:'none', display:'block' }}
                  scrolling="no"
                  title="Trip.com 機票"
                />
              </FoldRow>
            ) : (
              <FoldRow key="trip" {...fold('trip')} keepMounted source="Trip.com" sourceColor="#2f6bff"
                title={en ? '🔍 Search other train routes' : sc ? '🔍 搜索其他火车路线' : '🔍 搜尋其他火車路線'}
                subtitle={en ? 'Mainland China, Europe, Japan and more' : sc ? '内地、欧洲、日本等，自行输入出发地及目的地' : '內地、歐洲、日本等，自行輸入出發地及目的地'}>
                <iframe
                  src="https://hk.trip.com/partners/ad/S16742511?Allianceid=8171962&SID=309371812&trip_sub1="
                  style={{ width:'100%', height:465, border:'none', display:'block' }}
                  scrolling="no"
                  title="Trip.com 火車"
                />
              </FoldRow>
            );
            // HopeGoo (CJ)：香港西九龍 → 大陸高鐵，直接列出每個有連結的到達站；機票只有搜尋頁一條連結，作比價選項
            const hopegooRow = bkCrossMode==='train' ? (
              <HopeGooPanel key="hopegoo" collapsible {...fold('hopegoo')}
                title={en ? '🚄 High-speed rail from Hong Kong West Kowloon' : sc ? '🚄 香港西九龙高铁热门路线' : '🚄 香港西九龍高鐵熱門路線'}
                subtitle={en ? 'Pick a destination station to check timetable & fares' : sc ? '选择到达站，查阅时刻表与票价' : '選擇到達站，查閱時刻表與票價'}
                items={isMobile
                  // 逐站連結在手機上會開電腦版頁面（英文、版面過寬），手機改開 HopeGoo 手機版火車分頁
                  ? [{ key:'general', label: en ? 'Search trains on HopeGoo' : sc ? '在 HopeGoo 查询高铁' : '在 HopeGoo 查詢高鐵', href: withSid(HOPEGOO_TRAIN_SEARCH, 'train-m-general'), accent:true }]
                  : [
                      ...hopegooTrainStations().map(st => ({ key:st.slug, label: en ? st.en : sc ? st.sc : st.zh, href: withSid(st.url, `train-hk-${st.slug}`) })),
                      { key:'general', label: en ? 'More routes' : sc ? '更多路线' : '更多路線', href: withSid(HOPEGOO_TRAIN_SEARCH, 'train-general'), accent:true },
                    ]}/>
            ) : (
              <HopeGooPanel key="hopegoo" collapsible {...fold('hopegoo')}
                title={en ? '💰 Compare flight prices' : sc ? '💰 比较机票价格' : '💰 比較機票價錢'}
                subtitle={en ? 'Search on another platform (English interface)' : sc ? '在另一个平台搜索（英文界面）' : '在另一個平台搜尋（英文介面）'}
                items={[{ key:'flight', label: en ? 'Search flights' : sc ? '搜索机票' : '搜尋機票', href: withSid(HOPEGOO_FLIGHT_SEARCH, 'flight-general'), accent:true }]}/>
            );
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {bkCrossMode==='train' ? [hopegooRow, tripRow] : [tripRow, hopegooRow]}
              </div>
            );
          })() : bkType==='accommodation' && bkHotelMode==='trip' ? (
            <div style={{ overflow:'visible' }}>
              <iframe
                src="https://hk.trip.com/partners/ad/S16762391?Allianceid=8171962&SID=309371812&trip_sub1="
                style={{ width:'100%', height:465, border:'none', display:'block' }}
                scrolling="no"
                title="Trip.com 酒店"
              />
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {/* Breadcrumb of picks so far — click one to jump back and change it */}
              {(bkRegion || bkCountry || bkCity) && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {bkRegion && (
                    <button onClick={()=>{ setBkRegion(''); setBkCountry(''); setBkCity(''); setBkCountries([]); setBkCities([]); setBkCityCustom(''); setBkAirport(''); setBkTrainType(''); }}
                      style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20, border:'1.5px solid #f59e0b', background:'#fffbeb', color:'#92400e', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                      <Globe size={12}/>{t.regions[bkRegion]||bkRegion}<span style={{ opacity:0.6, marginLeft:2 }}>×</span>
                    </button>
                  )}
                  {bkCountry && (
                    <button onClick={()=>{ setBkCountry(''); setBkCity(''); setBkCities([]); setBkCityCustom(''); setBkAirport(''); }}
                      style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20, border:'1.5px solid #f59e0b', background:'#fffbeb', color:'#92400e', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                      <Compass size={12}/>{bkCntMap[bkCountry]||bkCountry}<span style={{ opacity:0.6, marginLeft:2 }}>×</span>
                    </button>
                  )}
                  {bkCity && (
                    <button onClick={()=>{ setBkCity(''); setBkCityCustom(''); setBkAirport(''); }}
                      style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20, border:'1.5px solid #f59e0b', background:'#fffbeb', color:'#92400e', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                      <MapPin size={12}/>{bkCityMap[bkCity]||bkCity}<span style={{ opacity:0.6, marginLeft:2 }}>×</span>
                    </button>
                  )}
                </div>
              )}

              {/* Step 1: region */}
              {!bkRegion && (
                <div>
                  <p style={S.label}>{t.region}</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {REGIONS.map(r=>(
                      <button key={r} onClick={()=>{ setBkRegion(r); fetchBkCountries(r,lang); setBkAirport(''); setBkTrainType(''); }}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                        <Globe size={14} color="#f59e0b"/>{t.regions[r]||r}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: country */}
              {bkRegion && !bkCountry && (
                <div>
                  <p style={S.label}>{t.country}</p>
                  {loadingBkGeo ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', color:'#94a3b8', fontSize:13 }}>
                      <Loader2 size={14} style={{ animation:'spin 1s linear infinite' }}/>{t.loading}
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                      {bkCountries.map(c=>(
                        <button key={c} onClick={()=>{ setBkCountry(c); fetchBkCities(c,lang); setBkAirport(''); }}
                          style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                          <Compass size={14} color="#f59e0b"/>{bkCntMap[c]||c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: city (skipped for dining/esim — booked by country, not city) */}
              {bkRegion && bkCountry && !bkCity && bkType!=='dining' && bkType!=='esim' && (
                <div>
                  <p style={S.label}>{t.city}</p>
                  {loadingBkGeo ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', color:'#94a3b8', fontSize:13 }}>
                      <Loader2 size={14} style={{ animation:'spin 1s linear infinite' }}/>{t.loading}
                    </div>
                  ) : (
                    <>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                        {bkCities.map(c=>(
                          <button key={c} onClick={()=>{ setBkCity(c); setBkAirport(''); }}
                            style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'white', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                            <MapPin size={14} color="#f59e0b"/>{bkCityMap[c]||c}
                          </button>
                        ))}
                      </div>
                      <input value={bkCityCustom} onChange={e=>setBkCityCustom(e.target.value)}
                        onKeyDown={e=>{ if(e.key==='Enter' && bkCityCustom.trim()){ setBkCity(bkCityCustom.trim()); setBkAirport(''); } }}
                        placeholder={lang==='en'?'Or type another city…':'或輸入其他城市…'}
                        style={{ marginTop:10, width:'100%', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'9px 12px', fontSize:13, fontWeight:600, outline:'none', color:'#1e293b', boxSizing:'border-box' }}/>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 住宿 · 多平台比價：選好城市後顯示 Stay22 酒店地圖（真實房源，經 STAY22_AID 計佣） */}
          {bkType==='accommodation' && bkHotelMode==='stay22' && bkCity && (
            <div style={{ borderRadius:16, border:'1.5px solid #e2e8f0', overflow:'hidden' }}>
              <iframe
                key={bkCity}
                src={`https://www.stay22.com/embed/gm?aid=${STAY22_AID}&address=${encodeURIComponent([bkCity, bkCountry].filter(Boolean).join(', '))}&currency=${currency}`}
                style={{ width:'100%', height:460, border:'none', display:'block' }}
                loading="lazy"
                title="Stay22 hotel map"
              />
            </div>
          )}

          {/* 住宿 · HopeGoo (CJ)：只在有 HopeGoo 酒店連結的大陸城市出現，放在 Stay22 地圖下方 */}
          {bkType==='accommodation' && bkHotelMode==='stay22' && bkCity && (() => {
            const key = hopegooCityKey(bkCity, bkCityMap[bkCity]);
            const url = hopegooHotelUrl(key);
            if (!url) return null;
            const name = bkCityMap[bkCity] || bkCity;
            return (
              <a href={withSid(url, `hotel-${key}`)} target="_blank" rel="sponsored noopener noreferrer"
                 style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:7, background:'#1f9d55', color:'#fff', borderRadius:12, padding:'11px', fontSize:13, fontWeight:900, textDecoration:'none' }}>
                <Hotel size={14}/> {(lang==='en'||lang==='ja'||lang==='ko') ? `View ${name} hotels on HopeGoo` : `在 HopeGoo 查看${name}酒店`} <ExternalLink size={11}/>
              </a>
            );
          })()}

          {/* Local Transport: departure point (station or airport) + destination station */}
          {bkType==='localTransport' && bkCity && (() => {
            const cityEn   = CITY_EN[bkCity] || CITY_EN[bkCityMap[bkCity]] || bkCity;
            const airports = CITY_AIRPORTS[cityEn] || [];
            const railSts   = bkLocalTransType&&RAIL_STATIONS[cityEn]?.[bkLocalTransType] ? RAIL_STATIONS[cityEn][bkLocalTransType] : (CITY_STATIONS[cityEn]||[]);
            const depPoints = bkDepType==='airport' ? airports.map(a=>`${a.code} · ${a.name}`) : railSts;
            const allPoints = bkDepType==='airport' ? railSts : [...railSts, ...airports.map(a=>`${a.code} · ${a.name}`)];
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {/* Departure type toggle */}
                <div>
                  <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:700, color:'#64748b' }}>🚏 {(lang==='en'||lang==='ja'||lang==='ko')?'Departure Type':lang==='zh-CN'?'出发点类型':'出發點類型'}</p>
                  <div style={{ display:'flex', gap:7 }}>
                    {[{k:'station',l:(lang==='en'||lang==='ja'||lang==='ko')?'🚉 Station':'🚉 車站'},{k:'airport',l:(lang==='en'||lang==='ja'||lang==='ko')?'✈️ Airport':'✈️ 機場'}].map(dt=>(
                      <button key={dt.k} onClick={()=>{ setBkDepType(dt.k); setBkDepStation(''); setBkAirport(''); setBkLocalTransType(''); }}
                        style={{ padding:'8px 18px', borderRadius:10, border:'1.5px solid', borderColor:bkDepType===dt.k?'#0f172a':'#e2e8f0', background:bkDepType===dt.k?'#0f172a':'#fff', color:bkDepType===dt.k?'#fff':'#64748b', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                        {dt.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Rail / transport type — city-specific */}
                {(() => {
                  const transTypes = CITY_LOCAL_TRANSPORT[cityEn] || [];
                  if (!transTypes.length) return null;
                  return (
                    <div>
                      <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:700, color:'#64748b' }}>🚌 {(lang==='en'||lang==='ja'||lang==='ko')?'Rail / Transport Type':lang==='zh-CN'?'铁路/交通种类':'鐵路/交通種類'}</p>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {transTypes.map(tr=>(
                          <button key={tr.k} onClick={()=>setBkLocalTransType(bkLocalTransType===tr.k?'':tr.k)}
                            style={{ padding:'7px 13px', borderRadius:10, border:'1.5px solid', fontSize:11, fontWeight:800, cursor:'pointer', transition:'all 0.15s',
                              borderColor:bkLocalTransType===tr.k?'#f59e0b':'#e2e8f0',
                              background:bkLocalTransType===tr.k?'#f59e0b':'#fff',
                              color:bkLocalTransType===tr.k?'#fff':'#64748b' }}>
                            {tr.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {/* Departure point list */}
                <div>
                  <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:700, color:'#64748b' }}>
                    {bkDepType==='airport'?((lang==='en'||lang==='ja'||lang==='ko')?'Select Departure Airport':lang==='zh-CN'?'选择出发机场':'選擇出發機場'):((lang==='en'||lang==='ja'||lang==='ko')?'Select Departure Station':lang==='zh-CN'?'选择出发车站':'選擇出發車站')}
                  </p>
                  {depPoints.length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                      {depPoints.map(pt=>(
                        <button key={pt} onClick={()=>{ bkDepType==='airport'?setBkAirport(pt):setBkDepStation(pt); }}
                          style={{ padding:'7px 12px', borderRadius:8, border:'1.5px solid', fontSize:11, fontWeight:700, cursor:'pointer',
                            borderColor:(bkDepType==='airport'?bkAirport:bkDepStation)===pt?'#f59e0b':'#e2e8f0',
                            background:(bkDepType==='airport'?bkAirport:bkDepStation)===pt?'#f59e0b':'#fff',
                            color:(bkDepType==='airport'?bkAirport:bkDepStation)===pt?'#fff':'#64748b' }}>
                          {pt}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Manual input for departure station (station mode only) */}
                  {bkDepType==='station' && (
                    <input
                      value={depPoints.includes(bkDepStation)?'':bkDepStation}
                      onChange={e=>setBkDepStation(e.target.value)}
                      placeholder={(lang==='en'||lang==='ja'||lang==='ko')?'Type station name…':lang==='zh-CN'?'手动输入出发车站名称…':'手動輸入出發車站名稱…'}
                      style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', boxSizing:'border-box', color:'#0f172a' }}
                      onFocus={e=>e.target.style.borderColor='#f59e0b'}
                      onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  )}
                </div>
                {/* Destination station — always shown once city is selected */}
                {allPoints.length > 0 && (
                  <div>
                    <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:700, color:'#64748b' }}>🏁 {(lang==='en'||lang==='ja'||lang==='ko')?'Destination Station / Airport':lang==='zh-CN'?'目的地车站／机场':'目的地車站／機場'}</p>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                      {allPoints.filter(pt=>pt!==(bkDepType==='airport'?bkAirport:bkDepStation)).map(pt=>(
                        <button key={pt} onClick={()=>setBkDestStation(pt)}
                          style={{ padding:'7px 12px', borderRadius:8, border:'1.5px solid', fontSize:11, fontWeight:700, cursor:'pointer',
                            borderColor:bkDestStation===pt?'#3b82f6':'#e2e8f0',
                            background:bkDestStation===pt?'#3b82f6':'#fff',
                            color:bkDestStation===pt?'#fff':'#64748b' }}>
                          {pt}
                        </button>
                      ))}
                    </div>
                    <input value={allPoints.includes(bkDestStation)?'':bkDestStation}
                      onChange={e=>setBkDestStation(e.target.value)}
                      placeholder={(lang==='en'||lang==='ja'||lang==='ko')?'Type destination station…':lang==='zh-CN'?'手动输入目的地车站名称…':'手動輸入目的地車站名稱…'}
                      style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', boxSizing:'border-box', color:'#0f172a' }}
                      onFocus={e=>e.target.style.borderColor='#3b82f6'}
                      onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Origin airport — shown under origin city for flight mode */}
          {bkType==='crossBorder' && bkCrossMode==='flight' && bkCity && (() => {
            const cityEn = CITY_EN[bkCity]||CITY_EN[bkCityMap[bkCity]]||bkCity;
            const aps    = CITY_AIRPORTS[cityEn]||[];
            return aps.length > 0 ? (
              <div>
                <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:700, color:'#64748b' }}>🛫 {(lang==='en'||lang==='ja'||lang==='ko')?'Departure Airport':lang==='zh-CN'?'出发机场':'出發機場'}</p>
                <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                  {aps.map(ap=>(
                    <button key={ap.code} onClick={()=>setBkAirport(ap.code)}
                      style={{ padding:'7px 14px', borderRadius:99, border:'1.5px solid', borderColor:bkAirport===ap.code?'#0f172a':'#e2e8f0', background:bkAirport===ap.code?'#0f172a':'#fff', color:bkAirport===ap.code?'#fff':'#64748b', fontSize:11, fontWeight:800, cursor:'pointer' }}>
                      {ap.code} · {ap.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Cross-border train: train type selector — only shows genuinely international trains */}
          {bkType==='crossBorder' && bkCrossMode==='train' && bkCity && (() => {
            const intlTrains = getTrainsForCountry().filter(tr => isIntlTrain(tr.k));
            return (
              <div>
                <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:700, color:'#64748b' }}>🚄 {(lang==='en'||lang==='ja'||lang==='ko')?'International Train':lang==='zh-CN'?'国外火车种类':'國外火車種類'}</p>
                {intlTrains.length > 0 ? (
                  <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                    {intlTrains.map(tr=>(
                      <button key={tr.k} onClick={()=>{ setBkTrainType(tr.k); setBkDestCountry(''); setBkDest(''); setBkDestStation(''); }}
                        style={{ padding:'8px 14px', borderRadius:10, border:'1.5px solid', borderColor:bkTrainType===tr.k?'#0f172a':'#e2e8f0', background:bkTrainType===tr.k?'#0f172a':'#fff', color:bkTrainType===tr.k?'#fff':'#64748b', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                        {tr.l}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:18 }}>✈️</span>
                    <div>
                      <p style={{ margin:0, fontSize:12, fontWeight:800, color:'#92400e' }}>{(lang==='en'||lang==='ja'||lang==='ko')?'No direct international rail from this origin':lang==='zh-CN'?'此出发地无直达国外铁路':'此出發地無直達國外鐵路服務'}</p>
                      <p style={{ margin:'2px 0 0', fontSize:11, color:'#b45309' }}>{(lang==='en'||lang==='ja'||lang==='ko')?'Shinkansen, JR, metro etc. are local transport. Switch to ✈️ Flights or use Local Transport':lang==='zh-CN'?'新干线、JR、地铁等属本地交通，请切换至「✈️ 机票」':'新幹線、JR、地鐵等屬本地交通，請切換至「✈️ 機票」或使用「本地交通」'}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Cross-border: destination country → city → station/airport */}
          {bkType==='crossBorder' && bkCity && (() => {
            // For train mode with specific train type: use TRAIN_ROUTES countries directly (strict filter)
            const trainRoute    = bkCrossMode==='train' && bkTrainType ? TRAIN_ROUTES[bkTrainType] : null;
            const strictCtries  = trainRoute?.countries?.length ? trainRoute.countries : null;
            // Build country options: if train selected → show ONLY allowed countries; else show dynamic list
            const baseCountries = [...new Set([...bkDestCountries, ...bkCountries.filter(c=>c!==bkCountry)])];
            const filteredCtries = strictCtries ? strictCtries : baseCountries;
            // For train mode: filter destination cities by selected train type
            const allowedCities = trainRoute?.cities?.[bkDestCountry] || trainRoute?.cities?.[Object.keys(trainRoute?.cities||{}).find(k=>bkDestCountry.includes(k)||k.includes(bkDestCountry))||''] || null;

            return (
            <div style={{ background:'#f8fafc', borderRadius:14, padding:'14px', border:'1px solid #e2e8f0', display:'flex', flexDirection:'column', gap:12 }}>
              <p style={{ margin:0, fontSize:12, fontWeight:800, color:'#475569' }}>🏁 {(lang==='en'||lang==='ja'||lang==='ko')?'Destination':lang==='zh-CN'?'目的地':'目的地'}</p>

              {/* Destination country */}
              <DD label={(lang==='en'||lang==='ja'||lang==='ko')?'Dest. Country':lang==='zh-CN'?'目的地国家':'目的地國家'} icon={Compass}
                options={filteredCtries.map(c=>({ key:c, label:bkDestCntMap[c]||bkCntMap[c]||c }))}
                value={bkDestCountry} allowCustom
                onChange={v=>{ setBkDestCountry(v); if(!strictCtries) fetchBkDestCities(v, lang); else setBkDestCities2([]); setBkDest(''); setBkDestStation(''); }}
                placeholder={loadingBkDest?t.loading:strictCtries?((lang==='en'||lang==='ja'||lang==='ko')?`Supported: ${strictCtries.slice(0,3).join(', ')}${strictCtries.length>3?'…':''}`:`僅支援：${strictCtries.slice(0,3).join('、')}${strictCtries.length>3?'…':''}`):((lang==='en'||lang==='ja'||lang==='ko')?'Select destination country (or type)':lang==='zh-CN'?'选择目的地国家（可手动输入）':'選擇目的地國家（可手動輸入）')}/>

              {/* Load countries button */}
              {!bkDestCountries.length && !bkDestCountry && bkRegion && (
                <button onClick={()=>fetchBkDestCountries(bkRegion, lang)} disabled={loadingBkDest}
                  style={{ background:'none', border:'1.5px dashed #e2e8f0', borderRadius:10, padding:'8px', fontSize:11, color:'#94a3b8', cursor:'pointer', fontWeight:700 }}>
                  {loadingBkDest?t.loading:((lang==='en'||lang==='ja'||lang==='ko')?'+ Load country list':lang==='zh-CN'?'+ 加载目的地国家列表':'+ 載入目的地國家列表')}
                </button>
              )}

              {/* Destination city — filtered by train type if applicable */}
              {bkDestCountry && (
                <DD label={(lang==='en'||lang==='ja'||lang==='ko')?'Dest. City':lang==='zh-CN'?'目的地城市':'目的地城市'} icon={ArrowRight} allowCustom
                  options={allowedCities
                    ? allowedCities.map(c=>({ key:c, label:c }))
                    : bkDestCities2.map(c=>({ key:c, label:bkDestCityMap2[c]||c }))}
                  value={bkDest}
                  onChange={v=>{ setBkDest(v); setBkDestStation(''); }}
                  placeholder={loadingBkDest?t.loading:allowedCities?((lang==='en'||lang==='ja'||lang==='ko')?`Supported: ${allowedCities.slice(0,3).join(', ')}…`:`支援城市：${allowedCities.slice(0,3).join('、')}…`):((lang==='en'||lang==='ja'||lang==='ko')?'Select destination city (or type)':lang==='zh-CN'?'选择目的地城市（可手动输入）':'選擇目的地城市（可手動輸入）')}/>
              )}

              {/* Destination airport (flight mode) */}
              {bkCrossMode==='flight' && bkDest && (() => {
                const destEn = CITY_EN[bkDest]||CITY_EN[bkDestCityMap2[bkDest]]||bkDest;
                const aps    = CITY_AIRPORTS[destEn]||[];
                return aps.length > 0 ? (
                  <div>
                    <p style={{ margin:'0 0 6px', fontSize:11, fontWeight:700, color:'#1e40af' }}>🛬 {(lang==='en'||lang==='ja'||lang==='ko')?'Destination Airport':lang==='zh-CN'?'目的地机场':'目的地機場'}</p>
                    <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                      {aps.map(ap=>(
                        <span key={ap.code} style={{ padding:'6px 12px', borderRadius:99, background:'#1e40af', color:'#fff', fontSize:11, fontWeight:900 }}>
                          {ap.code} · {ap.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Destination station (train mode) */}
              {bkCrossMode==='train' && bkDest && (() => {
                const destEn  = CITY_EN[bkDest]||CITY_EN[bkDestCityMap2[bkDest]]||bkDest;
                const destSts = CITY_STATIONS[destEn]||[];
                const destAps = (CITY_AIRPORTS[destEn]||[]).map(a=>`${a.code} · ${a.name}`);
                const all     = [...destSts, ...destAps];
                return (
                  <div>
                    <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:700, color:'#64748b' }}>🚉 {(lang==='en'||lang==='ja'||lang==='ko')?'Destination Station':lang==='zh-CN'?'目的地车站':'目的地車站'}</p>
                    {all.length>0 && (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                        {all.map(pt=>(
                          <button key={pt} onClick={()=>setBkDestStation(pt)}
                            style={{ padding:'6px 11px', borderRadius:8, border:'1.5px solid', fontSize:11, fontWeight:700, cursor:'pointer',
                              borderColor:bkDestStation===pt?'#3b82f6':'#e2e8f0',
                              background:bkDestStation===pt?'#3b82f6':'#fff',
                              color:bkDestStation===pt?'#fff':'#64748b' }}>
                            {pt}
                          </button>
                        ))}
                      </div>
                    )}
                    <input value={all.includes(bkDestStation)?'':bkDestStation}
                      onChange={e=>setBkDestStation(e.target.value)}
                      placeholder={(lang==='en'||lang==='ja'||lang==='ko')?'Type destination station…':lang==='zh-CN'?'手动输入目的地车站…':'手動輸入目的地車站…'}
                      style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'8px 12px', fontSize:12, outline:'none', boxSizing:'border-box' }}
                      onFocus={e=>e.target.style.borderColor='#3b82f6'}
                      onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  </div>
                );
              })()}
            </div>
          );
          })()}

          {/* HopeGoo (CJ)：本地交通選了大陸城市時，列出「香港西九龍 → 該城市車站」。選單與搜尋按鈕之間 */}
          {bkType==='localTransport' && bkCity && (() => {
            const trains = hopegooTrains(hopegooCityKey(bkCity, bkCityMap[bkCity]));
            if (!trains.length) return null;
            const en = lang==='en'||lang==='ja'||lang==='ko';
            const sc = lang==='zh-CN';
            const name = bkCityMap[bkCity] || bkCity;
            return (
              <HopeGooPanel
                title={en ? `From Hong Kong West Kowloon to ${name}` : sc ? `从香港西九龙前往${name}` : `從香港西九龍前往${name}`}
                subtitle={en ? 'Pick a station to check timetable & offers' : sc ? '选择车站，查阅时刻表与优惠' : '選擇車站，查閱時刻表與優惠'}
                items={isMobile
                  ? [{ key:'general', label: en ? 'Search trains on HopeGoo' : sc ? '在 HopeGoo 查询高铁' : '在 HopeGoo 查詢高鐵', href: withSid(HOPEGOO_TRAIN_SEARCH, `local-m-${trains[0].slug}`), accent:true }]
                  : trains.map(st => ({ key:st.slug, label: en ? st.en : sc ? st.sc : st.zh, href: withSid(st.url, `local-hk-${st.slug}`) }))}/>
            );
          })()}

          {/* HopeGoo (CJ)：本地交通選了日本顯示 JR Pass、選了首爾 / 仁川顯示 AREX；Klook 連結照舊保留，這裡作比價選項 */}
          {bkType==='localTransport' && (() => {
            const jp = hopegooIsJapan(bkCountry, bkCntMap[bkCountry]);
            const arex = hopegooIsArexCity(bkCity, bkCityMap[bkCity]);
            if (!jp && !arex) return null;
            const en = lang==='en'||lang==='ja'||lang==='ko';
            const sc = lang==='zh-CN';
            const items = jp
              ? [{ key:'jrpass', label:'JR Pass', href: withSid(HOPEGOO_JR_PASS, 'local-jrpass') }]
              : [{ key:'arex', label: en ? 'AREX Incheon Airport Express' : sc ? 'AREX 仁川机场快线' : 'AREX 仁川機場快線', href: withSid(HOPEGOO_AREX, 'local-arex') }];
            return (
              <HopeGooPanel
                title={en ? 'Also on HopeGoo' : sc ? '也可在 HopeGoo 预订' : '也可在 HopeGoo 預訂'}
                subtitle={en ? 'Compare prices before booking' : sc ? '订票前可比较价格（预订页为英文界面）' : '訂票前可比較價格（預訂頁為英文介面）'}
                items={items}/>
            );
          })()}

          {/* HopeGoo (CJ)：港陸跨境巴士，8 條路線（4 條線、雙向） */}
          {bkType==='crossBorder' && bkCrossMode==='bus' && (() => {
            const en = lang==='en'||lang==='ja'||lang==='ko';
            const sc = lang==='zh-CN';
            return (
              <HopeGooPanel
                title={en ? 'Hong Kong ⇄ Mainland cross-border bus' : sc ? '港陆跨境巴士' : '港陸跨境巴士'}
                subtitle={en ? 'Pick a route to check times & fares' : sc ? '选择路线，查阅班次与票价（预订页为英文界面）' : '選擇路線，查閱班次與票價（預訂頁為英文介面）'}
                items={HOPEGOO_BUSES.map(b => ({ key:b.slug, label: en ? b.en : sc ? b.sc : b.zh, href: withSid(b.url, `bus-${b.slug}`) }))}/>
            );
          })()}


          {bkType!=='crossBorder' && bkType!=='accommodation' && (() => {
            const needsCountryOnly = bkType==='dining' || bkType==='esim';
            const missing = needsCountryOnly ? !bkCountry : !bkCity;
            return (
              <button onClick={doBkSearch} disabled={missing||loadingBk}
                style={{ ...S.btnAmber, justifyContent:'center', opacity:missing?0.5:1 }}>
                {loadingBk?<><Loader2 size={16} style={{ animation:'spin 1s linear infinite' }}/>{t.searching}</>:<><Search size={16}/>{t.searchBtn}</>}
              </button>
            );
          })()}
        </div>
      )}

      {/* Results */}
      {/* Official transport cards — shown for local transport when city is selected */}
      {bkType==='localTransport' && bkCity && (() => {
        const cityEn = CITY_EN[bkCity] || CITY_EN[bkCityMap[bkCity]] || bkCity;
        const officials = OFFICIAL_TRANSPORT[cityEn] || [];
        if (!officials.length) return null;
        return (
          <div style={{ marginTop:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <div style={{ width:4, height:18, background:'#0f172a', borderRadius:2 }}/>
              <p style={{ fontSize:14, fontWeight:900, color:'#0f172a', margin:0 }}>🏛️ 官方訂票 / 交通卡</p>
              <span style={{ fontSize:10, background:'#e2e8f0', color:'#64748b', borderRadius:99, padding:'2px 8px', fontWeight:700 }}>官方網站</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {officials.map((o,i) => (
                <a key={i} href={o.url} target="_blank" rel="noopener noreferrer"
                  style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:12, background:'white', borderRadius:14, border:`1.5px solid ${o.color}30`, padding:'12px 14px', transition:'all 0.15s' }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor=o.color; e.currentTarget.style.boxShadow=`0 4px 14px ${o.color}20`; e.currentTarget.style.transform='translateY(-1px)'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor=`${o.color}30`; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none'; }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:`${o.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>{o.icon}</div>
                  <div style={{ flex:1 }}>
                    <p style={{ margin:'0 0 2px', fontSize:13, fontWeight:900, color:'#0f172a' }}>{o.name}</p>
                    <p style={{ margin:0, fontSize:11, color:'#64748b' }}>{o.desc}</p>
                  </div>
                  <div style={{ background:o.color, color:'#fff', borderRadius:8, padding:'6px 12px', fontSize:11, fontWeight:900, flexShrink:0, display:'flex', alignItems:'center', gap:4 }}>
                    官網 <ExternalLink size={10}/>
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      })()}

      {bkResults&&(
        <div style={{ marginTop:16 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <p style={{ fontSize:16, fontWeight:900, color:'#0f172a', margin:0 }}>{t.searchResults}</p>
            <span style={{ fontSize:11, color:'#94a3b8' }}>{bkResults.length} 個結果</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {bkResults.map((r,i)=>{
              const isPlatformCard = r.platform==='Klook'||r.platform==='KKday'||r.platform==='Peatix'||r.platform==='HKTicketing'||r.platform==='Cityline'||r.platform==='HKFA'||r.platform==='HK Sevens'||r.platform==='HKJC'||r.platform==='Timable';
              const platformColor  = r.platform==='Klook'?'#ff6600':r.platform==='KKday'?'#00b894':r.platform==='Ticketmaster'?'#026cdf':r.platform==='Eventbrite'?'#f05537':r.platform==='Peatix'?'#e91e63':r.platform==='HKTicketing'?'#c0392b':r.platform==='URBTIX 城市售票'?'#2980b9':r.platform==='Cityline'?'#8e44ad':r.platform==='HKFA'?'#27ae60':r.platform==='HK Sevens'?'#e74c3c':r.platform==='HKJC'?'#2c3e50':r.platform==='Viator'?'#00aa6c':r.platform==='Timable'?'#e05a00':r.platform==='Trip.com'?'#0239c5':r.noLink?'#64748b':'#f59e0b';
              const CardTag = r.noLink ? 'div' : 'a';
              const cardProps = r.noLink ? {} : { href:safeHref(r.link), target:'_blank', rel:'noopener noreferrer' };
              return (
                <CardTag key={i} {...cardProps}
                  style={{ textDecoration:'none', display:'block', borderRadius:14, border:`1.5px solid ${isPlatformCard?platformColor+'40':'#e2e8f0'}`, background: isPlatformCard?`${platformColor}08`:'#fff', overflow:'hidden', transition:'all 0.15s', cursor:r.noLink?'default':'pointer' }}
                  onMouseEnter={e=>{ if(r.noLink) return; e.currentTarget.style.borderColor=platformColor; e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow=`0 4px 16px ${platformColor}20`; }}
                  onMouseLeave={e=>{ if(r.noLink) return; e.currentTarget.style.borderColor=isPlatformCard?platformColor+'40':'#e2e8f0'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
                  {/* Platform badge */}
                  {r.platform&&(
                    <div style={{ background:platformColor, padding:'4px 12px', display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ color:'#fff', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:1 }}>{r.platform}</span>
                      {isPlatformCard&&<span style={{ color:'rgba(255,255,255,0.8)', fontSize:10 }}>即時票價</span>}
                      {r.platform==='Viator'&&<span style={{ color:'rgba(255,255,255,0.8)', fontSize:10 }}>真實數據</span>}
                    </div>
                  )}
                  {/* Viator product image */}
                  {r.img&&(
                    <div style={{ height:140, overflow:'hidden', background:'#f1f5f9' }}>
                      <img src={r.img} alt={r.title} loading="lazy" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                        onError={e=>e.target.style.display='none'}/>
                    </div>
                  )}
                  <div style={{ padding:'12px 14px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:13, fontWeight:900, color:'#0f172a', margin:'0 0 3px', lineHeight:1.3 }}>{r.title}</p>
                      {r.venue&&r.venue!==r.title&&<p style={{ fontSize:11, color:'#94a3b8', margin:'0 0 3px', fontWeight:600 }}>📍 {r.venue}</p>}
                      {r.desc&&<p style={{ fontSize:12, color:'#64748b', margin:'0 0 5px', lineHeight:1.5 }}>{r.desc}</p>}
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
                        {r.highlight&&<span style={{ fontSize:11, color: r.highlight.startsWith('📅')?'#3b82f6':'#f59e0b', fontWeight:700 }}>{r.highlight}</span>}
                        {r.duration&&<span style={{ fontSize:11, color:'#94a3b8' }}>⏱ {r.duration}</span>}
                        {r.operator&&<span style={{ fontSize:11, color:'#94a3b8' }}>{r.operator}</span>}
                        {r.coupon&&<span style={{ fontSize:11, fontWeight:800, color:'#92400e', background:'#fff7e6', border:'1px dashed #f59e0b', borderRadius:6, padding:'1px 7px' }}>🎟️ {r.coupon}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                      {r.price&&<p style={{ fontSize:14, fontWeight:900, color:isPlatformCard?platformColor:'#f59e0b', margin:0 }}>{r.price}{r.currency?' '+r.currency:''}</p>}
                      {r.stars&&<div style={{ display:'flex',gap:2 }}>{Array(r.stars||4).fill(0).map((_,j)=><Star key={j} size={11} color="#f59e0b" fill="#f59e0b"/>)}</div>}
                      {!r.noLink&&(
                        <span style={{ background:platformColor, color:'#fff', padding:'6px 12px', borderRadius:8, fontSize:11, fontWeight:900, display:'inline-flex', alignItems:'center', gap:4, whiteSpace:'nowrap' }}>
                          {isPlatformCard?'前往搜尋':t.bookLinks} <ExternalLink size={10}/>
                        </span>
                      )}
                    </div>
                  </div>
                </CardTag>
              );
            })}
          </div>

        </div>
      )}
      {/* 優惠碼：收合成一行放在搜尋結果之後。有搜尋鍵的類型搜尋完畢才顯示；
          住宿及跨境交通沒有搜尋鍵，選好城市（巴士模式不用選城市）就顯示 */}
      {((bkResults && !loadingBk) || (bkType==='accommodation' && bkCity) || (bkType==='crossBorder' && (bkCity || bkCrossMode==='bus'))) && (
        <RelevantCoupons city={bkCity||bkDest||''} country={bkCountry||''} lang={lang} collapsible/>
      )}
      </div>{/* end main content column */}
    </div>
  );

  // PROFILE — sub-section state
  const [profileTab, setProfileTab] = useState('main'); // 'main'|'account'|'favorites'|'trips'|'security'
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [pwNew, setPwNew]     = useState('');
  const [pwConf, setPwConf]   = useState('');
  const [pwMsg, setPwMsg]     = useState(null); // {ok,text}
  const [pwLoading, setPwLoading] = useState(false);
  const [residencyEdit, setResidencyEdit] = useState(false);
  const [residencyVal, setResidencyVal]   = useState('');
  const [residencySaving, setResidencySaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ec_dark')==='1');
  const [savedTrips, setSavedTrips] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ec_saved_trips')||'[]'); } catch { return []; }
  });

  const MAX_SAVED_TRIPS = 5;

  // Load saved trips from Supabase when user logs in — merges with localStorage
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('saved_trips').select('*').eq('user_id', user.id)
      .order('created_at', { ascending: false }).limit(MAX_SAVED_TRIPS)
      .then(({ data }) => {
        if (!data?.length) return;
        const remote = data.map(r => ({ id: r.trip_id, cities: r.cities, country: r.country, days: r.days, total: r.total, saved: r.saved, itinerary: r.itinerary }));
        const local = (() => { try { return JSON.parse(localStorage.getItem('ec_saved_trips')||'[]'); } catch { return []; } })();
        const remoteIds = new Set(remote.map(t => t.id));
        const merged = [...remote, ...local.filter(t => !remoteIds.has(t.id))].slice(0, MAX_SAVED_TRIPS);
        setSavedTrips(merged);
        localStorage.setItem('ec_saved_trips', JSON.stringify(merged));
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 訪客按「儲存行程」→ Google 登入成功後，自動把試用生成的行程存入帳戶
  useEffect(() => {
    if (!user?.id || !pendingSaveAfterLoginRef.current) return;
    pendingSaveAfterLoginRef.current = false;
    if (!itinerary?.days?.length) return;
    saveCurrentTrip();
    showTripToast(t.tripSaved||'✅ 行程已儲存！可在「我的 → 儲存行程」查看');
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCurrentTrip = async () => {
    const activeCities = trDestinations.filter(d => d.city).map(d => d.city);
    if (!itinerary || !activeCities.length) return;
    if (savedTrips.length >= MAX_SAVED_TRIPS) return;
    const trip = { id: Date.now(), cities: activeCities, country: trCountry, days: trDays, total: itinerary.estimatedTotal, saved: new Date().toLocaleDateString('zh-HK'), itinerary };
    const updated = [trip, ...savedTrips].slice(0, MAX_SAVED_TRIPS);
    setSavedTrips(updated);
    localStorage.setItem('ec_saved_trips', JSON.stringify(updated));
    if (user?.id) {
      await supabase.from('saved_trips').insert({
        user_id: user.id, trip_id: trip.id, cities: trip.cities, country: trip.country,
        days: trip.days, total: trip.total, saved: trip.saved, itinerary: trip.itinerary,
      });
    }
  };

  const deleteSavedTrip = async (id) => {
    const updated = savedTrips.filter(t => t.id !== id);
    setSavedTrips(updated);
    localStorage.setItem('ec_saved_trips', JSON.stringify(updated));
    if (user?.id) {
      await supabase.from('saved_trips').delete().eq('user_id', user.id).eq('trip_id', id);
    }
  };

  const changePassword = async () => {
    setPwMsg(null);
    if (!validatePw(pwNew)) { setPwMsg({ ok:false, text:'密碼須最少8字元，包含大小寫字母及數字' }); return; }
    if (pwNew !== pwConf) { setPwMsg({ ok:false, text:'兩次輸入密碼不一致' }); return; }
    setPwLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pwNew });
    setPwLoading(false);
    if (error) setPwMsg({ ok:false, text:error.message });
    else { setPwMsg({ ok:true, text:'密碼已成功更新' }); setPwNew(''); setPwConf(''); }
  };

  const saveResidency = async () => {
    if (!residencyVal) return;
    setResidencySaving(true);
    const { data, error } = await supabase.auth.updateUser({ data: { residency_country: residencyVal } });
    setResidencySaving(false);
    if (!error) { setUser(data.user); setResidencyEdit(false); }
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== user?.email) { setDeleteMsg({ ok:false, text:'電郵地址不正確，請重新輸入' }); return; }
    setDeleteLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
      const r = await fetch(apiBase + '/api/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      const result = await r.json();
      if (!r.ok) { setDeleteMsg({ ok:false, text: result.error || '刪除失敗，請稍後再試' }); setDeleteLoading(false); return; }
    } catch(e) {
      setDeleteMsg({ ok:false, text:'網絡錯誤，請稍後再試' }); setDeleteLoading(false); return;
    }
    // Clear all local data after successful deletion
    ['ec_saved_trips','ec_cookie_consent','ec_dark','ec_likes','ec_liked','ec_trip_usage']
      .forEach(k => localStorage.removeItem(k));
    Object.keys(localStorage).filter(k=>k.startsWith('ec_ai_')).forEach(k=>localStorage.removeItem(k));
    await supabase.auth.signOut();
    setDeleteLoading(false);
    setProfileTab('main');
  };

  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('ec_dark', next ? '1' : '0');
  };

  const renderProfile = () => {
    const PL = {
      L: (zh, zhCN, en, ja, ko) => lang==='en'?en:lang==='ja'?(ja||en):lang==='ko'?(ko||en):lang==='zh-CN'?zhCN:zh,
    };
    const L = PL.L;
    const PL2 = {
      back:          L('返回','返回','Back','戻る','돌아가기'),
      myAcc:         L('我的帳戶','我的账户','My Account','マイアカウント','내 계정'),
      favs:          L('收藏景點','收藏景点','Saved Attractions','保存スポット','저장된 명소'),
      trips:         L('儲存行程','储存行程','Saved Trips','保存済み旅程','저장된 여행'),
      security:      L('安全設定','安全设置','Security','セキュリティ','보안 설정'),
      darkMode:      L('深色','深色','Dark','ダーク','다크'),
      lightMode:     L('淺色','浅色','Light','ライト','라이트'),
      favStat:       L('收藏景點','收藏景点','Attractions','スポット','명소'),
      tripStat:      L('儲存行程','储存行程','Saved Trips','保存旅程','저장 여행'),
      maxStat:       L('最多行程','最多行程','Max Trips','最大旅程','최대 여행'),
      noFavs:        L('尚未收藏任何景點','尚未收藏任何景点','No saved attractions yet','まだ保存したスポットがありません','저장된 명소가 없습니다'),
      noFavsHint:    L('在搜尋頁面點擊 ❤️ 按鈕即可收藏','在搜索页面点击 ❤️ 按钮即可收藏','Tap ❤️ on any attraction in Search to save','検索でスポットの ❤️ をタップして保存','검색에서 ❤️를 눌러 저장하세요'),
      viewDetails:   L('點擊查看詳情','点击查看详情','Tap to view details','タップして詳細を見る','눌러서 상세 보기'),
      remove:        L('移除','移除','Remove','削除','삭제'),
      noTrips:       L('尚未儲存任何行程','尚未储存任何行程','No saved itineraries yet','保存した旅程がありません','저장된 여행이 없습니다'),
      noTripsHint:   L('在「行程」頁面生成行程後即可儲存','在「行程」页面生成行程后即可储存','Generate an itinerary in the Trips tab to save','旅程タブで旅程を生成して保存してください','여행 탭에서 일정을 생성하고 저장하세요'),
      goTrips:       L('前往規劃行程 →','前往规划行程 →','Plan a Trip →','旅程を計画する →','여행 계획하기 →'),
      daysSuffix:    L('天','天',' days','日','일'),
      savedOn:       L('儲存於','储存于','Saved','保存','저장'),
      maxTripsWarn:  L(`⚠️ 已達上限（${MAX_SAVED_TRIPS}個），請先刪除舊行程`,`⚠️ 已达上限（${MAX_SAVED_TRIPS}个），请先删除旧行程`,`⚠️ Max ${MAX_SAVED_TRIPS} trips reached. Delete an old trip first`,`⚠️ 最大${MAX_SAVED_TRIPS}件に達しました。古い旅程を削除してください`,`⚠️ 최대 ${MAX_SAVED_TRIPS}개 도달. 오래된 여행을 삭제해주세요`),
      changePw:      L('更改密碼','更改密码','Change Password','パスワード変更','비밀번호 변경'),
      newPw:         L('新密碼','新密码','New Password','新しいパスワード','새 비밀번호'),
      confirmPw:     L('確認新密碼','确认新密码','Confirm New Password','パスワードを確認','새 비밀번호 확인'),
      pwHint:        L('最少8字元，含大小寫及數字','最少8字符，含大小写及数字','Min 8 chars, upper/lower & number','8文字以上、大小英字と数字を含む','8자 이상, 대소문자 및 숫자 포함'),
      pwConfirmHint: L('再次輸入新密碼','再次输入新密码','Re-enter new password','パスワードを再入力','새 비밀번호 재입력'),
      updatePwBtn:   L('確認更改密碼','确认更改密码','Update Password','パスワードを更新','비밀번호 업데이트'),
      updatingPw:    L('更新中…','更新中…','Updating…','更新中…','업데이트 중…'),
      pwThirdParty:  (p) => L(`您正使用第三方登入（${p}），無需設定密碼`,`您正使用第三方登录（${p}），无需设置密码`,`You are signed in with ${p} — no password needed`,`${p}でログイン中のため、パスワードは不要です`,`${p}로 로그인 중, 비밀번호 불필요`),
      deleteTitle:   L('刪除帳戶','删除账户','Delete Account','アカウント削除','계정 삭제'),
      deleteDesc:    L('刪除後所有資料（收藏、行程）將永久清除，此操作不可撤銷。請輸入您的電郵地址以確認：','删除后所有资料（收藏、行程）将永久清除，此操作不可撤销。请输入您的邮箱地址以确认：','All data (favourites, trips) will be permanently deleted and cannot be undone. Enter your email to confirm:','全データ（お気に入り・旅程）が完全に削除され、取り消せません。メールアドレスを入力して確認してください：','모든 데이터(즐겨찾기, 여행)가 영구 삭제되며 되돌릴 수 없습니다. 이메일을 입력하여 확인하세요:'),
      deleteBtn:     L('確認刪除我的帳戶','确认删除我的账户','Confirm Delete Account','アカウントを削除する','계정 삭제 확인'),
      deleting:      L('處理中…','处理中…','Processing…','処理中…','처리 중…'),
      accTitle:      L('我的帳戶','我的账户','My Account','マイアカウント','내 계정'),
      accName:       L('顯示名稱','显示名称','Display Name','表示名','표시 이름'),
      accEmail:      L('電郵地址','邮箱地址','Email','メールアドレス','이메일'),
      accProvider:   L('登入方式','登录方式','Sign-in Method','ログイン方法','로그인 방법'),
      accStatus:     L('帳戶狀態','账户状态','Account Status','アカウントの状態','계정 상태'),
      accJoined:     L('登記日期','注册日期','Joined','登録日','가입일'),
      accId:         L('用戶 ID','用户 ID','User ID','ユーザーID','사용자 ID'),
      accResidency:  L('居住地','居住地','Residency','居住国','거주 국가'),
      accResidencyHint: L('用於顯示您可使用的優惠碼','用于显示您可使用的优惠码','Used to show promo codes valid for you','ご利用可能なプロモコードの表示に使用します','사용 가능한 프로모 코드 표시에 사용됩니다'),
      accEdit:       L('編輯','编辑','Edit','編集','편집'),
      accSave:       L('儲存','储存','Save','保存','저장'),
      accCancel:     L('取消','取消','Cancel','キャンセル','취소'),
      accNotSet:     L('尚未設定','尚未设置','Not set','未設定','미설정'),
      verified:      L('✅ 已驗證','✅ 已验证','✅ Verified','✅ 確認済み','✅ 인증됨'),
      pending:       L('⏳ 待驗證','⏳ 待验证','⏳ Pending','⏳ 確認待ち','⏳ 대기 중'),
      googleLogin:   L('🔵 Google 帳戶','🔵 Google 账户','🔵 Google Account','🔵 Google アカウント','🔵 Google 계정'),
      fbLogin:       L('🔵 Facebook 帳戶','🔵 Facebook 账户','🔵 Facebook Account','🔵 Facebook アカウント','🔵 Facebook 계정'),
      emailLogin:    L('📧 電郵 / 密碼','📧 邮箱 / 密码','📧 Email / Password','📧 メール / パスワード','📧 이메일 / 비밀번호'),
      newsletter:    L('旅遊情報電郵通知','旅游资讯邮件通知','Travel Newsletter','旅行情報メール','여행 뉴스레터'),
      newsletterOn:  L('✅ 已訂閱 — 新文章發佈時自動通知','✅ 已订阅 — 新文章发布时自动通知','✅ Subscribed — notified on new articles','✅ 購読中 — 新記事が公開されると通知','✅ 구독 중 — 새 기사 알림'),
      newsletterOff: L('訂閱後，後台發佈新旅遊情報時自動收到電郵','订阅后，后台发布新旅游资讯时自动收到邮件','Subscribe to receive travel updates by email','購読すると旅行情報を受け取れます','구독하면 여행 정보를 받아보실 수 있습니다'),
      subBtn:        L('訂閱','订阅','Subscribe','購読','구독'),
      unsubBtn:      L('取消訂閱','取消订阅','Unsubscribe','購読解除','구독 취소'),
      loginTitle:    L('登入後解鎖更多功能','登录后解锁更多功能','Login to unlock more features','ログインしてさらに機能を解放','로그인 후 더 많은 기능 이용'),
      loginSub:      L('收藏景點 · 儲存行程 · 個人化設定','收藏景点 · 储存行程 · 个性化设置','Save Attractions · Itineraries · Personalise','スポット保存 · 旅程保存 · 個人設定','명소 저장 · 일정 저장 · 개인화'),
    };
    const p = PL2;

    if (!isLoggedIn) return (
      <div style={{ maxWidth:420, margin:'0 auto', padding:'14px 14px 96px' }}>
        <div style={{ background:'white', borderRadius:20, border:'1.5px solid #e2e8f0', overflow:'hidden', marginBottom:16 }}>
          <div style={{ background:'linear-gradient(135deg,#f59e0b,#ef4444)', padding:'24px 20px', textAlign:'center' }}>
            <User size={40} color="white" style={{ marginBottom:10 }}/>
            <p style={{ margin:0, fontSize:18, fontWeight:900, color:'white' }}>{p.loginTitle}</p>
            <p style={{ margin:'6px 0 0', fontSize:12, color:'rgba(255,255,255,0.8)' }}>{p.loginSub}</p>
          </div>
          <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>
            {[
              ['❤️', L('收藏景點','Save Attractions','スポット保存','명소 저장'), L('登入後即可收藏喜愛景點','Save your favourite attractions after login','ログイン後にお気に入りスポットを保存','로그인 후 좋아하는 명소 저장')],
              ['🗺', L('儲存行程','Save Itineraries','旅程を保存','일정 저장'), L('最多儲存5個行程計劃','Save up to 5 trip plans','最大5件の旅程を保存可能','최대 5개 일정 저장 가능')],
              ['🔒', L('安全設定','Security Settings','セキュリティ設定','보안 설정'), L('管理密碼及帳戶','Manage your password and account','パスワードとアカウントを管理','비밀번호 및 계정 관리')],
            ].map(([icon, label, desc], i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:i<2?'1px solid #f8fafc':'none' }}>
                <span style={{ fontSize:20 }}>{icon}</span>
                <div><p style={{ margin:0, fontSize:12, fontWeight:800, color:'#0f172a' }}>{label}</p><p style={{ margin:0, fontSize:11, color:'#94a3b8' }}>{desc}</p></div>
              </div>
            ))}
          </div>
        </div>
        <AuthModal show inline t={t} lang={lang} onClose={()=>{}} onLogin={()=>setTab('home')}/>
      </div>
    );

    const IS = { width:'100%', background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'11px 14px', fontSize:13, outline:'none', color:'#1e293b', boxSizing:'border-box' };
    const displayName = user?.user_metadata?.name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || '—';
    const joinDate = user?.created_at ? new Date(user.created_at).toLocaleDateString(lang==='en'?'en-GB':lang==='ja'?'ja-JP':lang==='ko'?'ko-KR':'zh-HK', { year:'numeric', month:'long', day:'numeric' }) : '—';

    // ── Sub-section: Account Info ──
    if (profileTab === 'account') return (
      <div style={{ maxWidth:540, margin:'0 auto', padding:'14px 14px 96px' }}>
        <button onClick={()=>setProfileTab('main')} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', color:'#64748b', fontWeight:700, fontSize:12, cursor:'pointer', marginBottom:16, padding:'4px 0' }}>
          <ChevronLeft size={14}/> {p.back}
        </button>
        <div style={{ ...S.card, display:'flex', flexDirection:'column', gap:0 }}>
          <p style={{ fontSize:15, fontWeight:900, color:'#0f172a', margin:'0 0 16px', display:'flex', alignItems:'center', gap:8 }}><User size={16} color="#f59e0b"/> {p.accTitle}</p>
          {[
            { label: p.accName, value: displayName },
            { label: p.accEmail, value: user?.email || '—' },
            { label: p.accProvider, value: user?.app_metadata?.provider === 'google' ? p.googleLogin : user?.app_metadata?.provider === 'facebook' ? p.fbLogin : p.emailLogin },
            { label: p.accStatus, value: user?.email_confirmed_at ? p.verified : p.pending },
            { label: p.accJoined, value: joinDate },
            { label: p.accId, value: user?.id?.slice(0, 18)+'…' || '—' },
          ].map((row, i, arr) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid #f8fafc' }}>
              <span style={{ fontSize:12, color:'#94a3b8', fontWeight:700 }}>{row.label}</span>
              <span style={{ fontSize:12, fontWeight:800, color:'#0f172a', textAlign:'right', maxWidth:'60%', wordBreak:'break-all' }}>{row.value}</span>
            </div>
          ))}
          <div style={{ padding:'12px 0' }}>
            {residencyEdit ? (
              <div>
                <span style={{ fontSize:12, color:'#94a3b8', fontWeight:700, display:'block', marginBottom:6 }}>{p.accResidency}</span>
                <div style={{ display:'flex', gap:8 }}>
                  <select value={residencyVal} onChange={e=>setResidencyVal(e.target.value)} style={{ ...IS, flex:1 }}>
                    <option value="">—</option>
                    {RESIDENCY_CODES.map(code => <option key={code} value={code}>{residencyLabel(code, lang)}</option>)}
                  </select>
                  <button onClick={saveResidency} disabled={!residencyVal||residencySaving} style={{ background:'#0f172a', color:'white', border:'none', borderRadius:10, padding:'0 16px', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                    {residencySaving ? '…' : p.accSave}
                  </button>
                  <button onClick={()=>setResidencyEdit(false)} style={{ background:'#f1f5f9', color:'#64748b', border:'none', borderRadius:10, padding:'0 14px', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                    {p.accCancel}
                  </button>
                </div>
                <p style={{ fontSize:10, color:'#94a3b8', margin:'6px 0 0' }}>{p.accResidencyHint}</p>
              </div>
            ) : (
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:12, color:'#94a3b8', fontWeight:700 }}>{p.accResidency}</span>
                <button onClick={()=>{setResidencyVal(user?.user_metadata?.residency_country||'');setResidencyEdit(true);}}
                  style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', fontSize:12, fontWeight:800, color:'#0f172a' }}>
                  {user?.user_metadata?.residency_country ? residencyLabel(user.user_metadata.residency_country, lang) : <span style={{color:'#f59e0b'}}>{p.accNotSet}</span>}
                  <span style={{ color:'#94a3b8', fontWeight:700 }}>· {p.accEdit}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );

    // ── Sub-section: Favorites ──
    if (profileTab === 'favorites') return (
      <div style={{ maxWidth:540, margin:'0 auto', padding:'14px 14px 96px' }}>
        <button onClick={()=>setProfileTab('main')} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', color:'#64748b', fontWeight:700, fontSize:12, cursor:'pointer', marginBottom:16, padding:'4px 0' }}>
          <ChevronLeft size={14}/> {p.back}
        </button>
        <div style={{ ...S.card }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <p style={{ fontSize:15, fontWeight:900, color:'#0f172a', margin:0, display:'flex', alignItems:'center', gap:8 }}><Heart size={16} color="#ef4444"/> {p.favs}</p>
            <span style={{ background:'#fef2f2', color:'#ef4444', fontSize:11, fontWeight:900, padding:'3px 9px', borderRadius:99 }}>{favorites.length} / {MAX_FAVORITES}</span>
          </div>
          {favorites.length === 0 ? (
            <div style={{ textAlign:'center', padding:'28px 0' }}>
              <Heart size={36} color="#e2e8f0" style={{ marginBottom:10 }}/>
              <p style={{ fontSize:13, color:'#94a3b8', margin:0, fontWeight:700 }}>{p.noFavs}</p>
              <p style={{ fontSize:11, color:'#cbd5e1', margin:'6px 0 0' }}>{p.noFavsHint}</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
              {favorites.map((name, i) => {
                const cityKey = favCities[name] || '';
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 0', borderBottom: i<favorites.length-1 ? '1px solid #f8fafc' : 'none' }}>
                    <MapPin size={14} color="#f59e0b" style={{ flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={()=>{
                      const a = { name, category:'culture', photoKw:name };
                      setSelAttraction({ ...a, cityKey });
                      fetchAttractionDetail(a, cityKey);
                    }}>
                      <p style={{ fontSize:13, fontWeight:700, color:'#0f172a', margin:'0 0 1px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</p>
                      {cityKey && <p style={{ fontSize:10, color:'#94a3b8', margin:0, fontWeight:600 }}>{cityKey} · <span style={{ color:'#f59e0b' }}>{p.viewDetails}</span></p>}
                    </div>
                    <button onClick={()=>toggleFav(name)} style={{ background:'#fef2f2', border:'none', borderRadius:8, padding:'5px 10px', cursor:'pointer', color:'#ef4444', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                      <X size={11}/> {p.remove}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );

    // ── Sub-section: Saved Trips ──
    if (profileTab === 'trips') return (
      <div style={{ maxWidth:540, margin:'0 auto', padding:'14px 14px 96px' }}>
        <button onClick={()=>setProfileTab('main')} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', color:'#64748b', fontWeight:700, fontSize:12, cursor:'pointer', marginBottom:16, padding:'4px 0' }}>
          <ChevronLeft size={14}/> {p.back}
        </button>
        <div style={{ ...S.card }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <p style={{ fontSize:15, fontWeight:900, color:'#0f172a', margin:0, display:'flex', alignItems:'center', gap:8 }}>🗺 {p.trips}</p>
            <span style={{ background: savedTrips.length >= MAX_SAVED_TRIPS ? '#fef2f2':'#f0fdf4', color: savedTrips.length >= MAX_SAVED_TRIPS ? '#ef4444':'#16a34a', fontSize:11, fontWeight:900, padding:'3px 9px', borderRadius:99 }}>
              {savedTrips.length} / {MAX_SAVED_TRIPS}
            </span>
          </div>
          {savedTrips.length === 0 ? (
            <div style={{ textAlign:'center', padding:'28px 0' }}>
              <span style={{ fontSize:40 }}>🗺</span>
              <p style={{ fontSize:13, color:'#94a3b8', margin:'10px 0 0', fontWeight:700 }}>{p.noTrips}</p>
              <p style={{ fontSize:11, color:'#cbd5e1', margin:'6px 0 16px' }}>{p.noTripsHint}</p>
              <button onClick={()=>setTab('trips')} style={{ background:'#f59e0b', color:'#0f172a', border:'none', borderRadius:10, padding:'10px 20px', fontWeight:900, fontSize:12, cursor:'pointer' }}>
                {p.goTrips}
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {savedTrips.map((trip, i) => {
                const [c1,c2,acc] = CC[trip.cities?.[0]] || CC.default;
                return (
                  <div key={trip.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px', background:'#f8fafc', borderRadius:12, border:'1.5px solid #e2e8f0' }}>
                    <svg width="50" height="34" viewBox="0 0 50 34" style={{ borderRadius:8, flexShrink:0 }}>
                      <defs><linearGradient id={`sg${i}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={c1}/><stop offset="100%" stopColor={c2}/></linearGradient></defs>
                      <rect width="50" height="34" fill={`url(#sg${i})`} rx="8"/>
                      <text x="25" y="21" textAnchor="middle" fill={acc} fontSize="9" fontWeight="bold">{(trip.cities?.[0]||'').slice(0,3).toUpperCase()}</text>
                    </svg>
                    <div style={{ flex:1 }}>
                      <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#0f172a' }}>{trip.cities?.join(' · ')||'—'}</p>
                      <p style={{ margin:'2px 0 0', fontSize:11, color:'#94a3b8' }}>{trip.country} · {trip.days}{p.daysSuffix} · {p.savedOn} {trip.saved}</p>
                    </div>
                    {trip.itinerary && (
                      <button onClick={()=>downloadPDF(trip)} title={t.downloadPDF||'PDF'} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4 }} onMouseEnter={e=>e.currentTarget.style.color='#f59e0b'} onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                        <Download size={14}/>
                      </button>
                    )}
                    <button aria-label={p.remove} onClick={()=>deleteSavedTrip(trip.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4 }} onMouseEnter={e=>e.currentTarget.style.color='#ef4444'} onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                      <X size={14}/>
                    </button>
                  </div>
                );
              })}
              {savedTrips.length >= MAX_SAVED_TRIPS && (
                <p style={{ margin:0, fontSize:11, color:'#f59e0b', fontWeight:700, textAlign:'center' }}>{p.maxTripsWarn}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );

    // ── Sub-section: Security ──
    if (profileTab === 'security') return (
      <div style={{ maxWidth:540, margin:'0 auto', padding:'14px 14px 96px', display:'flex', flexDirection:'column', gap:14 }}>
        <button onClick={()=>{ setProfileTab('main'); setPwMsg(null); setDeleteMsg(null); setDeleteConfirm(''); }} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', color:'#64748b', fontWeight:700, fontSize:12, cursor:'pointer', padding:'4px 0' }}>
          <ChevronLeft size={14}/> {p.back}
        </button>

        {/* Change password */}
        <div style={{ ...S.card }}>
          <p style={{ fontSize:15, fontWeight:900, color:'#0f172a', margin:'0 0 16px', display:'flex', alignItems:'center', gap:8 }}><ShieldCheck size={16} color="#16a34a"/> {p.changePw}</p>
          {user?.app_metadata?.provider && user.app_metadata.provider !== 'email' ? (
            <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:10, padding:'12px 14px' }}>
              <p style={{ margin:0, fontSize:12, color:'#0369a1', fontWeight:700 }}>{p.pwThirdParty(user.app_metadata.provider)}</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                <p style={{ fontSize:11, fontWeight:700, color:'#64748b', margin:'0 0 6px' }}>{p.newPw}</p>
                <input type="password" value={pwNew} onChange={e=>setPwNew(e.target.value)} placeholder={p.pwHint} style={IS}/>
              </div>
              <div>
                <p style={{ fontSize:11, fontWeight:700, color:'#64748b', margin:'0 0 6px' }}>{p.confirmPw}</p>
                <input type="password" value={pwConf} onChange={e=>setPwConf(e.target.value)} placeholder={p.pwConfirmHint} style={IS}/>
              </div>
              {pwMsg && (
                <div style={{ background: pwMsg.ok ? '#f0fdf4':'#fef2f2', border:`1px solid ${pwMsg.ok?'#bbf7d0':'#fecaca'}`, borderRadius:8, padding:'8px 12px' }}>
                  <p style={{ margin:0, fontSize:12, fontWeight:700, color: pwMsg.ok?'#16a34a':'#dc2626' }}>{pwMsg.ok?'✅':'❌'} {pwMsg.text}</p>
                </div>
              )}
              <button onClick={changePassword} disabled={pwLoading}
                style={{ background:'#0f172a', color:'white', border:'none', borderRadius:10, padding:'12px', fontWeight:900, fontSize:13, cursor:'pointer', opacity:pwLoading?0.6:1 }}>
                {pwLoading ? p.updatingPw : p.updatePwBtn}
              </button>
            </div>
          )}
        </div>

        {/* Delete account */}
        <div style={{ ...S.card, border:'1.5px solid #fecaca' }}>
          <p style={{ fontSize:15, fontWeight:900, color:'#dc2626', margin:'0 0 8px', display:'flex', alignItems:'center', gap:8 }}>⚠️ {p.deleteTitle}</p>
          <p style={{ fontSize:12, color:'#64748b', margin:'0 0 14px', lineHeight:1.7 }}>{p.deleteDesc}</p>
          <input type="email" value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)}
            placeholder={user?.email || 'your@email.com'} style={{ ...IS, borderColor:'#fecaca' }}/>
          {deleteMsg && (
            <div style={{ marginTop:10, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'8px 12px' }}>
              <p style={{ margin:0, fontSize:12, fontWeight:700, color:'#dc2626' }}>{deleteMsg.text}</p>
            </div>
          )}
          <button onClick={deleteAccount} disabled={deleteLoading || deleteConfirm !== user?.email}
            style={{ marginTop:12, width:'100%', background: deleteConfirm===user?.email ? '#dc2626':'#f1f5f9', color: deleteConfirm===user?.email ? 'white':'#94a3b8', border:'none', borderRadius:10, padding:'12px', fontWeight:900, fontSize:13, cursor: deleteConfirm===user?.email ? 'pointer':'not-allowed', transition:'all 0.2s' }}>
            {deleteLoading ? p.deleting : p.deleteBtn}
          </button>
        </div>
      </div>
    );

    // ── Main profile page ──
    return (
      <div style={{ maxWidth:540, margin:'0 auto', padding:'14px 14px 96px', display:'flex', flexDirection:'column', gap:12 }}>
        {/* Header card */}
        <div style={{ background:'white', borderRadius:20, border:'1.5px solid #e2e8f0', overflow:'hidden' }}>
          <div style={{ height:80, background:'linear-gradient(135deg,#f59e0b,#ef4444)' }}/>
          <div style={{ padding:'0 18px 18px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:-28, marginBottom:10 }}>
              <div style={{ width:56,height:56,borderRadius:16,background:'linear-gradient(135deg,#f59e0b,#ef4444)',border:'3px solid white',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 4px 12px rgba(245,158,11,0.3)' }}>
                <span style={{ fontSize:22,fontWeight:900,color:'white' }}>{displayName[0]?.toUpperCase()||'?'}</span>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {/* Dark mode toggle */}
                <button onClick={toggleDark}
                  style={{ padding:'6px 12px', background: darkMode ? '#0f172a':'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:11, fontWeight:900, cursor:'pointer', color: darkMode?'#f59e0b':'#64748b', display:'flex', alignItems:'center', gap:5 }}>
                  {darkMode ? `☀️ ${p.lightMode}` : `🌙 ${p.darkMode}`}
                </button>
              </div>
            </div>
            <p style={{ fontSize:17,fontWeight:900,color:'#0f172a',margin:'0 0 2px' }}>{displayName}</p>
            <p style={{ fontSize:11,color:'#94a3b8',margin:'0 0 12px',display:'flex',alignItems:'center',gap:5 }}><Mail size={11}/> {user?.email}</p>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',paddingTop:10,borderTop:'1px solid #f1f5f9' }}>
              {[[favorites.length, p.favStat],[savedTrips.length, p.tripStat],[MAX_SAVED_TRIPS, p.maxStat]].map(([n,l],i)=>(
                <div key={i} style={{ textAlign:'center',padding:'7px 0' }}>
                  <p style={{ fontSize:22,fontWeight:900,color: i===2?'#94a3b8':'#0f172a',margin:0 }}>{n}</p>
                  <p style={{ fontSize:10,color:'#94a3b8',margin:0 }}>{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Menu items */}
        <div style={{ background:'white',borderRadius:18,border:'1.5px solid #e2e8f0',overflow:'hidden' }}>
          {[
            { label: p.myAcc, sub:`${user?.email||''}`, ic:User, bg:'#eff6ff', c:'#1d4ed8', key:'account' },
            { label: p.favs, sub:`${favorites.length} ${L('個景點','attractions','スポット','명소')}`, ic:Heart, bg:'#fef2f2', c:'#dc2626', key:'favorites' },
            { label: p.trips, sub:`${savedTrips.length} / ${MAX_SAVED_TRIPS}`, ic:MapPin, bg:'#f0fdf4', c:'#16a34a', key:'trips' },
            { label: p.security, sub: p.changePw, ic:ShieldCheck, bg:'#f8fafc', c:'#ca8a04', key:'security' },
          ].map((item, i, arr) => (
            <button key={item.key} onClick={()=>setProfileTab(item.key)}
              style={{ width:'100%',display:'flex',alignItems:'center',gap:12,padding:'13px 16px',background:'none',border:'none',borderBottom:i<arr.length-1?'1px solid #f8fafc':'none',cursor:'pointer',textAlign:'left',transition:'background 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <div style={{ width:36,height:36,borderRadius:11,background:item.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}><item.ic size={16} color={item.c}/></div>
              <div style={{ flex:1 }}>
                <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#0f172a' }}>{item.label}</p>
                <p style={{ margin:0, fontSize:11, color:'#94a3b8' }}>{item.sub}</p>
              </div>
              <ChevronRight size={14} color="#cbd5e1"/>
            </button>
          ))}
        </div>

        {/* Affiliate reward status — hidden feature, only shows when earned */}
        {affiliateReward && (() => {
          const expDate = new Date(affiliateReward.expires_at).toLocaleDateString(lang==='en'?'en-GB':lang==='ja'?'ja-JP':lang==='ko'?'ko-KR':'zh-HK',{year:'numeric',month:'long',day:'numeric'});
          return (
            <div style={{ background:'linear-gradient(135deg,#fffbeb,#fef3c7)', borderRadius:18, border:'1.5px solid #fde68a', padding:'16px 18px', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:42, height:42, borderRadius:13, background:'#fef3c7', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:22 }}>🎁</div>
              <div style={{ flex:1 }}>
                <p style={{ margin:'0 0 1px', fontSize:13, fontWeight:900, color:'#92400e' }}>{L('特別旅遊禮遇','Special Travel Perk','特別旅行特典','특별 여행 혜택')}</p>
                <p style={{ margin:0, fontSize:11, color:'#78350f' }}>{L(`可規劃一次最多 ${AFFILIATE_MAX_DAYS} 天行程 · 有效至 ${expDate}`,`Plan one trip up to ${AFFILIATE_MAX_DAYS} days · Valid until ${expDate}`,`最大${AFFILIATE_MAX_DAYS}日間の旅程を1回計画可能 · 有効期限 ${expDate}`,`최대 ${AFFILIATE_MAX_DAYS}일 여행 1회 계획 가능 · 유효 기간 ${expDate}`)}</p>
              </div>
              <Zap size={16} color="#f59e0b"/>
            </div>
          );
        })()}

        {/* Newsletter subscription */}
        <div style={{ background:'white', borderRadius:18, border:'1.5px solid #e2e8f0', padding:'16px 18px', display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:42, height:42, borderRadius:13, background: isSubscribed?'#f0fdf4':'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Mail size={18} color={isSubscribed?'#16a34a':'#94a3b8'}/>
          </div>
          <div style={{ flex:1 }}>
            <p style={{ margin:'0 0 1px', fontSize:13, fontWeight:900, color:'#0f172a' }}>{p.newsletter}</p>
            <p style={{ margin:0, fontSize:11, color:'#94a3b8' }}>{isSubscribed ? p.newsletterOn : p.newsletterOff}</p>
          </div>
          <button onClick={toggleSubscription} disabled={subLoading}
            style={{ padding:'8px 16px', borderRadius:99, border:'none', background: isSubscribed?'#fef2f2':'#f59e0b', color: isSubscribed?'#dc2626':'#0f172a', fontSize:12, fontWeight:900, cursor:'pointer', flexShrink:0, opacity:subLoading?0.6:1 }}>
            {subLoading?'…':isSubscribed ? p.unsubBtn : p.subBtn}
          </button>
        </div>

        {/* Logout */}
        <button onClick={()=>{ supabase.auth.signOut(); setProfileTab('main'); }}
          style={{ background:'#fef2f2',color:'#dc2626',border:'none',borderRadius:15,padding:'14px',fontWeight:900,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8 }}
          onMouseEnter={e=>{e.currentTarget.style.background='#dc2626';e.currentTarget.style.color='white';}}
          onMouseLeave={e=>{e.currentTarget.style.background='#fef2f2';e.currentTarget.style.color='#dc2626';}}>
          <LogOut size={15}/> {t.logout}
        </button>
      </div>
    );
  };

  // ── LEGAL PAGES ────────────────────────────────────────────────────────────
  const [legalPage, setLegalPage] = useState(null); // null | 'terms' | 'privacy' | 'ads'
  // 網址 ↔ 彈出視窗同步：進入 /privacy、/terms 就打開；離開（包括按返回鍵）就關閉
  useEffect(() => {
    const m = location.pathname.match(LEGAL_PATH_RE);
    if (m) setLegalPage(m[1]);
    else setLegalPage(p => (p === 'privacy' || p === 'terms') ? null : p);
  }, [location.pathname]);
  const openLegalPage = (key) => navigate(`/${key}`);
  const [adsForm, setAdsForm] = useState({ name:'', company:'', phone:'', email:'', message:'' });
  const [adsErrs, setAdsErrs] = useState({});
  const [adsSent, setAdsSent] = useState(false);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsApiErr, setAdsApiErr] = useState('');

  // ── FEEDBACK PAGE ──────────────────────────────────────────────────────────
  const [fbForm, setFbForm] = useState({ name:'', email:'', category:'suggestion', rating:0, message:'' });
  const [fbErrs, setFbErrs] = useState({});
  const [fbSent, setFbSent] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);

  const validateAds = () => {
    const e = {};
    if (!adsForm.name.trim()) e.name = true;
    if (!adsForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adsForm.email)) e.email = true;
    if (!adsForm.message.trim()) e.message = true;
    return e;
  };

  const renderLegalModal = () => {
    if (!legalPage) return null;
    const close = () => {
      setLegalPage(null); setAdsSent(false); setAdsErrs({});
      if (LEGAL_PATH_RE.test(location.pathname)) navigate(TAB_TO_PATH[tab] || '/trips');
    };
    const iStyle = { width:'100%', background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'11px 14px', fontSize:13, fontWeight:600, outline:'none', color:'#1e293b', boxSizing:'border-box' };
    const Section = ({title, children}) => (
      <div style={{ marginBottom:24 }}>
        <p style={{ fontSize:15, fontWeight:900, color:'#0f172a', margin:'0 0 8px', borderLeft:'3px solid #f59e0b', paddingLeft:10 }}>{title}</p>
        <div style={{ fontSize:13, color:'#475569', lineHeight:1.8 }}>{children}</div>
      </div>
    );

    return (
      <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}
           onClick={e=>{ if(e.target===e.currentTarget) close(); }}>
        <div style={{ width:'100%', maxWidth:680, maxHeight:'90vh', background:'white', borderRadius:'20px 20px 0 0', display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {/* Modal header */}
          <div style={{ padding:'18px 22px 14px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:32, height:32, background:'#fffbeb', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Plane size={14} color="#f59e0b"/>
              </div>
              <div>
                <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#0f172a' }}>
                  {legalPage==='terms'?(t.termsTitle||'使用條款'):legalPage==='privacy'?(t.privacyTitle||'私隱政策'):(t.adsTitle||'廣告查詢')}
                </p>
                <p style={{ margin:0, fontSize:10, color:'#94a3b8' }}>EC Travel</p>
              </div>
            </div>
            <button aria-label="關閉景點詳情" onClick={close} style={{ width:30, height:30, borderRadius:'50%', background:'#f1f5f9', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}><X size={14}/></button>
          </div>

          {/* Scrollable content */}
          <div style={{ overflowY:'auto', padding:'22px 22px 32px', flex:1 }}>

            {/* ── 使用條款 ── */}
            {legalPage==='terms' && ((lang==='en'||lang==='ja'||lang==='ko') ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>Last updated: May 14, 2026</p>
              <Section title="1. Acceptance of Terms"><p>Welcome to EC Travel. By using this website, you agree to these Terms of Service. If you do not agree, please stop using the website.</p></Section>
              <Section title="2. Service Description">
                <p>EC Travel is a travel information platform providing:</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>AI-powered travel search and information</li>
                  <li>Intelligent itinerary planning</li>
                  <li>Hotel, flight, and attraction booking links (via third-party platforms)</li>
                  <li>Local attractions, dining, and deal information</li>
                </ul>
              </Section>
              <Section title="3. Usage Restrictions">
                <p>You agree not to:</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Use automated tools to mass-access this website</li>
                  <li>Copy or redistribute content for commercial purposes</li>
                  <li>Use the website for any illegal activities</li>
                  <li>Interfere with or disrupt website operations</li>
                </ul>
              </Section>
              <Section title="4. Third-Party Links"><p>This website contains links to third-party platforms including Klook, KKday, Booking.com, Agoda, and Airbnb. EC Travel is not responsible for third-party content, services, or privacy policies. Transactions on third-party platforms are governed by their respective terms.</p></Section>
              <Section title="5. AI-Generated Content Disclaimer"><p>This website uses AI to generate travel information and itinerary suggestions. AI-generated content is for reference only. EC Travel does not guarantee its accuracy, completeness, or timeliness. Before traveling, please verify visa requirements, opening hours, prices, and policies independently.</p></Section>
              <Section title="6. Commission Disclosure"><p>This website earns affiliate commissions by referring users to partner booking platforms. This does not affect your booking price or service quality. EC Travel commits to recommending only reputable partners.</p></Section>
              <Section title="7. Limitation of Liability"><p>EC Travel and its affiliates are not liable for any losses (direct or indirect) arising from use of this website, including trip cancellations, booking disputes, or inaccurate AI-generated content.</p></Section>
              <Section title="8. Copyright Notice">
                <p>All content on this website — including text, images, AI-generated articles, itinerary suggestions, interface design, and logos — is owned by EC Travel or its licensors, protected under Hong Kong copyright law.</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Unauthorized commercial reproduction, redistribution, or modification is prohibited</li>
                  <li>Personal non-commercial use of itinerary content is permitted</li>
                  <li>Third-party content referenced on this site (e.g. news summaries) remains the property of its original authors</li>
                </ul>
              </Section>
              <Section title="9. Amendments"><p>EC Travel reserves the right to modify these Terms at any time. Significant changes will be announced on the website. Continued use constitutes acceptance of the updated Terms.</p></Section>
              <Section title="10. Governing Law"><p>These Terms are governed by the laws of the Hong Kong Special Administrative Region. Disputes shall be resolved in Hong Kong courts.</p></Section>
              <Section title="11. Contact Us"><p>For questions about these Terms, please contact us via the Ad Enquiry page.</p></Section>
            </>) : lang==='ja' ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>最終更新：2026年5月14日</p>
              <Section title="1. 利用規約の承諾"><p>EC Travel（以下「本ウェブサイト」）へようこそ。本ウェブサイトを利用することで、本利用規約に同意したものとみなします。同意されない場合は、利用を中止してください。</p></Section>
              <Section title="2. サービスの説明">
                <p>EC Travel は以下のサービスを提供する旅行情報プラットフォームです：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>AI による旅行検索および情報照会</li>
                  <li>AI によるスマートな旅程計画</li>
                  <li>ホテル・航空券・チケット予約リンク（第三者プラットフォーム提供）</li>
                  <li>現地観光スポット・グルメ・お得情報</li>
                </ul>
              </Section>
              <Section title="3. 利用制限">
                <p>以下の行為を行わないことに同意するものとします：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>自動化ツールによる大量アクセス</li>
                  <li>商業目的でのコンテンツの複製または再配布</li>
                  <li>違法行為への利用</li>
                  <li>ウェブサイトの正常な運営への妨害</li>
                </ul>
              </Section>
              <Section title="4. 第三者リンク"><p>本ウェブサイトには Klook・KKday・Booking.com・Agoda・Airbnb などの第三者プラットフォームへのリンクが含まれています。EC Travel は第三者ウェブサイトのコンテンツ、サービス、プライバシーポリシーについて責任を負いません。</p></Section>
              <Section title="5. AI 生成コンテンツの免責事項"><p>本ウェブサイトは AI 技術を使用して旅行情報および旅程の提案を生成しています。AI が生成したコンテンツは参考情報に過ぎず、正確性・完全性・適時性を保証しません。旅行前にビザ要件・営業時間・料金等をご自身でご確認ください。</p></Section>
              <Section title="6. 手数料の開示"><p>本ウェブサイトは提携予約プラットフォームへのユーザー紹介によりアフィリエイト手数料を得ています。これはご予約価格やサービス品質に影響しません。EC Travel は信頼できる提携プラットフォームのみを推薦することをお約束します。</p></Section>
              <Section title="7. 責任の制限"><p>EC Travel およびその関連会社は、本ウェブサイトの利用に起因するいかなる損失（直接的または間接的損失を含む）についても責任を負いません。</p></Section>
              <Section title="8. 著作権に関する通知">
                <p>本ウェブサイトのすべてのコンテンツは EC Travel またはそのライセンサーに帰属し、香港著作権法により保護されています。</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>書面による許可なく、商業目的での複製・転載・改変または再配布は禁止されています</li>
                  <li>個人的かつ非商業的利用のためのダウンロードは許可されています</li>
                  <li>引用されている第三者コンテンツの著作権は原著者に帰属します</li>
                </ul>
              </Section>
              <Section title="9. 規約の改訂"><p>EC Travel はいつでも本規約を変更する権利を有します。重要な変更は本ウェブサイトでお知らせします。引き続き利用することで改訂後の規約に同意したものとみなします。</p></Section>
              <Section title="10. 準拠法"><p>本規約は香港特別行政区の法律に準拠します。紛争が生じた場合、両当事者は香港の裁判所での解決に合意します。</p></Section>
              <Section title="11. お問い合わせ"><p>本規約についてご不明な点は、広告に関するお問い合わせページよりご連絡ください。</p></Section>
            </>) : lang==='ko' ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>최종 업데이트：2026년 5월 14일</p>
              <Section title="1. 이용약관 동의"><p>EC Travel（이하 「본 웹사이트」）에 오신 것을 환영합니다. 본 웹사이트를 이용하면 이 이용약관에 동의한 것으로 간주합니다. 동의하지 않으신다면 이용을 중단해 주세요.</p></Section>
              <Section title="2. 서비스 설명">
                <p>EC Travel은 다음 서비스를 제공하는 여행 정보 플랫폼입니다：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>AI 기반 여행 검색 및 정보 조회</li>
                  <li>AI 여행 일정 계획</li>
                  <li>호텔, 항공권, 입장권 예약 링크（제3자 플랫폼 제공）</li>
                  <li>현지 명소, 음식, 할인 정보</li>
                </ul>
              </Section>
              <Section title="3. 이용 제한">
                <p>다음 행위를 하지 않을 것에 동의합니다：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>자동화 도구를 이용한 대량 접근</li>
                  <li>상업적 목적의 콘텐츠 복제 또는 재배포</li>
                  <li>불법 활동을 위한 웹사이트 이용</li>
                  <li>웹사이트 정상 운영 방해 또는 교란</li>
                </ul>
              </Section>
              <Section title="4. 제3자 링크"><p>본 웹사이트는 Klook, KKday, Booking.com, Agoda, Airbnb 등 제3자 플랫폼으로의 링크를 포함합니다. EC Travel은 제3자 웹사이트의 콘텐츠, 서비스 및 개인정보 처리방침에 대해 책임지지 않습니다.</p></Section>
              <Section title="5. AI 생성 콘텐츠 면책 조항"><p>본 웹사이트는 AI 기술을 사용하여 여행 정보 및 일정 제안을 생성합니다. AI 생성 콘텐츠는 참고용이며, EC Travel은 정확성·완전성·적시성을 보장하지 않습니다. 여행 전에 비자 요건·운영 시간·가격 등을 직접 확인해 주세요.</p></Section>
              <Section title="6. 수수료 공개"><p>본 웹사이트는 제휴 예약 플랫폼으로 사용자를 안내하여 제휴 수수료를 받습니다. 이는 예약 가격이나 서비스 품질에 영향을 미치지 않습니다. EC Travel은 신뢰할 수 있는 제휴 플랫폼만 추천할 것을 약속합니다.</p></Section>
              <Section title="7. 책임 제한"><p>EC Travel 및 그 계열사는 본 웹사이트 이용으로 인한 어떠한 손실（직간접적 손실 포함）에 대해서도 책임을 지지 않습니다.</p></Section>
              <Section title="8. 저작권 공지">
                <p>본 웹사이트의 모든 콘텐츠는 EC Travel 또는 라이선서의 소유이며 홍콩 저작권법에 의해 보호됩니다.</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>서면 허가 없이 상업적 목적의 복제, 전재, 수정 또는 재배포는 금지됩니다</li>
                  <li>개인 비상업적 용도의 다운로드는 허용됩니다</li>
                  <li>인용된 제3자 콘텐츠의 저작권은 원저작자에게 귀속됩니다</li>
                </ul>
              </Section>
              <Section title="9. 약관 개정"><p>EC Travel은 언제든지 이 약관을 수정할 권리를 보유합니다. 중요한 변경 사항은 웹사이트에 공지됩니다. 계속 이용하면 개정된 약관에 동의한 것으로 간주됩니다.</p></Section>
              <Section title="10. 준거법"><p>이 약관은 홍콩특별행정구 법률에 따라 규율됩니다. 분쟁이 발생할 경우 양 당사자는 홍콩 법원에서 해결하기로 동의합니다.</p></Section>
              <Section title="11. 문의하기"><p>이 약관에 대한 문의 사항이 있으시면 광고 문의 페이지를 통해 연락해 주세요.</p></Section>
            </>) : lang==='zh-CN' ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>最后更新：2026年5月14日</p>
              <Section title="1. 接受条款"><p>欢迎使用 EC Travel（以下简称「本网站」）。使用本网站即表示您同意遵守本使用条款。如您不同意，请停止使用本网站。</p></Section>
              <Section title="2. 服务描述">
                <p>EC Travel 是一个旅游资讯平台，提供以下服务：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>AI 智能旅游搜索及资讯查询</li>
                  <li>旅游行程智能规划</li>
                  <li>酒店、机票、门票预订链接（由第三方平台提供）</li>
                  <li>当地景点、美食、优惠资讯</li>
                </ul>
              </Section>
              <Section title="3. 使用限制">
                <p>您同意不会：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>以自动化方式大量存取本网站</li>
                  <li>复制或再发布本网站内容作商业用途</li>
                  <li>利用本网站进行任何违法活动</li>
                  <li>干扰或破坏本网站的正常运作</li>
                </ul>
              </Section>
              <Section title="4. 第三方链接"><p>本网站包含 Klook、KKday、Booking.com、Agoda、Airbnb 等第三方平台的链接。EC Travel 对第三方网站的内容、服务及隐私政策概不负责。用户在第三方平台的交易及资料，受该平台的条款约束。</p></Section>
              <Section title="5. AI 生成内容免责声明"><p>本网站使用人工智能技术生成旅游资讯及行程建议。AI 生成的内容仅供参考，EC Travel 不保证其准确性、完整性或时效性。用户在规划旅游前，应自行核实相关资讯，包括：签证要求、开放时间、价格及政策等。</p></Section>
              <Section title="6. 佣金披露"><p>本网站透过推荐用户至合作预订平台赚取联盟佣金。此安排不影响用户的预订价格或服务质量。EC Travel 承诺以用户利益为先，仅推荐信誉良好的合作平台。</p></Section>
              <Section title="7. 责任限制"><p>EC Travel 及其关联公司不对因使用本网站而导致的任何损失（包括直接或间接损失）承担责任，包括但不限于旅游行程取消、预订纠纷或 AI 内容不准确所造成的损失。</p></Section>
              <Section title="8. 版权声明">
                <p>本网站所有内容，包括但不限于文字、图片、AI 生成文章、行程建议、界面设计及标志，均为 EC Travel 或其授权方所有，受香港版权法保护。</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>未经书面许可，不得复制、转载、修改或以任何形式再发布本网站内容作商业用途</li>
                  <li>用户可将行程计划等内容下载供个人非商业使用</li>
                  <li>本网站引用之第三方资料（如 The Guardian 新闻摘要）版权归原作者所有</li>
                </ul>
              </Section>
              <Section title="9. 条款修订"><p>EC Travel 保留随时修改本条款的权利。重大变更将于本网站公告。继续使用本网站即视为接受更新后的条款。</p></Section>
              <Section title="10. 适用法律"><p>本条款受香港特别行政区法律管辖。如有争议，双方同意在香港法院解决。</p></Section>
              <Section title="11. 联系我们"><p>如对本条款有任何疑问，请透过「广告咨询」页面联系我们。</p></Section>
            </>) : (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>最後更新：2026年5月14日</p>
              <Section title="1. 接受條款">
                <p>歡迎使用 EC Travel（以下簡稱「本網站」）。使用本網站即表示您同意遵守本使用條款。如您不同意，請停止使用本網站。</p>
              </Section>
              <Section title="2. 服務描述">
                <p>EC Travel 是一個旅遊資訊平台，提供以下服務：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>AI 智能旅遊搜尋及資訊查詢</li>
                  <li>旅遊行程智能規劃</li>
                  <li>酒店、機票、門票預訂連結（由第三方平台提供）</li>
                  <li>當地景點、美食、優惠資訊</li>
                </ul>
              </Section>
              <Section title="3. 使用限制">
                <p>您同意不會：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>以自動化方式大量存取本網站</li>
                  <li>複製或再發布本網站內容作商業用途</li>
                  <li>利用本網站進行任何違法活動</li>
                  <li>干擾或破壞本網站的正常運作</li>
                </ul>
              </Section>
              <Section title="4. 第三方連結">
                <p>本網站包含 Klook、KKday、Booking.com、Agoda、Airbnb 等第三方平台的連結。EC Travel 對第三方網站的內容、服務及私隱政策概不負責。用戶在第三方平台的交易及資料，受該平台的條款約束。</p>
              </Section>
              <Section title="5. AI 生成內容免責聲明">
                <p>本網站使用人工智能技術生成旅遊資訊及行程建議。AI 生成的內容僅供參考，EC Travel 不保證其準確性、完整性或時效性。用戶在規劃旅遊前，應自行核實相關資訊，包括：簽證要求、開放時間、價格及政策等。</p>
              </Section>
              <Section title="6. 佣金披露">
                <p>本網站透過推薦用戶至合作預訂平台賺取聯盟佣金。此安排不影響用戶的預訂價格或服務質素。EC Travel 承諾以用戶利益為先，僅推薦信譽良好的合作平台。</p>
              </Section>
              <Section title="7. 責任限制">
                <p>EC Travel 及其關聯公司不對因使用本網站而導致的任何損失（包括直接或間接損失）承擔責任，包括但不限於旅遊行程取消、預訂糾紛或 AI 內容不準確所造成的損失。</p>
              </Section>
              <Section title="8. 版權聲明">
                <p>本網站所有內容，包括但不限於文字、圖片、AI 生成文章、行程建議、介面設計及標誌，均為 EC Travel 或其授權方所有，受香港版權法保護。</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>未經書面許可，不得複製、轉載、修改或以任何形式再發布本網站內容作商業用途</li>
                  <li>用戶可將行程計劃等內容下載供個人非商業使用</li>
                  <li>本網站引用之第三方資料（如 The Guardian 新聞摘要）版權歸原作者所有</li>
                </ul>
              </Section>
              <Section title="9. 條款修訂">
                <p>EC Travel 保留隨時修改本條款的權利。重大變更將於本網站公告。繼續使用本網站即視為接受更新後的條款。</p>
              </Section>
              <Section title="10. 適用法律">
                <p>本條款受香港特別行政區法律管轄。如有爭議，雙方同意在香港法院解決。</p>
              </Section>
              <Section title="11. 聯絡我們">
                <p>如對本條款有任何疑問，請透過「廣告查詢」頁面聯絡我們。</p>
              </Section>
            </>))}

            {/* ── 私隱政策 ── */}
            {legalPage==='privacy' && ((lang==='en'||lang==='ja'||lang==='ko') ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>Last updated: May 14, 2026</p>
              <Section title="1. Overview"><p>EC Travel values your privacy. This policy explains how we collect, use, and protect your personal data, in compliance with Hong Kong's Personal Data (Privacy) Ordinance (PDPO).</p></Section>
              <Section title="2. Data We Collect">
                <p><strong>Data you voluntarily provide:</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Email address (account registration or email subscription)</li>
                  <li>Phone number (optional)</li>
                  <li>Date of birth (optional)</li>
                  <li>Ad enquiry form content</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>Automatically collected data:</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Search query records</li>
                  <li>Browsing behaviour and usage records</li>
                  <li>Device information and IP address</li>
                  <li>Cookies (essential functions only)</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>Location data (GPS):</strong></p>
                <p style={{ margin:'4px 0 0', fontSize:13, lineHeight:1.7 }}>This website may request your device location (GPS) on first use of the homepage to automatically display travel information for your city. This requires your explicit permission, which you can revoke in your browser settings at any time. We only use location data to display local travel content and do not store or transmit your exact coordinates to any server.</p>
              </Section>
              <Section title="3. Purpose of Data Use">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Provide and improve travel search and itinerary planning services</li>
                  <li>Maintain user accounts and saved itineraries</li>
                  <li>Respond to ad enquiries and customer service requests</li>
                  <li>Analyse website usage to improve user experience</li>
                  <li>Comply with legal requirements</li>
                </ul>
              </Section>
              <Section title="4. Third-Party Data Sharing">
                <p>We do not sell your personal data. We may share necessary data with:</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li><strong>Groq</strong> (AI service) — your search queries are used to generate travel recommendations</li>
                  <li><strong>Supabase</strong> (database) — account and saved data stored securely</li>
                  <li><strong>Vercel</strong> (website hosting) — technical infrastructure</li>
                  <li><strong>Resend</strong> (email delivery) — your email address is shared with Resend when you subscribe to email updates. Resend's data processing is governed by their <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>Privacy Policy</a>.</li>
                </ul>
                <p style={{ marginTop:8 }}>All third-party partners are contractually bound to use your data only within the scope of providing their services.</p>
              </Section>
              <Section title="5. AI and Your Data"><p>Your search queries on this website (e.g. searching for a city name) are sent to Groq's AI system to generate results. Please avoid entering personally sensitive information in your searches. Groq's data processing is governed by their <a href="https://groq.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>Privacy Policy</a>.</p></Section>
              <Section title="6. Data Retention">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Account data: Retained while account exists</li>
                  <li>Deleted within 30 days after account deletion</li>
                  <li>Anonymous usage records: Up to 12 months</li>
                  <li>Ad enquiry records: 3 years</li>
                </ul>
              </Section>
              <Section title="7. Your Rights">
                <p>Under Hong Kong PDPO and EU GDPR, you have the right to:</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>Access your personal data held by us</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your account and data</li>
                  <li>Object to our processing of your data</li>
                  <li>Download a copy of your personal data</li>
                </ul>
                <p style={{ marginTop:8 }}>To exercise these rights, contact us via the Ad Enquiry form, noting "Privacy Request" in your message.</p>
              </Section>
              <Section title="8. Security Measures">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>All data transmission uses HTTPS encryption</li>
                  <li>Passwords are encrypted with bcrypt — we cannot read your password</li>
                  <li>Database has row-level security — users can only access their own data</li>
                  <li>Regular security audits</li>
                </ul>
              </Section>
              <Section title="9. Cookie Policy"><p>This website only uses essential functional cookies (to maintain login status). We do not use advertising tracking cookies and do not require your consent.</p></Section>
              <Section title="10. Email Subscription">
                <p>If you subscribe to EC Travel travel updates:</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>We will occasionally send travel information, attraction recommendations, and deals — no fixed schedule</li>
                  <li>You can unsubscribe at any time via the "Unsubscribe" link in any email, effective immediately</li>
                  <li>After unsubscribing, your email is marked inactive; contact us for complete deletion</li>
                  <li>Emails are sent via Resend — your email address will be shared with Resend for delivery</li>
                  <li>We will not use your email for any purpose other than subscriptions</li>
                </ul>
              </Section>
              <Section title="11. Contact Us"><p>For questions about this policy, contact us via the Ad Enquiry page, noting "Privacy Enquiry" in your message. We will respond within 30 business days.</p></Section>
            </>) : lang==='ja' ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>最終更新：2026年5月14日</p>
              <Section title="1. 概要"><p>EC Travel はお客様のプライバシーを重視しています。本ポリシーは香港の個人情報保護法（PDPO）に準拠し、個人データの収集・使用・保護方法を説明します。</p></Section>
              <Section title="2. 収集するデータ">
                <p><strong>お客様が提供するデータ：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>メールアドレス（アカウント登録またはメール購読）</li>
                  <li>電話番号（任意）</li>
                  <li>生年月日（任意）</li>
                  <li>広告お問い合わせフォームの内容</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>自動収集されるデータ：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>検索クエリの記録</li>
                  <li>閲覧行動および利用記録</li>
                  <li>デバイス情報および IP アドレス</li>
                  <li>Cookie（必要な機能のみ）</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>位置情報（GPS）：</strong></p>
                <p style={{ margin:'4px 0 0', fontSize:13, lineHeight:1.7 }}>本ウェブサイトはホームページの初回利用時にデバイス位置（GPS）を要求し、現地旅行情報を自動表示します。お客様の明示的な許可が必要で、ブラウザ設定でいつでも拒否・取り消しが可能です。位置情報はコンテンツ表示のみに使用し、座標をサーバーに保存・送信しません。</p>
              </Section>
              <Section title="3. データの利用目的">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>旅行検索および旅程計画サービスの提供・改善</li>
                  <li>ユーザーアカウントおよび保存済み旅程の管理</li>
                  <li>広告お問い合わせおよびカスタマーサービスへの対応</li>
                  <li>ユーザーエクスペリエンス向上のための利用分析</li>
                  <li>法的要件の遵守</li>
                </ul>
              </Section>
              <Section title="4. 第三者とのデータ共有">
                <p>当社はお客様の個人データを販売しません。以下の第三者と必要なデータを共有する場合があります：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li><strong>Groq</strong>（AI サービス）— 旅行提案生成のために検索クエリを使用</li>
                  <li><strong>Supabase</strong>（データベース）— アカウントおよびデータを安全に保存</li>
                  <li><strong>Vercel</strong>（ウェブサイトホスティング）— 技術インフラ</li>
                  <li><strong>Resend</strong>（メール配信）— 購読時にメールアドレスが Resend と共有されます。Resend のデータ処理は同社の<a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>プライバシーポリシー</a>に従います。</li>
                </ul>
                <p style={{ marginTop:8 }}>すべての第三者パートナーはサービス提供の範囲内でのみデータを使用するよう契約上義務付けられています。</p>
              </Section>
              <Section title="5. AI とお客様のデータ"><p>本ウェブサイトでの検索クエリは Groq の AI システムに送信されて結果を生成します。検索で個人的な機密情報を入力しないようにしてください。Groq のデータ処理は同社の<a href="https://groq.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>プライバシーポリシー</a>に従います。</p></Section>
              <Section title="6. データの保持">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>アカウントデータ：アカウントが存在する間保持</li>
                  <li>アカウント削除後：30日以内に完全削除</li>
                  <li>匿名利用記録：最長12ヶ月</li>
                  <li>広告お問い合わせ記録：3年間</li>
                </ul>
              </Section>
              <Section title="7. お客様の権利">
                <p>香港の PDPO および EU の GDPR に基づき、お客様には以下の権利があります：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>当社が保有する個人データへのアクセス</li>
                  <li>不正確なデータの訂正の要求</li>
                  <li>アカウントおよびデータの削除の要求</li>
                  <li>データ処理への異議申し立て</li>
                  <li>個人データのコピーのダウンロード</li>
                </ul>
                <p style={{ marginTop:8 }}>権利を行使するには、広告お問い合わせフォームから「プライバシーリクエスト」と明記してご連絡ください。</p>
              </Section>
              <Section title="8. セキュリティ対策">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>すべてのデータ転送は HTTPS 暗号化を使用</li>
                  <li>パスワードは bcrypt で暗号化されており読み取り不可</li>
                  <li>データベースには行レベルセキュリティポリシーが適用</li>
                  <li>定期的なセキュリティ監査</li>
                </ul>
              </Section>
              <Section title="9. Cookie ポリシー"><p>本ウェブサイトはログイン状態維持のための必要な機能 Cookie のみを使用します。広告追跡 Cookie は使用せず、お客様の同意は不要です。</p></Section>
              <Section title="10. メール購読">
                <p>EC Travel のメール更新を購読する場合：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>旅行情報・観光スポット・お得情報を不定期に送信します（定期送信は保証しません）</li>
                  <li>メール下部の「購読解除」リンクでいつでも解除でき、即時に有効になります</li>
                  <li>解除後はメールアドレスを非アクティブとしてマークします（完全削除はご連絡ください）</li>
                  <li>メールは Resend を通じて送信され、メールアドレスは配信のために共有されます</li>
                  <li>メールアドレスは購読目的以外には使用しません</li>
                </ul>
              </Section>
              <Section title="11. お問い合わせ"><p>本ポリシーについてのご質問は、広告お問い合わせページから「プライバシーお問い合わせ」と明記してご連絡ください。30営業日以内にご回答いたします。</p></Section>
            </>) : lang==='ko' ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>최종 업데이트：2026년 5월 14일</p>
              <Section title="1. 개요"><p>EC Travel은 귀하의 개인정보를 소중히 여깁니다. 본 정책은 홍콩 개인정보보호법（PDPO）에 따라 개인 데이터를 수집·사용·보호하는 방법을 설명합니다.</p></Section>
              <Section title="2. 수집하는 데이터">
                <p><strong>귀하가 직접 제공하는 데이터：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>이메일 주소（계정 등록 또는 이메일 구독）</li>
                  <li>전화번호（선택）</li>
                  <li>생년월일（선택）</li>
                  <li>광고 문의 양식 내용</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>자동으로 수집되는 데이터：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>검색 쿼리 기록</li>
                  <li>브라우징 행동 및 이용 기록</li>
                  <li>기기 정보 및 IP 주소</li>
                  <li>쿠키（필수 기능에만 해당）</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>위치 데이터（GPS）：</strong></p>
                <p style={{ margin:'4px 0 0', fontSize:13, lineHeight:1.7 }}>본 웹사이트는 홈페이지 최초 이용 시 기기 위치（GPS）를 요청하여 현지 여행 정보를 자동 표시합니다. 귀하의 명시적 허가가 필요하며 브라우저 설정에서 언제든지 거부하거나 취소할 수 있습니다. 위치 데이터는 콘텐츠 표시에만 사용하며 좌표를 서버에 저장하거나 전송하지 않습니다.</p>
              </Section>
              <Section title="3. 데이터 이용 목적">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>여행 검색 및 일정 계획 서비스 제공 및 개선</li>
                  <li>사용자 계정 및 저장된 일정 관리</li>
                  <li>광고 문의 및 고객 서비스 응답</li>
                  <li>사용자 경험 개선을 위한 이용 분석</li>
                  <li>법적 요구 사항 준수</li>
                </ul>
              </Section>
              <Section title="4. 제3자 데이터 공유">
                <p>당사는 귀하의 개인 데이터를 판매하지 않습니다. 다음 제3자와 필요한 데이터를 공유할 수 있습니다：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li><strong>Groq</strong>（AI 서비스）— 여행 추천 생성을 위해 검색 쿼리 사용</li>
                  <li><strong>Supabase</strong>（데이터베이스）— 계정 및 저장 데이터를 안전하게 보관</li>
                  <li><strong>Vercel</strong>（웹사이트 호스팅）— 기술 인프라</li>
                  <li><strong>Resend</strong>（이메일 발송）— 구독 시 이메일 주소가 Resend에 공유됩니다. Resend의 데이터 처리는 해당 <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>개인정보 처리방침</a>에 따릅니다.</li>
                </ul>
                <p style={{ marginTop:8 }}>모든 제3자 파트너는 서비스 제공 범위 내에서만 데이터를 사용하도록 계약상 의무화되어 있습니다.</p>
              </Section>
              <Section title="5. AI와 귀하의 데이터"><p>본 웹사이트에서의 검색 쿼리는 Groq의 AI 시스템으로 전송되어 결과를 생성합니다. 검색에서 개인 민감 정보를 입력하지 않도록 주의해 주세요. Groq의 데이터 처리는 해당 <a href="https://groq.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>개인정보 처리방침</a>에 따릅니다.</p></Section>
              <Section title="6. 데이터 보존">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>계정 데이터：계정 존재 기간 동안 보존</li>
                  <li>계정 삭제 후：30일 이내에 완전 삭제</li>
                  <li>익명 이용 기록：최대 12개월</li>
                  <li>광고 문의 기록：3년</li>
                </ul>
              </Section>
              <Section title="7. 귀하의 권리">
                <p>홍콩 PDPO 및 EU GDPR에 따라 귀하는 다음의 권리를 갖습니다：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>당사가 보유한 개인 데이터에 대한 열람</li>
                  <li>부정확한 데이터의 정정 요청</li>
                  <li>계정 및 데이터 삭제 요청</li>
                  <li>데이터 처리에 대한 이의 제기</li>
                  <li>개인 데이터 사본 다운로드</li>
                </ul>
                <p style={{ marginTop:8 }}>권리 행사는 광고 문의 양식을 통해 「개인정보 요청」으로 명시하여 연락해 주세요.</p>
              </Section>
              <Section title="8. 보안 조치">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>모든 데이터 전송에 HTTPS 암호화 사용</li>
                  <li>비밀번호는 bcrypt로 암호화되어 읽을 수 없습니다</li>
                  <li>데이터베이스에는 행 수준 보안 정책 적용</li>
                  <li>정기적인 보안 감사</li>
                </ul>
              </Section>
              <Section title="9. 쿠키 정책"><p>본 웹사이트는 로그인 상태 유지를 위한 필수 기능 쿠키만 사용합니다. 광고 추적 쿠키는 사용하지 않으며 귀하의 동의가 필요하지 않습니다.</p></Section>
              <Section title="10. 이메일 구독">
                <p>EC Travel 이메일 업데이트를 구독하는 경우：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>여행 정보·명소 추천·할인 정보를 비정기적으로 발송합니다（정기 발송은 보장하지 않습니다）</li>
                  <li>이메일 하단의 「구독 취소」 링크로 언제든지 취소할 수 있으며 즉시 효력이 발생합니다</li>
                  <li>취소 후 이메일 주소는 비활성으로 표시됩니다（완전 삭제는 문의해 주세요）</li>
                  <li>이메일은 Resend를 통해 발송되며 이메일 주소는 발송을 위해 공유됩니다</li>
                  <li>이메일 주소는 구독 목적 이외에는 사용하지 않습니다</li>
                </ul>
              </Section>
              <Section title="11. 문의하기"><p>본 정책에 대한 질문은 광고 문의 페이지를 통해 「개인정보 문의」로 명시하여 연락해 주세요. 30 영업일 이내에 회신하겠습니다.</p></Section>
            </>) : lang==='zh-CN' ? (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>最后更新：2026年5月14日</p>
              <Section title="1. 概述"><p>EC Travel 重视您的隐私。本政策说明我们如何收集、使用及保护您的个人资料，并符合香港《个人资料（隐私）条例》（PDPO）的要求。</p></Section>
              <Section title="2. 收集的资料">
                <p><strong>您主动提供的资料：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>电邮地址（注册账号或电邮订阅）</li>
                  <li>电话号码（选填）</li>
                  <li>出生日期（选填）</li>
                  <li>广告咨询表格内容</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>自动收集的资料：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>搜索查询记录</li>
                  <li>浏览行为及使用记录</li>
                  <li>设备资讯及 IP 地址</li>
                  <li>Cookie（仅限必要功能）</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>位置资料（GPS）：</strong></p>
                <p style={{ margin:'4px 0 0', fontSize:13, lineHeight:1.7 }}>本网站会在您首次使用主页时，透过浏览器请求您的设备位置（GPS），以自动显示您所在城市的旅游资讯。此请求需要您明确授权，您可随时在浏览器设置中拒绝或撤回授权。我们仅将位置资料用于显示当地旅游内容，不会储存或传送您的精确坐标至任何服务器。</p>
              </Section>
              <Section title="3. 资料使用目的">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>提供及改善旅游搜索及行程规划服务</li>
                  <li>维护用户账号及已收藏行程</li>
                  <li>回应广告咨询及客户服务请求</li>
                  <li>分析网站使用情况以改善用户体验</li>
                  <li>符合法律要求</li>
                </ul>
              </Section>
              <Section title="4. 第三方资料共享">
                <p>我们不会出售您的个人资料。我们可能与以下第三方共享必要资料：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li><strong>Groq</strong>（AI 服务）— 您的搜索查询用于生成旅游建议</li>
                  <li><strong>Supabase</strong>（数据库）— 账号及收藏资料安全储存</li>
                  <li><strong>Vercel</strong>（网站托管）— 技术基础架构</li>
                  <li><strong>Resend</strong>（电邮发送服务）— 当您订阅电邮更新时，您的电邮地址会传送至 Resend 以处理邮件发送。Resend 的资料处理受其<a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>隐私政策</a>约束。</li>
                </ul>
                <p style={{ marginTop:8 }}>所有第三方合作伙伴均受合约约束，只能在提供服务的范围内使用您的资料。</p>
              </Section>
              <Section title="5. AI 与您的资料"><p>您在本网站的搜索查询（例如：搜索城市名称）会传送至 Groq 的 AI 系统以生成结果。请避免在搜索中输入个人敏感资讯。Groq 的资料处理受其<a href="https://groq.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>隐私政策</a>约束。</p></Section>
              <Section title="6. 资料保留">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>账号资料：账号存在期间保留</li>
                  <li>删除账号后：30天内完全清除</li>
                  <li>匿名使用记录：最多保留12个月</li>
                  <li>广告咨询记录：3年</li>
                </ul>
              </Section>
              <Section title="7. 您的权利">
                <p>根据香港 PDPO 及欧盟 GDPR，您有权：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>查阅我们持有的您的个人资料</li>
                  <li>要求更正不准确的资料</li>
                  <li>要求删除您的账号及资料</li>
                  <li>反对我们处理您的资料</li>
                  <li>下载您的个人资料副本</li>
                </ul>
                <p style={{ marginTop:8 }}>如需行使上述权利，请透过广告咨询表格联系我们，标题注明「隐私请求」。</p>
              </Section>
              <Section title="8. 安全措施">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>所有资料传输使用 HTTPS 加密</li>
                  <li>密码使用 bcrypt 加密，我们无法读取您的密码</li>
                  <li>数据库设有行级安全政策，用户只能存取自己的资料</li>
                  <li>定期安全审计</li>
                </ul>
              </Section>
              <Section title="9. Cookie 政策"><p>本网站仅使用必要功能性 Cookie（维持登录状态）。我们不使用广告追踪 Cookie，不需要您的同意。</p></Section>
              <Section title="10. 电邮订阅">
                <p>如您选择订阅 EC Travel 旅游情报电邮更新：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>我们将不定期发送旅游资讯、景点推介及优惠资讯，不保证固定发送频率</li>
                  <li>您可随时点击电邮底部的「取消订阅」链接退订，即时生效</li>
                  <li>退订后您的电邮地址将标记为非活跃，不会再收到电邮，但资料仍保留于系统（如需完全删除，请联系我们）</li>
                  <li>电邮由 Resend 代为发送，您的电邮地址将共享至 Resend 以完成发送</li>
                  <li>我们不会将您的电邮地址用于订阅目的以外的任何用途</li>
                </ul>
              </Section>
              <Section title="11. 联系我们"><p>如对本政策有疑问，请透过广告咨询页面联系我们，标题注明「隐私查询」。我们将于30个工作天内回复。</p></Section>
            </>) : (<>
              <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 20px' }}>最後更新：2026年5月14日</p>
              <Section title="1. 概述">
                <p>EC Travel 重視您的私隱。本政策說明我們如何收集、使用及保護您的個人資料，並符合香港《個人資料（私隱）條例》（PDPO）的要求。</p>
              </Section>
              <Section title="2. 收集的資料">
                <p><strong>您主動提供的資料：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>電郵地址（註冊帳號或電郵訂閱）</li>
                  <li>電話號碼（選填）</li>
                  <li>出生日期（選填）</li>
                  <li>廣告查詢表格內容</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>自動收集的資料：</strong></p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>搜尋查詢記錄</li>
                  <li>瀏覽行為及使用記錄</li>
                  <li>設備資訊及 IP 地址</li>
                  <li>Cookie（僅限必要功能）</li>
                </ul>
                <p style={{ marginTop:10 }}><strong>位置資料（GPS）：</strong></p>
                <p style={{ margin:'4px 0 0', fontSize:13, lineHeight:1.7 }}>本網站會在您首次使用主頁時，透過瀏覽器請求您的裝置位置（GPS），以自動顯示您所在城市的旅遊資訊。此請求需要您明確授權，您可隨時在瀏覽器設定中拒絕或撤回授權。我們僅將位置資料用於顯示當地旅遊內容，不會儲存或傳送您的精確座標至任何伺服器。</p>
              </Section>
              <Section title="3. 資料使用目的">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>提供及改善旅遊搜尋及行程規劃服務</li>
                  <li>維護用戶帳號及已收藏行程</li>
                  <li>回應廣告查詢及客戶服務請求</li>
                  <li>分析網站使用情況以改善用戶體驗</li>
                  <li>符合法律要求</li>
                </ul>
              </Section>
              <Section title="4. 第三方資料共享">
                <p>我們不會出售您的個人資料。我們可能與以下第三方共享必要資料：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li><strong>Groq</strong>（AI 服務）— 您的搜尋查詢用於生成旅遊建議</li>
                  <li><strong>Supabase</strong>（資料庫）— 帳號及收藏資料安全儲存</li>
                  <li><strong>Vercel</strong>（網站託管）— 技術基礎架構</li>
                  <li><strong>Resend</strong>（電郵發送服務）— 當您訂閱電郵更新時，您的電郵地址會傳送至 Resend 以處理郵件發送。Resend 的資料處理受其<a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>私隱政策</a>約束。</li>
                </ul>
                <p style={{ marginTop:8 }}>所有第三方合作夥伴均受合約約束，只能在提供服務的範圍內使用您的資料。</p>
              </Section>
              <Section title="5. AI 與您的資料">
                <p>您在本網站的搜尋查詢（例如：搜尋城市名稱）會傳送至 Groq 的 AI 系統以生成結果。請避免在搜尋中輸入個人敏感資訊。Groq 的資料處理受其<a href="https://groq.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color:'#f59e0b' }}>私隱政策</a>約束。</p>
              </Section>
              <Section title="6. 資料保留">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>帳號資料：帳號存在期間保留</li>
                  <li>刪除帳號後：30天內完全清除</li>
                  <li>匿名使用記錄：最多保留12個月</li>
                  <li>廣告查詢記錄：3年</li>
                </ul>
              </Section>
              <Section title="7. 您的權利">
                <p>根據香港 PDPO 及歐盟 GDPR，您有權：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>查閱我們持有的您的個人資料</li>
                  <li>要求更正不準確的資料</li>
                  <li>要求刪除您的帳號及資料</li>
                  <li>反對我們處理您的資料</li>
                  <li>下載您的個人資料副本</li>
                </ul>
                <p style={{ marginTop:8 }}>如需行使上述權利，請透過廣告查詢表格聯絡我們，標題註明「私隱請求」。</p>
              </Section>
              <Section title="8. 安全措施">
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>所有資料傳輸使用 HTTPS 加密</li>
                  <li>密碼使用 bcrypt 加密，我們無法讀取您的密碼</li>
                  <li>資料庫設有行級安全政策，用戶只能存取自己的資料</li>
                  <li>定期安全審計</li>
                </ul>
              </Section>
              <Section title="9. Cookie 政策">
                <p>本網站僅使用必要功能性 Cookie（維持登入狀態）。我們不使用廣告追蹤 Cookie，不需要您的同意。</p>
              </Section>
              <Section title="10. 電郵訂閱">
                <p>如您選擇訂閱 EC Travel 旅遊情報電郵更新：</p>
                <ul style={{ paddingLeft:20, margin:'8px 0' }}>
                  <li>我們將不定期發送旅遊資訊、景點推介及優惠資訊，不保證固定發送頻率</li>
                  <li>您可隨時點擊電郵底部的「取消訂閱」連結退訂，即時生效</li>
                  <li>退訂後您的電郵地址將標記為非活躍，不會再收到電郵，但資料仍保留於系統（如需完全刪除，請聯絡我們）</li>
                  <li>電郵由 Resend 代為發送，您的電郵地址將共享至 Resend 以完成發送</li>
                  <li>我們不會將您的電郵地址用於訂閱目的以外的任何用途</li>
                </ul>
              </Section>
              <Section title="11. 聯絡我們">
                <p>如對本政策有疑問，請透過廣告查詢頁面聯絡我們，標題註明「私隱查詢」。我們將於30個工作天內回覆。</p>
              </Section>
            </>))}

            {/* ── 廣告查詢 ── */}
            {legalPage==='ads' && (<>
              <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:12, padding:'12px 16px', marginBottom:20 }}>
                <p style={{ fontSize:13, color:'#92400e', margin:0, lineHeight:1.6 }}>
                  查詢網站廣告位置及收費事項。我們提供多種廣告方案，包括橫幅廣告、景點推薦贊助及優惠推廣合作。
                </p>
              </div>

              {adsSent ? (
                <div style={{ textAlign:'center', padding:'30px 0' }}>
                  <div style={{ width:60, height:60, background:'#f0fdf4', borderRadius:20, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    <CheckCircle2 size={32} color="#16a34a"/>
                  </div>
                  <p style={{ fontSize:17, fontWeight:900, color:'#0f172a', margin:'0 0 8px' }}>查詢已送出！</p>
                  <p style={{ fontSize:13, color:'#64748b', margin:'0 0 24px' }}>我們將於 2 個工作天內以電郵回覆您。</p>
                  <button onClick={()=>{ setAdsSent(false); setAdsForm({name:'',company:'',phone:'',email:'',message:''}); }}
                    style={{ background:'#f1f5f9', border:'none', borderRadius:12, padding:'11px 24px', fontSize:13, fontWeight:900, cursor:'pointer', color:'#475569' }}>
                    再次查詢
                  </button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  {/* Contact name */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', alignItems:'center', gap:12 }}>
                    <label style={{ fontSize:13, color:'#374151', fontWeight:700 }}>聯絡人 <span style={{ color:'#ef4444' }}>*</span></label>
                    <input placeholder="請輸入聯絡人姓名" value={adsForm.name}
                      onChange={e=>setAdsForm({...adsForm,name:e.target.value})}
                      style={adsErrs.name?{...iStyle,borderColor:'#fca5a5'}:iStyle}/>
                  </div>
                  {/* Company */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', alignItems:'center', gap:12 }}>
                    <label style={{ fontSize:13, color:'#374151', fontWeight:700 }}>公司名稱</label>
                    <input placeholder="請輸入公司名稱（選填）" value={adsForm.company}
                      onChange={e=>setAdsForm({...adsForm,company:e.target.value})} style={iStyle}/>
                  </div>
                  {/* Phone */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', alignItems:'center', gap:12 }}>
                    <label style={{ fontSize:13, color:'#374151', fontWeight:700 }}>電話</label>
                    <input placeholder="請輸入聯絡電話（選填）" value={adsForm.phone} type="tel"
                      onChange={e=>setAdsForm({...adsForm,phone:e.target.value})} style={iStyle}/>
                  </div>
                  {/* Email */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', alignItems:'center', gap:12 }}>
                    <label style={{ fontSize:13, color:'#374151', fontWeight:700 }}>電郵地址 <span style={{ color:'#ef4444' }}>*</span></label>
                    <input placeholder="請輸入電郵地址" value={adsForm.email} type="email"
                      onChange={e=>setAdsForm({...adsForm,email:e.target.value})}
                      style={adsErrs.email?{...iStyle,borderColor:'#fca5a5'}:iStyle}/>
                  </div>
                  {/* Message */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', alignItems:'flex-start', gap:12 }}>
                    <label style={{ fontSize:13, color:'#374151', fontWeight:700, paddingTop:10 }}>查詢詳情 <span style={{ color:'#ef4444' }}>*</span></label>
                    <textarea placeholder="請描述您的廣告需求、預算及投放時間..." value={adsForm.message}
                      onChange={e=>setAdsForm({...adsForm,message:e.target.value})} rows={5}
                      style={{ ...(adsErrs.message?{...iStyle,borderColor:'#fca5a5'}:iStyle), resize:'vertical', fontFamily:'inherit' }}/>
                  </div>
                  {/* Submit */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12 }}>
                    <div/>
                    {adsApiErr && <p style={{ fontSize:11, color:'#dc2626', margin:'0 0 4px' }}>{adsApiErr}</p>}
                    <button onClick={async()=>{
                      const e = validateAds();
                      setAdsErrs(e);
                      if (Object.keys(e).length) return;
                      setAdsLoading(true); setAdsApiErr('');
                      try {
                        const r = await fetch('/api/feedback', {
                          method:'POST', headers:{'Content-Type':'application/json'},
                          body: JSON.stringify({ type:'contact', name:adsForm.name.trim(), email:adsForm.email.trim(), company:adsForm.company.trim(), phone:adsForm.phone.trim(), message:adsForm.message.trim() }),
                        });
                        if (r.ok) { setAdsSent(true); }
                        else { setAdsApiErr('送出失敗，請稍後再試或直接電郵 partners@travel-ec.com'); }
                      } catch { setAdsApiErr('網絡錯誤，請稍後再試。'); }
                      setAdsLoading(false);
                    }} disabled={adsLoading} style={{ background:'#0f172a', color:'white', border:'none', borderRadius:12, padding:'13px', fontSize:13, fontWeight:900, cursor:adsLoading?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:adsLoading?0.7:1 }}>
                      {adsLoading ? <><Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/>送出中…</> : '送出查詢'}
                    </button>
                  </div>
                  <p style={{ fontSize:11, color:'#94a3b8', textAlign:'center', margin:0 }}>
                    標有 <span style={{ color:'#ef4444' }}>*</span> 為必填項目
                  </p>
                </div>
              )}
            </>)}
          </div>
        </div>
      </div>
    );
  };

  // ── ROOT ───────────────────────────────────────────────────────────────────
  const navItems = [
    {k:'home',ic:Globe,l:t.home},{k:'search',ic:Search,l:t.search},
    {k:'trips',ic:Calendar,l:t.trips},{k:'booking',ic:Ticket,l:t.booking},{k:'profile',ic:User,l:t.profile}
  ];

  return (
    <>
    <div style={{ minHeight:'100vh', background: darkMode ? '#0f172a' : '#f8fafc', color: darkMode ? '#e2e8f0' : '#0f172a', fontFamily:'system-ui,-apple-system,sans-serif', transition:'background 0.3s, color 0.3s', zoom: fontLarge ? 1.15 : 1 }}>
      <style>{`*{box-sizing:border-box;} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.45}} @keyframes spin{to{transform:rotate(360deg)}} @keyframes slideInLeft{from{transform:translateX(-100%)}to{transform:translateX(0)}} @keyframes fadeIn{from{opacity:0}to{opacity:1}} @media(max-width:640px){.klook-sidebar{display:none!important;}}`}</style>
      {/* Header */}
      <div style={{ position:'sticky',top:0,zIndex:50,background:'rgba(254,252,232,0.97)',backdropFilter:'blur(16px)',padding:'6px 18px',display:'grid',gridTemplateColumns:'1fr auto 1fr',alignItems:'center',borderBottom:'1px solid rgba(0,0,0,0.08)',boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
        {/* Hamburger button */}
        <button aria-label="開啟選單" onClick={()=>setMenuOpen(true)} style={{ background:'none',border:'none',color:'rgba(15,23,42,0.7)',cursor:'pointer',padding:'8px',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',transition:'background 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,0.05)'}
          onMouseLeave={e=>e.currentTarget.style.background='none'}>
          <Menu size={22}/>
        </button>
        <button aria-label="返回首頁" onClick={()=>setTab('home')} style={{ background:'none',border:'none',cursor:'pointer',display:'flex',justifyContent:'center',alignItems:'center' }}>
          <img src="/ec-travel-logo.svg" alt="EC Travel" width="272" height="88" style={{ height:88,width:'auto' }}/>
        </button>
        <div style={{ display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end' }}>
          <CompactDD icon={Languages} options={[{key:'zh-HK',label:'繁體'},{key:'zh-CN',label:'简体'},{key:'en',label:'EN'},{key:'ja',label:'日本語'},{key:'ko',label:'한국어'}]} value={lang} onChange={setLang}/>
          {!isMobile && <CompactDD
            options={CURRENCIES.map(c=>({ key:c.key, label:`${c.sym} ${c.key}`, sublabel:c.name }))}
            value={currency}
            onChange={setCurrency}/>}
          {!isMobile && <div style={{ display:'flex',alignItems:'center',background:'#f1f5f9',borderRadius:8,padding:'2px 4px',gap:1 }}>
            <button aria-label="標準字體" onClick={()=>setFontLarge(false)} title="標準字體"
              style={{ border:'none',cursor:'pointer',borderRadius:6,padding:'3px 6px',fontSize:11,fontWeight:900,background:!fontLarge?'white':'transparent',color:!fontLarge?'#f59e0b':'#94a3b8',boxShadow:!fontLarge?'0 1px 4px rgba(0,0,0,0.1)':'none',transition:'all 0.15s' }}>A</button>
            <button aria-label="大字體" onClick={()=>setFontLarge(true)} title="大字體"
              style={{ border:'none',cursor:'pointer',borderRadius:6,padding:'3px 6px',fontSize:14,fontWeight:900,background:fontLarge?'white':'transparent',color:fontLarge?'#f59e0b':'#94a3b8',boxShadow:fontLarge?'0 1px 4px rgba(0,0,0,0.1)':'none',transition:'all 0.15s' }}>A</button>
          </div>}
          {isLoggedIn
            ? <button aria-label="個人資料" onClick={()=>setTab('profile')} style={{ width:30,height:30,borderRadius:'50%',background:'#f59e0b',border:'none',fontWeight:900,color:'#0f172a',fontSize:11,cursor:'pointer' }}>
                {(user?.user_metadata?.full_name||user?.user_metadata?.name||user?.email||'?')[0].toUpperCase()}
              </button>
            : <button onClick={()=>setShowLogin(true)} style={{ background:'#f59e0b',color:'#0f172a',border:'none',borderRadius:10,padding:'7px 14px',fontWeight:900,fontSize:11,cursor:'pointer' }}>{t.login}</button>
          }
        </div>
      </div>
      {/* Content */}
      <main>
      {tab==='home'&&renderHome()}
      {tab==='search'&&renderSearch()}
      {tab==='trips'&&renderTrip()}
      {tab==='booking'&&renderBooking()}
      {tab==='profile'&&renderProfile()}
      {tab==='about'&&<AboutPage onBack={()=>setTab('home')}/>}
      {tab==='intel'&&renderIntelPage()}
      {tab==='compare'&&renderComparePage()}
      {tab==='deals'&&<React.Suspense fallback={null}><DealsPage lang={lang} dealsCat={dealsCat} setDealsCat={setDealsCat} onBack={()=>setTab('home')}/></React.Suspense>}
      {tab==='events'&&<React.Suspense fallback={null}><EventsPage lang={lang} onBack={()=>setTab('home')}/></React.Suspense>}
      {tab==='feedback'&&<FeedbackPage lang={lang} t={t} fbForm={fbForm} setFbForm={setFbForm} fbErrs={fbErrs} setFbErrs={setFbErrs} fbSent={fbSent} setFbSent={setFbSent} fbLoading={fbLoading} setFbLoading={setFbLoading} onBack={()=>setTab('home')}/>}
      {tab==='contact'&&<ContactPage onBack={()=>setTab('home')}/>}
      {tab==='enquiry'&&<EnquiryPage lang={lang} onBack={()=>setTab('home')}/>}
      </main>
      {tab==='admin'&&<React.Suspense fallback={null}><AdminPanel isOpen onClose={()=>setTab('home')}/></React.Suspense>}

      {/* ── Site Footer ── */}
      <footer style={{ background:'#0f172a', color:'rgba(255,255,255,0.55)', padding:'28px 24px 110px', marginTop:0 }}>
        <div style={{ maxWidth:980, margin:'0 auto' }}>
          {/* Logo row */}
          <div style={{ marginBottom:18 }}>
            <img src="/ec-travel-logo.svg" alt="EC Travel" width="161" height="52" style={{ height:52, width:'auto' }}/>
          </div>
          <p style={{ fontSize:12, lineHeight:1.7, margin:'0 0 20px', maxWidth:480 }}>
            {lang==='en' ? 'EC Travel is your smart travel companion — AI-powered search, itinerary planning and booking for every trip.'
              : lang==='ja' ? 'EC TravelはAIを活用した旅行検索・旅程計画・予約サービスで、あなたの旅をより便利に、より楽しくします。'
              : lang==='ko' ? 'EC Travel은 AI 기반 여행 검색, 일정 계획 및 예약 서비스로 여행을 더 쉽고 즐겁게 만들어 드립니다.'
              : lang==='zh-CN' ? 'EC Travel 是您的智能旅游助手，结合 AI 技术提供旅游搜索、行程规划及预订服务，让每一次旅行更轻松、更精彩。'
              : 'EC Travel 是您的智能旅遊助手，結合 AI 技術提供旅遊搜尋、行程規劃及預訂服務，讓每一次旅行更輕鬆、更精彩。'}
          </p>
          {/* Divider */}
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:18, display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            {/* Legal links */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:4, alignItems:'center' }}>
              {[
                { label: t.termsTitle||'使用條款', key:'terms' },
                { label: t.privacyTitle||'私隱政策', key:'privacy' },
                { label: t.adsTitle||'廣告查詢', key:'ads' },
                { label: t.menuFeedback||'用戶意見', key:'feedback' },
              ].map((item, i, arr) => (
                <React.Fragment key={item.key}>
                  <button onClick={()=>item.key==='feedback' ? setTab('feedback') : item.key==='ads' ? setTab('enquiry') : openLegalPage(item.key)}
                    style={{ background:'none', border:'none', color:'rgba(255,255,255,0.6)', fontSize:12, fontWeight:700, cursor:'pointer', padding:'4px 6px', borderRadius:6, transition:'color 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.color='#f59e0b'}
                    onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.6)'}>
                    {item.label}
                  </button>
                  {i < arr.length-1 && <span style={{ color:'rgba(255,255,255,0.2)', fontSize:12 }}>·</span>}
                </React.Fragment>
              ))}
            </div>
            {/* Copyright + Version */}
            <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'6px 16px' }}>
              <p style={{ fontSize:11, color:'rgba(255,255,255,0.6)', margin:0 }}>
                {lang==='ja' ? 'Copyright © 2026 EC Travel. 無断転載禁止。'
                  : lang==='ko' ? 'Copyright © 2026 EC Travel. 무단 전재 금지.'
                  : 'Copyright © 2026 EC Travel. All rights reserved.'}
              </p>
              <p style={{ fontSize:10, color:'rgba(255,255,255,0.45)', margin:0, fontFamily:'monospace', letterSpacing:0.5 }}>
                {APP_VERSION}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom nav */}
      <div style={{ position:'fixed',bottom:16,left:'50%',transform:'translateX(-50%)',background:'rgba(15,23,42,0.97)',backdropFilter:'blur(16px)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:28,padding:'10px 20px',display:'flex',gap:22,zIndex:50,boxShadow:'0 20px 50px rgba(0,0,0,0.25)' }}>
        {navItems.map(({k,ic:Ic,l})=>(
          <button key={k} onClick={()=>setTab(k)} style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:3,background:'none',border:'none',cursor:'pointer',color:tab===k?'#f59e0b':'rgba(255,255,255,0.65)',transform:tab===k?'scale(1.1)':'scale(1)',transition:'all 0.2s' }}>
            <Ic size={19}/><span style={{ fontSize:8,fontWeight:900,textTransform:'uppercase',letterSpacing:0.8 }}>{l}</span>
          </button>
        ))}
      </div>
      {googleGate&&!isLoggedIn&&<GuestGoogleGate reason={googleGate} lang={lang}
        onClose={()=>{ pendingSaveAfterLoginRef.current = false; setGoogleGate(null); }}
        onLogin={()=>{ trackEvent('login_completed', `guest_gate_${googleGate}`); setGoogleGate(null); }}
        onEmailLogin={()=>{ pendingSaveAfterLoginRef.current = false; setGoogleGate(null); openLogin('trips'); }}/>}
      {showTripGuide&&<TripGuideModal lang={lang} onClose={()=>setShowTripGuide(false)}/>}
      {showLogin&&<AuthModal show t={t} lang={lang} onClose={()=>setShowLogin(false)} onLogin={()=>{trackEvent('login_completed', loginReturnTab||null);setShowLogin(false);setTab(loginReturnTab||'profile');setLoginReturnTab(null);}}/>}
      {renderLegalModal()}

      {/* Share toast — generic confirmation for copy-link actions */}
      {shareToast && (
        <div style={{ position:'fixed', bottom:90, left:'50%', transform:'translateX(-50%)', background:'#f0fdf4', border:'1.5px solid #bbf7d0', borderRadius:14, padding:'12px 20px', fontSize:12, fontWeight:700, color:'#16a34a', zIndex:9999, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap' }}>
          {shareToast}
        </div>
      )}

      {/* Affiliate reward toast — appears silently when reward is earned */}
      {affToast && (
        <div style={{ position:'fixed', bottom:90, left:'50%', transform:'translateX(-50%)', background:'#fffbeb', border:'1.5px solid #fde68a', borderRadius:14, padding:'12px 20px', fontSize:12, fontWeight:700, color:'#92400e', zIndex:9999, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap' }}>
          🎁 已解鎖特別禮遇 — 可規劃一次最多 {AFFILIATE_MAX_DAYS} 天行程！
        </div>
      )}

      {/* ── Rating Popup Modal ── */}
      {showRatingPopup && !tripRating && (()=>{
        const dest = trDestinations.filter(d=>d.city).map(d=>d.city).join(' & ') || trDestinations[0]?.country || '';
        const days = itinerary?.days?.length || 0;
        const labels = {
          'zh-HK':{ title:'行程規劃完成！', sub:`您對這次 ${dest} ${days} 天 AI 行程有幾滿意？`, ph:'有什麼想告訴我們？（可略過）', submit:'遞交評分', skip:'略過', s1:'很差', s2:'一般', s3:'還可以', s4:'很好', s5:'非常滿意！' },
          'zh-CN':{ title:'行程规划完成！', sub:`您对这次 ${dest} ${days} 天 AI 行程有多满意？`, ph:'有什么想告诉我们？（可略过）', submit:'递交评分', skip:'略过', s1:'很差', s2:'一般', s3:'还可以', s4:'很好', s5:'非常满意！' },
          'en':   { title:'Itinerary Ready!', sub:`How satisfied are you with this ${days}-day ${dest} AI itinerary?`, ph:'Any comments? (optional)', submit:'Submit', skip:'Skip', s1:'Poor', s2:'Fair', s3:'Good', s4:'Very Good', s5:'Excellent!' },
          'ja':   { title:'旅程が完成しました！', sub:`${dest} ${days}日間の旅程はいかがでしたか？`, ph:'ご意見があればどうぞ（任意）', submit:'評価を送る', skip:'スキップ', s1:'残念', s2:'普通', s3:'まずまず', s4:'良い', s5:'最高！' },
          'ko':   { title:'여행 일정 완성!', sub:`${dest} ${days}일 AI 일정이 마음에 드셨나요?`, ph:'의견이 있으시면 남겨주세요 (선택)', submit:'평가 제출', skip:'건너뛰기', s1:'별로', s2:'보통', s3:'괜찮아요', s4:'좋아요', s5:'최고!' },
        };
        const c = labels[lang] || labels['en'];
        const starLabels = [c.s1, c.s2, c.s3, c.s4, c.s5];
        return (
          <div style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
            onClick={e=>{ if(e.target===e.currentTarget){ setRatingDismissed(true); setShowRatingPopup(false); } }}>
            <div style={{ background:'white', borderRadius:28, padding:'28px 24px 24px', maxWidth:360, width:'100%', textAlign:'center', position:'relative', boxShadow:'0 32px 80px rgba(0,0,0,0.3)', animation:'fadeIn 0.25s ease' }}>
              {/* Close */}
              <button onClick={()=>{ setRatingDismissed(true); setShowRatingPopup(false); }}
                style={{ position:'absolute', top:14, right:16, background:'none', border:'none', fontSize:20, color:'#94a3b8', cursor:'pointer', lineHeight:1 }}>✕</button>
              {/* Icon */}
              <div style={{ fontSize:44, marginBottom:10 }}>🎉</div>
              <p style={{ fontSize:17, fontWeight:900, color:'#0f172a', margin:'0 0 6px' }}>{c.title}</p>
              <p style={{ fontSize:12, color:'#64748b', margin:'0 0 22px', lineHeight:1.5 }}>{c.sub}</p>
              {/* 5 Stars */}
              <div style={{ display:'flex', justifyContent:'center', gap:6, marginBottom:6 }}>
                {[1,2,3,4,5].map(n=>(
                  <button key={n} onClick={()=>setTripRating(n)}
                    onMouseEnter={e=>{ for(let i=1;i<=5;i++){ const b=e.currentTarget.parentNode.children[i-1]; b.style.color=i<=n?'#f59e0b':'#e2e8f0'; b.style.transform=i<=n?'scale(1.15)':'scale(1)'; } }}
                    onMouseLeave={e=>{ for(let i=1;i<=5;i++){ const b=e.currentTarget.parentNode.children[i-1]; b.style.color='#e2e8f0'; b.style.transform='scale(1)'; } }}
                    style={{ background:'none', border:'none', fontSize:40, cursor:'pointer', color:'#e2e8f0', transition:'all 0.12s', lineHeight:1 }}>★</button>
                ))}
              </div>
              <p style={{ fontSize:11, color:'#94a3b8', margin:'0 0 18px', minHeight:16 }}>{starLabels[0]}{'　'}{'　'}{'　'}{'　'}{starLabels[4]}</p>
              {/* Skip */}
              <button onClick={()=>{ setRatingDismissed(true); setShowRatingPopup(false); }}
                style={{ background:'none', border:'none', fontSize:12, color:'#94a3b8', cursor:'pointer', fontWeight:700 }}>{c.skip}</button>
            </div>
          </div>
        );
      })()}

      {/* Rating comment popup — shows after star selected */}
      {showRatingPopup && tripRating && (()=>{
        const labels = {
          'zh-HK':{ title:`您給了 ${'★'.repeat(tripRating)}${'☆'.repeat(5-tripRating)}`, sub:'想分享更多嗎？', ph:'有什麼想告訴我們？（可略過）', submit:'完成', s5:'非常滿意！', s4:'相當不錯！', s3:'一般般', s2:'有點失望', s1:'需要改進' },
          'zh-CN':{ title:`您给了 ${'★'.repeat(tripRating)}${'☆'.repeat(5-tripRating)}`, sub:'想分享更多吗？', ph:'有什么想告诉我们？（可略过）', submit:'完成', s5:'非常满意！', s4:'相当不错！', s3:'一般般', s2:'有点失望', s1:'需要改进' },
          'en':   { title:`You rated ${'★'.repeat(tripRating)}${'☆'.repeat(5-tripRating)}`, sub:'Want to share more?', ph:'Any comments? (optional)', submit:'Done', s5:'Excellent!', s4:'Very Good!', s3:'Average', s2:'Disappointing', s1:'Needs Improvement' },
          'ja':   { title:`${'★'.repeat(tripRating)}${'☆'.repeat(5-tripRating)} を選択しました`, sub:'もう少し教えていただけますか？', ph:'ご意見があればどうぞ（任意）', submit:'完了', s5:'最高！', s4:'良かった！', s3:'普通', s2:'残念', s1:'改善が必要' },
          'ko':   { title:`${'★'.repeat(tripRating)}${'☆'.repeat(5-tripRating)} 선택하셨습니다`, sub:'더 나누고 싶은 이야기가 있나요?', ph:'의견이 있으시면 남겨주세요 (선택)', submit:'완료', s5:'최고!', s4:'좋아요!', s3:'보통', s2:'아쉬워요', s1:'개선 필요' },
        };
        const c = labels[lang] || labels['en'];
        const mood = tripRating>=5?c.s5:tripRating>=4?c.s4:tripRating>=3?c.s3:tripRating>=2?c.s2:c.s1;
        const moodEmoji = tripRating>=5?'🌟':tripRating>=4?'😊':tripRating>=3?'😐':tripRating>=2?'😕':'😞';
        return (
          <div style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
            <div style={{ background:'white', borderRadius:28, padding:'28px 24px 24px', maxWidth:360, width:'100%', textAlign:'center', position:'relative', boxShadow:'0 32px 80px rgba(0,0,0,0.3)', animation:'fadeIn 0.2s ease' }}>
              <div style={{ fontSize:40, marginBottom:8 }}>{moodEmoji}</div>
              <p style={{ fontSize:18, fontWeight:900, color:'#f59e0b', margin:'0 0 4px', letterSpacing:2 }}>{c.title}</p>
              <p style={{ fontSize:13, fontWeight:700, color:'#0f172a', margin:'0 0 16px' }}>{mood}</p>
              <textarea value={tripRatingComment} onChange={e=>setTripRatingComment(e.target.value)}
                placeholder={c.ph} maxLength={300}
                style={{ width:'100%', borderRadius:14, border:'1.5px solid #e2e8f0', padding:'10px 14px', fontSize:13, color:'#0f172a', resize:'none', height:90, outline:'none', fontFamily:'inherit', boxSizing:'border-box', marginBottom:14 }}/>
              <button onClick={()=>{ setShowRatingPopup(false); setRatingDismissed(true); }}
                style={{ width:'100%', background:'#0f172a', color:'white', border:'none', borderRadius:14, padding:'13px', fontSize:14, fontWeight:900, cursor:'pointer' }}>{c.submit}</button>
            </div>
          </div>
        );
      })()}

      {/* ── Sponsor Card Modal (itinerary unlock) ── */}
      {showSponsor && (() => {
        const cityKey  = trDestinations.find(d=>d.city)?.city || '';
        const cityDisp = cityKey || '目的地';
        const cityEn   = CITY_EN[cityKey] || cityKey;
        const klookUrl = `https://www.klook.com/en-HK/city/${KLOOK_CITY[cityEn]||'1-hong-kong'}/?aid=${KLOOK_AID}`;
        const tripUrl  = `https://www.trip.com/travel-guide/${cityEn.toLowerCase().replace(/ /g,'-')}/?Allianceid=${TRIP_AID}&SID=${TRIP_SID}`;
        return (
          <div style={{ position:'fixed', inset:0, zIndex:300, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'white', borderRadius:24, width:'100%', maxWidth:440, overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.4)' }}>
              {/* Header */}
              <div style={{ background:'linear-gradient(135deg,#f59e0b,#ef4444)', padding:'20px 20px 16px', textAlign:'center' }}>
                <p style={{ margin:'0 0 4px', fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.8)' }}>📢 贊助商訊息 · 觀看後解鎖行程生成</p>
                <p style={{ margin:0, fontSize:20, fontWeight:900, color:'white' }}>{cityDisp} 旅遊優惠</p>
              </div>
              {/* Countdown bar */}
              <div style={{ height:4, background:'#e2e8f0' }}>
                <div style={{ height:'100%', background:'#f59e0b', width:`${(sponsorCountdown/10)*100}%`, transition:'width 1s linear' }}/>
              </div>
              {/* Sponsor cards */}
              <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  { name:'Klook', color:'#ff5a00', bg:'linear-gradient(135deg,#ff5a00,#ff8c42)', desc:`${cityDisp} 景點門票 · 即買即用`, badge:'最受歡迎', url:klookUrl },
                  { name:'Trip.com', color:'#1677ff', bg:'linear-gradient(135deg,#1677ff,#40a9ff)', desc:`${cityDisp} 酒店 · 機票 · 最低價保證`, badge:'即時確認', url:tripUrl },
                ].map(p=>(
                  <a key={p.name} href={p.url} target="_blank" rel="sponsored noopener noreferrer"
                    style={{ textDecoration:'none', borderRadius:14, overflow:'hidden', border:'1.5px solid #e2e8f0', display:'block', transition:'transform 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
                    onMouseLeave={e=>e.currentTarget.style.transform='none'}>
                    <div style={{ background:p.bg, padding:'8px 14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <p style={{ margin:0, fontSize:15, fontWeight:900, color:'white' }}>{p.name}</p>
                      <span style={{ background:'rgba(255,255,255,0.25)', color:'white', fontSize:9, fontWeight:900, padding:'2px 8px', borderRadius:99 }}>{p.badge}</span>
                    </div>
                    <div style={{ padding:'10px 14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <p style={{ margin:0, fontSize:12, color:'#475569' }}>{p.desc}</p>
                      <span style={{ background:p.color, color:'white', padding:'5px 12px', borderRadius:8, fontSize:11, fontWeight:900, whiteSpace:'nowrap', marginLeft:8 }}>立即查看 →</span>
                    </div>
                  </a>
                ))}
                <p style={{ margin:0, fontSize:11, color:'#94a3b8', textAlign:'center' }}>
                  {sponsorDone ? '✅ 感謝觀看！現在可以解鎖生成行程' : `⏳ 請稍候 ${sponsorCountdown} 秒…`}
                </p>
                <button onClick={onSponsorUnlock} disabled={!sponsorDone}
                  style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', background: sponsorDone ? '#0f172a':'#e2e8f0', color: sponsorDone ? 'white':'#94a3b8', fontWeight:900, fontSize:14, cursor: sponsorDone ? 'pointer':'not-allowed', transition:'all 0.3s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  <Sparkles size={16}/> {sponsorDone ? '解鎖 · 生成行程' : `等待 ${sponsorCountdown} 秒…`}
                </button>
                <button onClick={()=>{ setShowSponsor(false); setPendingGen(false); }}
                  style={{ background:'none', border:'none', color:'#94a3b8', fontSize:11, cursor:'pointer', padding:'4px 0' }}>
                  取消
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── AdSense Ad Slots (activate after approval) ── */}
      {/* Replace ca-pub-XXXXXXXXXX with your actual AdSense publisher ID */}
      {/* <ins className="adsbygoogle" style={{display:'block'}} data-ad-client="ca-pub-XXXXXXXXXX" data-ad-slot="XXXXXXXXXX" data-ad-format="auto" data-full-width-responsive="true"/> */}


      {/* ── Cookie Consent Banner ── */}
      {cookieConsent === null && (
        <div style={{ position:'fixed', bottom:90, left:'50%', transform:'translateX(-50%)', width:'calc(100% - 32px)', maxWidth:680, zIndex:200, animation:'fadeIn 0.4s ease' }}>
          <div style={{ background:'#0f172a', borderRadius:20, padding:'18px 20px', boxShadow:'0 8px 40px rgba(0,0,0,0.35)', border:'1px solid rgba(255,255,255,0.08)', display:'flex', flexDirection:'column', gap:14 }}>
            {/* Icon + title row */}
            <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
              <span style={{ fontSize:26, flexShrink:0, marginTop:2 }}>🍪</span>
              <div>
                <p style={{ margin:'0 0 4px', fontSize:14, fontWeight:900, color:'#fff' }}>
                  {(lang==='en'||lang==='ja'||lang==='ko') ? 'We use cookies' : lang==='zh-CN' ? '我们使用 Cookies' : '我們使用 Cookies'}
                </p>
                <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.6)', lineHeight:1.65 }}>
                  {(lang==='en'||lang==='ja'||lang==='ko')
                    ? 'EC Travel uses cookies to personalise content, analyse traffic, and serve relevant ads via our advertising partners (Klook, Trip.com, KKday, Agoda). You can accept all cookies or use essential cookies only.'
                    : lang==='zh-CN'
                    ? 'EC Travel 使用 Cookies 来个性化内容、分析流量，并通过我们的广告合作伙伴（Klook、Trip.com、KKday、Agoda）提供相关广告。您可以接受所有 Cookies，或仅使用必要 Cookies。'
                    : 'EC Travel 使用 Cookies 來個人化內容、分析流量，並透過我們的廣告合作夥伴（Klook、Trip.com、KKday、Agoda）提供相關廣告。您可以接受全部 Cookies，或僅使用必要 Cookies。'}
                </p>
              </div>
            </div>
            {/* Buttons */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button onClick={acceptAllCookies}
                style={{ flex:1, minWidth:140, padding:'11px 16px', borderRadius:12, border:'none', background:'#f59e0b', color:'#0f172a', fontSize:13, fontWeight:900, cursor:'pointer', transition:'background 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.background='#fbbf24'}
                onMouseLeave={e=>e.currentTarget.style.background='#f59e0b'}>
                {(lang==='en'||lang==='ja'||lang==='ko') ? '✓ Accept All Cookies' : lang==='zh-CN' ? '✓ 接受所有 Cookies' : '✓ 接受所有 Cookies'}
              </button>
              <button onClick={essentialOnly}
                style={{ flex:1, minWidth:120, padding:'11px 16px', borderRadius:12, border:'1.5px solid rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:700, cursor:'pointer', transition:'all 0.15s' }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,0.5)'; e.currentTarget.style.color='#fff'; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,0.2)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
                {(lang==='en'||lang==='ja'||lang==='ko') ? 'Essential Only' : lang==='zh-CN' ? '仅必要 Cookies' : '僅必要 Cookies'}
              </button>
              <button onClick={()=>openLegalPage('privacy')}
                style={{ padding:'11px 14px', borderRadius:12, border:'none', background:'transparent', color:'rgba(255,255,255,0.35)', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
                onMouseEnter={e=>e.currentTarget.style.color='rgba(255,255,255,0.7)'}
                onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.35)'}>
                {t.privacyTitle||'私隱政策'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer overlay */}
      {menuOpen&&(
        <div onClick={()=>setMenuOpen(false)} style={{ position:'fixed',inset:0,zIndex:300,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(3px)',animation:'fadeIn 0.2s ease' }}>
          {/* Drawer panel */}
          <div onClick={e=>e.stopPropagation()} style={{ position:'absolute',top:0,left:0,bottom:0,width:300,background:'#ffffff',display:'flex',flexDirection:'column',animation:'slideInLeft 0.28s cubic-bezier(0.32,0.72,0,1)',boxShadow:'12px 0 48px rgba(0,0,0,0.15)' }}>
            {/* Drawer header */}
            <div style={{ padding:'20px 20px 16px',borderBottom:'1px solid rgba(0,0,0,0.07)',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <img src="/ec-travel-logo.svg" alt="EC Travel" width="161" height="52" style={{ height:52,width:'auto' }}/>
              <button aria-label="關閉選單" onClick={()=>setMenuOpen(false)} style={{ background:'rgba(0,0,0,0.06)',border:'none',color:'#64748b',borderRadius:50,width:34,height:34,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                <X size={16}/>
              </button>
            </div>
            {/* Menu items */}
            <nav style={{ flex:1,padding:'8px 0',overflowY:'auto' }}>
              {[
                { key:'about',   label:t.menuAbout,   icon:Globe,    sub:t.menuAboutSub },
                { key:'intel',   label:t.menuIntel,   icon:MapPin,   sub:t.menuIntelSub },
                { key:'deals',   label:t.menuDeals,   icon:Sparkles, sub:t.menuDealsSub },
                { key:'events',  label:t.menuEvents,  icon:Ticket,   sub:t.menuEventsSub },
                { key:'contact', label:t.menuContact, icon:Mail,     sub:t.menuContactSub },
              ].map(item=>(
                <button key={item.key} onClick={()=>{ setTab(item.key); setMenuOpen(false); }}
                  style={{ display:'flex',alignItems:'center',gap:14,width:'100%',padding:'14px 20px',background:'none',border:'none',cursor:'pointer',textAlign:'left',transition:'background 0.15s',borderBottom:'1px solid rgba(0,0,0,0.05)' }}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,0.04)'}
                  onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  {/* Icon badge */}
                  <div style={{ width:42,height:42,borderRadius:12,background:'rgba(245,158,11,0.12)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                    <item.icon size={19} color='#f59e0b'/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ color:'#0f172a',fontSize:14,fontWeight:700,lineHeight:1.3 }}>{item.label}</div>
                  </div>
                  <ChevronRight size={15} color='#cbd5e1'/>
                </button>
              ))}
            </nav>
            {/* Drawer footer */}
            <div style={{ padding:'16px 20px',borderTop:'1px solid rgba(0,0,0,0.07)' }}>
              <p style={{ margin:0,fontSize:10,color:'#94a3b8',fontFamily:'monospace',letterSpacing:0.5 }}>EC Travel {APP_VERSION}</p>
            </div>
          </div>
        </div>
      )}
    </div>
    {/* ── Attraction Detail Bottom Sheet ── */}
    {selAttraction && (() => {
      const ck = selAttraction.cityKey || '';
      const cacheKey = `${ck}_${selAttraction.name}_${lang}`;
      const detail = attrDetail[cacheKey];
      const catEmoji = { culture:'🏛️', play:'🎡', nature:'🌿', shopping:'🛍️', food:'🍜' };
      return (
        <>
          <div onClick={()=>setSelAttraction(null)}
            style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }}/>
          <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:501, background:'white', borderRadius:'24px 24px 0 0', maxHeight:'82vh', overflowY:'auto', boxShadow:'0 -8px 40px rgba(0,0,0,0.18)', paddingBottom:40 }}>
            {/* Handle */}
            <div style={{ display:'flex', justifyContent:'center', padding:'12px 0 0' }}>
              <div style={{ width:40, height:4, borderRadius:99, background:'#e2e8f0' }}/>
            </div>
            {/* Hero */}
            <div style={{ position:'relative', height:200, overflow:'hidden', margin:'12px 0 0' }}>
              {selAttraction.imageUrl
                ? <img src={selAttraction.imageUrl} alt={selAttraction.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : <ArticlePhoto key={`${ck}_${selAttraction.name}`} keyword={selAttraction.photoKw||selAttraction.name} destination={ck}
                    style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
              }
              <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top,rgba(0,0,0,0.65) 0%,transparent 55%)' }}/>
              <button aria-label="關閉" onClick={()=>setSelAttraction(null)}
                style={{ position:'absolute', top:12, right:12, width:32, height:32, borderRadius:'50%', background:'rgba(0,0,0,0.4)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <X size={16} color="white"/>
              </button>
              <div style={{ position:'absolute', bottom:14, left:16, right:16 }}>
                <p style={{ margin:'0 0 4px', fontSize:11, color:'rgba(255,255,255,0.7)', fontWeight:700, textTransform:'uppercase', letterSpacing:1 }}>
                  {catEmoji[selAttraction.category]||'📍'} {ck}
                </p>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <p style={{ margin:0, fontSize:20, fontWeight:900, color:'white', lineHeight:1.2 }}>{selAttraction.name}</p>
                  <a href={selAttraction.lat!=null && selAttraction.lng!=null
                      ? `https://www.google.com/maps/dir/?api=1&destination=${selAttraction.lat},${selAttraction.lng}`
                      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${selAttraction.name} ${ck}`)}`}
                    target="_blank" rel="noopener noreferrer" title={lang==='en'?'Navigate':'導航前往'}
                    onClick={e=>e.stopPropagation()}
                    style={{ flexShrink:0, width:26, height:26, borderRadius:'50%', background:'rgba(255,255,255,0.22)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Navigation2 size={13} color="white"/>
                  </a>
                </div>
              </div>
            </div>
            {/* Body */}
            <div style={{ padding:'20px 20px 0' }}>
              {(!detail || detail._loading) ? (
                <div style={{ textAlign:'center', padding:'32px 0' }}>
                  <Loader2 size={28} color="#f59e0b" style={{ animation:'spin 1s linear infinite' }}/>
                  <p style={{ color:'#94a3b8', fontSize:13, marginTop:10 }}>
                    {lang==='en'?'Loading details…':lang==='ja'?'詳細を読み込み中…':lang==='ko'?'상세 정보 로딩 중…':'載入景點資料中…'}
                  </p>
                </div>
              ) : detail ? (
                <>
                  {/* Intro */}
                  {detail.intro && (
                    <p style={{ fontSize:14, lineHeight:1.85, color:'#334155', margin:'0 0 20px' }}>{translatedSelAttrIntro || detail.intro}</p>
                  )}
                  {/* Suggested itinerary/route/transport — 深度體驗 wilderness picks only */}
                  {detail.route && (
                    <div style={{ marginBottom:20, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:12, padding:'12px 14px' }}>
                      <p style={{ margin:'0 0 6px', fontSize:12, fontWeight:900, color:'#14532d' }}>
                        🚶 {lang==='en'?'Suggested Route':lang==='ja'?'おすすめルート':lang==='ko'?'추천 경로':'建議路線'}
                      </p>
                      <p style={{ margin:0, fontSize:13, color:'#166534', lineHeight:1.75 }}>{detail.route}</p>
                    </div>
                  )}
                  {/* Best for + Duration row */}
                  <div style={{ display:'flex', gap:10, marginBottom:20 }}>
                    {detail.bestFor && (
                      <div style={{ flex:1, background:'#fef3c7', borderRadius:12, padding:'10px 14px' }}>
                        <p style={{ margin:'0 0 2px', fontSize:10, fontWeight:900, color:'#92400e', textTransform:'uppercase', letterSpacing:0.8 }}>
                          {lang==='en'?'Best For':lang==='ja'?'おすすめの人':lang==='ko'?'추천 대상':'最適合'}
                        </p>
                        <p style={{ margin:0, fontSize:13, fontWeight:700, color:'#78350f' }}>{detail.bestFor}</p>
                      </div>
                    )}
                    {detail.duration && (
                      <div style={{ flex:1, background:'#f0fdf4', borderRadius:12, padding:'10px 14px' }}>
                        <p style={{ margin:'0 0 2px', fontSize:10, fontWeight:900, color:'#14532d', textTransform:'uppercase', letterSpacing:0.8 }}>
                          {lang==='en'?'Duration':lang==='ja'?'所要時間':lang==='ko'?'소요 시간':'建議時間'}
                        </p>
                        <p style={{ margin:0, fontSize:13, fontWeight:700, color:'#15803d' }}>{detail.duration}</p>
                      </div>
                    )}
                  </div>
                  {/* Highlights */}
                  {detail.highlights?.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                      <p style={{ margin:'0 0 10px', fontSize:13, fontWeight:900, color:'#0f172a' }}>
                        {lang==='en'?'✨ Highlights':lang==='ja'?'✨ 見どころ':lang==='ko'?'✨ 볼거리':'✨ 好玩之處'}
                      </p>
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        {detail.highlights.map((h,i)=>(
                          <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', background:'#f8fafc', borderRadius:12, padding:'10px 14px' }}>
                            <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{['🎯','🎪','🌟','✨','💫'][i]||'•'}</span>
                            <p style={{ margin:0, fontSize:13, color:'#334155', lineHeight:1.65 }}>{h}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Tips */}
                  {detail.tips?.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                      <p style={{ margin:'0 0 10px', fontSize:13, fontWeight:900, color:'#0f172a' }}>
                        {lang==='en'?'💡 Tips':lang==='ja'?'💡 旅のヒント':lang==='ko'?'💡 유용한 팁':'💡 實用貼士'}
                      </p>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {detail.tips.map((tip,i)=>(
                          <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                            <span style={{ color:'#f59e0b', fontWeight:900, fontSize:13, flexShrink:0 }}>{i+1}.</span>
                            <p style={{ margin:0, fontSize:13, color:'#475569', lineHeight:1.65 }}>{tip}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
              {/* 位置細地圖：只喺有資料庫座標時顯示，附近已核實地區淡色作參考 */}
              {selAttraction.lat != null && selAttraction.lng != null && (
                <div style={{ marginBottom:20 }}>
                  <p style={{ margin:'0 0 10px', fontSize:13, fontWeight:900, color:'#0f172a' }}>
                    📍 {lang==='en'?'Location':lang==='ja'?'場所':lang==='ko'?'위치':'位置'}
                  </p>
                  <SpotMiniMap key={`${selAttraction.lat},${selAttraction.lng}`} lat={selAttraction.lat} lng={selAttraction.lng} name={selAttraction.name}
                    pins={getDistrictPins(CITY_EN_MAP[ck] || ck)} lang={lang} labelOf={(d)=>districtLabels[d]?.[lang] || d}/>
                </div>
              )}
            </div>
          </div>
        </>
      );
    })()}

    {/* Floating social links */}
    <div style={{ position:'fixed', bottom:92, right:16, zIndex:60, display:'flex', flexDirection:'column', gap:8 }}>
      {[
        { key:'facebook', label:'Facebook', bg:'#1877F2', url:SOCIAL_LINKS.facebook, path:'M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z' },
        { key:'instagram', label:'Instagram', bg:'linear-gradient(45deg,#feda75 0%,#fa7e1e 25%,#d62976 50%,#962fbf 75%,#4f5bd5 100%)', url:SOCIAL_LINKS.instagram, path:'M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2zm-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6A3.6 3.6 0 0 0 16.4 4H7.6zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm5.25-3.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z' },
      ].filter(s => s.url).map(s => (
        <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label}
          style={{ width:40, height:40, borderRadius:'50%', background:s.bg, boxShadow:'0 4px 14px rgba(0,0,0,0.25)', display:'flex', alignItems:'center', justifyContent:'center', transition:'transform 0.2s' }}
          onMouseEnter={e=>e.currentTarget.style.transform='scale(1.1)'}
          onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d={s.path}/></svg>
        </a>
      ))}
    </div>

    <button onClick={()=>setChatOpen(o=>!o)} title="EC 旅遊小助手"
      style={{ position:'fixed',top:100,right:16,zIndex:9999,width:44,height:44,borderRadius:'50%',background:chatOpen?'#d97706':'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,boxShadow:'0 4px 16px rgba(245,158,11,0.5)',transition:'transform 0.2s' }}
      onMouseEnter={e=>e.currentTarget.style.transform='scale(1.1)'}
      onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
      {chatOpen ? '✕' : '✈️'}
    </button>
    <TravelChatbot open={chatOpen} onToggle={()=>setChatOpen(o=>!o)} lang={lang}/>
    </>
  );
}
